// MSNA Time Tracker v55: score card and Partners' one-on-one discussion are collapsible (choice remembered per browser); the discussion box sits between the score card and the engagement list in each quarter of Performance Appraisal (one per staff per quarter, date + notes, any partner can edit with history kept; confidential: only partners and that staff member can read it, enforced by Firestore rules). v54: goal points count from the staff member's submitted self-assessment (Provisional), and become Final once a partner approves; a rejected entry drops out until resubmitted. v53: Overall quarterly score in Performance Appraisal = appraisal average × 70% + goals 30% (achieved and partner-approved goals only), with a "how it's arrived at" panel. From Q3 FY 2026-27 managers set 3 mandatory goals (10% each) and interns 2 (15% each, optional goal 3 not scored); managers who submitted 2 goals in Q3 add goal 3. v52: Engagement Letter gets a "Not applicable" answer (new and older codes; no EL flag, signed EL check skipped at closure). v51: Assigned Work takes several managers per item, all equal. v50: older codes confirm "Signed EL received" instead of draft; EL saves are field-level; failed saves now show a notice. v49: Assigned Work (firm work given by partners to managers, accept/reopen). v48: Engagement Letter tracking (draft EL asked at code creation, EL pending flags, signed EL check at closure). v47: fixes v46 Update button (openDetail called itself). v46: latest update at top of drawer, update line and "New" pills in lists.
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, terminate, clearIndexedDbPersistence, waitForPendingWrites, collection, doc, onSnapshot, setDoc, updateDoc, deleteDoc, getDocs, query, where } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider, createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";

// ── FIREBASE CONFIG ──
const firebaseConfig = {
  apiKey: "AIzaSyDJCeqOJl1EGFj7NcGCGn47OL51ipo2GeE",
  authDomain: "msna-time-tracker.firebaseapp.com",
  projectId: "msna-time-tracker",
  storageBucket: "msna-time-tracker.firebasestorage.app",
  messagingSenderId: "839069130181",
  appId: "1:839069130181:web:d8b6873bfedf3d42603fac",
  measurementId: "G-Z9ZCXNGYRC"
};
const firebaseApp = initializeApp(firebaseConfig);
// v35: keep a local copy of the data in the browser (shared by all open tabs).
// A reload or a second tab then only fetches what changed, instead of re-reading every document.
// The copy is wiped on Sign Out (see clearLocalDataAndReload).
const db = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
const auth = getAuth(firebaseApp);
// Second, independent Auth instance used ONLY to create new staff accounts,
// so the partner creating the account stays signed in as themselves.
const secondaryAuth = getAuth(initializeApp(firebaseConfig, "secondary"));

// ── CONSTANTS ──
const ADMIN_EMAIL = "nitesh@msna.co.in";
const MAX_BACKDATE_DAYS = 30;
const ENGAGEMENT_CATEGORIES = ["Assurance","Virtual CFO","Compliance","Consulting"];
const INTERNAL_CATEGORIES = ["Leave","Holiday","Idle","Reading"];
// eslint-disable-next-line no-unused-vars
const TASK_CATEGORIES = [...ENGAGEMENT_CATEGORIES,...INTERNAL_CATEGORIES];

// ── PERFORMANCE APPRAISAL ──
// Prospective only: only engagement hours booked on/after this date create an appraisal requirement.
const APPRAISAL_START_DATE = "2026-07-01"; // Q2 of FY 2026-27
const APPRAISAL_METRICS = [
  { key:"communication",      label:"Client communication skills",   traits:"Listening, questioning, fluency, telephonic conversation, email conversation" },
  { key:"engagementMgmt",     label:"Engagement management skills",  traits:"Time management, efficiency, persuasion skills, rapport with client" },
  { key:"execution",          label:"Execution skills",              traits:"Planning, reading, research work, usage of AI, business understanding" },
  { key:"personality",        label:"Overall personality",           traits:"Confidence, go-getter ability, accountability, willingness to learn, persistent" },
  { key:"clientSatisfaction", label:"Client satisfaction",           traits:"Based on client feedback on the overall engagement" },
];

// ── FIRESTORE HELPERS ──
// v50: a refused or failed save is announced to the app (see the notice in App), instead of failing silently.
const reportWriteError = (col, e) => {
  try { window.dispatchEvent(new window.CustomEvent("msna-write-error", { detail:{ col, code:e?.code||"" } })); } catch(_) {}
};
const fsSet = async (col, id, data) => {
  try { await setDoc(doc(db, col, id), data); } catch(e) { console.error("fsSet error", e); reportWriteError(col, e); }
};
const fsDel = async (col, id) => {
  try { await deleteDoc(doc(db, col, id)); } catch(e) { console.error("fsDel error", e); reportWriteError(col, e); }
};
// Field-level update: changes only the fields given, so it never overwrites other people's edits on the same record.
const fsUpdate = async (col, id, fields) => {
  try { await updateDoc(doc(db, col, id), fields); return true; } catch(e) { console.error("fsUpdate error", e); reportWriteError(col, e); return false; }
};

// ── SECURITY (v34) ──
// Passwords live ONLY in Firebase Auth. Nothing password-related is stored in Firestore or the browser.
// Older versions cached passwords in localStorage; wipe that cache on every load.
const clearLegacyPasswordCache = () => { try { localStorage.removeItem("msna_passwords"); } catch(e) {} };
// Cost rates are partner-only and live in the "rates" collection, not on the user doc.
const RATE_FIELDS = ["actualRate","actualRateEffectiveDate"];
const stripRates = u => { const c = { ...u }; RATE_FIELDS.forEach(f => { delete c[f]; }); return c; };
const hasRateFields = u => RATE_FIELDS.some(f => f in u);
const roleKey = email => (email||"").trim().toLowerCase();

// ── FIRESTORE REAL-TIME HOOK ──
// Listens to one or more queries and merges the results (deduped by id).
// emit(docs, fromServer): fromServer is true once every listener has confirmed data from the server
// (not just the local cache), so one-time jobs never act on stale cached data.
function listenMerged(targets, emit, label) {
  const parts = targets.map(() => null);
  const fromServer = targets.map(() => false);
  const unsubs = targets.map((q, i) => onSnapshot(q, { includeMetadataChanges: true },
    snap => {
      parts[i] = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      if (!snap.metadata.fromCache) fromServer[i] = true;
      if (parts.some(x => x === null)) return;
      const merged = new Map();
      parts.forEach(list => list.forEach(d => merged.set(d.id, d)));
      emit([...merged.values()], fromServer.every(Boolean));
    },
    err => console.error("onSnapshot error", label, err)
  ));
  return () => unsubs.forEach(u => u());
}
const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

// v35: interns only need (a) their own entries and (b) all entries on the engagements they work on
// (for budget bars and appraisal quarters). Instead of the whole firm's timesheets, load exactly that.
// Engagements = currently assigned + any engagement they have ever booked time to.
function internTimesheetScope(myId, assignedIds) {
  return (colName, emit) => {
    const col = collection(db, colName);
    let own = null, ownServer = false, team = null, teamServer = false, stopTeam = null;
    const push = () => {
      if (own === null) return;
      const merged = new Map();
      own.forEach(d => merged.set(d.id, d));
      (team || []).forEach(d => merged.set(d.id, d));
      emit([...merged.values()], ownServer && (team !== null && teamServer));
    };
    const stopOwn = listenMerged(
      [query(col, where("userId", "==", myId)), query(col, where("filedById", "==", myId))],
      (docs, server) => {
        own = docs; ownServer = server;
        if (!stopTeam) {
          const ids = [...new Set([...assignedIds, ...docs.map(t => t.projectId).filter(Boolean)])];
          if (!ids.length) { team = []; teamServer = true; }
          else {
            stopTeam = listenMerged(
              chunk(ids, 30).map(part => query(col, where("projectId", "in", part))),
              (tdocs, tserver) => { team = tdocs; teamServer = tserver; push(); },
              colName + " (engagements)"
            );
          }
        }
        push();
      },
      colName + " (own)"
    );
    return () => { stopOwn(); if (stopTeam) stopTeam(); };
  };
}

// enabled=false → no listener (e.g. before sign-in, or a partner-only collection for non-partners).
// A Firestore listener that is refused by security rules dies permanently, so we only
// open it once the user is signed in, and re-open it whenever "enabled" flips on.
// scope: optional (colName, emit) => unsubscribe, to load only part of a collection;
// scopeKey must change whenever the scope's inputs change.
// Returns [data, set, loaded] — loaded is true once the server (not just the cache) has answered.
function useLS(colName, fallback=[], enabled=true, scope=null, scopeKey="all") {
  const [data, setData] = useState(fallback);
  const [loaded, setLoaded] = useState(false);
  const isArr = Array.isArray(fallback);
  // Keep a ref to latest data for use in set() without stale closure
  const dataRef = useRef(fallback);

  // Subscribe to Firestore real-time updates
  useEffect(() => {
    if (!isArr) return;
    if (!enabled) { dataRef.current = fallback; setData(fallback); setLoaded(false); return; }
    const emit = (docs, fromServer) => {
      dataRef.current = docs;
      setData(docs);
      if (fromServer) setLoaded(true);
    };
    return scope ? scope(colName, emit) : listenMerged([collection(db, colName)], emit, colName);
  }, [colName, isArr, enabled, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Write to Firestore FIRST, then update local state
  // This avoids calling fsSet inside a state updater
  const set = useCallback((updater) => {
    const prev = dataRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;

    // Update local state immediately for responsive UI
    dataRef.current = next;
    setData(next);

    // Compute diff and write only changed items to Firestore
    const prevMap = new Map(prev.map(x=>[x.id, JSON.stringify(x)]));
    const nextMap = new Map(next.map(x=>[x.id, x]));

    // Write new or changed items
    next.forEach(item => {
      const prevStr = prevMap.get(item.id);
      if(!prevStr || prevStr !== JSON.stringify(item)) {
        fsSet(colName, item.id, item);
      }
    });

    // Delete removed items
    prev.forEach(item => {
      if(!nextMap.has(item.id)) fsDel(colName, item.id);
    });
  }, [colName]);

  return [data, set, loaded];
}

// ── UTILS ──
const genId       = () => Math.random().toString(36).slice(2,10);
const todayStr    = () => new Date().toISOString().slice(0,10);
const fmtDate     = d  => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—";
const fmtCurrency = n  => "₹"+Math.round(Number(n||0)).toLocaleString("en-IN");
// Format hours: 1 decimal place max, strip trailing .0
const fmtHrs     = h  => { const n = Number(h||0); return Number.isInteger(n)?n:Math.round(n*10)/10; };
const monthKey    = d  => d ? d.slice(0,7) : "";
const monthLabel  = mk => { if(!mk) return ""; const [y,m]=mk.split("-"); return new Date(y,m-1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); };

function getWeekDates(offsetWeeks=0) {
  const d = new Date(); d.setDate(d.getDate() + offsetWeeks*7);
  const day = d.getDay(); const mon = new Date(d);
  mon.setDate(d.getDate()-(day===0?6:day-1));
  return Array.from({length:7},(_,i)=>{ const x=new Date(mon); x.setDate(mon.getDate()+i); return x.toISOString().slice(0,10); });
}
function minDate() { const d=new Date(); d.setDate(d.getDate()-MAX_BACKDATE_DAYS); const computed=d.toISOString().slice(0,10); return computed>"2026-04-01"?computed:"2026-04-01"; }

// ── APPRAISAL: FY / quarter helpers (Indian FY: Apr–Mar; Q1 Apr-Jun … Q4 Jan-Mar) ──
function fyOfDate(dateStr) {
  const d = new Date(dateStr); const y = d.getFullYear(); const m = d.getMonth();
  const startY = m>=3 ? y : y-1;
  return `${startY}-${String((startY+1)%100).padStart(2,"0")}`;
}
function fyQuarterOfDate(dateStr) {
  const m = new Date(dateStr).getMonth();
  if(m>=3&&m<=5) return "Q1";
  if(m>=6&&m<=8) return "Q2";
  if(m>=9&&m<=11) return "Q3";
  return "Q4";
}
const currentFY = () => fyOfDate(todayStr());
function calcAppraisalScore(metrics={}) {
  let sum=0, max=0;
  APPRAISAL_METRICS.forEach(m=>{
    const v = metrics[m.key];
    if(!v || v.na) return;
    max += 5; sum += Number(v.score)||0;
  });
  const pct = max ? Math.round((sum/max)*1000)/10 : null;
  return { sum, max, pct };
}

// ── APPRAISAL: eligibility engine ──
// Trigger = staff allotted to a project code AND has at least one approved hour booked on it (on/after APPRAISAL_START_DATE).
// Returns a flat list of "required appraisal slots".
//  Fixed:    one slot per staff per engagement.
//  Retainer (v42): one COMBINED slot per staff, per appraising manager, per quarter. It covers every
//            retainer client where that manager is assigned AND booked approved time in the quarter.
//            A retainer where no assigned manager booked time that quarter goes to its assigned partner
//            (combined per partner). Managers on retainers get one combined partner card per quarter.
function getRequiredAppraisals(users, projects, tss) {
  const approvedTs = tss.filter(t => t.status==="approved" && !t.isInternal && t.projectId && t.hours>0 && t.date>=APPRAISAL_START_DATE);
  const merged = {};

  const upsert = (key, base, clientName) => {
    if(!merged[key]) merged[key] = { ...base, clientNames:[clientName] };
    else if(clientName && !merged[key].clientNames.includes(clientName)) merged[key].clientNames.push(clientName);
  };
  // Retainer groups: merge clients, engagements and eligible appraisers into one combined slot.
  const upsertRt = (key, base, appraisers, proj) => {
    if(!merged[key]) merged[key] = { type:"retainer", ...base, groupKey:key, clientNames:[], projects:[], projectIds:[], eligibleAppraisers:[] };
    const g = merged[key];
    if(proj.clientName && !g.clientNames.includes(proj.clientName)) g.clientNames.push(proj.clientName);
    if(!g.projectIds.includes(proj.id)) { g.projectIds.push(proj.id); g.projects.push(proj); }
    appraisers.forEach(a => { if(a && !g.eligibleAppraisers.includes(a)) g.eligibleAppraisers.push(a); });
  };

  projects.forEach(p => {
    const projTs = approvedTs.filter(t => t.projectId===p.id);
    if(projTs.length===0) return;
    const managers = p.assignedManagers||[];
    const partners = [p.assignedPartnerId, ...(p.assignedPartners||[])].filter(Boolean);
    const isRetainer = p.feeType==="retainer";

    if(!isRetainer) {
      const staffBooked = [...new Set(projTs.map(t=>t.userId))];
      // Mapped to the quarter of the first approved hours; managers/partners can push it to later quarters.
      const firstDate = projTs.reduce((a,b)=>a.date<b.date?a:b).date;
      const baseFy = fyOfDate(firstDate), baseQuarter = fyQuarterOfDate(firstDate);
      const fy = p.appraisalQuarter?.fy || baseFy;
      const quarter = p.appraisalQuarter?.quarter || baseQuarter;
      const fxExtra = { quarter, baseFy, baseQuarter, pushes:p.appraisalPushes||[], managers };
      staffBooked.forEach(uid => {
        const u = users.find(x=>x.id===uid); if(!u) return;
        if(u.role==="partner") return; // partners are never appraised, regardless of project assignment arrays
        if(managers.includes(uid)) {
          upsert(`fx-mgr-${p.id}-${uid}`, { type:"fixed", fy, ...fxExtra, projectId:p.id, projectName:p.name, projectCode:p.code, staffId:uid, appraiserRole:"partner", eligibleAppraisers:partners, primaryAppraiser:p.assignedPartnerId }, p.clientName);
        } else {
          const appraisers = managers.length ? managers : partners;
          upsert(`fx-staff-${p.id}-${uid}`, { type:"fixed", fy, ...fxExtra, projectId:p.id, projectName:p.name, projectCode:p.code, staffId:uid, appraiserRole: managers.length?"manager":"partner", eligibleAppraisers:appraisers, primaryAppraiser: managers[0]||p.assignedPartnerId }, p.clientName);
        }
      });
    } else {
      const byQ = {};
      projTs.forEach(t => { const k = `${fyOfDate(t.date)}|${fyQuarterOfDate(t.date)}`; (byQ[k]=byQ[k]||[]).push(t); });
      Object.entries(byQ).forEach(([k, list]) => {
        const [fy, quarter] = k.split("|");
        const staffInQ = [...new Set(list.map(t=>t.userId))];
        // Managers who are assigned AND booked approved time on this retainer in this quarter
        const mgrsBooked = managers.filter(m => list.some(t=>t.userId===m));
        staffInQ.forEach(uid => {
          const u = users.find(x=>x.id===uid); if(!u) return;
          if(u.role==="partner") return; // partners are never appraised, regardless of project assignment arrays
          if(isStaffInactiveForQuarter(u, fy, quarter)) return;
          if(isGraceExcludedQuarter(fy, quarter, effectiveJoinDate(u))) return;
          const proj = { id:p.id, name:p.name, code:p.code, clientName:p.clientName };
          if(managers.includes(uid)) {
            // Manager on a retainer: one combined partner card per quarter across all their retainers
            upsertRt(`rt-mgr-${uid}-${fy}-${quarter}`, { fy, quarter, staffId:uid, appraiserRole:"partner", groupLeadId:null, groupLeadName:"Partners", primaryAppraiser:p.assignedPartnerId }, partners, proj);
          } else if(mgrsBooked.length) {
            mgrsBooked.filter(m=>m!==uid).forEach(m => {
              const mu = users.find(x=>x.id===m);
              upsertRt(`rt-staff-${uid}-${m}-${fy}-${quarter}`, { fy, quarter, staffId:uid, appraiserRole:"manager", groupLeadId:m, groupLeadName:mu?.name||"Manager", primaryAppraiser:m }, [m], proj);
            });
          } else {
            // No assigned manager booked time this quarter: the assigned partner appraises (combined per partner)
            const lead = p.assignedPartnerId || partners[0];
            const pu = users.find(x=>x.id===lead);
            upsertRt(`rt-staff-${uid}-P${lead}-${fy}-${quarter}`, { fy, quarter, staffId:uid, appraiserRole:"partner", groupLeadId:lead, groupLeadName:pu?.name||"Partner", primaryAppraiser:lead }, partners, proj);
          }
        });
      });
    }
  });

  return Object.entries(merged).map(([key, v]) => ({ key, ...v }));
}
// Find an already-created appraisal record matching a required slot.
// Retainer (v42): records carry the groupKey of their combined slot. Older retainer records still match:
//  - pre-v40 (one per manager, no projectId): same appraiser as the slot's manager
//  - v40 (one per engagement, has projectId): only if submitted, and its engagement is in this slot
// If several records match, a submitted one wins, then a draft.
function findAppraisalFor(appraisals, req) {
  const matches = appraisals.filter(a => {
    if(a.type!==req.type || a.staffId!==req.staffId || a.appraiserRole!==req.appraiserRole) return false;
    if(req.type==="fixed") return a.projectId===req.projectId;
    if(a.fy!==req.fy || a.quarter!==req.quarter) return false;
    if(a.groupKey) return a.groupKey===req.key;
    const leadOk = req.appraiserRole==="partner" ? (req.eligibleAppraisers||[]).includes(a.appraiserId) : a.appraiserId===req.groupLeadId;
    if(!leadOk) return false;
    if(a.projectId) return a.status==="submitted" && (req.projectIds||[]).includes(a.projectId);
    return (a.clientNames||[]).some(c=>(req.clientNames||[]).includes(c));
  });
  return matches.find(a=>a.status==="submitted") || matches.find(a=>a.status==="draft") || matches[0];
}

// ── APPRAISAL: quarter placement + quarter average (v30) ──
// Retainer appraisals belong to their quarter. A fixed engagement sits in the quarter of its first approved
// hours, or the later quarter it was pushed to while still running. A submitted appraisal is pinned to the
// quarter it was submitted under, so a later push of the engagement never moves an existing score.
function appraisalPeriod(req, ex) {
  if(req.type==="retainer") return { fy:req.fy, quarter:req.quarter };
  if(ex?.status==="submitted") {
    if(ex.periodFy && ex.periodQuarter) return { fy:ex.periodFy, quarter:ex.periodQuarter };
    return { fy:req.baseFy, quarter:req.baseQuarter }; // submitted before pushes existed
  }
  return { fy:req.fy, quarter:req.quarter };
}
function nextQuarterOf(fy, quarter) {
  const i = GOAL_QUARTER_ORDER.indexOf(quarter);
  if(i<3) return { fy, quarter:GOAL_QUARTER_ORDER[i+1] };
  const y = Number(fy.split("-")[0])+1;
  return { fy:`${y}-${String((y+1)%100).padStart(2,"0")}`, quarter:"Q1" };
}
// Score of one appraisal for averaging.
//  kind "score"   → pct is the % over scored metrics only (per-metric N/A never pulls it down)
//  kind "na"      → whole appraisal marked Not applicable, or every metric N/A: excluded from the average
//  kind "pending" → not submitted yet: no score
function appraisalScoreOf(ex) {
  if(!ex || ex.status!=="submitted") return { kind:"pending", pct:null };
  if(ex.notApplicable) return { kind:"na", pct:null };
  const { pct } = calcAppraisalScore(ex.metrics);
  return pct==null ? { kind:"na", pct:null } : { kind:"score", pct };
}
// Quarter average = sum of engagement scores ÷ number of engagements scored.
// N/A engagements are left out of both numerator and denominator.
function quarterAverage(items) {
  const scores = items.map(i=>appraisalScoreOf(i.ex));
  const scored = scores.filter(s=>s.kind==="score");
  const total = scored.reduce((a,s)=>a+s.pct,0);
  return {
    avg: scored.length ? Math.round((total/scored.length)*10)/10 : null,
    scored: scored.length,
    na: scores.filter(s=>s.kind==="na").length,
    pending: scores.filter(s=>s.kind==="pending").length,
    total: items.length,
  };
}
// Attach the record and its display quarter to each required slot.
function appraisalItems(reqs, appraisals) {
  return reqs.map(r => { const ex = findAppraisalFor(appraisals, r); return { r, ex, ...appraisalPeriod(r, ex) }; });
}

// ── GOAL SETTING ──
// Scheme starts Q2 of FY 2026-27 — no retroactive Q1.
const GOAL_SCHEME_START_FY = "2026-27";
const GOAL_SCHEME_START_QUARTER = "Q2";
const GOAL_QUARTER_ORDER = ["Q1","Q2","Q3","Q4"];
function quarterDateRange(fy, quarter) {
  const y = Number(fy.split("-")[0]);
  const ranges = { Q1:[`${y}-04-01`,`${y}-06-30`], Q2:[`${y}-07-01`,`${y}-09-30`], Q3:[`${y}-10-01`,`${y}-12-31`], Q4:[`${y+1}-01-01`,`${y+1}-03-31`] };
  return ranges[quarter];
}
function quarterLabel(fy, quarter) {
  const y = Number(fy.split("-")[0]);
  const labels = { Q1:`Apr–Jun ${y}`, Q2:`Jul–Sep ${y}`, Q3:`Oct–Dec ${y}`, Q4:`Jan–Mar ${y+1}` };
  return labels[quarter];
}
function isQuarterInGoalScheme(fy, quarter) {
  const fyYear = Number(fy.split("-")[0]);
  const qIdx = GOAL_QUARTER_ORDER.indexOf(quarter);
  const startFyYear = Number(GOAL_SCHEME_START_FY.split("-")[0]);
  const startQIdx = GOAL_QUARTER_ORDER.indexOf(GOAL_SCHEME_START_QUARTER);
  return fyYear>startFyYear || (fyYear===startFyYear && qIdx>=startQIdx);
}
// v44: from Q3 FY 2026-27, goals 1 and 2 are mandatory and goal 3 is optional (earlier quarters: all 3 mandatory).
// A blank optional goal is dropped from the record on submission, so it is never reviewed or self-assessed.
const GOAL_OPTIONAL_THIRD_FROM = { fy:"2026-27", quarter:"Q3" };
function isQuarterOnOrAfter(fy, quarter, ref) {
  const a = Number(fy.split("-")[0]), b = Number(ref.fy.split("-")[0]);
  return a>b || (a===b && GOAL_QUARTER_ORDER.indexOf(quarter)>=GOAL_QUARTER_ORDER.indexOf(ref.quarter));
}
// v53: from Q3 FY 2026-27 the count depends on role. Managers: 3 mandatory goals. Interns (and others): 2 mandatory
// goals, goal 3 stays optional and is never scored. Before Q3: 3 mandatory goals for everyone.
// The role is the one at the time goals were submitted (stored as goalRole); older records fall back to the current role.
function requiredGoalCount(fy, quarter, role) {
  if(!isQuarterOnOrAfter(fy, quarter, GOAL_OPTIONAL_THIRD_FROM)) return 3;
  return role==="manager" ? 3 : 2;
}
const goalRoleOf = (rec, u) => rec?.goalRole || u?.role || "intern";
// Mandatory goals still missing from a SUBMITTED record (managers who submitted 2 goals in Q3 under the old rule).
function goalMissingCount(rec, u) {
  if(rec?.goalStatus!=="submitted") return 0;
  const n = requiredGoalCount(rec.fy, rec.quarter, goalRoleOf(rec, u));
  const have = (rec.goals||[]).filter(g=>g.sl<=n && (g.desc||"").trim()).length;
  return Math.max(0, n-have);
}

// ── v53: COMBINED QUARTER SCORE ──
// Overall = performance appraisal average × 70% + goal points (out of 30).
// Goals: 30 points split equally across the mandatory goals (3 goals → 10 each, 2 goals → 15 each).
// v54: a goal counts as soon as the self-assessment is submitted (Completed = points), Provisional until the partner
// approves that entry, then Final. A rejected entry drops out until it is resubmitted.
// WIP / Zero progress earn 0. Intern's optional goal 3 earns 0 (not scored).
// No goals for the quarter (joined late, before joining, inactive, before the scheme) → appraisal counts 100%.
// No appraisals for the quarter → goals count 100%.
// Until everything is decided the score is Provisional and is worked out on the parts decided so far,
// so an unassessed goal or an open appraisal never drags the number down in the meantime.
const OVERALL_W_APPRAISAL = 70, OVERALL_W_GOALS = 30;
const GOAL_ACHIEVED = "Completed";
function goalScoreFor(fy, quarter, staffId, goals, users) {
  const u = users.find(x=>x.id===staffId);
  if(!isQuarterInGoalScheme(fy, quarter)) return { applicable:false, reason:"Goal setting started in Q2 FY 2026-27" };
  const excl = goalQuarterExclusion(u, fy, quarter, goals);
  if(excl) return { applicable:false, reason: excl==="grace" ? "Joined late in the quarter, goals skipped" : excl==="beforeJoining" ? "Before joining" : "Not applicable" };
  const [qStart, qEnd] = quarterDateRange(fy, quarter);
  if(qStart>todayStr()) return { applicable:false, reason:"Quarter not started" };
  const rec = goals.find(g=>g.fy===fy&&g.quarter===quarter&&g.staffId===staffId);
  const role = goalRoleOf(rec, u);
  const n = requiredGoalCount(fy, quarter, role);
  const each = OVERALL_W_GOALS / n;
  const ended = todayStr() > qEnd;
  const assessSubmitted = rec?.assessStatus==="submitted";
  const rows = [1,2,3].map(sl => {
    const g = (rec?.goals||[]).find(x=>x.sl===sl);
    const a = (rec?.assess||[]).find(x=>x.sl===sl);
    const scored = sl<=n;
    if(!g || !((g.desc||"").trim())) {
      if(!scored) return null;                                    // optional goal not set: nothing to show
      return { sl, desc:"", scored, state:"notset", points:0, decided:false, each };
    }
    const reviewed = !!(assessSubmitted && a?.status && a?.reviewStatus==="approved");
    const counted = !!(ended && assessSubmitted && a?.status && a?.reviewStatus!=="rejected"); // v54
    let state;
    if(!ended) state = "running";
    else if(!assessSubmitted || !a?.status) state = "toassess";
    else if(a.reviewStatus==="rejected") state = "rejected";
    else if(!reviewed) state = "review";
    else state = "approved";
    const achieved = counted && a.status===GOAL_ACHIEVED;
    return { sl, desc:g.desc, scored, state, selfStatus:a?.status||"", points: scored && achieved ? each : 0, decided: counted, approved: reviewed, each };
  }).filter(Boolean);
  const scoredRows = rows.filter(r=>r.scored);
  const decided = scoredRows.filter(r=>r.decided);
  return {
    applicable:true, role, n, each, rows,
    points: decided.reduce((s,r)=>s+r.points,0),
    achieved: decided.filter(r=>r.points>0).length,
    decidedWeight: decided.length*each,                         // part of the 30 already counted
    final: scoredRows.every(r=>r.approved),                     // v54: Final only once every scored goal is partner-approved
  };
}
// Combine the appraisal average for the quarter with the goal score.
// appr = quarterAverage(items) for that staff member and quarter; gs = goalScoreFor(...)
const round1 = x => Math.round(x*10)/10;
function combinedScore(appr, gs) {
  const apprApplicable = appr.total>0 && !(appr.scored===0 && appr.pending===0); // every engagement N/A → no appraisal part
  const goalsApplicable = !!gs?.applicable;
  const wA = apprApplicable ? OVERALL_W_APPRAISAL : 0;
  const wG = goalsApplicable ? OVERALL_W_GOALS : 0;
  const decA = apprApplicable && appr.avg!=null ? wA : 0;
  const decG = goalsApplicable ? gs.decidedWeight : 0;
  const apprPts = decA ? appr.avg*wA/100 : 0;                 // e.g. 82% × 70 = 57.4
  const goalPts = goalsApplicable ? gs.points : 0;
  const provisional = (apprApplicable && appr.pending>0) || (goalsApplicable && !gs.final);
  const base = decA + decG;
  const pct = base>0 ? round1((apprPts + goalPts) / base * 100) : null;
  let mode = "both";
  if(!apprApplicable && !goalsApplicable) mode = "none";
  else if(!goalsApplicable) mode = "appraisalOnly";
  else if(!apprApplicable) mode = "goalsOnly";
  return { pct, provisional, mode, wA, wG, decA, decG, apprPts:round1(apprPts), goalPts:round1(goalPts), apprApplicable, goalsApplicable,
    partial: provisional && base>0 && base<(wA+wG) };
}
function blankGoalRecord(fy, quarter, staffId) {
  return {
    id:`goal-${staffId}-${fy}-${quarter}`, fy, quarter, staffId,
    goals:[1,2,3].map(sl=>({sl,desc:"",steps:""})), goalStatus:null,
    assess:[1,2,3].map(sl=>({sl,status:"",remark:""})), assessStatus:null,
  };
}
// The two most recent quarters (of the in-scheme ones) that have actually started, for a given FY —
// used to show a "previous quarter + current quarter" status summary on the staff card.
function twoRecentQuartersForFY(fy) {
  const today = todayStr();
  return GOAL_QUARTER_ORDER.filter(q => isQuarterInGoalScheme(fy,q) && quarterDateRange(fy,q)[0]<=today).slice(-2);
}
// Per-goal/self-assessment-entry review status set by the partner; defaults to "pending" until reviewed.
function itemReviewStatus(item) { return item?.reviewStatus || "pending"; }
// Overall status for a set of 3 items once that phase has been submitted at least once.
function phaseReviewStatus(items, phaseSubmitted) {
  if(!phaseSubmitted) return null;
  if((items||[]).some(i=>itemReviewStatus(i)==="rejected")) return "resubmission";
  if((items||[]).every(i=>itemReviewStatus(i)==="approved")) return "approved";
  return "submitted";
}
// Combined goal + self-assessment status for one quarter, for the staff card summary.
function goalQuarterStatus(fy, quarter, staffId, goals, users) {
  const u = users.find(x=>x.id===staffId);
  const excl = goalQuarterExclusion(u, fy, quarter, goals);
  if(excl==="beforeJoining") return { label:"Before joining", cls:"bcl" };
  if(excl==="grace") return { label:"Skipped: joined late", cls:"bcl" };
  if(excl) return { label:"Not applicable", cls:"bcl" };
  const rec = goals.find(g=>g.fy===fy&&g.quarter===quarter&&g.staffId===staffId) || blankGoalRecord(fy,quarter,staffId);
  const goalSubmitted = rec.goalStatus==="submitted";
  if(!goalSubmitted) return { label:"Goals pending", cls:"brs" };
  const goalPhase = phaseReviewStatus(rec.goals, true);
  if(goalPhase==="resubmission") return { label:"Goals: resubmission needed", cls:"bp2" };
  if(goalPhase==="submitted") return { label:"Goals: pending review", cls:"brs" };
  if(goalMissingCount(rec, u)>0) return { label:"Goal 3 to add", cls:"bp2" }; // v53: managers need 3 goals from Q3
  const [, qEnd] = quarterDateRange(fy, quarter);
  const ended = todayStr() > qEnd;
  if(!ended) return { label:"Goals approved", cls:"bac" };
  const assessSubmitted = rec.assessStatus==="submitted";
  if(!assessSubmitted) return { label:"Self-assessment pending", cls:"brs" };
  const assessPhase = phaseReviewStatus(rec.assess, true);
  if(assessPhase==="resubmission") return { label:"Assessment: resubmission needed", cls:"bp2" };
  if(assessPhase==="submitted") return { label:"Assessment: pending review", cls:"brs" };
  return { label:"Complete", cls:"bac" };
}
// Effective join date for grace-period purposes: a reactivation counts as a fresh join.
function effectiveJoinDate(u) { return u?.reactivatedFrom || u?.dateOfJoining || null; }
// Grace windows: someone joining in the final N days of a quarter is excluded from that one quarter.
// Retainer appraisals use 14 days; Goal Setting uses 45 days (joining day and quarter-end day both count).
const APPRAISAL_GRACE_DAYS = 14;
const GOAL_GRACE_DAYS = 45;
function isGraceExcludedQuarter(fy, quarter, joinDate, graceDays=APPRAISAL_GRACE_DAYS) {
  if(!joinDate) return false;
  const [, qEnd] = quarterDateRange(fy, quarter);
  const d = new Date(qEnd); d.setDate(d.getDate()-(graceDays-1));
  const graceStart = d.toISOString().slice(0,10);
  return joinDate>=graceStart && joinDate<=qEnd;
}
// Quarter ended before the person joined (or rejoined) the firm.
function isBeforeJoiningQuarter(fy, quarter, joinDate) {
  if(!joinDate) return false;
  const [, qEnd] = quarterDateRange(fy, quarter);
  return qEnd < joinDate;
}
// Why a goal quarter does not apply to a staff member, or null if it applies.
// An existing record always keeps the quarter visible (historical, e.g. before a reactivation).
function goalQuarterExclusion(u, fy, quarter, goals=[]) {
  if(goals.some(g=>g.fy===fy&&g.quarter===quarter&&g.staffId===u?.id)) return null;
  if(isStaffInactiveForQuarter(u, fy, quarter)) return "inactive";
  const jd = effectiveJoinDate(u);
  if(isBeforeJoiningQuarter(fy, quarter, jd)) return "beforeJoining";
  if(isGraceExcludedQuarter(fy, quarter, jd, GOAL_GRACE_DAYS)) return "grace";
  return null;
}
// A staff member is treated as absent for a quarter if they'd already gone inactive before that quarter began.
function isStaffInactiveForQuarter(u, fy, quarter) {
  if(!u || u.active!==false || !u.inactiveFrom) return false;
  const [qStart] = quarterDateRange(fy, quarter);
  return u.inactiveFrom <= qStart;
}

// Inactive staff drop out of PENDING requirements they haven't started (nothing to enable),
// but any appraisal already recorded stays visible — greyed out — as historical record.
function gateInactiveAppraisals(required, appraisals, users) {
  return required.filter(r => {
    const u = users.find(x=>x.id===r.staffId);
    if(u?.active!==false) return true;
    return !!findAppraisalFor(appraisals, r);
  });
}

function addAudit(userId, userName, action, detail) {
  const id = genId();
  fsSet("audit", id, { id, userId, userName, action, detail, ts: new Date().toISOString() });
}

// ── ENGAGEMENT LETTER (v48) ──
// Draft EL: asked (mandatory) when a code is created or edited. Answering No never blocks creation;
// the code is flagged "EL pending" until a partner or an assigned manager marks the draft as shared.
// Signed EL: asked at closure (close, closure request). No = the code cannot be closed.
// Codes created before v48 have no answer and show "EL not recorded".
// Ageing runs from code creation, or from EL_TRACKING_START for older codes: amber up to 7 days, red after.
// v52: "Not applicable" (e.g. small-value engagements) is stored as elDraftShared:"na". It raises no flag and
// skips the signed EL check at closure. Using the existing field means no Firestore rules change is needed.
const EL_TRACKING_START = "2026-10-08";
const EL_AGE_RED_DAYS = 7;
const EL_OPEN_STATUSES = ["active","pending_approval","pending_closure"];
function elDraftState(p) {
  if(p?.elDraftShared===true) return "shared";
  if(p?.elDraftShared===false) return "pending";
  if(p?.elDraftShared==="na") return "na";
  return "unrecorded";
}
function elAgeDays(p) {
  const created = (p?.createdAt||"").slice(0,10);
  const start = created>EL_TRACKING_START ? created : EL_TRACKING_START;
  return Math.max(0, Math.floor((new Date(todayStr()) - new Date(start)) / 86400000));
}
const elNeedsAttention = p => EL_OPEN_STATUSES.includes(p?.status) && !["shared","na"].includes(elDraftState(p));
const elIsNA = p => elDraftState(p)==="na" && !p?.elSignedReceived;
// Partners always; managers if assigned to the code (or they created it and it is still pending approval).
function canUpdateEL(user, p) {
  if(user.role==="partner") return true;
  if(user.role==="manager") return (p.assignedManagers||[]).includes(user.id) || (p.status==="pending_approval"&&p.createdBy===user.id);
  return false;
}
// v50: EL changes from the quick buttons are field-level writes; the real-time listener updates every screen.
function applyELShared(user, p, date) {
  fsUpdate("projects", p.id, { elDraftShared:true, elDraftSharedDate:date, elDraftAutoFromSigned:false, elDraftUpdatedBy:user.id, elDraftUpdatedAt:new Date().toISOString() })
    .then(ok=>{ if(ok) addAudit(user.id,user.name,"EL_DRAFT_SHARED",`Draft engagement letter for ${p.code} marked as shared on ${fmtDate(date)}`); });
}
// Older codes (created before EL tracking, status "not recorded"): the signed EL is normally already in place,
// so the quick action confirms the signed EL directly. That also counts as the draft being shared.
const elIsLegacy = p => elDraftState(p)==="unrecorded" && !p.elSignedReceived;
function applyELSignedLegacy(user, p) {
  fsUpdate("projects", p.id, { ...elSignedPatch(user, p), elLegacySigned:true })
    .then(ok=>{ if(ok) addAudit(user.id,user.name,"EL_SIGNED_RECEIVED",`Signed engagement letter for ${p.code} confirmed as received (code created before EL tracking)`); });
}
// v52: mark a code as not needing an engagement letter (new or older codes).
function applyELNotApplicable(user, p) {
  fsUpdate("projects", p.id, { elDraftShared:"na", elDraftSharedDate:null, elDraftAutoFromSigned:false, elDraftUpdatedBy:user.id, elDraftUpdatedAt:new Date().toISOString() })
    .then(ok=>{ if(ok) addAudit(user.id,user.name,"EL_NOT_APPLICABLE",`Engagement letter for ${p.code} marked as not applicable`); });
}
// The right quick action for a flagged code: older codes confirm the signed EL, newer ones mark the draft as shared.
// v52: both also offer "Not applicable".
function ELActionButton({ user, p, onShared, onSigned, onNA }) {
  if(!elNeedsAttention(p) || !canUpdateEL(user,p)) return null;
  return <>
    {elIsLegacy(p)
      ? <button className="btn bgh bxs" onClick={()=>onSigned(p)}>Mark signed EL received</button>
      : <button className="btn bgh bxs" onClick={()=>onShared(p)}>Mark as shared</button>}
    {onNA&&<button className="btn bgh bxs" title="No engagement letter needed for this code" onClick={()=>onNA(p)}>Not applicable</button>}
  </>;
}
// Fields written when the signed EL is confirmed at closure. A signed EL implies the draft was shared.
function elSignedPatch(user, p) {
  if(p.elSignedReceived || elDraftState(p)==="na") return {};
  const now = new Date().toISOString();
  return {
    elSignedReceived:true, elSignedConfirmedBy:user.id, elSignedConfirmedAt:now,
    ...(elDraftState(p)!=="shared" ? { elDraftShared:true, elDraftSharedDate:null, elDraftAutoFromSigned:true, elDraftUpdatedBy:user.id, elDraftUpdatedAt:now } : {}),
  };
}

function ELPill({ p }) {
  const st = elDraftState(p);
  if(p.elSignedReceived) return <span className="bdg ba"><I n="check" s={11}/>Signed EL received</span>;
  if(st==="shared") return <span className="bdg ba"><I n="check" s={11}/>EL shared{p.elDraftSharedDate?` ${fmtDate(p.elDraftSharedDate)}`:""}</span>;
  if(st==="na") return <span className="bdg bcl" title="No engagement letter needed for this code"><I n="block" s={11}/>EL not applicable</span>;
  if(!EL_OPEN_STATUSES.includes(p.status)) return null;
  const age = elAgeDays(p);
  const red = age>EL_AGE_RED_DAYS;
  const cls = red ? "br" : st==="pending" ? "brs" : "bcl";
  const ageTxt = age===0 ? "today" : `${age} day${age===1?"":"s"}`;
  return <span className={`bdg ${cls}`} title={red?`Over ${EL_AGE_RED_DAYS} days`:""}><I n="alert" s={11}/>{st==="pending"?"EL pending":"EL not recorded"} · {ageTxt}</span>;
}

// Yes / No pair used for both EL questions. v52: withNA adds a third "Not applicable" choice (value "na").
function YesNo({ value, onChange, noTone="amber", withNA=false }) {
  const tones = { yes:["var(--green)","#f0fdf4","#065f46"], amber:["var(--amber)","#fffbeb","#92400e"], red:["var(--red)","#fef2f2","#991b1b"], na:["var(--slate)","#f1f5f9","#334155"] };
  const opts = [[true,"Yes","check"],[false,"No","x"],...(withNA?[["na","Not applicable","block"]]:[])];
  return (
    <div style={{display:"grid",gridTemplateColumns:withNA?"1fr 1fr 1.3fr":"1fr 1fr",gap:10}}>
      {opts.map(([v,l,ic])=>{
        const on = value===v;
        const [bd,bg,fg] = tones[v==="na"?"na":v?"yes":noTone];
        return (
          <button key={l} type="button" onClick={()=>onChange(v)}
            style={{padding:"10px 14px",borderRadius:8,border:"1.5px solid",borderColor:on?bd:"var(--border)",background:on?bg:"#fff",color:on?fg:"var(--navy)",
              fontWeight:on?600:500,fontFamily:"'DM Sans',sans-serif",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:7,transition:"all .15s"}}>
            <I n={ic} s={15}/>{l}
          </button>
        );
      })}
    </div>
  );
}

function ELMarkSharedModal({ project, onSave, onClose }) {
  const [date,setDate] = useState(todayStr());
  const [err,setErr] = useState("");
  const go = () => {
    if(!date){ setErr("Enter the date the draft was shared."); return; }
    if(date>todayStr()){ setErr("The date cannot be in the future."); return; }
    onSave(date);
  };
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
        <div className="md-title">Mark draft EL as shared</div>
        <div className="ts tsl mb16"><span className="mono fw6 tnv">{project.code}</span> · {project.clientName} · {project.name}</div>
        {err&&<div className="err">{err}</div>}
        <div className="fg"><label className="fl">Date shared with the client *</label>
          <input type="date" className="fi" value={date} max={todayStr()} onChange={e=>{setDate(e.target.value);setErr("");}}/>
        </div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bp" onClick={go}><I n="check" s={15}/>Mark as shared</button>
        </div>
      </div>
    </div>
  );
}

function ELSignedLegacyModal({ project, onSave, onClose }) {
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
        <div className="md-title">Confirm signed EL</div>
        <div className="ts tsl mb16"><span className="mono fw6 tnv">{project.code}</span> · {project.clientName} · {project.name}</div>
        <div className="al al-i"><I n="info" s={15}/><div>This code was created before EL tracking started, so the signed engagement letter is usually already on file. Confirm it has been received from the client.</div></div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bsc" onClick={onSave}><I n="check" s={15}/>Signed EL received</button>
        </div>
      </div>
    </div>
  );
}

// v52: confirm that a code needs no engagement letter.
function ELNotApplicableModal({ project, onSave, onClose }) {
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
        <div className="md-title">Engagement letter not applicable</div>
        <div className="ts tsl mb16"><span className="mono fw6 tnv">{project.code}</span> · {project.clientName} · {project.name}</div>
        <div className="al al-i"><I n="info" s={15}/><div>Use this when no engagement letter is needed for this code, for example a small-value engagement. The EL flag is removed and the signed EL question is skipped at closure. A partner can change it later through Edit.</div></div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bp" onClick={onSave}><I n="block" s={15}/>Mark not applicable</button>
        </div>
      </div>
    </div>
  );
}

// Closure check (v48): signed EL + performance appraisals. v52: skipped for codes marked "Not applicable".
// mode "close"   → assigned partner / admin closing directly
// mode "request" → manager (or non-assigned partner) raising a closure request; appraisals are shown, not enforced
// mode "approve" → partner approving a closure request; shows the requester's EL confirmation
function ClosureModal({ project:p, mode, pendingNames=[], users=[], onConfirm, onClose }) {
  const confirmed = !!p.elSignedReceived;
  const elNA = elIsNA(p); // v52: no EL needed for this code, so there is nothing to ask
  const [signed,setSigned] = useState(confirmed||elNA ? true : null);
  const [tried,setTried] = useState(false);
  const isRetainer = p.feeType==="retainer";
  const apprBlocks = mode!=="request" && pendingNames.length>0;
  const ok = signed===true && !apprBlocks;
  const by = users.find(u=>u.id===p.elSignedConfirmedBy);
  const title = mode==="request" ? `Request closure of ${p.code}` : mode==="approve" ? `Approve closure of ${p.code}` : `Close ${p.code}`;
  const btn = mode==="request" ? "Send closure request" : mode==="approve" ? "Approve closure" : "Close engagement";
  const Row = ({ okState, label, value }) => (
    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:500,color:"var(--navy)"}}>
        <span style={{color:okState==="ok"?"var(--green)":okState==="bad"?"var(--red)":okState==="warn"?"var(--amber)":"var(--slate-light)"}}><I n={okState==="ok"?"check":okState==="bad"?"x":okState==="warn"?"alert":"info"} s={15}/></span>{label}
      </div>
      <div style={{fontSize:12,textAlign:"right",maxWidth:220,color:okState==="ok"?"#065f46":okState==="bad"?"#991b1b":okState==="warn"?"#92400e":"var(--slate)"}}>{value}</div>
    </div>
  );
  const elValue = elNA ? "Not applicable for this code" : signed===true ? (confirmed&&by ? `Received · confirmed by ${by.name}${p.elSignedConfirmedAt?` on ${fmtDate(p.elSignedConfirmedAt)}`:""}` : "Received") : signed===false ? "Not received" : "Not answered";
  const apprValue = isRetainer ? "Not required for retainers" : pendingNames.length===0 ? "All submitted" : `Pending: ${pendingNames.join(", ")}`;
  const apprState = isRetainer||pendingNames.length===0 ? "ok" : mode==="request" ? "warn" : "bad";
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
        <div className="md-title" style={{marginBottom:6}}>{title}</div>
        <div className="ts tsl mb16">{p.clientName} · {p.name}</div>
        {elNA ? (
          <div className="al al-i"><I n="info" s={15}/><div>The engagement letter was marked <strong>not applicable</strong> for this code, so no signed EL is needed.</div></div>
        ) : confirmed ? (
          <div className="al al-s"><I n="check" s={15}/><div>Signed engagement letter confirmed as received{by?` by ${by.name}`:""}.</div></div>
        ) : (
          <div className="fg">
            <label style={{display:"block",fontSize:13,fontWeight:600,color:"var(--navy)",marginBottom:10,lineHeight:1.45}}>Has the signed Engagement Letter been received from the client? *</label>
            <YesNo value={signed} onChange={v=>{setSigned(v);setTried(false);}} noTone="red"/>
          </div>
        )}
        <div style={{marginTop:4}}>
          <Row okState={elNA?"na":signed===true?"ok":signed===false?"bad":"na"} label="Signed engagement letter" value={elValue}/>
          <Row okState={apprState} label="Performance appraisals" value={apprValue}/>
        </div>
        {signed===false&&<div className="al al-d" style={{marginTop:14,marginBottom:0}}><I n="alert" s={15}/><div>{mode==="request"?"A closure request can't be raised":"The engagement can't be closed"} until the signed engagement letter is received from the client.</div></div>}
        {apprBlocks&&signed!==false&&<div className="al al-d" style={{marginTop:14,marginBottom:0}}><I n="alert" s={15}/><div>Every team member's appraisal, including the manager's, must be submitted first.</div></div>}
        {mode==="request"&&!isRetainer&&pendingNames.length>0&&signed!==false&&<div className="al al-w" style={{marginTop:14,marginBottom:0}}><I n="info" s={15}/><div>You can send the request now. A partner can approve it only after these appraisals are submitted.</div></div>}
        {signed===true&&!confirmed&&!elNA&&elDraftState(p)!=="shared"&&<div className="tx tsl" style={{marginTop:12}}>The draft engagement letter will also be marked as shared.</div>}
        {tried&&signed===null&&<div className="tx tdn" style={{marginTop:12}}>Answer the engagement letter question to continue.</div>}
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className={`btn ${mode==="request"?"bp":"bsc"}`} disabled={signed===false||apprBlocks}
            onClick={()=>{ if(signed===null){ setTried(true); return; } if(ok) onConfirm(); }}>
            <I n={mode==="request"?"send":"check"} s={15}/>{btn}
          </button>
        </div>
      </div>
    </div>
  );
}

// Dashboard card: engagements whose draft EL is pending or not recorded. Partners firm-wide, managers their own.
function ELDashboardCard({ user, users=[], projects=[], onOpenProjects }) {
  const [markP,setMarkP] = useState(null);
  const [signP,setSignP] = useState(null);
  const [naP,setNaP] = useState(null); // v52
  const [showAll,setShowAll] = useState(false);
  if(user.role==="intern") return null;
  const list = projects
    .filter(elNeedsAttention)
    .filter(p=>user.role==="partner" || (p.assignedManagers||[]).includes(user.id) || p.createdBy===user.id)
    .sort((a,b)=>elAgeDays(b)-elAgeDays(a));
  if(list.length===0) return null;
  const overdue = list.filter(p=>elAgeDays(p)>EL_AGE_RED_DAYS).length;
  const shown = showAll ? list : list.slice(0,6);
  return (
    <div className="card mb22" style={{padding:"18px 22px"}}>
      <div className="fxb" style={{marginBottom:12,gap:12,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{color:overdue?"var(--red)":"var(--amber)"}}><I n="alert" s={18}/></span>
          <div>
            <div className="card-title">Engagement letters pending · {list.length}</div>
            <div className="tx tsl mt4">Draft not yet shared with the client, or signed EL not confirmed on older codes{overdue?` · ${overdue} over ${EL_AGE_RED_DAYS} days`:""}</div>
          </div>
        </div>
        {onOpenProjects&&<button className="btn bgh bsm" onClick={onOpenProjects}>Open Projects →</button>}
      </div>
      <div>
        {shown.map(p=>{
          const partner = users.find(u=>u.id===p.assignedPartnerId);
          return (
            <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"10px 0",borderTop:"1px solid var(--border)"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span className="mono fw6" style={{fontSize:13}}>{p.code}</span>
                  <span className="ts fw6">{p.clientName}</span>
                  {p.status==="pending_approval"&&<span className="bdg bp2">Awaiting approval</span>}
                </div>
                <div className="tx tsl" style={{marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}{partner?` · ${partner.name}`:""}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                <ELPill p={p}/>
                <ELActionButton user={user} p={p} onShared={setMarkP} onSigned={setSignP} onNA={setNaP}/>
              </div>
            </div>
          );
        })}
      </div>
      {list.length>6&&<div style={{borderTop:"1px solid var(--border)",paddingTop:10,textAlign:"center"}}>
        <button className="btn bgh bxs" onClick={()=>setShowAll(s=>!s)}>{showAll?"Show fewer":`Show all ${list.length}`}</button>
      </div>}
      {markP&&<ELMarkSharedModal project={markP} onClose={()=>setMarkP(null)} onSave={date=>{applyELShared(user,markP,date);setMarkP(null);}}/>}
      {signP&&<ELSignedLegacyModal project={signP} onClose={()=>setSignP(null)} onSave={()=>{applyELSignedLegacy(user,signP);setSignP(null);}}/>}
      {naP&&<ELNotApplicableModal project={naP} onClose={()=>setNaP(null)} onSave={()=>{applyELNotApplicable(user,naP);setNaP(null);}}/>}
    </div>
  );
}

// ── ICONS (filled, material-style) ──
const I = ({ n, s=18 }) => {
  const paths = {
    block:    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.902 7.902 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.902 7.902 0 0 1 20 12c0 4.42-3.58 8-8 8z",
    clock:    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 11H11V7h1.5v5.25l4.5 2.67-.75 1.23L12.5 13z",
    check:    "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
    x:        "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
    plus:     "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
    edit:     "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
    trash:    "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z",
    users:    "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
    folder:   "M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
    chart:    "M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z",
    logout:   "M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z",
    lock:     "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z",
    download: "M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z",
    shield:   "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z",
    archive:  "M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.82-1h12l.93 1H5.12z",
    alert:    "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
    calendar: "M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z",
    history:  "M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z",
    target:   "M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.07-12.83l-4.58 4.58-1.56-1.56L7.5 11.62l3 3L16.5 7l-1.43-1.83z",
    send:     "M2.01 21L23 12 2.01 3 2 10l15 2-15 2z",
    bell:     "M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z",
    info:     "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
    star:     "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
    arrowleft:"M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z",
    arrowright:"M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4z",
    chevron:  "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z",
    task:     "M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",
  };
  return <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d={paths[n]||""}/></svg>;
};

// ── CSS ──
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
:root{
  --navy:#0f2044;--navy-mid:#1a3360;
  --gold:#c9a84c;--gold-light:#e8c96a;--gold-pale:#f5edd6;
  --cream:#faf8f4;--white:#fff;
  --slate:#64748b;--slate-light:#94a3b8;--border:#e2e8f0;
  --green:#10b981;--red:#ef4444;--amber:#f59e0b;--purple:#6366f1;
  --sh:0 1px 3px rgba(15,32,68,.08);--sh-md:0 4px 16px rgba(15,32,68,.12);--sh-lg:0 12px 40px rgba(15,32,68,.18);
  --r:10px;--rl:16px;
}
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;background:var(--cream);color:var(--navy);font-size:14px;}
.app{display:flex;min-height:100vh;}

/* ── LOGIN ── */
.lw{min-height:100vh;display:flex;}
.ll{flex:1;background:var(--navy);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px;position:relative;overflow:hidden;}
.ll::before{content:'';position:absolute;top:-80px;right:-80px;width:320px;height:320px;border-radius:50%;border:60px solid rgba(201,168,76,.12);}
.ll::after{content:'';position:absolute;bottom:-60px;left:-60px;width:240px;height:240px;border-radius:50%;border:40px solid rgba(201,168,76,.08);}
.lbrand{text-align:center;z-index:1;position:relative;}
.lbrand-logo{font-family:'Playfair Display',serif;font-size:56px;color:var(--gold);letter-spacing:3px;}
.lbrand-sub{color:rgba(255,255,255,.45);font-size:12px;letter-spacing:3px;text-transform:uppercase;margin-top:10px;}
.lbrand-tag{color:rgba(255,255,255,.28);font-size:13px;margin-top:36px;max-width:280px;text-align:center;line-height:1.9;}
.lr{width:500px;display:flex;align-items:center;justify-content:center;padding:60px 48px;background:var(--white);}
.lfw{width:100%;}
.ltitle{font-family:'Playfair Display',serif;font-size:32px;margin-bottom:8px;}
.lsub{color:var(--slate);font-size:13px;margin-bottom:36px;}
.demo-box{margin-top:22px;padding:14px 16px;background:var(--cream);border-radius:var(--r);font-size:12px;color:var(--slate);line-height:2;}

/* ── FORMS ── */
.fg{margin-bottom:18px;}
.fl{display:block;font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:var(--slate);margin-bottom:7px;}
.fi,.fs,.fta{width:100%;border:1.5px solid var(--border);border-radius:var(--r);padding:11px 14px;font-family:'DM Sans',sans-serif;font-size:14px;color:var(--navy);outline:none;background:var(--cream);transition:border-color .2s;}
.fi:focus,.fs:focus,.fta:focus{border-color:var(--navy-mid);background:#fff;}
.fi:disabled{opacity:.5;cursor:not-allowed;}
.fs{appearance:none;cursor:pointer;}
.fta{resize:vertical;min-height:78px;}
.err{background:#fef2f2;border:1px solid #fecaca;color:#dc2626;border-radius:var(--r);padding:10px 14px;font-size:13px;margin-bottom:14px;}

/* ── BUTTONS ── */
.btn{display:inline-flex;align-items:center;gap:7px;border:none;border-radius:var(--r);padding:10px 18px;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:500;cursor:pointer;transition:all .15s;white-space:nowrap;line-height:1;}
.btn:disabled{opacity:.45;cursor:not-allowed;}
.bp{background:var(--navy);color:#fff;} .bp:hover{background:var(--navy-mid);}
.bgo{background:var(--gold);color:var(--navy);font-weight:600;} .bgo:hover{background:var(--gold-light);}
.bsc{background:var(--green);color:#fff;} .bsc:hover{background:#059669;}
.bd{background:var(--red);color:#fff;} .bd:hover{background:#dc2626;}
.bam{background:var(--amber);color:#fff;} .bam:hover{background:#d97706;}
.bgh{background:transparent;color:var(--slate);border:1.5px solid var(--border);} .bgh:hover{background:var(--cream);color:var(--navy);}
.bsm{padding:6px 13px;font-size:13px;} .bxs{padding:4px 9px;font-size:12px;}
.bfl{width:100%;justify-content:center;}
.bic{padding:7px;border-radius:8px;}

/* ── SIDEBAR ── */
.sb{width:252px;background:var(--navy);min-height:100vh;display:flex;flex-direction:column;flex-shrink:0;}
.sb-hd{padding:24px 22px 16px;border-bottom:1px solid rgba(255,255,255,.08);}
.sb-brand{font-family:'Playfair Display',serif;font-size:22px;color:var(--gold);}
.sb-bsub{font-size:10px;color:rgba(255,255,255,.3);letter-spacing:2px;text-transform:uppercase;margin-top:3px;}
.sb-user{padding:16px 22px;border-bottom:1px solid rgba(255,255,255,.08);}
.sb-name{color:#fff;font-size:14px;font-weight:500;}
.sb-role{font-size:11px;text-transform:uppercase;letter-spacing:1px;margin-top:3px;}
.rp{color:var(--gold);} .rm{color:#60a5fa;} .ri{color:#86efac;}
.sb-nav{flex:1;padding:10px 10px;}
.ni{display:flex;align-items:center;gap:10px;padding:9px 12px;color:rgba(255,255,255,.5);font-size:13px;cursor:pointer;transition:all .15s;position:relative;border-radius:8px;margin-bottom:1px;}
.ni:hover{background:rgba(255,255,255,.07);color:rgba(255,255,255,.85);}
.ni.active{background:rgba(201,168,76,.14);color:var(--gold-light);}
.nb{position:absolute;right:10px;background:var(--gold);color:var(--navy);font-size:11px;font-weight:700;border-radius:20px;padding:1px 8px;min-width:20px;text-align:center;}
.nb3{position:absolute;right:10px;background:#dc2626;color:#fff;font-size:11px;font-weight:700;border-radius:20px;padding:1px 8px;min-width:20px;text-align:center;}
.nb2{position:absolute;right:10px;background:#6d28d9;color:#fff;font-size:11px;font-weight:700;border-radius:20px;padding:1px 8px;min-width:20px;text-align:center;}
.sb-ft{padding:14px 10px;border-top:1px solid rgba(255,255,255,.08);}
.sb-section-label{font-size:10px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:rgba(255,255,255,.22);padding:10px 12px 3px;margin-top:2px;}
.sb-divider{height:1px;background:rgba(255,255,255,.07);margin:8px 12px;}

/* ── LAYOUT ── */
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0;}
.topbar{background:#fff;border-bottom:1px solid var(--border);padding:0 28px;height:60px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.tb-title{font-family:'Playfair Display',serif;font-size:20px;}
.tb-right{display:flex;align-items:center;gap:12px;}
.tb-pill{display:flex;align-items:center;gap:7px;background:var(--gold-pale);padding:5px 13px;border-radius:8px;font-size:13px;}
.content{flex:1;padding:26px 28px;overflow-y:auto;}

/* ── CARDS ── */
.card{background:#fff;border-radius:var(--rl);padding:22px 24px;box-shadow:var(--sh);border:1px solid var(--border);}
.card-title{font-size:15px;font-weight:600;}
.card-sub{font-size:13px;color:var(--slate);}
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px;}
.sg3{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:22px;}
.sc{background:#fff;border-radius:var(--rl);padding:18px 20px;border:1px solid var(--border);}
.sv{font-family:'Playfair Display',serif;font-size:30px;}
.sl{font-size:11px;color:var(--slate);text-transform:uppercase;letter-spacing:1px;margin-top:5px;}

/* ── TABLE ── */
.tw{overflow-x:auto;}
table{width:100%;border-collapse:collapse;}
th{text-align:left;font-size:11px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:var(--slate);padding:9px 13px;background:var(--cream);border-bottom:1px solid var(--border);}
td{padding:11px 13px;border-bottom:1px solid var(--border);vertical-align:middle;}
tr:last-child td{border-bottom:none;}
tr:hover td{background:#fcfcfc;}

/* ── BADGES ── */
.bdg{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11.5px;font-weight:500;}
.bp2{background:#ede9fe;color:#6d28d9;}
.ba{background:#d1fae5;color:#065f46;}
.br{background:#fee2e2;color:#991b1b;}
.brs{background:#fef3c7;color:#92400e;}
.bac{background:#dbeafe;color:#1e40af;}
.bcl{background:#f1f5f9;color:#64748b;}
.bpc{background:#f3e8ff;color:#6d28d9;}
.blk{background:#fee2e2;color:#991b1b;}
.rpa{background:var(--gold-pale);color:#92400e;}
.rma{background:#dbeafe;color:#1e40af;}
.ria{background:#dcfce7;color:#166534;}

/* ── MODAL ── */
.mo{position:fixed;inset:0;background:rgba(15,32,68,.5);backdrop-filter:blur(4px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px;}
.md{background:#fff;border-radius:var(--rl);padding:28px 30px;width:100%;max-width:540px;max-height:92vh;overflow-y:auto;box-shadow:var(--sh-lg);}
.md-title{font-family:'Playfair Display',serif;font-size:22px;margin-bottom:20px;}
.md-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;}

/* ── HELPERS ── */
.g2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;}
.fx{display:flex;} .fxc{display:flex;align-items:center;} .fxb{display:flex;align-items:center;justify-content:space-between;}
.g8{gap:8px;} .g12{gap:12px;} .g16{gap:16px;}
.mb8{margin-bottom:8px;} .mb16{margin-bottom:16px;} .mb22{margin-bottom:22px;}
.mt4{margin-top:4px;} .mt8{margin-top:8px;}
.ts{font-size:13px;} .tx{font-size:11.5px;} .tsl{color:var(--slate);} .tgo{color:var(--gold);} .tnv{color:var(--navy);}
.tsc{color:var(--green);} .tdn{color:var(--red);} .tam{color:var(--amber);}
.fw6{font-weight:600;} .mono{font-family:monospace;}
.sh{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;}
.tabs{display:flex;gap:3px;background:var(--cream);border-radius:10px;padding:4px;margin-bottom:20px;flex-wrap:wrap;}
.tab{padding:7px 15px;border-radius:7px;font-size:13px;cursor:pointer;color:var(--slate);font-weight:500;transition:all .15s;}
.tab.active{background:#fff;color:var(--navy);box-shadow:var(--sh);}
.al{padding:11px 14px;border-radius:var(--r);font-size:13px;margin-bottom:16px;display:flex;align-items:flex-start;gap:8px;}
.al-w{background:#fffbeb;border:1px solid #fde68a;color:#92400e;}
.al-i{background:#eff6ff;border:1px solid #bfdbfe;color:#1e40af;}
.al-d{background:#fef2f2;border:1px solid #fecaca;color:#dc2626;}
.al-s{background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;}
.es{text-align:center;padding:50px 24px;color:var(--slate);}
.es-icon{margin-bottom:14px;opacity:.2;}

/* ── PROGRESS ── */
.pbw{background:var(--cream);border-radius:20px;height:7px;overflow:hidden;margin-top:5px;}
.pbf{height:100%;border-radius:20px;transition:width .3s;}
.pbok{background:var(--green);} .pbwn{background:var(--amber);} .pbov{background:var(--red);}

/* ── WEEK GRID ── */
.wk-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:9px;margin-bottom:22px;}
.wkd{background:#fff;border-radius:var(--r);padding:13px 10px;border:1px solid var(--border);text-align:center;cursor:default;}
.wkd.today{border-color:var(--gold);background:var(--gold-pale);}
.wkd.missing{border-color:#fca5a5;background:#fef2f2;}
.wkd.future{opacity:.4;}
.wkd-lbl{font-size:10px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:var(--slate);}
.wkd-num{font-size:20px;font-weight:600;margin:3px 0;}
.wkd-hrs{font-size:12px;font-weight:600;}
.wkd-dot{width:7px;height:7px;border-radius:50%;margin:5px auto 0;}
.dok{background:var(--green);} .dwn{background:var(--amber);} .dno{background:#e2e8f0;}

/* ── AUDIT ── */
.audit-row{display:flex;gap:13px;padding:12px 0;border-bottom:1px solid var(--border);}
.audit-row:last-child{border-bottom:none;}
.audit-ts{font-size:11px;color:var(--slate-light);}
`;

// useLS is defined above using Firestore

// ══════════════════════════════════════════════════════════════
// LOGIN
// ══════════════════════════════════════════════════════════════
function Login({ onLogin }) {
  const [email,setEmail]=useState(""); const [pw,setPw]=useState(""); const [err,setErr]=useState(""); const [loading,setLoading]=useState(false);
  const go = async () => {
    setErr(""); setLoading(true);
    try {
      // Sign in via Firebase Auth
      await signInWithEmailAndPassword(auth, email.trim(), pw);
      // Load user profile from Firestore (security rules only allow this for staff on the access list)
      let usersSnap;
      try { usersSnap = await getDocs(collection(db, "users")); }
      catch(e) {
        console.error("Profile load refused", e);
        await signOut(auth).catch(()=>{});
        setErr("Your account does not have access yet. Please contact the Admin.");
        setLoading(false); return;
      }
      const users = usersSnap.docs.map(d=>({...d.data(),id:d.id}));
      const u = users.find(x=>roleKey(x.email)===roleKey(email)&&x.active);
      if(!u){ await signOut(auth).catch(()=>{}); setErr("No active account found for this email."); setLoading(false); return; }
      onLogin(u);
    } catch(e) {
      if(e.code==="auth/invalid-credential"||e.code==="auth/wrong-password"||e.code==="auth/user-not-found"){
        setErr("Incorrect email or password. Please try again.");
      } else if(e.code==="auth/too-many-requests"){
        setErr("Too many attempts. Please try again later.");
      } else {
        setErr("Sign in failed. Please try again.");
      }
    }
    setLoading(false);
  };
  return (
    <div className="lw">
      <div className="ll">
        <div className="lbrand">
          <div className="lbrand-logo">MSNA</div>
          <div className="lbrand-sub">& Associates LLP</div>
          <div className="lbrand-tag">Time Intelligence Platform — track every hour, correlate every rupee.</div>
        </div>
      </div>
      <div className="lr">
        <div className="lfw">
          <div className="ltitle">Welcome back</div>
          <div className="lsub">Sign in with your MSNA email</div>
          {err&&<div className="err">{err}</div>}
          <div className="fg"><label className="fl">Email</label><input className="fi" type="email" placeholder="you@msna.co.in" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{if(e.key==="Enter") go();}}/></div>
          <div className="fg"><label className="fl">Password</label><input className="fi" type="password" placeholder="••••••••" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>{if(e.key==="Enter") go();}}/></div>
          <button className="btn bp bfl" style={{padding:"13px",marginTop:4}} onClick={go} disabled={loading}><I n="lock" s={16}/>{loading?"Signing in...":"Sign In"}</button>

        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SIDEBAR
// ══════════════════════════════════════════════════════════════
function Sidebar({ user, tab, setTab, onLogout, pendingCount, leavePendingCount=0, projPendingCount=0, projClosurePendingCount=0, appraisalPendingCount=0, goalPendingCount=0, paOverdueCount=0, awCount=0 }) {
  const isAdmin = user.email===ADMIN_EMAIL;
  const role = user.role; // "partner" | "manager" | "intern"

  // ── Nav groups with role gates ──────────────────────────────
  // Each group: { label, items[] } — label null = no section header (top-level)
  // Item roles: which roles can see this item
  const groups = [
    {
      label: null,
      items: [
        { id:"dashboard", icon:"chart",    label:"Dashboard" },
      ]
    },
    {
      label: "My Work",
      roles: ["partner","manager","intern"], // group visible to all
      items: [
        { id:"week",       icon:"calendar", label:"My Week" },
        { id:"timesheets", icon:"clock",    label:"Timesheets" },
        { id:"leave",      icon:"calendar", label:"Leave",       leaveBadge:true },
        { id:"assignedwork", icon:"task",   label:"Assigned Work", awBadge:true, roles:["partner","manager"] },
      ]
    },
    {
      label: "Engagements",
      roles: ["partner","manager","intern"], // all roles see Projects; Approvals filtered per-item below
      items: [
        { id:"projects",  icon:"folder",  label:"Projects",  projBadge:true, roles:["partner","manager","intern"] },
        { id:"approvals", icon:"shield",  label:"Approvals", badge:true,     roles:["partner","manager"] },
      ]
    },
    {
      label: "Insights",
      roles: ["partner"], // partner-only analytics
      items: [
        { id:"reports",       icon:"chart",  label:"Reports" },
        { id:"profitability", icon:"target", label:"Profitability" },
        { id:"productivity",  icon:"chart",  label:"Productivity" },
      ]
    },
    {
      label: "Partners",
      roles: ["partner"], // partner-only: actionables from partners' meetings (v37)
      items: [
        { id:"partneractions", icon:"task", label:"Partner Actionables", paBadge:true },
      ]
    },
    {
      label: "Compliance",
      roles: ["partner","manager"], // managers need this to track intern non-compliance
      items: [
        { id:"compliance", icon:"shield", label:"Compliance" },
      ]
    },
    {
      label: "Performance",
      roles: ["partner","manager","intern"], // everyone sees their own appraisals; scope enforced inside the module
      items: [
        { id:"appraisal", icon:"star", label:"Performance Appraisal", appraisalBadge:true },
        { id:"goals",     icon:"target", label:"Goal Setting", goalBadge:true },
      ]
    },
    {
      label: "Admin",
      roles: ["partner"], // partner-only governance
      items: [
        { id:"audit",      icon:"history", label:"Audit Trail" },
        { id:"users",      icon:"users",   label:"User Management" },
      ]
    },
  ];

  // Filter groups and items by role
  const visibleGroups = groups
    .filter(g => !g.roles || g.roles.includes(role))
    .map(g => ({ ...g, items: g.items.filter(item => !item.roles || item.roles.includes(role)) }))
    .filter(g => g.items.length > 0);

  return (
    <div className="sb">
      <div className="sb-hd"><div className="sb-brand">MSNA</div><div className="sb-bsub">Time Tracker</div></div>
      <div className="sb-user">
        <div className="sb-name">{user.name}</div>
        <div className={`sb-role r${user.role[0]}`}>{user.role.charAt(0).toUpperCase()+user.role.slice(1)}{isAdmin?" · Admin":""}</div>
        <div style={{display:"flex",gap:6,marginTop:12}}>
          <button onClick={()=>setTab("changepassword")}
            title="Change Password"
            style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"7px 10px",borderRadius:7,
              background:tab==="changepassword"?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.05)",
              color:tab==="changepassword"?"var(--gold)":"rgba(255,255,255,0.7)",
              border:"1px solid",
              borderColor:tab==="changepassword"?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.08)",
              fontSize:11,fontWeight:500,cursor:"pointer",transition:"all .15s",letterSpacing:"0.3px"}}>
            <I n="lock" s={12}/>Password
          </button>
          <button onClick={onLogout}
            title="Sign Out"
            style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"7px 10px",borderRadius:7,
              background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.7)",
              border:"1px solid rgba(255,255,255,0.08)",
              fontSize:11,fontWeight:500,cursor:"pointer",transition:"all .15s",letterSpacing:"0.3px"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(220,38,38,0.15)";e.currentTarget.style.color="#fca5a5";e.currentTarget.style.borderColor="rgba(220,38,38,0.3)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.05)";e.currentTarget.style.color="rgba(255,255,255,0.7)";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";}}>
            <I n="logout" s={12}/>Sign Out
          </button>
        </div>
      </div>

      <div className="sb-nav">
        {visibleGroups.map((g, gi) => (
          <div key={gi}>
            {/* Divider between groups (not before the very first) */}
            {gi > 0 && <div className="sb-divider"/>}
            {/* Section label (skip for the unlabelled top group) */}
            {g.label && <div className="sb-section-label">{g.label}</div>}
            {/* Nav items */}
            {g.items.map(n => (
              <div key={n.id} className={`ni ${tab===n.id?"active":""}`} onClick={()=>setTab(n.id)}>
                <I n={n.icon} s={16}/>{n.label}
                {n.badge     && pendingCount>0     && <span className="nb">{pendingCount}</span>}
                {n.leaveBadge && leavePendingCount>0 && <span className="nb">{leavePendingCount}</span>}
                {n.projBadge  && projPendingCount>0  && <span className="nb" style={projClosurePendingCount>0?{right:44}:undefined}>{projPendingCount}</span>}
                {n.projBadge  && projClosurePendingCount>0  && <span className="nb2" title="Pending closure requests">{projClosurePendingCount}</span>}
                {n.appraisalBadge && appraisalPendingCount>0 && <span className="nb">{appraisalPendingCount}</span>}
                {n.goalBadge && goalPendingCount>0 && <span className="nb">{goalPendingCount}</span>}
                {n.paBadge && paOverdueCount>0 && <span className="nb3" title="Your overdue actionables">{paOverdueCount}</span>}
                {n.awBadge && awCount>0 && <span className={user.role==="partner"?"nb":"nb3"} title={user.role==="partner"?"Work awaiting acceptance":"Your overdue work"}>{awCount}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════
function Dashboard({ user, users=[], projects=[], tss=[], paActions=[], onOpenActions, onOpenProjects, awItems=[], onOpenWork }) {
  const isP=user.role==="partner";
  const mySheets = isP?tss:tss.filter(t=>t.userId===user.id);
  const approved = mySheets.filter(t=>t.status==="approved");
  const totalHrs = approved.reduce((s,t)=>s+t.hours,0);
  const billVal  = approved.filter(t=>t.billable).reduce((s,t)=>s+t.hours*(users.find(u=>u.id===t.userId)?.billingRate||0),0);
  const pending  = isP
    ? tss.filter(t=>["pending","resubmitted"].includes(t.status)&&users.find(u=>u.id===t.userId)?.role==="manager").length
    : user.role==="manager"
    ? tss.filter(t=>["pending","resubmitted"].includes(t.status)&&users.find(u=>u.id===t.userId)?.role==="intern").length
    : mySheets.filter(t=>t.status==="pending").length;
  const activeP  = projects.filter(p=>p.status==="active").length;

  const budgetAlerts = isP?projects.filter(p=>{
    if(p.status!=="active"||!p.budgetHours) return false;
    const used=tss.filter(t=>t.projectId===p.id&&t.status==="approved").reduce((s,t)=>s+t.hours,0);
    return used/p.budgetHours>=0.8;
  }):[];

  const recent=(isP?tss:mySheets).slice().reverse().slice(0,6);

  return (
    <div>
      {budgetAlerts.length>0&&(
        <div style={{background:"#fef9ec",border:"1px solid #fde68a",borderRadius:12,padding:"16px 20px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <I n="alert" s={18}/>
            <div style={{fontSize:14,fontWeight:600,color:"#92400e"}}>Budget Alert — {budgetAlerts.length} engagement{budgetAlerts.length>1?"s":""} nearing or exceeding budget</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))",gap:10}}>
            {budgetAlerts.map(p=>{
              const used=tss.filter(t=>t.projectId===p.id&&t.status==="approved").reduce((s,t)=>s+t.hours,0);
              const pct=Math.round(used/p.budgetHours*100);
              const isOver=pct>=100;
              return (
                <div key={p.id} style={{background:"#fff",borderRadius:8,padding:"12px 14px",border:"1px solid #fde68a"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontFamily:"monospace",color:"var(--slate)",marginBottom:2}}>{p.code}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--navy)",lineHeight:1.3}}>{p.clientName}</div>
                      <div style={{fontSize:11,color:"var(--slate)",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                    </div>
                    <div style={{fontSize:16,fontWeight:700,color:isOver?"var(--red)":"#d97706",flexShrink:0}}>{pct}%</div>
                  </div>
                  <div style={{background:"#fef3c7",borderRadius:4,height:6,overflow:"hidden"}}>
                    <div style={{background:isOver?"var(--red)":"#d97706",height:"100%",width:Math.min(pct,100)+"%",transition:"width .3s"}}/>
                  </div>
                  <div style={{fontSize:11,color:"var(--slate)",marginTop:6,display:"flex",justifyContent:"space-between"}}>
                    <span>{fmtHrs(used)}h used</span>
                    <span>of {p.budgetHours}h budget</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ELDashboardCard user={user} users={users} projects={projects} onOpenProjects={onOpenProjects}/>
      <div className="sg">
        <div className="sc"><div className="sv">{activeP}</div><div className="sl">Active Engagements</div></div>
        <div className="sc"><div className="sv">{totalHrs.toFixed(1)}</div><div className="sl">{isP?"Firm Hours":"My Approved Hrs"}</div></div>
        {user.role!=="intern"&&<div className="sc"><div className="sv" style={{color:"var(--gold)"}}>{fmtCurrency(billVal)}</div><div className="sl">Billing Value</div></div>}
        <div className="sc"><div className="sv" style={{color:pending>0?"var(--amber)":"var(--green)"}}>{pending}</div><div className="sl">{user.role==="intern"?"Pending Entries":"Pending Approvals"}</div></div>
      </div>
      {isP&&<PADashboardCard user={user} actions={paActions} onOpen={onOpenActions}/>}
      <AWDashboardCard user={user} items={awItems} onOpen={onOpenWork}/>
      <div className="card">
        <div className="card-title mb16">Recent Activity</div>
        {recent.length===0?<div className="es"><div className="es-icon"><I n="clock" s={40}/></div>No entries yet.</div>:(
          <div className="tw"><table>
            <thead><tr>{isP&&<th>Staff</th>}<th>Date</th><th>Project</th><th>Hrs</th><th>Billable</th><th>Status</th></tr></thead>
            <tbody>{recent.map(ts=>{
              const u2=users.find(u=>u.id===ts.userId); const p=projects.find(p=>p.id===ts.projectId);
              return <tr key={ts.id}>
                {isP&&<td className="fw6">{u2?.name}<div className="tx tsl">{u2?.role}</div></td>}
                <td>{fmtDate(ts.date)}</td>
                <td>{p?<><span className="fw6">{p.code}</span><div className="tx tsl">{p.name}</div></>:"—"}</td>
                <td className="fw6">{fmtHrs(ts.hours)}h</td>
                <td>{ts.billable?<span className="tsc fw6">✓</span>:<span className="tsl">—</span>}</td>
                <td><span className={`bdg ${ts.status==="approved"?"ba":ts.status==="rejected"?"br":ts.status==="resubmitted"?"brs":"bp2"}`}>{ts.status}</span></td>
              </tr>;
            })}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// WEEK VIEW
// ══════════════════════════════════════════════════════════════
function WeekView({ user, tss=[], projects=[] }) {
  const [offset,setOffset]=useState(0);
  const week=getWeekDates(offset); const td=todayStr();
  const mine=tss.filter(t=>t.userId===user.id);
  const dayNames=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const missingWorkdays=week.filter((d,i)=>i<5&&d<=td&&!mine.some(t=>t.date===d));

  return (
    <div>
      <div className="sh">
        <div><div className="card-title">My Week</div><div className="card-sub mt4 ts">{fmtDate(week[0])} — {fmtDate(week[6])}</div></div>
        <div className="fxc g8">
          <button className="btn bgh bsm" onClick={()=>setOffset(o=>o-1)}>← Previous</button>
          {offset!==0&&<button className="btn bgh bsm" onClick={()=>setOffset(0)}>This Week</button>}
          {offset<0&&<button className="btn bgh bsm" onClick={()=>setOffset(o=>o+1)}>Next →</button>}
        </div>
      </div>

      {missingWorkdays.length>0&&(
        <div className="al al-w">
          <I n="alert" s={16}/>
          <div><strong>Missing entries on: </strong>{missingWorkdays.map(d=>fmtDate(d)).join(", ")}</div>
        </div>
      )}

      <div className="wk-grid">
        {week.map((d,i)=>{
          const ds=mine.filter(t=>t.date===d);
          const hrs=ds.reduce((s,t)=>s+t.hours,0);
          const isFut=d>td; const isTod=d===td; const isMis=i<5&&d<=td&&ds.length===0;
          return (
            <div key={d} className={`wkd ${isTod?"today":""} ${isMis?"missing":""} ${isFut?"future":""}`}>
              <div className="wkd-lbl">{dayNames[i]}</div>
              <div className="wkd-num">{d.slice(8)}</div>
              <div className={`wkd-hrs ${isFut?"tsl":hrs>0?"tsc":isMis?"tdn":"tsl"}`}>{isFut?"—":hrs>0?hrs+"h":"0h"}</div>
              <div className={`wkd-dot ${isFut?"":hrs>=6?"dok":hrs>0?"dwn":"dno"}`}/>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-title mb16">Entries This Week</div>
        {mine.filter(t=>week.includes(t.date)).length===0
          ?<div className="es"><div className="es-icon"><I n="calendar" s={36}/></div>No entries this week.</div>
          :<div className="tw"><table>
            <thead><tr><th>Date</th><th>Project</th><th>Category</th><th>Hrs</th><th>Billable</th><th>Description</th><th>Status</th></tr></thead>
            <tbody>{mine.filter(t=>week.includes(t.date)).sort((a,b)=>a.date>b.date?1:-1).map(ts=>{
              const p=projects.find(p=>p.id===ts.projectId);
              return <tr key={ts.id}>
                <td>{fmtDate(ts.date)}</td>
                <td>{p?<><div className="fw6">{p.code}</div><div className="tx tsl">{p.name}</div></>:"—"}</td>
                <td className="ts">{ts.category}</td>
                <td className="fw6">{fmtHrs(ts.hours)}h</td>
                <td>{ts.billable?<span className="tsc fw6">✓</span>:<span className="tsl">—</span>}</td>
                <td className="ts tsl" style={{maxWidth:180}}>{ts.description}</td>
                <td>
                  <span className={`bdg ${ts.status==="approved"?"ba":ts.status==="rejected"?"br":ts.status==="resubmitted"?"brs":"bp2"}`}>{ts.status}</span>
                  {ts.status==="rejected"&&ts.rejectReason&&<div className="tx tdn mt4">{ts.rejectReason}</div>}
                </td>
              </tr>;
            })}</tbody>
          </table></div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TIMESHEETS
// ══════════════════════════════════════════════════════════════
function Timesheets({ user, tss=[], setTss, users=[], projects=[], locked:lockedMonths=[] }) {
  const [showM,setSM]        = useState(false);
  const [editE,setEE]        = useState(null);
  const [fs,setFs]           = useState("all");
  const [onBehalf,setOB]     = useState(false);
  const [form,setF]          = useState({date:todayStr(),projectId:"",hours:"",category:"Assurance",description:"",billable:false,onBehalfOfId:"",isInternal:false,internalType:"Leave",internalApprovers:[],internalPartnerApprovers:[]});
  const [isInternal,setIsInt]= useState(false);
  const [ferr,setFerr]       = useState("");
  // Filters, sort, pagination
  const [filterStaff,setFStaff]   = useState("");
  const [filterProj,setFProj]     = useState("");
  const [filterCat,setFCat]       = useState("");
  const [filterBill,setFBill]     = useState("");
  const [filterFrom,setFFrom]     = useState("");
  const [filterTo,setFTo]         = useState("");
  const [sortCol,setSortCol]      = useState("date");
  const [sortDir,setSortDir]      = useState("desc");
  const [tsPage,setTsPage]        = useState(1);
  const TS_PAGE = 10;
  const isP = user.role==="partner";
  const isMgr = user.role==="manager";
  const toggleSort = col => { if(sortCol===col){setSortDir(d=>d==="asc"?"desc":"asc");}else{setSortCol(col);setSortDir("desc");} setTsPage(1); };
  const sortIcon = col => sortCol===col?(sortDir==="asc"?" ↑":" ↓"):"";

  // Projects this user is assigned to
  const bookable = isP
    ? projects.filter(p=>p.status==="active"&&(
        p.assignedPartnerId===user.id ||
        (p.assignedPartners||[]).includes(user.id)
      ))
    : projects.filter(p=>p.status==="active"&&(
        [...(p.assignedStaff||[]),...(p.assignedManagers||[])].includes(user.id)
      ));

  // For "on behalf" mode: projects where the user (manager OR intern) is assigned
  const onBehalfProjects = !isP
    ? projects.filter(p=>p.status==="active"&&(
        (isMgr && (p.assignedManagers||[]).includes(user.id)) ||
        (!isMgr && (p.assignedStaff||[]).includes(user.id))
      ))
    : [];

  // Get ALL partners for a given project (assigned + additional)
  const getProjectPartners = (projId) => {
    const p = projects.find(x=>x.id===projId);
    if(!p) return [];
    const assignedP = users.find(u=>u.id===p.assignedPartnerId);
    const additionalP = (p.assignedPartners||[]).map(id=>users.find(u=>u.id===id)).filter(Boolean);
    return assignedP ? [assignedP,...additionalP] : additionalP;
  };


  // All entries visible to this user
  // Partners see all; managers/interns see own + entries filed on their behalf (rejected, needs refiling)
  const mine = isP ? tss : tss.filter(t=>
    t.userId===user.id ||
    (t.filedById===user.id && ["pending_partner","rejected_partner"].includes(t.status))
  );
  const filtered = mine.filter(t=>{
    if(fs!=="all"&&t.status!==fs&&
      !(fs==="pending"&&t.status==="pending_partner")&&
      !(fs==="rejected"&&t.status==="rejected_partner")) return false;
    if(filterStaff&&t.userId!==filterStaff) return false;
    if(filterProj&&t.projectId!==filterProj) return false;
    if(filterCat&&t.category!==filterCat) return false;
    if(filterBill==="yes"&&!t.billable) return false;
    if(filterBill==="no"&&t.billable) return false;
    if(filterFrom&&t.date<filterFrom) return false;
    if(filterTo&&t.date>filterTo) return false;
    return true;
  }).slice().sort((a,b)=>{
    let va,vb;
    if(sortCol==="date"){va=a.date;vb=b.date;}
    else if(sortCol==="hours"){va=a.hours;vb=b.hours;}
    else if(sortCol==="staff"){va=users.find(u=>u.id===a.userId)?.name||"";vb=users.find(u=>u.id===b.userId)?.name||"";}
    else if(sortCol==="project"){va=projects.find(p=>p.id===a.projectId)?.code||"";vb=projects.find(p=>p.id===b.projectId)?.code||"";}
    else if(sortCol==="category"){va=a.category||"";vb=b.category||"";}
    else if(sortCol==="status"){va=a.status;vb=b.status;}
    else{va=a.date;vb=b.date;}
    if(va<vb) return sortDir==="asc"?-1:1;
    if(va>vb) return sortDir==="asc"?1:-1;
    return 0;
  });
  const totalTsPages = Math.max(1,Math.ceil(filtered.length/TS_PAGE));
  const paginated = filtered.slice((tsPage-1)*TS_PAGE, tsPage*TS_PAGE);
  const hasFilters = filterStaff||filterProj||filterCat||filterBill||filterFrom||filterTo;

  const openAdd = (behalf=false, internal=false) => {
    setOB(behalf);
    setIsInt(internal);
    setEE(null);
    setF({date:todayStr(),projectId:"",hours:"",
      category:internal?"Leave":"Assurance",  // default category matches mode
      description:"",billable:!internal,  // internal entries are never billable
      onBehalfOfId:"",
      isInternal:internal,internalType:"Leave",
      internalApprovers:[],internalPartnerApprovers:[]});
    setFerr("");
    setSM(true);
  };

  const openEdit = ts => {
    const isBehalf = !!ts.filedById;
    const internal = !!ts.isInternal;
    setOB(isBehalf);
    setIsInt(internal);
    setEE(ts);
    setF({date:ts.date,projectId:ts.projectId||"",hours:ts.hours,category:ts.category,description:ts.description||"",billable:ts.billable,onBehalfOfId:ts.onBehalfOfId||"",isInternal:internal,internalType:ts.internalType||"Leave"});
    setFerr("");
    setSM(true);
  };

  const save = () => {
    if(isInternal){
      if(!form.date||!form.hours){ setFerr("Date and hours are required."); return; }
      if(!isP){
        if(!isMgr&&(form.internalApprovers||[]).length===0){ setFerr("Please select at least one manager approver."); return; }
        if((form.internalPartnerApprovers||[]).length===0){ setFerr("Please select at least one partner approver."); return; }
      }
    } else {
      if(!form.date||!form.projectId||!form.hours||!form.description.trim()){ setFerr("All fields are required."); return; }
      if(onBehalf&&!form.onBehalfOfId){ setFerr("Please select the Partner you are filing for."); return; }
    }
    // onBehalf check now handled above
    const h=Number(form.hours);
    if(isNaN(h)||h<0.5||h>24){ setFerr("Hours must be between 0.5 and 24."); return; }
    if(form.date<minDate()){ setFerr(`Cannot log time more than ${MAX_BACKDATE_DAYS} days in the past.`); return; }
    if(form.date>todayStr()){ setFerr("Cannot log time for a future date."); return; }
    if(lockedMonths.includes(monthKey(form.date))){ setFerr(`${monthLabel(monthKey(form.date))} is locked. Contact the Admin.`); return; }

    // Determine the actual owner of this entry
    const ownerId = (!isInternal && onBehalf) ? form.onBehalfOfId : user.id;
    const dayHrs=tss.filter(t=>t.userId===ownerId&&t.date===form.date&&t.id!==(editE?.id)).reduce((s,t)=>s+t.hours,0);
    if(dayHrs+h>24){ setFerr(`Total hours for ${fmtDate(form.date)} would exceed 24 (${dayHrs}h already logged for this person).`); return; }

    if(editE){
      let newStatus;
      if(onBehalf) newStatus = "pending_partner"; // re-filed, back to partner review
      else if(editE.status==="approved") newStatus = "approved"; // partner editing approved entry — stays approved
      else if(user.role==="partner") newStatus = "approved";
      else newStatus = editE.status==="rejected"?"resubmitted":"pending";

      // When a partner edits an already-approved entry, preserve original approval but record the edit
      const isPartnerEditOfApproved = user.role==="partner" && editE.status==="approved" && !onBehalf;
      const editedEntry = {
        ...form, hours:h, status:newStatus,
        // Internal entries are NEVER billable
        billable: (editE.isInternal || isInternal) ? false : form.billable,
        userId: ownerId,
        updatedAt:new Date().toISOString(), updatedBy:user.id,
        ...(onBehalf?{filedById:user.id,filedByName:user.name}:{}),
        // New approval stamp only if this is a fresh approval (not a re-edit of already-approved)
        ...(user.role==="partner"&&!onBehalf&&!isPartnerEditOfApproved?{approvedBy:user.id,approvedAt:new Date().toISOString()}:{}),
        // Track partner edits on approved entries separately for audit clarity
        ...(isPartnerEditOfApproved?{partnerEditedBy:user.id,partnerEditedByName:user.name,partnerEditedAt:new Date().toISOString()}:{}),
      };
      setTss(prev=>prev.map(t=>t.id===editE.id?{...t,...editedEntry}:t));
      addAudit(user.id,user.name,"EDIT_TIMESHEET",
        isPartnerEditOfApproved
          ? `Partner revised approved entry for ${users.find(u=>u.id===ownerId)?.name} on ${form.date} (${h}h)`
          : onBehalf?`Re-filed on behalf of ${users.find(u=>u.id===ownerId)?.name} on ${form.date}`:`Edited entry on ${form.date} (${h}h)`);
    } else {
      const autoApprove = user.role==="partner" && !onBehalf && !isInternal;
      const newEntry = {
        id:genId(),
        userId: ownerId,
        ...form, hours:h,
        // Internal entries are NEVER billable regardless of form state
        billable: isInternal ? false : form.billable,
        // Internal entries: auto-approve if partner, otherwise pending
        status: isInternal
          ? (user.role==="partner" ? "approved" : "pending")
          : onBehalf ? "pending_partner" : autoApprove ? "approved" : "pending",
        // Store selected approvers for internal entries
        ...(isInternal&&!isP?{internalApprovers:form.internalApprovers||[],internalPartnerApprovers:form.internalPartnerApprovers||[]}:{}),
        ...(onBehalf?{filedById:user.id,filedByName:user.name}:{}),
        ...(autoApprove?{approvedBy:user.id,approvedAt:new Date().toISOString()}:{}),
        createdAt:new Date().toISOString()
      };
      setTss(prev=>[...prev,newEntry]);
      addAudit(user.id,user.name,"ADD_TIMESHEET",
        onBehalf
          ? `Filed ${h}h on behalf of ${users.find(u=>u.id===ownerId)?.name} on ${form.date} — ${projects.find(p=>p.id===form.projectId)?.code}`
          : `Logged ${h}h on ${form.date} — ${projects.find(p=>p.id===form.projectId)?.code}`
      );
    }
    setSM(false);
  };

  const del = id => {
    if(!window.confirm("Delete this entry?")) return;
    addAudit(user.id,user.name,"DELETE_TIMESHEET",`Deleted entry ${id}`);
    setTss(prev=>prev.filter(t=>t.id!==id));
  };

  const statusLabel = s => {
    if(s==="pending_partner") return "Pending Partner";
    if(s==="rejected_partner") return "Rejected by Partner";
    return s.charAt(0).toUpperCase()+s.slice(1);
  };
  const statusClass = s =>
    s==="approved"?"ba":s==="rejected"||s==="rejected_partner"?"br":
    s==="resubmitted"||s==="pending_partner"?"brs":"bp2";

  // For on-behalf form: get partner for selected project
  // selectedProjectPartner replaced by getProjectPartners() for multi-partner support

  return (
    <div>
      <div className="sh">
        <div><div className="card-title">Timesheets</div><div className="card-sub mt4 ts">{isP?"All staff entries":"Your daily time log"}</div></div>
        <div className="fxc g8">
          {!isP&&onBehalfProjects.length>0&&(
            <button className="btn bgh bsm" onClick={()=>openAdd(true,false)}><I n="users" s={14}/>File for Partner</button>
          )}
          <button className="btn bgh bsm" onClick={()=>openAdd(false,true)} style={{borderColor:"var(--amber)",color:"var(--amber)"}}><I n="calendar" s={14}/>Internal Time</button>
          <button className="btn bp" onClick={()=>openAdd(false,false)}><I n="plus" s={15}/>Log Engagement Time</button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="tabs">
        {["all","pending","resubmitted","approved","rejected"].map(s=>(
          <div key={s} className={`tab ${fs===s?"active":""}`} onClick={()=>{setFs(s);setTsPage(1);}}>
            {s.charAt(0).toUpperCase()+s.slice(1)}
            {s!=="all"&&<span style={{marginLeft:5,fontSize:11,background:"var(--border)",borderRadius:20,padding:"1px 6px"}}>
              {mine.filter(t=>t.status===s||(s==="pending"&&t.status==="pending_partner")||(s==="rejected"&&t.status==="rejected_partner")).length}
            </span>}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
        {isP&&<select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filterStaff} onChange={e=>{setFStaff(e.target.value);setTsPage(1);}}>
          <option value="">All Staff</option>
          {users.filter(u=>u.active).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>}
        <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filterProj} onChange={e=>{setFProj(e.target.value);setTsPage(1);}}>
          <option value="">All Projects</option>
          {projects.slice().sort((a,b)=>a.code.localeCompare(b.code)).map(p=><option key={p.id} value={p.id}>{p.code} — {p.clientName}</option>)}
        </select>
        <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filterCat} onChange={e=>{setFCat(e.target.value);setTsPage(1);}}>
          <option value="">All Categories</option>
          {[...ENGAGEMENT_CATEGORIES,...INTERNAL_CATEGORIES].map(c=><option key={c}>{c}</option>)}
        </select>
        <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filterBill} onChange={e=>{setFBill(e.target.value);setTsPage(1);}}>
          <option value="">Billable: All</option>
          <option value="yes">Billable only</option>
          <option value="no">Non-billable only</option>
        </select>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <span style={{fontSize:11,color:"var(--slate)"}}>From</span>
          <input type="date" className="fi" style={{fontSize:12,padding:"6px 8px",width:130}} value={filterFrom} onChange={e=>{setFFrom(e.target.value);setTsPage(1);}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <span style={{fontSize:11,color:"var(--slate)"}}>To</span>
          <input type="date" className="fi" style={{fontSize:12,padding:"6px 8px",width:130}} value={filterTo} onChange={e=>{setFTo(e.target.value);setTsPage(1);}}/>
        </div>
        {hasFilters&&<button className="btn bgh bsm" onClick={()=>{setFStaff("");setFProj("");setFCat("");setFBill("");setFFrom("");setFTo("");setTsPage(1);}}>✕ Clear</button>}
        <span className="tx tsl" style={{fontSize:12,marginLeft:"auto"}}>{filtered.length} entr{filtered.length===1?"y":"ies"}</span>
      </div>

      <div className="card">
        {filtered.length===0?<div className="es"><div className="es-icon"><I n="clock" s={36}/></div>No entries found.</div>:(
          <>
          <div className="tw"><table>
            <thead><tr>
              <th style={{width:32}}>#</th>
              {isP&&<th style={{cursor:"pointer"}} onClick={()=>toggleSort("staff")}>Staff{sortIcon("staff")}</th>}
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("date")}>Date{sortIcon("date")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("project")}>Project{sortIcon("project")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("category")}>Category{sortIcon("category")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("hours")}>Hrs{sortIcon("hours")}</th>
              <th>Billable</th><th>Description</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("status")}>Status{sortIcon("status")}</th>
              <th></th>
            </tr></thead>
            <tbody>{paginated.map((ts,idx)=>{
              const u2=users.find(u=>u.id===ts.userId); const p=projects.find(p=>p.id===ts.projectId);
              const locked=lockedMonths.includes(monthKey(ts.date));
              const isOnBehalf = !!ts.filedById;
              // After approval, ONLY partners may edit — at all levels (own, manager, intern entries)
              const canEdit = isOnBehalf
                ? (user.id===ts.userId&&["pending_partner","rejected_partner"].includes(ts.status))
                  || (user.id===ts.filedById&&ts.status==="rejected_partner")
                : ts.status==="approved"
                  ? isP&&!locked
                  : isP||(ts.userId===user.id&&["pending","rejected","resubmitted"].includes(ts.status)&&!locked);
              // Can delete: partner (assigned), or own entry not yet approved
              // eslint-disable-next-line no-mixed-operators
              const canDelete = (isP&&!locked&&(user.email===ADMIN_EMAIL||projects.find(p=>p.id===ts.projectId)?.assignedPartnerId===user.id))
                || (!isP&&ts.userId===user.id&&["pending","rejected","resubmitted"].includes(ts.status)&&!locked);
              return <tr key={ts.id}>
                <td className="tx tsl" style={{fontSize:12}}>{(tsPage-1)*TS_PAGE+idx+1}</td>
                {isP&&<td className="fw6">{u2?.name}<div className="tx tsl">{u2?.role}{isOnBehalf&&<span className="tx tsl"> · filed by {ts.filedByName}</span>}</div></td>}
                <td>{fmtDate(ts.date)}{locked&&<div><span className="bdg blk tx" style={{marginTop:3}}><I n="lock" s={10}/>locked</span></div>}</td>
                <td>{p?<><div className="fw6 mono">{p.code}</div><div className="tx tsl">{p.name}</div></>:"—"}</td>
                <td className="ts">{ts.category}</td>
                <td className="fw6">{fmtHrs(ts.hours)}h</td>
                <td>{ts.billable?<span className="tsc fw6">✓</span>:<span className="tsl">—</span>}</td>
                <td className="ts tsl" style={{maxWidth:180}}>
                  {ts.description}
                  {isOnBehalf&&!isP&&<div className="tx tsl mt4">Filed by {ts.filedByName}</div>}
                  {(ts.status==="rejected"||ts.status==="rejected_partner")&&ts.rejectReason&&<div className="tx tdn mt4">↩ {ts.rejectReason}</div>}
                </td>
                <td><span className={`bdg ${statusClass(ts.status)}`}>{statusLabel(ts.status)}</span></td>
                <td><div className="fx g8">
                  {canEdit&&<button className="btn bgh bic bsm" onClick={()=>openEdit(ts)}><I n="edit" s={14}/></button>}
                  {canDelete&&<button className="btn bd bic bsm" title="Delete entry" onClick={()=>del(ts.id)}><I n="trash" s={14}/></button>}
                </div></td>
              </tr>;
            })}</tbody>
          </table></div>
          {totalTsPages>1&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
              <button className="btn bgh bsm" disabled={tsPage===1} onClick={()=>setTsPage(p=>p-1)}>← Prev</button>
              <span className="ts tsl">Page {tsPage} of {totalTsPages} · {filtered.length} entr{filtered.length===1?"y":"ies"}</span>
              <button className="btn bgh bsm" disabled={tsPage===totalTsPages} onClick={()=>setTsPage(p=>p+1)}>Next →</button>
            </div>
          )}
          </>
        )}
      </div>

      {showM&&(
        <div className="mo" onClick={()=>setSM(false)}>
          <div className="md" onClick={e=>e.stopPropagation()}>
            <div className="md-title">
              {isInternal?"Log Internal Time":onBehalf?"File for Partner":editE?(editE.status==="approved"&&isP&&editE.userId!==user.id?"Edit Entry (Partner Revision)":"Edit Entry"):"Log Engagement Time"}
            </div>
            {editE?.status==="approved"&&isP&&<div className="al al-w mb16"><I n="alert" s={15}/><div>You are revising an <strong>approved</strong> entry. The revised hours will be reflected immediately across all reports and profitability calculations.</div></div>}
            {onBehalf&&<div className="al al-i mb16"><I n="info" s={15}/><div>Filing on behalf of a Partner. They will review and approve before it counts.</div></div>}
            {isInternal&&<div className="al al-w mb16"><I n="info" s={15}/><div>Internal time is not linked to any client engagement. Goes to your manager for approval.</div></div>}
            {editE?.status==="rejected_partner"&&<div className="al al-d"><I n="alert" s={15}/><div>Rejected by Partner: <em>{editE.rejectReason}</em></div></div>}
            {editE?.status==="rejected"&&<div className="al al-d"><I n="alert" s={15}/><div>Rejected: <em>{editE.rejectReason}</em> — please correct and resubmit.</div></div>}
            {ferr&&<div className="err">{ferr}</div>}
            <div className="g2">
              <div className="fg"><label className="fl">Date</label><input type="date" className="fi" value={form.date} min={minDate()} max={todayStr()} onChange={e=>setF(f=>({...f,date:e.target.value}))}/></div>
              <div className="fg"><label className="fl">Hours</label><input type="number" className="fi" placeholder="e.g. 8" min="0.5" max="24" step="0.5" value={form.hours} onChange={e=>setF(f=>({...f,hours:e.target.value}))}/></div>
            </div>
            {isInternal?(
              <>
                <div className="fg">
                  <label className="fl">Type</label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {INTERNAL_CATEGORIES.map(cat=>(
                      <label key={cat} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:"1.5px solid",borderColor:form.internalType===cat?"var(--navy)":"var(--border)",background:form.internalType===cat?"var(--cream)":"#fff",cursor:"pointer"}}>
                        <input type="radio" name="internalType" checked={form.internalType===cat} onChange={()=>setF(f=>({...f,internalType:cat,category:cat}))} style={{accentColor:"var(--navy)"}}/>
                        <div>
                          <div style={{fontSize:13,fontWeight:form.internalType===cat?600:400,color:"var(--navy)"}}>{cat}</div>
                          <div style={{fontSize:11,color:"var(--slate)"}}>{cat==="Leave"?"Personal/sick leave":cat==="Holiday"?"Public/firm holiday":cat==="Idle"?"No billable work":"Training & development"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="fg"><label className="fl">Description (optional)</label>
                  <textarea className="fta" placeholder="Brief notes..." value={form.description} onChange={e=>setF(f=>({...f,description:e.target.value}))}/>
                </div>
                {/* Approver selection for internal time */}
                {!isP&&(
                  <div style={{background:"var(--cream)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--border)"}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--navy)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>Select Approver(s)</div>
                    {!isMgr&&(
                      <div className="fg" style={{marginBottom:10}}>
                        <label className="fl">Manager(s)</label>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                          {users.filter(u=>u.role==="manager"&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(m=>(
                            <label key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:7,border:"1.5px solid",borderColor:(form.internalApprovers||[]).includes(m.id)?"var(--navy)":"var(--border)",background:(form.internalApprovers||[]).includes(m.id)?"var(--cream)":"#fff",cursor:"pointer"}}>
                              <input type="checkbox" checked={(form.internalApprovers||[]).includes(m.id)} onChange={()=>setF(f=>({...f,internalApprovers:(f.internalApprovers||[]).includes(m.id)?(f.internalApprovers||[]).filter(x=>x!==m.id):[...(f.internalApprovers||[]),m.id]}))} style={{accentColor:"var(--navy)"}}/>
                              <span style={{fontSize:13}}>{m.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="fg">
                      <label className="fl">Partner(s)</label>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                        {users.filter(u=>u.role==="partner"&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(p=>(
                          <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:7,border:"1.5px solid",borderColor:(form.internalPartnerApprovers||[]).includes(p.id)?"var(--navy)":"var(--border)",background:(form.internalPartnerApprovers||[]).includes(p.id)?"var(--cream)":"#fff",cursor:"pointer"}}>
                            <input type="checkbox" checked={(form.internalPartnerApprovers||[]).includes(p.id)} onChange={()=>setF(f=>({...f,internalPartnerApprovers:(f.internalPartnerApprovers||[]).includes(p.id)?(f.internalPartnerApprovers||[]).filter(x=>x!==p.id):[...(f.internalPartnerApprovers||[]),p.id]}))} style={{accentColor:"var(--navy)"}}/>
                            <span style={{fontSize:13}}>{p.name}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{fontSize:11,color:"var(--slate)",marginTop:6}}>Visible to all partners — any partner can approve, not only those selected here.</div>
                    </div>
                  </div>
                )}
              </>
            ):(
              <>
                <div className="fg">
                  <label className="fl">Project / Engagement</label>
                  <select className="fs" value={form.projectId} onChange={e=>{
                    const selProj=projects.find(p=>p.id===e.target.value);
                    setF(f=>({...f,projectId:e.target.value,onBehalfOfId:"",
                      category:selProj?.category||f.category,
                      billable:selProj?.billable!==undefined?selProj.billable:f.billable}));
                  }}>
                    <option value="">-- Select Project --</option>
                    {(onBehalf?onBehalfProjects:bookable).slice().sort((a,b)=>a.code.localeCompare(b.code)).map(p=><option key={p.id} value={p.id}>{p.code} · {p.clientName} — {p.name}</option>)}
                  </select>
                  {(onBehalf?onBehalfProjects:bookable).length===0&&<div className="tx tdn mt4">Not assigned to any active engagement. Contact your Partner.</div>}
                  {form.projectId&&(()=>{
                    const sel=projects.find(p=>p.id===form.projectId);
                    if(!sel) return null;
                    const used=tss.filter(t=>t.projectId===sel.id&&t.status==="approved").reduce((s,t)=>s+t.hours,0);
                    const pct=sel.budgetHours?Math.min(Math.round(used/sel.budgetHours*100),100):null;
                    return (
                      <div style={{marginTop:10,padding:"12px 14px",background:"var(--cream)",borderRadius:8,border:"1.5px solid var(--border)"}}>
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                              <span style={{fontFamily:"monospace",fontWeight:700,fontSize:14,color:"var(--navy)"}}>{sel.code}</span>
                              {sel.feeType==="retainer"&&<span style={{fontSize:11,background:"var(--gold-pale)",color:"#92400e",padding:"2px 8px",borderRadius:20,fontWeight:500}}>Retainer</span>}
                              <span style={{fontSize:11,background:"#dbeafe",color:"#1e40af",padding:"2px 8px",borderRadius:20,fontWeight:500}}>{sel.category}</span>
                              <span style={{fontSize:11,background:sel.billable?"#d1fae5":"#f1f5f9",color:sel.billable?"#065f46":"#64748b",padding:"2px 8px",borderRadius:20,fontWeight:500}}>{sel.billable?"Billable":"Non-billable"}</span>
                            </div>
                            <div style={{fontSize:14,fontWeight:600,color:"var(--navy)",marginBottom:2}}>{sel.name}</div>
                            <div style={{fontSize:13,color:"var(--slate)"}}>{sel.clientName}</div>
                          </div>
                          {pct!==null&&(
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:12,color:"var(--slate)",marginBottom:4}}>Budget</div>
                              <div style={{fontSize:13,fontWeight:600,color:pct>=100?"var(--red)":pct>=80?"var(--amber)":"var(--green)"}}>{fmtHrs(used)}h / {sel.budgetHours}h ({pct}%)</div>
                              <div style={{width:90,height:5,background:"var(--border)",borderRadius:4,marginTop:5,overflow:"hidden"}}>
                                <div style={{width:pct+"%",height:"100%",borderRadius:4,background:pct>=100?"var(--red)":pct>=80?"var(--amber)":"var(--green)"}}/>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {onBehalf&&form.projectId&&(
                  <div className="fg">
                    <label className="fl">Filing On Behalf Of</label>
                    {(()=>{
                      const allPartners=getProjectPartners(form.projectId);
                      if(allPartners.length===0) return <div className="tx tdn">No partners assigned.</div>;
                      if(allPartners.length===1) return (
                        <div>
                          <div className="fi" style={{color:"var(--navy)",fontWeight:600}}>{allPartners[0].name} <span className="tx tsl">(Assigned Partner)</span></div>
                          {!form.onBehalfOfId&&setTimeout(()=>setF(f=>({...f,onBehalfOfId:allPartners[0].id})),0)}
                        </div>
                      );
                      return (
                        <select className="fs" value={form.onBehalfOfId} onChange={e=>setF(f=>({...f,onBehalfOfId:e.target.value}))}>
                          <option value="">-- Select Partner --</option>
                          {allPartners.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(p=><option key={p.id} value={p.id}>{p.name}{p.id===projects.find(x=>x.id===form.projectId)?.assignedPartnerId?" (Assigned Partner)":""}</option>)}
                        </select>
                      );
                    })()}
                  </div>
                )}
                <div className="g2">
                  <div className="fg">
                    <label className="fl">Category <span className="tx tsl">(pre-filled)</span></label>
                    <select className="fs" value={form.category} onChange={e=>setF(f=>({...f,category:e.target.value}))}>
                      {ENGAGEMENT_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="fg">
                    <label className="fl">Billable? <span className="tx tsl">(pre-filled)</span></label>
                    <select className="fs" value={form.billable?"yes":"no"} onChange={e=>setF(f=>({...f,billable:e.target.value==="yes"}))}>
                      <option value="yes">Yes — Billable</option>
                      <option value="no">No — Non-billable</option>
                    </select>
                  </div>
                </div>
                <div className="fg"><label className="fl">Work Description</label>
                  <textarea className="fta" placeholder="Describe the work done..." value={form.description} onChange={e=>setF(f=>({...f,description:e.target.value}))}/>
                </div>
              </>
            )}
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setSM(false)}>Cancel</button>
              <button className="btn bp" onClick={()=>{
                const allP=onBehalf?getProjectPartners(form.projectId):[];
                if(onBehalf&&allP.length===1&&!form.onBehalfOfId){setF(f=>({...f,onBehalfOfId:allP[0].id}));setTimeout(save,50);}
                else{save();}
              }}>
                <I n={editE?.status==="rejected_partner"||editE?.status==="rejected"?"send":"check"} s={15}/>
                {isInternal?"Save Internal Time":onBehalf?"Submit for Partner Review":editE?.status==="rejected"?"Resubmit":"Save Entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PROJECTS
// ══════════════════════════════════════════════════════════════
function AssignModal({ project, users:propUsers=[], onSave, onClose }) {
  // Load users directly from Firestore to guarantee data is available
  const [liveUsers, setLiveUsers] = useState(propUsers);
  useEffect(() => {
    getDocs(collection(db, "users")).then(snap => {
      if (!snap.empty) setLiveUsers(snap.docs.map(d=>({...d.data(),id:d.id})));
    }).catch(()=>{});
  }, []);
  const users = liveUsers.length > 0 ? liveUsers : propUsers;
  const [staff,    setStaff]    = useState(project.assignedStaff||[]);
  const [managers, setManagers] = useState(project.assignedManagers||[]);
  const [partners, setPartners] = useState(project.assignedPartners||[]);
  const assignedPartnerId = project.assignedPartnerId;

  const toggle = (id, role) => {
    if(role==="manager") setManagers(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
    else if(role==="partner") setPartners(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
    else setStaff(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  };

  return (
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()}>
        <div className="md-title">Assign Staff — {project.code}</div>
        <div className="al al-i"><I n="info" s={15}/><div>Only assigned staff can log time to this engagement.</div></div>
        <div style={{maxHeight:400,overflowY:"auto"}}>
          {/* Partners section */}
          <div className="mb16">
            <div className="fl" style={{marginBottom:10}}>Additional Partners</div>
            <div className="al al-i" style={{marginBottom:10,padding:"8px 12px"}}><I n="info" s={13}/><div className="tx">The Assigned Partner is already included. Add other partners who will also work on this engagement.</div></div>
            {users.filter(u=>u.role==="partner"&&u.active&&u.id!==assignedPartnerId).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(u=>{
              const checked=partners.includes(u.id);
              return <label key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",cursor:"pointer",borderBottom:"1px solid var(--border)"}}>
                <input type="checkbox" checked={checked} onChange={()=>toggle(u.id,"partner")} style={{width:15,height:15}}/>
                <span className="fw6 ts">{u.name}</span><span className="tx tsl">{u.email}</span>
              </label>;
            })}
          </div>
          {/* Managers and Interns */}
          {["manager","intern"].map(role=>(
            <div key={role} className="mb16">
              <div className="fl" style={{marginBottom:10}}>{role.charAt(0).toUpperCase()+role.slice(1)}s</div>
              {users.filter(u=>u.role===role&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(u=>{
                const checked=role==="manager"?managers.includes(u.id):staff.includes(u.id);
                return <label key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",cursor:"pointer",borderBottom:"1px solid var(--border)"}}>
                  <input type="checkbox" checked={checked} onChange={()=>toggle(u.id,role)} style={{width:15,height:15}}/>
                  <span className="fw6 ts">{u.name}</span><span className="tx tsl">{u.email}</span>
                </label>;
              })}
            </div>
          ))}
        </div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bp" onClick={()=>onSave(project.id,staff,managers,partners)}><I n="check" s={15}/>Save Assignments</button>
        </div>
      </div>
    </div>
  );
}

function Projects({ user, projects=[], setProjects, users=[], tss=[], appraisals=[] }) {
  const [showM,setSM]         =useState(false);
  const [editP,setEditP]      =useState(null); // project being edited
  const [assignM,setAM]       =useState(null);
  const [projTab,setProjTab]  =useState("active"); // active, closed, pending, closure, rejected, all
  const [rejectM,setRejectM]  =useState(null);
  const [rejectReason,setRR]  =useState("");
  const [closeRejectM,setCRM] =useState(null); // project being rejected at closure stage
  const [closeRejectReason,setCRR] =useState("");
  const [page,setPage]        =useState(1);
  const PAGE_SIZE             =10;
  const [search,setSearch]    =useState("");
  const [filterPartner,setFP] =useState("");
  const [filterCat,setFC]     =useState("");
  const [filterFee,setFF]     =useState("");
  const [filterEL,setFEL]     =useState(""); // v48: "", attention, pending, unrecorded, shared (v52: na)
  const [closureM,setClosureM]=useState(null); // v48: { id, mode: close | request | approve }
  const [markELP,setMarkELP]  =useState(null); // v48: project whose draft EL is being marked as shared
  const [signELP,setSignELP]  =useState(null); // v50: older code whose signed EL is being confirmed
  const [naELP,setNaELP]      =useState(null); // v52: code being marked as EL not applicable
  const [sortBy,setSortBy]    =useState("code");
  const [sortDir,setSortDir]  =useState("asc");
  const [form,setF]           =useState({code:"",name:"",clientName:"",description:"",assignedPartnerId:"",budgetHours:"",monthlyBudgetHours:"",engagementFee:"",feeType:"fixed",retainerMonths:"",category:"Assurance",billable:true,assignedStaff:[],assignedManagers:[],assignedPartners:[],elDraftShared:null,elDraftSharedDate:""});
  const [ferr,setFerr]        =useState("");
  const isP=user.role==="partner";
  const isMgr=user.role==="manager";
  const legacyEL = !!editP && elIsLegacy(editP); // v50: older code being edited → ask about the signed EL instead
  const partners=users.filter(u=>u.role==="partner"&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const lastProjectCode=(()=>{const inCat=projects.filter(p=>(p.category||"Assurance")===(form.category||"Assurance"));if(!inCat.length)return null;return inCat.slice().sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""))[0].code;})();

  const openAdd=()=>{setEditP(null);setF({code:"",name:"",clientName:"",description:"",assignedPartnerId:isP?user.id:"",budgetHours:"",monthlyBudgetHours:"",engagementFee:"",feeType:"fixed",retainerMonths:"",category:"Assurance",billable:true,assignedStaff:[],assignedManagers:[],assignedPartners:[],elDraftShared:null,elDraftSharedDate:""});setFerr("");setSM(true);};
  const openEdit=(p)=>{setEditP(p);setF({code:p.code,name:p.name,clientName:p.clientName,description:p.description||"",assignedPartnerId:p.assignedPartnerId,budgetHours:p.budgetHours||"",monthlyBudgetHours:p.monthlyBudgetHours||"",engagementFee:p.monthlyFee||p.engagementFee||"",feeType:p.feeType||"fixed",retainerMonths:p.retainerMonths||"",category:p.category||"Assurance",billable:p.billable!==false,assignedStaff:p.assignedStaff||[],assignedManagers:p.assignedManagers||[],assignedPartners:p.assignedPartners||[],elDraftShared:(typeof p.elDraftShared==="boolean"||p.elDraftShared==="na")?p.elDraftShared:null,elDraftSharedDate:p.elDraftSharedDate||""});setFerr("");setSM(true);};

  const save=()=>{
    if(!form.code||!form.name||!form.clientName||!form.assignedPartnerId){setFerr("Code, name, client and partner are required.");return;}
    if(!editP&&projects.find(p=>p.code.toUpperCase()===form.code.toUpperCase())){setFerr("Project code already exists.");return;}
    // v48: draft EL question is mandatory; either answer lets the code be created
    if(![true,false,"na"].includes(form.elDraftShared)){setFerr("Answer the Engagement Letter question (Yes, No or Not applicable).");return;}
    if(form.elDraftShared===true&&!form.elDraftSharedDate&&!editP?.elDraftAutoFromSigned&&!legacyEL){setFerr("Enter the date the draft Engagement Letter was shared.");return;}
    if(form.elDraftShared===true&&form.elDraftSharedDate>todayStr()){setFerr("The Engagement Letter date cannot be in the future.");return;}
    // Calculate total fee: for retainer = monthly fee × months; for fixed = as entered
    const totalEngFee = form.engagementFee ? (
      form.feeType==="retainer" && form.retainerMonths
        ? Number(form.engagementFee) * Number(form.retainerMonths)
        : Number(form.engagementFee)
    ) : null;
    // Calculate total budget hours: for retainer = monthly hrs × months; for fixed = as entered
    const totalBudgetHours = form.feeType==="retainer" && form.monthlyBudgetHours && form.retainerMonths
      ? Number(form.monthlyBudgetHours) * Number(form.retainerMonths)
      : form.budgetHours ? Number(form.budgetHours) : null;
    const baseObj = {
      budgetHours:totalBudgetHours,
      monthlyBudgetHours:form.feeType==="retainer"&&form.monthlyBudgetHours?Number(form.monthlyBudgetHours):null,
      engagementFee:totalEngFee,monthlyFee:form.feeType==="retainer"&&form.engagementFee?Number(form.engagementFee):null,
      retainerMonths:form.retainerMonths?Number(form.retainerMonths):null,feeType:form.feeType,
      category:form.category||"Assurance",billable:form.billable!==false,
      assignedStaff:form.assignedStaff,assignedManagers:form.assignedManagers,assignedPartners:form.assignedPartners||[],
      name:form.name,clientName:form.clientName,description:form.description,
      assignedPartnerId:form.assignedPartnerId,
      elDraftShared:form.elDraftShared,
      elDraftSharedDate:form.elDraftShared===true?(form.elDraftSharedDate||null):null,
    };
    // v50: older code answered Yes to the signed-EL question → record the signed EL (implies the draft was shared)
    const legacySigned = legacyEL && form.elDraftShared===true ? { ...elSignedPatch(user, editP), elLegacySigned:true } : null;
    if(legacySigned) Object.assign(baseObj, legacySigned);
    const elChanged = !editP || editP.elDraftShared!==form.elDraftShared || (editP.elDraftSharedDate||"")!==(baseObj.elDraftSharedDate||"");
    const elStamp = elChanged ? {elDraftUpdatedBy:user.id,elDraftUpdatedAt:new Date().toISOString(),...(editP&&editP.elDraftAutoFromSigned&&baseObj.elDraftSharedDate?{elDraftAutoFromSigned:false}:{})} : {};
    const elText = form.elDraftShared==="na"?"not applicable":form.elDraftShared?`shared on ${fmtDate(baseObj.elDraftSharedDate)}`:"not yet shared";
    if(editP){
      setProjects(p=>p.map(x=>x.id===editP.id?{...x,...baseObj,...elStamp,updatedBy:user.id,updatedAt:new Date().toISOString()}:x));
      addAudit(user.id,user.name,"EDIT_PROJECT",`Edited ${editP.code} — ${form.name}`);
      if(legacySigned) addAudit(user.id,user.name,"EL_SIGNED_RECEIVED",`Signed engagement letter for ${editP.code} confirmed as received (code created before EL tracking)`);
      else if(elChanged) addAudit(user.id,user.name,form.elDraftShared==="na"?"EL_NOT_APPLICABLE":form.elDraftShared?"EL_DRAFT_SHARED":"EL_DRAFT_PENDING",`${form.elDraftShared==="na"?"Engagement letter":"Draft engagement letter"} for ${editP.code}: ${elText}`);
    } else {
      const np={id:genId(),...form,code:form.code.toUpperCase(),...baseObj,
        ...elStamp,status:isP?"active":"pending_approval",createdBy:user.id,createdAt:new Date().toISOString()};
      setProjects(p=>[...p,np]);
      addAudit(user.id,user.name,"CREATE_PROJECT",`Created ${form.code.toUpperCase()} — ${form.name} (${form.elDraftShared==="na"?"EL":"draft EL"} ${elText})`);
    }
    setSM(false);
  };

  const approve=id=>{setProjects(p=>p.map(x=>x.id===id?{...x,status:"active",approvedBy:user.id,approvedAt:new Date().toISOString()}:x));addAudit(user.id,user.name,"APPROVE_PROJECT",`Approved ${id}`);setProjTab("active");};
  const reject=(id,reason)=>{setProjects(p=>p.map(x=>x.id===id?{...x,status:"rejected",rejectReason:reason,rejectedBy:user.id}:x));addAudit(user.id,user.name,"REJECT_PROJECT",`Rejected ${id}: ${reason}`);setRejectM(null);setRR("");setProjTab("rejected");};
  // Helper: pending appraisals for a project (non-retainer only — retainers are exempt from the appraisal gate)
  const pendingAppraisalsFor = proj => {
    if(!proj || proj.feeType==="retainer") return [];
    const required = getRequiredAppraisals(users, [proj], tss).filter(r=>users.find(u=>u.id===r.staffId)?.active!==false);
    return required.filter(r=>findAppraisalFor(appraisals,r)?.status!=="submitted");
  };

  // v48: closing, requesting closure and approving closure all go through ClosureModal (signed EL + appraisal checks).
  const close = id => setClosureM({ id, mode:"close" });
  const requestClose = id => setClosureM({ id, mode:"request" });
  const closeApprove = id => setClosureM({ id, mode:"approve" });

  const confirmClosure = () => {
    const { id, mode } = closureM;
    const proj = projects.find(x=>x.id===id);
    if(!proj) { setClosureM(null); return; }
    const now = new Date().toISOString();
    const elPatch = elSignedPatch(user, proj);
    if(mode==="request") {
      setProjects(p=>p.map(x=>x.id===id?{...x,...elPatch,status:"pending_closure",closeRequestedBy:user.id,closeRequestedAt:now,closeRejectReason:null}:x));
      addAudit(user.id,user.name,"REQUEST_CLOSE_PROJECT",`Requested closure of ${proj.code}`);
    } else {
      if(pendingAppraisalsFor(proj).length>0) return; // guarded in the modal too
      setProjects(p=>p.map(x=>x.id===id?{...x,...elPatch,status:"closed",closedAt:now,closedBy:user.id}:x));
      addAudit(user.id,user.name,mode==="approve"?"APPROVE_CLOSE_PROJECT":"CLOSE_PROJECT",`${mode==="approve"?"Approved closure of":"Closed"} ${proj.code}`);
    }
    if(elPatch.elSignedReceived) addAudit(user.id,user.name,"EL_SIGNED_RECEIVED",`Signed engagement letter for ${proj.code} confirmed as received`);
    setClosureM(null);
  };

  // Any partner can reject a closure request — reverts to Active with a reason.
  const closeReject = (id,reason)=>{
    setProjects(p=>p.map(x=>x.id===id?{...x,status:"active",closeRejectReason:reason,closeRejectedBy:user.id}:x));
    addAudit(user.id,user.name,"REJECT_CLOSE_PROJECT",`Rejected closure of ${id}: ${reason}`);
    setCRM(null);setCRR("");
  };
  const deleteProject=id=>{if(!window.confirm("Permanently delete this project code? This cannot be undone."))return;setProjects(p=>p.filter(x=>x.id!==id));addAudit(user.id,user.name,"DELETE_PROJECT",`Deleted project ${id}`);};
  const saveAssign=(pid,staff,managers,partners=[])=>{setProjects(p=>p.map(x=>x.id===pid?{...x,assignedStaff:staff,assignedManagers:managers,assignedPartners:partners}:x));addAudit(user.id,user.name,"ASSIGN_STAFF",`Updated assignments for ${pid}`);setAM(null);};

  const statusClass=s=>s==="active"?"bac":s==="closed"?"bcl":s==="pending_approval"?"bp2":s==="pending_closure"?"bpc":"br";
  const toggleSort = (col) => { if(sortBy===col){setSortDir(d=>d==="asc"?"desc":"asc");}else{setSortBy(col);setSortDir("asc");} setPage(1); };
  const sortIcon = (col) => sortBy===col ? (sortDir==="asc"?" ↑":" ↓") : "";

  // Filter by tab
  const allVisible = isP?projects:projects.filter(p=>p.status==="active"&&[...(p.assignedStaff||[]),...(p.assignedManagers||[]),...(p.assignedPartners||[])].includes(user.id));
  const tabFiltered = isP ? (
    projTab==="active"    ? allVisible.filter(p=>p.status==="active") :
    projTab==="closed"    ? allVisible.filter(p=>p.status==="closed") :
    projTab==="pending"   ? allVisible.filter(p=>p.status==="pending_approval") :
    projTab==="closure"   ? allVisible.filter(p=>p.status==="pending_closure") :
    projTab==="rejected"  ? allVisible.filter(p=>p.status==="rejected") :
    allVisible
  ) : allVisible;

  // Search + filters
  const searchFiltered = tabFiltered.filter(p=>{
    const q = search.toLowerCase();
    if(q&&!p.code.toLowerCase().includes(q)&&!p.name.toLowerCase().includes(q)&&!p.clientName.toLowerCase().includes(q)) return false;
    if(filterPartner&&p.assignedPartnerId!==filterPartner) return false;
    if(filterCat&&p.category!==filterCat) return false;
    if(filterFee&&p.feeType!==filterFee) return false;
    if(filterEL==="attention"&&!elNeedsAttention(p)) return false;
    if(filterEL==="pending"&&!(elNeedsAttention(p)&&elDraftState(p)==="pending")) return false;
    if(filterEL==="unrecorded"&&!(elNeedsAttention(p)&&elDraftState(p)==="unrecorded")) return false;
    if(filterEL==="shared"&&elDraftState(p)!=="shared") return false;
    if(filterEL==="na"&&elDraftState(p)!=="na") return false;
    return true;
  });

  // Sort
  const sorted = [...searchFiltered].sort((a,b)=>{
    let va,vb;
    if(sortBy==="code"){va=a.code;vb=b.code;}
    else if(sortBy==="client"){va=a.clientName;vb=b.clientName;}
    else if(sortBy==="billing"){va=a.engagementFee||0;vb=b.engagementFee||0;}
    else if(sortBy==="budget"){va=a.budgetHours||0;vb=b.budgetHours||0;}
    else if(sortBy==="partner"){va=users.find(u=>u.id===a.assignedPartnerId)?.name||"";vb=users.find(u=>u.id===b.assignedPartnerId)?.name||"";}
    else{va=a.name;vb=b.name;}
    if(va<vb) return sortDir==="asc"?-1:1;
    if(va>vb) return sortDir==="asc"?1:-1;
    return 0;
  });

  const totalPages = Math.max(1,Math.ceil(sorted.length/PAGE_SIZE));
  const paginated = sorted.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const pendingCount = projects.filter(p=>p.status==="pending_approval").length;
  const closurePendingCount = projects.filter(p=>p.status==="pending_closure").length;
  const hasFilters = search||filterPartner||filterCat||filterFee||filterEL;
  const showEL = user.role!=="intern"; // v48: interns don't see EL flags

  return (
    <div>
      <div className="sh">
        <div><div className="card-title">Engagements</div><div className="card-sub mt4 ts">Project codes for time booking</div></div>
        <button className="btn bp" onClick={openAdd}><I n="plus" s={15}/>New Project Code</button>
      </div>

      {/* Tabs — partners only */}
      {isP&&(
        <div className="tabs">
          <div className={`tab ${projTab==="active"?"active":""}`} onClick={()=>{setProjTab("active");setPage(1);}}>
            Active<span style={{marginLeft:5,fontSize:11,background:"var(--border)",borderRadius:20,padding:"1px 6px"}}>{projects.filter(p=>p.status==="active").length}</span>
          </div>
          <div className={`tab ${projTab==="closed"?"active":""}`} onClick={()=>{setProjTab("closed");setPage(1);}}>
            Closed<span style={{marginLeft:5,fontSize:11,background:"var(--border)",borderRadius:20,padding:"1px 6px"}}>{projects.filter(p=>p.status==="closed").length}</span>
          </div>
          <div className={`tab ${projTab==="pending"?"active":""}`} onClick={()=>{setProjTab("pending");setPage(1);}}>
            Pending Approval{pendingCount>0&&<span style={{background:"var(--amber)",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,marginLeft:5}}>{pendingCount}</span>}
          </div>
          <div className={`tab ${projTab==="closure"?"active":""}`} onClick={()=>{setProjTab("closure");setPage(1);}}>
            Pending Closure{closurePendingCount>0&&<span style={{background:"#6d28d9",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,marginLeft:5}}>{closurePendingCount}</span>}
          </div>
          <div className={`tab ${projTab==="rejected"?"active":""}`} onClick={()=>{setProjTab("rejected");setPage(1);}}>Rejected</div>
          <div className={`tab ${projTab==="all"?"active":""}`} onClick={()=>{setProjTab("all");setPage(1);}}>All</div>
        </div>
      )}

      {/* Search + Filters */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
        <input className="fi" placeholder="Search code, engagement, client..." value={search}
          style={{flex:1,minWidth:200,padding:"8px 12px",fontSize:13}}
          onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        {isP&&<select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterPartner} onChange={e=>{setFP(e.target.value);setPage(1);}}>
          <option value="">All Partners</option>
          {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>}
        <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterCat} onChange={e=>{setFC(e.target.value);setPage(1);}}>
          <option value="">All Categories</option>
          {ENGAGEMENT_CATEGORIES.map(c=><option key={c}>{c}</option>)}
        </select>
        <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterFee} onChange={e=>{setFF(e.target.value);setPage(1);}}>
          <option value="">All Fee Types</option>
          <option value="fixed">Fixed Fee</option>
          <option value="retainer">Retainer</option>
        </select>
        {showEL&&<select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterEL} onChange={e=>{setFEL(e.target.value);setPage(1);}}>
          <option value="">EL: All</option>
          <option value="attention">EL: Needs attention</option>
          <option value="pending">EL: Pending</option>
          <option value="unrecorded">EL: Not recorded</option>
          <option value="shared">EL: Shared</option>
          <option value="na">EL: Not applicable</option>
        </select>}
        {hasFilters&&<button className="btn bgh bsm" onClick={()=>{setSearch("");setFP("");setFC("");setFF("");setFEL("");setPage(1);}}>✕ Clear</button>}
        <span className="tx tsl" style={{fontSize:12}}>{sorted.length} engagement{sorted.length!==1?"s":""}</span>
      </div>

      <div className="card">
        {paginated.length===0?<div className="es"><div className="es-icon"><I n="folder" s={36}/></div>No engagements found.</div>:(
          <div className="tw"><table>
            <thead><tr>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("code")}>Code{sortIcon("code")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("name")}>Engagement{sortIcon("name")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("client")}>Client{sortIcon("client")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("partner")}>Partner{sortIcon("partner")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("budget")}>Budget{sortIcon("budget")}</th>
              <th style={{cursor:"pointer"}} onClick={()=>toggleSort("billing")}>Billing{sortIcon("billing")}</th>
              <th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>{paginated.map(p=>{
              const usedH=tss.filter(t=>t.projectId===p.id&&t.status==="approved").reduce((s,t)=>s+t.hours,0);
              const pct=p.budgetHours?Math.min(Math.round(usedH/p.budgetHours*100),100):null;
              const partner=users.find(u=>u.id===p.assignedPartnerId);
              // Billing display
              const totalBilling = p.engagementFee||0;
              const billingDisplay = totalBilling>0
                ? <><div className="fw6 tgo">{fmtCurrency(totalBilling)}</div>
                    <div className="tx tsl">{p.feeType==="retainer"?`${fmtCurrency(p.monthlyFee||0)}/mo × ${p.retainerMonths||0}m`:"Fixed Fee"}</div></>
                : <span className="tx tsl">Not set</span>;
              // Edit rights: manager can edit only if pending; partner can edit anytime
              const canEditProj = isP || (!isP && p.status==="pending_approval" && p.createdBy===user.id);
              return <tr key={p.id}>
                <td><span className="fw6 mono" style={{fontSize:14}}>{p.code}</span></td>
                <td><div className="fw6">{p.name}</div><div className="tx tsl">{p.description}</div>
                  {p.status==="rejected"&&p.rejectReason&&<div className="tx tdn mt4">↩ {p.rejectReason}</div>}
                  {p.status==="active"&&p.closeRejectReason&&<div className="tx tdn mt4">↩ Closure request rejected: {p.closeRejectReason}</div>}
                  {showEL&&<div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,flexWrap:"wrap"}}>
                    <ELPill p={p}/>
                    <ELActionButton user={user} p={p} onShared={setMarkELP} onSigned={setSignELP} onNA={setNaELP}/>
                  </div>}
                </td>
                <td>{p.clientName}</td>
                <td className="ts">{partner?.name||"—"}</td>
                <td style={{minWidth:120}}>{p.budgetHours
                  ?<><div className="ts">{fmtHrs(usedH)}h / {p.budgetHours}h <span className={pct>=100?"tdn":pct>=80?"tam":"tsc"}>({pct}%)</span></div>
                    <div className="pbw"><div className={`pbf ${pct>=100?"pbov":pct>=80?"pbwn":"pbok"}`} style={{width:pct+"%"}}/></div></>
                  :<span className="tx tsl">No budget set</span>}</td>
                <td style={{minWidth:110}}>{billingDisplay}</td>
                <td><span className={`bdg ${statusClass(p.status)}`}>{p.status==="pending_approval"?"Pending":p.status==="pending_closure"?"Pending Closure":p.status.charAt(0).toUpperCase()+p.status.slice(1)}</span></td>
                <td><div className="fx g8" style={{flexWrap:"wrap"}}>
                  {isP&&p.status==="pending_approval"&&<>
                    <button className="btn bsc bsm" onClick={()=>approve(p.id)}><I n="check" s={12}/>Approve</button>
                    <button className="btn bd bsm" onClick={()=>{setRejectM(p);setRR("");}}><I n="x" s={12}/>Reject</button>
                  </>}
                  {p.status==="active"&&<>
                    {/* Partners always see Assign; Managers see it if assigned to the project */}
                    {(isP||(isMgr&&(p.assignedManagers||[]).includes(user.id)))&&
                      <button className="btn bp bsm" onClick={()=>setAM(p)}><I n="users" s={12}/>Assign</button>}
                    {/* Assigned partner (or admin) can still close instantly */}
                    {isP&&(user.email===ADMIN_EMAIL||p.assignedPartnerId===user.id)&&
                      <button className="btn bgh bsm" onClick={()=>close(p.id)}><I n="archive" s={12}/>Close</button>}
                    {/* Managers (and non-assigned partners) must raise a closure request */}
                    {((isMgr&&(p.assignedManagers||[]).includes(user.id))||(isP&&user.email!==ADMIN_EMAIL&&p.assignedPartnerId!==user.id))&&
                      <button className="btn bgh bsm" onClick={()=>requestClose(p.id)}><I n="send" s={12}/>Request Closure</button>}
                  </>}
                  {p.status==="pending_closure"&&isP&&(()=>{
                    const pending = pendingAppraisalsFor(p);
                    const elBy = users.find(u=>u.id===p.elSignedConfirmedBy);
                    return <div style={{display:"flex",flexDirection:"column",gap:6,minWidth:220}}>
                      <div className={`tx ${elIsNA(p)?"tsl":p.elSignedReceived?"tsc":"tdn"}`} style={{lineHeight:1.4}}>
                        <I n={elIsNA(p)?"block":p.elSignedReceived?"check":"alert"} s={11}/> {elIsNA(p)?"EL not applicable":p.elSignedReceived?`Signed EL received${elBy?` (confirmed by ${elBy.name})`:""}`:"Signed EL not confirmed"}
                      </div>
                      {pending.length>0&&<div className="tx tdn" style={{lineHeight:1.4}}>
                        <I n="alert" s={11}/> Appraisal pending — {pending.map(r=>users.find(u=>u.id===r.staffId)?.name||"—").join(", ")}
                      </div>}
                      <div className="fx g8" style={{flexWrap:"wrap"}}>
                        <button className="btn bsc bsm" onClick={()=>closeApprove(p.id)}><I n="check" s={12}/>Approve Closure</button>
                        <button className="btn bd bsm" onClick={()=>{setCRM(p);setCRR("");}}><I n="x" s={12}/>Reject</button>
                      </div>
                    </div>;
                  })()}
                  {/* Edit: partner always, manager only if pending */}
                  {canEditProj&&<button className="btn bgh bic bsm" title="Edit project" onClick={()=>openEdit(p)}><I n="edit" s={13}/></button>}
                  {/* Delete: assigned partner or admin only */}
                  {(user.email===ADMIN_EMAIL||p.assignedPartnerId===user.id)&&<button className="btn bd bic bsm" title="Delete project" onClick={()=>deleteProject(p.id)}><I n="trash" s={13}/></button>}
                </div></td>
              </tr>;
            })}</tbody>
          </table></div>
        )}
      </div>

      {/* Pagination */}
      {totalPages>1&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12}}>
          <button className="btn bgh bsm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
          <span className="ts tsl">Page {page} of {totalPages} ({tabFiltered.length} total)</span>
          <button className="btn bgh bsm" disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {/* Reject modal with remarks */}
      {rejectM&&(
        <div className="mo" onClick={()=>setRejectM(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <div className="md-title">Reject Project Code</div>
            <div className="al al-w mb16"><I n="alert" s={14}/><div>Rejecting <strong>{rejectM.code}</strong> — {rejectM.name}</div></div>
            <div className="fg"><label className="fl">Reason / Remarks</label>
              <textarea className="fta" placeholder="Please give a reason for rejection..." value={rejectReason} onChange={e=>setRR(e.target.value)}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setRejectM(null)}>Cancel</button>
              <button className="btn bd" onClick={()=>{if(!rejectReason.trim()){return;}reject(rejectM.id,rejectReason);}}><I n="x" s={14}/>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject closure-request modal with remarks */}
      {closeRejectM&&(
        <div className="mo" onClick={()=>setCRM(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <div className="md-title">Reject Closure Request</div>
            <div className="al al-w mb16"><I n="alert" s={14}/><div>Rejecting closure of <strong>{closeRejectM.code}</strong> — {closeRejectM.name}. The engagement will revert to Active.</div></div>
            <div className="fg"><label className="fl">Reason / Remarks</label>
              <textarea className="fta" placeholder="Please give a reason for rejecting this closure request..." value={closeRejectReason} onChange={e=>setCRR(e.target.value)}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setCRM(null)}>Cancel</button>
              <button className="btn bd" onClick={()=>{if(!closeRejectReason.trim()){return;}closeReject(closeRejectM.id,closeRejectReason);}}><I n="x" s={14}/>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {showM&&(
        <div className="mo" onClick={()=>setSM(false)}>
          <div className="md" onClick={e=>e.stopPropagation()}>
            <div className="md-title">{editP?"Edit Project Code":"Create Project Code"}</div>
            {!isP&&<div className="al al-i"><I n="info" s={15}/><div>Requires Partner approval before staff can book time.</div></div>}
            {ferr&&<div className="err">{ferr}</div>}
            <div className="fg"><label className="fl">Engagement Category</label>
              <select className="fs" value={form.category||"Assurance"} onChange={e=>setF(f=>({...f,category:e.target.value}))}>
                {ENGAGEMENT_CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="g2">
              <div className="fg">
                <label className="fl">Project Code</label>
                <input className="fi" placeholder="e.g. VCFO-2025-001" value={form.code} onChange={e=>setF(f=>({...f,code:e.target.value}))} style={{textTransform:"uppercase"}}/>
                {!editP&&lastProjectCode&&<div className="ts mt4" style={{color:"var(--slate)"}}>Last code created ({form.category||"Assurance"}): <b style={{color:"var(--navy)"}}>{lastProjectCode}</b></div>}
                {!editP&&!lastProjectCode&&<div className="ts mt4" style={{color:"var(--slate)"}}>No prior codes under {form.category||"Assurance"} yet.</div>}
              </div>
              <div className="fg"><label className="fl">Client Name</label><input className="fi" placeholder="Client / Entity" value={form.clientName} onChange={e=>setF(f=>({...f,clientName:e.target.value}))}/></div>
            </div>
            <div className="fg"><label className="fl">Engagement Name</label><input className="fi" placeholder="e.g. Virtual CFO Services FY2025-26" value={form.name} onChange={e=>setF(f=>({...f,name:e.target.value}))}/></div>
            {/* v48: draft Engagement Letter — mandatory, but either answer lets the code be created */}
            <div className="fg" style={{background:"var(--cream)",borderRadius:10,padding:"14px 16px",border:"1px solid var(--border)"}}>
              <label style={{display:"block",fontSize:13,fontWeight:600,color:"var(--navy)",marginBottom:10,lineHeight:1.45}}>{legacyEL?"Has the signed Engagement Letter been received from the client?":"Is the draft Engagement Letter prepared and shared with the client?"} <span className="tdn">*</span></label>
              <YesNo withNA value={form.elDraftShared} onChange={v=>setF(f=>({...f,elDraftShared:v,elDraftSharedDate:v===true&&!legacyEL?(f.elDraftSharedDate||todayStr()):""}))}/>
              {legacyEL&&<div className="tx tsl" style={{marginTop:8}}>This code was created before EL tracking started, so the signed EL is confirmed directly.</div>}
              {form.elDraftShared===true&&!legacyEL&&(
                <div style={{marginTop:12}}>
                  <label className="fl">Date shared with the client *</label>
                  <input type="date" className="fi" style={{maxWidth:220,background:"#fff"}} max={todayStr()} value={form.elDraftSharedDate} onChange={e=>setF(f=>({...f,elDraftSharedDate:e.target.value}))}/>
                  {editP?.elDraftAutoFromSigned&&!form.elDraftSharedDate&&<div className="tx tsl mt4">Marked as shared automatically when the signed EL was confirmed. The date is optional here.</div>}
                </div>
              )}
              {form.elDraftShared==="na"&&(
                <div className="tx tsl" style={{marginTop:10,lineHeight:1.5}}>No engagement letter is needed for this code (for example, a small-value engagement). No EL flag is raised and the signed EL question is skipped at closure.</div>
              )}
              {form.elDraftShared===false&&(
                <div className="al al-w" style={{marginTop:12,marginBottom:0}}><I n="alert" s={14}/><div>This engagement will be flagged as <strong>EL pending</strong> until {legacyEL?"the signed EL is confirmed":"the draft is shared"}. The code can still be {editP?"saved":"created"}.</div></div>
              )}
            </div>
            <div className="fg"><label className="fl">Description</label><textarea className="fta" placeholder="Brief engagement scope..." value={form.description} onChange={e=>setF(f=>({...f,description:e.target.value}))}/></div>
            <div className="g2">
              <div className="fg"><label className="fl">Assigned Partner</label>
                <select className="fs" value={form.assignedPartnerId} onChange={e=>setF(f=>({...f,assignedPartnerId:e.target.value}))}>
                  <option value="">-- Select --</option>{partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {form.feeType==="fixed"&&(
                <div className="fg">
                  <label className="fl">Total Budget Hours (optional)</label>
                  <input type="number" className="fi" placeholder="e.g. 200" value={form.budgetHours} onChange={e=>setF(f=>({...f,budgetHours:e.target.value}))}/>
                </div>
              )}
              {form.feeType==="retainer"&&(
                <div className="fg">
                  <label className="fl">Budget Hours / Month (optional)</label>
                  <input type="number" className="fi" placeholder="e.g. 20" value={form.monthlyBudgetHours} onChange={e=>setF(f=>({...f,monthlyBudgetHours:e.target.value}))}/>
                  {form.monthlyBudgetHours&&form.retainerMonths&&(
                    <div className="tx tsl mt4">Total budget: {Number(form.monthlyBudgetHours)*Number(form.retainerMonths)}h ({form.monthlyBudgetHours}h/month × {form.retainerMonths} months)</div>
                  )}
                </div>
              )}
            </div>
            <div className="g2">
              <div className="fg"><label className="fl">Fee Type</label>
                <select className="fs" value={form.feeType} onChange={e=>setF(f=>({...f,feeType:e.target.value,retainerMonths:""}))}>
                  <option value="fixed">Fixed Fee</option>
                  <option value="retainer">Monthly Retainer</option>
                </select>
              </div>
              <div className="fg"><label className="fl">Billable to Client?</label>
                <select className="fs" value={form.billable!==false?"yes":"no"} onChange={e=>setF(f=>({...f,billable:e.target.value==="yes"}))}>
                  <option value="yes">Yes — Billable</option>
                  <option value="no">No — Non-billable</option>
                </select>
              </div>
              {form.feeType==="fixed"&&(
                <div className="fg"><label className="fl">Total Fee (₹)</label>
                  <input type="number" className="fi" placeholder="e.g. 500000" value={form.engagementFee} onChange={e=>setF(f=>({...f,engagementFee:e.target.value}))}/>
                </div>
              )}
              {form.feeType==="retainer"&&(<>
                <div className="fg"><label className="fl">Monthly Fee (₹)</label>
                  <input type="number" className="fi" placeholder="e.g. 50000" value={form.engagementFee} onChange={e=>setF(f=>({...f,engagementFee:e.target.value}))}/>
                </div>
                <div className="fg"><label className="fl">Number of Months</label>
                  <input type="number" className="fi" placeholder="e.g. 12" min="1" value={form.retainerMonths} onChange={e=>setF(f=>({...f,retainerMonths:e.target.value}))}/>
                </div>
                {form.engagementFee&&form.retainerMonths&&(
                  <div className="al al-s" style={{marginTop:-8}}>
                    <I n="info" s={14}/><div>Total engagement fee: <strong>{fmtCurrency(Number(form.engagementFee)*Number(form.retainerMonths))}</strong> ({form.retainerMonths} months × {fmtCurrency(Number(form.engagementFee))}/month)</div>
                  </div>
                )}
              </>)}
            </div>
            {/* Inline staff assignment */}
            <div style={{borderTop:"1.5px solid var(--border)",marginTop:8,paddingTop:18}}>
              <div className="fl" style={{marginBottom:12}}>Assign Staff (optional — can also be done later)</div>
              {["partner","manager","intern"].map(role=>(
                <div key={role} className="mb16">
                  <div style={{fontSize:12,fontWeight:600,color:"var(--slate)",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                    {role==="partner"?"Additional Partners":role.charAt(0).toUpperCase()+role.slice(1)+"s"}
                  </div>
                  {role==="partner"&&<div className="tx tsl" style={{marginBottom:8,fontSize:12}}>The Assigned Partner above is already included. Add other partners working on this engagement.</div>}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    {users.filter(u=>u.role===role&&u.active&&(role!=="partner"||u.id!==form.assignedPartnerId)).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(u=>{
                      const isAssigned = role==="manager"
                        ? (form.assignedManagers||[]).includes(u.id)
                        : role==="partner"
                        ? (form.assignedPartners||[]).includes(u.id)
                        : (form.assignedStaff||[]).includes(u.id);
                      return (
                        <label key={u.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,border:"1.5px solid",borderColor:isAssigned?"var(--navy)":"var(--border)",background:isAssigned?"var(--cream)":"#fff",cursor:"pointer",transition:"all .15s"}}>
                          <input type="checkbox" checked={isAssigned} onChange={()=>{
                            if(role==="manager"){
                              setF(f=>({...f,assignedManagers:isAssigned?f.assignedManagers.filter(x=>x!==u.id):[...(f.assignedManagers||[]),u.id]}));
                            } else if(role==="partner"){
                              setF(f=>({...f,assignedPartners:isAssigned?(f.assignedPartners||[]).filter(x=>x!==u.id):[...(f.assignedPartners||[]),u.id]}));
                            } else {
                              setF(f=>({...f,assignedStaff:isAssigned?f.assignedStaff.filter(x=>x!==u.id):[...(f.assignedStaff||[]),u.id]}));
                            }
                          }} style={{width:14,height:14,accentColor:"var(--navy)"}}/>
                          <span style={{fontSize:13,fontWeight:isAssigned?600:400,color:"var(--navy)"}}>{u.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setSM(false)}>Cancel</button>
              <button className="btn bp" onClick={save}><I n="check" s={15}/>{editP?"Save Changes":isP?"Create & Activate":"Submit for Approval"}</button>
            </div>
          </div>
        </div>
      )}
      {assignM&&<AssignModal project={assignM} users={users.filter(u=>u.active)} onSave={saveAssign} onClose={()=>setAM(null)}/> }
      {closureM&&(()=>{
        const cp = projects.find(x=>x.id===closureM.id);
        if(!cp) return null;
        const names = pendingAppraisalsFor(cp).map(r=>users.find(u=>u.id===r.staffId)?.name||"—");
        return <ClosureModal project={cp} mode={closureM.mode} pendingNames={names} users={users} onConfirm={confirmClosure} onClose={()=>setClosureM(null)}/>;
      })()}
      {markELP&&<ELMarkSharedModal project={markELP} onClose={()=>setMarkELP(null)} onSave={date=>{applyELShared(user,markELP,date);setMarkELP(null);}}/>}
      {signELP&&<ELSignedLegacyModal project={signELP} onClose={()=>setSignELP(null)} onSave={()=>{applyELSignedLegacy(user,signELP);setSignELP(null);}}/>}
      {naELP&&<ELNotApplicableModal project={naELP} onClose={()=>setNaELP(null)} onSave={()=>{applyELNotApplicable(user,naELP);setNaELP(null);}}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// APPROVALS
// ══════════════════════════════════════════════════════════════
function Approvals({ user, tss=[], setTss, users=[], projects=[] }) {
  const [rejectM,setRM]=useState(null);
  const [reason,setR]  =useState("");
  const [reasonErr,setRE]=useState("");
  const [histPage,setHistPage]=useState(1);
  const HIST_PAGE=10;
  const [pendPage,setPendPage]=useState(1);
  const PEND_PAGE=10;
  // Filters
  const [fStaff,setFStaff]=useState("");
  const [fProj,setFProj]=useState("");
  const [fCat,setFCat]=useState("");
  const [fBill,setFBill]=useState("");
  const [fPartner,setFPartner]=useState("");
  // Sort
  const [sortCol,setSortCol]=useState("date");
  const [sortDir,setSortDir]=useState("desc");
  const toggleSort=col=>{if(sortCol===col){setSortDir(d=>d==="asc"?"desc":"asc");}else{setSortCol(col);setSortDir("desc");}setPendPage(1);};
  const sortIcon=col=>sortCol===col?(sortDir==="asc"?" ↑":" ↓"):"";
  const isP=user.role==="partner";
  const myMgrProjIds = !isP
    ? projects.filter(p=>(p.assignedManagers||[]).includes(user.id)).map(p=>p.id)
    : [];

  const pending=tss.filter(t=>{
    const u2=users.find(u=>u.id===t.userId);
    if(!["pending","resubmitted"].includes(t.status)) return false;

    if(isP){
      // Partners approve: managers, AND interns on projects with no managers
      if(u2?.role!=="manager"&&u2?.role!=="intern") return false;
      // Internal time: visible to ALL partners (any partner can approve as backup),
      // even though specific partner(s) were originally requested by the staff member.
      if(t.isInternal) return true;
      // Engagement time
      const proj = projects.find(px=>px.id===t.projectId);
      if(!proj) return false;
      const isMyProject = proj.assignedPartnerId===user.id || (proj.assignedPartners||[]).includes(user.id);
      if(!isMyProject) return false;
      const projHasManagers = (proj.assignedManagers||[]).length > 0;
      if(u2?.role==="manager") return true; // manager entries always go to assigned partner
      if(u2?.role==="intern") return !projHasManagers; // interns only if no managers on project
      return false;
    }

    // Manager: only approve interns
    if(u2?.role!=="intern") return false;
    if(t.isInternal) return (t.internalApprovers||[]).includes(user.id);
    return myMgrProjIds.includes(t.projectId);
  });

  // On-behalf pending: entries filed by managers on behalf of this partner
  const pendingOnBehalf = isP ? tss.filter(t=>
    t.status==="pending_partner" &&
    t.userId===user.id // this partner is the attributed person
  ) : [];

  const allPending = [...pending, ...pendingOnBehalf];

  const history=tss.filter(t=>{
    const u2=users.find(u=>u.id===t.userId);
    if(["pending","resubmitted"].includes(t.status)) return false;
    if(isP){
      if(u2?.role!=="manager"&&u2?.role!=="intern") return false;
      if(t.isInternal) return true; // history: visible to all partners, same as pending
      const proj = projects.find(px=>px.id===t.projectId);
      if(!proj) return false;
      const isMyProject = proj.assignedPartnerId===user.id||(proj.assignedPartners||[]).includes(user.id);
      if(!isMyProject) return false;
      if(u2?.role==="manager") return true;
      if(u2?.role==="intern") return (proj.assignedManagers||[]).length===0;
      return false;
    }
    if(u2?.role!=="intern") return false;
    if(!isP&&!t.isInternal&&!myMgrProjIds.includes(t.projectId)) return false;
    return true;
  }).slice().reverse();

  const approve=id=>{
    setTss(p=>p.map(t=>t.id===id?{...t,status:"approved",approvedBy:user.id,approvedAt:new Date().toISOString()}:t));
    addAudit(user.id,user.name,"APPROVE_TIMESHEET",`Approved entry ${id}`);
  };
  const approveOnBehalf=id=>{
    setTss(p=>p.map(t=>t.id===id?{...t,status:"approved",approvedBy:user.id,approvedAt:new Date().toISOString()}:t));
    addAudit(user.id,user.name,"APPROVE_ON_BEHALF",`Partner approved on-behalf entry ${id}`);
  };
  const confirmReject=()=>{
    if(!reason.trim()){setRE("Please provide a reason.");return;}
    const newRejStatus = rejectM.status==="pending_partner" ? "rejected_partner" : "rejected";
    setTss(p=>p.map(t=>t.id===rejectM.id?{...t,status:newRejStatus,rejectedBy:user.id,rejectedAt:new Date().toISOString(),rejectReason:reason}:t));
    addAudit(user.id,user.name,"REJECT_TIMESHEET",`Rejected ${rejectM.id}: ${reason}`);
    setRM(null);
  };

  const sc=s=>s==="approved"?"ba":s==="rejected"?"br":s==="resubmitted"?"brs":"bp2";

  // Resolve the responsible partner for a timesheet entry
  const getEntryPartner = ts => {
    // On-behalf: the attributed user IS the partner
    if(ts.filedById || ts.status==="pending_partner"){
      const attr = users.find(u=>u.id===ts.userId);
      if(attr?.role==="partner") return attr;
    }
    // Internal time: first originally-requested partner approver (for filtering only —
    // ALL partners can see and act on internal-time entries, not just this one)
    if(ts.isInternal){
      const pid = (ts.internalPartnerApprovers||[])[0];
      return pid ? (users.find(u=>u.id===pid) || null) : null;
    }
    // Regular entry: look at project's assigned partner
    const proj = projects.find(p=>p.id===ts.projectId);
    if(!proj) return null;
    const pid = proj.assignedPartnerId || (proj.assignedPartners||[])[0];
    return users.find(u=>u.id===pid) || null;
  };

  // Build unique sorted partner list from all pending entries (regardless of role)
  const partnerOptions = [...new Map(
    allPending.map(ts=>getEntryPartner(ts)).filter(Boolean).map(p=>[p.id,p])
  ).values()].sort((a,b)=>a.name.localeCompare(b.name));

  return (
    <div>
      <div className="sh"><div><div className="card-title">Approvals</div><div className="card-sub mt4 ts">Review pending timesheets</div></div></div>

      {/* Filters + Sort */}
      {allPending.length>0&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
          <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={fStaff} onChange={e=>{setFStaff(e.target.value);setPendPage(1);}}>
            <option value="">All Staff</option>
            {[...new Set(allPending.map(t=>t.userId))]
              .map(uid=>users.find(u=>u.id===uid)).filter(Boolean)
              .sort((a,b)=>a.name.localeCompare(b.name))
              .map(u2=><option key={u2.id} value={u2.id}>{u2.name}</option>)}
          </select>
          <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={fProj} onChange={e=>{setFProj(e.target.value);setPendPage(1);}}>
            <option value="">All Projects</option>
            {[...new Set(allPending.map(t=>t.projectId).filter(Boolean))]
              .map(pid=>projects.find(p=>p.id===pid)).filter(Boolean)
              .sort((a,b)=>a.code.localeCompare(b.code))
              .map(p=><option key={p.id} value={p.id}>{p.code} — {p.clientName}</option>)}
          </select>
          <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={fCat} onChange={e=>{setFCat(e.target.value);setPendPage(1);}}>
            <option value="">All Categories</option>
            {[...new Set(allPending.map(t=>t.category).filter(Boolean))].sort().map(c=><option key={c}>{c}</option>)}
          </select>
          <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={fBill} onChange={e=>{setFBill(e.target.value);setPendPage(1);}}>
            <option value="">Billable: All</option>
            <option value="yes">Billable only</option>
            <option value="no">Non-billable only</option>
          </select>
          {isP&&partnerOptions.length>0&&(
            <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={fPartner} onChange={e=>{setFPartner(e.target.value);setPendPage(1);}}>
              <option value="">All Partners</option>
              {partnerOptions.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {(fStaff||fProj||fCat||fBill||fPartner)&&<button className="btn bgh bsm" onClick={()=>{setFStaff("");setFProj("");setFCat("");setFBill("");setFPartner("");setPendPage(1);}}>✕ Clear</button>}
        </div>
      )}

      {(() => {
        // Apply filters + sort
        const filteredPending = allPending.filter(t=>{
          if(fStaff&&t.userId!==fStaff) return false;
          if(fProj&&t.projectId!==fProj) return false;
          if(fCat&&t.category!==fCat) return false;
          if(fBill==="yes"&&!t.billable) return false;
          if(fBill==="no"&&t.billable) return false;
          if(fPartner){ const ep=getEntryPartner(t); if(!ep||ep.id!==fPartner) return false; }
          return true;
        }).slice().sort((a,b)=>{
          let va,vb;
          if(sortCol==="date"){va=a.date;vb=b.date;}
          else if(sortCol==="hours"){va=a.hours;vb=b.hours;}
          else if(sortCol==="staff"){va=users.find(u=>u.id===a.userId)?.name||"";vb=users.find(u=>u.id===b.userId)?.name||"";}
          else if(sortCol==="project"){va=projects.find(p=>p.id===a.projectId)?.code||"";vb=projects.find(p=>p.id===b.projectId)?.code||"";}
          else if(sortCol==="category"){va=a.category||"";vb=b.category||"";}
          else{va=a.date;vb=b.date;}
          if(va<vb) return sortDir==="asc"?-1:1;
          if(va>vb) return sortDir==="asc"?1:-1;
          return 0;
        });
        const totalPages = Math.max(1,Math.ceil(filteredPending.length/PEND_PAGE));
        const paginatedPending = filteredPending.slice((pendPage-1)*PEND_PAGE, pendPage*PEND_PAGE);

        return (
          <div className="card mb22">
            <div className="fxb mb16">
              <div className="card-title">Pending <span style={{color:"var(--amber)",fontSize:14}}>({filteredPending.length}{filteredPending.length!==allPending.length&&` of ${allPending.length}`})</span></div>
            </div>
            {allPending.length===0
              ?<div className="es"><div className="es-icon"><I n="check" s={36}/></div>All clear — no pending approvals.</div>
              :filteredPending.length===0
              ?<div className="es">No entries match your filters.</div>
              :<>
              <div className="tw"><table>
                <thead><tr>
                  <th style={{width:32}}>#</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleSort("staff")}>Staff{sortIcon("staff")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleSort("date")}>Date{sortIcon("date")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleSort("project")}>Project{sortIcon("project")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleSort("category")}>Category{sortIcon("category")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleSort("hours")}>Hrs{sortIcon("hours")}</th>
                  <th>Description</th>
                  <th>Partner</th>
                  <th>Actions</th>
                </tr></thead>
                <tbody>{paginatedPending.map((ts,idx)=>{
                  const u2=users.find(u=>u.id===ts.userId); const p=projects.find(p=>p.id===ts.projectId); const rate=u2?.billingRate||0;
                  const entryPartner=getEntryPartner(ts);
                  return <tr key={ts.id}>
                    <td className="tx tsl" style={{fontSize:12}}>{(pendPage-1)*PEND_PAGE+idx+1}</td>
                    <td className="fw6">{u2?.name}
                      {ts.filedById&&<div className="tx" style={{color:"var(--gold)",fontSize:11}}>Filed by {ts.filedByName}</div>}
                      <div className="tx tsl">{fmtCurrency(rate)}/hr</div>
                    </td>
                    <td>{fmtDate(ts.date)}</td>
                    <td><div className="fw6 mono">{p?.code}</div><div className="tx tsl">{p?.clientName} — {p?.name}</div></td>
                    <td className="ts">{ts.category}</td>
                    <td className="fw6">{fmtHrs(ts.hours)}h{ts.billable&&<div className="tx tgo">{fmtCurrency(ts.hours*rate)}</div>}</td>
                    <td className="ts tsl" style={{maxWidth:170}}>{ts.description}</td>
                    <td style={{whiteSpace:"nowrap"}}>
                      {ts.isInternal
                        ?(ts.internalPartnerApprovers?.length>0
                          ?<><div className="fw6" style={{fontSize:13}}>{ts.internalPartnerApprovers.map(id=>users.find(u=>u.id===id)?.name).filter(Boolean).join(", ")}</div><div className="tx tsl" style={{fontSize:11}}>Requested · any partner can approve</div></>
                          :<span className="tsl">—</span>)
                        :entryPartner
                        ?<><div className="fw6" style={{fontSize:13}}>{entryPartner.name}</div><div className="tx tsl" style={{fontSize:11}}>Partner</div></>
                        :<span className="tsl">—</span>}
                    </td>
                    <td><div className="fx g8">
                      <button className="btn bsc bsm" onClick={()=>ts.status==="pending_partner"?approveOnBehalf(ts.id):approve(ts.id)}><I n="check" s={13}/>Approve</button>
                      <button className="btn bd bsm" onClick={()=>{setRM(ts);setR("");setRE("");}}><I n="x" s={13}/>Reject</button>
                    </div></td>
                  </tr>;
                })}</tbody>
              </table></div>
              {totalPages>1&&(
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                  <button className="btn bgh bsm" disabled={pendPage===1} onClick={()=>setPendPage(p=>p-1)}>← Prev</button>
                  <span className="ts tsl">Page {pendPage} of {totalPages} · {filteredPending.length} entries</span>
                  <button className="btn bgh bsm" disabled={pendPage===totalPages} onClick={()=>setPendPage(p=>p+1)}>Next →</button>
                </div>
              )}
              </>}
          </div>
        );
      })()}

      <div className="card">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div className="card-title">History</div>
          <span className="ts tsl" style={{fontSize:12}}>{history.length} entries</span>
        </div>
        {history.length===0?<div className="es"><div className="es-icon"><I n="history" s={36}/></div>No history yet.</div>:(
          <>
          <div className="tw"><table>
            <thead><tr><th style={{width:32}}>#</th><th>Staff</th><th>Date</th><th>Project</th><th>Hrs</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>{history.slice((histPage-1)*HIST_PAGE, histPage*HIST_PAGE).map((ts,idx)=>{
              const u2=users.find(u=>u.id===ts.userId); const p=projects.find(p=>p.id===ts.projectId);
              return <tr key={ts.id}>
                <td className="tx tsl" style={{fontSize:12}}>{(histPage-1)*HIST_PAGE+idx+1}</td>
                <td className="fw6">{u2?.name}</td><td>{fmtDate(ts.date)}</td>
                <td><div className="mono fw6">{p?.code}</div><div className="tx tsl">{p?.clientName}</div></td><td>{fmtHrs(ts.hours)}h</td>
                <td><span className={`bdg ${sc(ts.status)}`}>{ts.status}</span></td>
                <td className="ts tsl">{ts.rejectReason||"—"}</td>
              </tr>;
            })}</tbody>
          </table></div>
          {Math.ceil(history.length/HIST_PAGE)>1&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
              <button className="btn bgh bsm" disabled={histPage===1} onClick={()=>setHistPage(p=>p-1)}>← Prev</button>
              <span className="ts tsl">Page {histPage} of {Math.ceil(history.length/HIST_PAGE)} · {history.length} entries</span>
              <button className="btn bgh bsm" disabled={histPage===Math.ceil(history.length/HIST_PAGE)} onClick={()=>setHistPage(p=>p+1)}>Next →</button>
            </div>
          )}
          </>
        )}
      </div>

      {rejectM&&(
        <div className="mo" onClick={()=>setRM(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
            <div className="md-title">Reject Entry</div>
            <div className="ts tsl mb16">Staff will see this reason and can correct and resubmit.</div>
            <div className="fg"><label className="fl">Rejection Reason *</label><textarea className="fta" placeholder="e.g. Wrong project code, hours seem excessive..." value={reason} onChange={e=>setR(e.target.value)}/></div>
            {reasonErr&&<div className="tx tdn mb8">{reasonErr}</div>}
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setRM(null)}>Cancel</button>
              <button className="btn bd" onClick={confirmReject}><I n="x" s={15}/>Confirm Rejection</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REPORTS
// ══════════════════════════════════════════════════════════════
// v38: engagement type filter (All · Fixed · Retainer), shared by Profitability and Reports > By Engagement.
// Anything not marked "retainer" is treated as fixed fee (older codes may have no feeType).
const engTypeOf = p => p?.feeType==="retainer" ? "retainer" : "fixed";
function TypePills({ value, onChange, counts }) {
  const opts = [["","All"],["fixed","Fixed"],["retainer","Retainer"]];
  return (
    <div style={{display:"inline-flex",background:"var(--cream)",borderRadius:10,padding:3,border:"1px solid var(--border)",flexShrink:0}}>
      {opts.map(([v,l])=>{
        const on = value===v;
        return (
          <button key={l} type="button" onClick={()=>onChange(v)}
            style={{padding:"6px 14px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:500,fontFamily:"'DM Sans',sans-serif",display:"inline-flex",alignItems:"center",gap:6,transition:"all .15s",
              background:on?"var(--navy)":"transparent",color:on?"#fff":"var(--slate)"}}>
            {l}
            {counts&&<span style={{fontSize:11,borderRadius:20,padding:"0 6px",background:on?"rgba(255,255,255,.18)":"var(--border)",color:on?"#fff":"var(--slate)"}}>{counts[v]??0}</span>}
          </button>
        );
      })}
    </div>
  );
}

function Reports({ user, users=[], projects=[], tss=[], locked:lockedMonths=[], setLocked, audit=[] }) {
  const [tab,setTab]=useState("engagement");
  const [engType,setEngType]=useState(""); // v38: By Engagement tab only
  const [dlgOpen,setDlgOpen]=useState(false);
  // Date range filters
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
  const [fromDate,setFrom]=useState(firstDay);
  const [toDate,setTo]=useState(todayStr());
  // Drill-down selection
  const [selEngId,setSelEngId]=useState("");
  const [selUserId,setSelUserId]=useState("");
  const isAdmin=user.email===ADMIN_EMAIL;

  const approved=tss.filter(t=>t.status==="approved"&&t.date>=fromDate&&t.date<=toDate);

  const engData=projects.map(p=>{
    const s=approved.filter(t=>t.projectId===p.id);
    const totalH=s.reduce((a,t)=>a+t.hours,0);
    const billH=s.filter(t=>t.billable).reduce((a,t)=>a+t.hours,0);
    const val=s.filter(t=>t.billable).reduce((a,t)=>a+t.hours*(users.find(u=>u.id===t.userId)?.billingRate||0),0);
    return {p,totalH,billH,val,n:s.length};
  }).filter(d=>d.n>0);
  const engTypeCounts = { "":engData.length, fixed:engData.filter(d=>engTypeOf(d.p)==="fixed").length, retainer:engData.filter(d=>engTypeOf(d.p)==="retainer").length };
  const engRows = engType ? engData.filter(d=>engTypeOf(d.p)===engType) : engData;

  const staffData=users.map(u=>{
    const s=approved.filter(t=>t.userId===u.id);
    const totalH=s.reduce((a,t)=>a+t.hours,0);
    const billH=s.filter(t=>t.billable).reduce((a,t)=>a+t.hours,0);
    return {u,totalH,billH,val:billH*u.billingRate};
  }).filter(d=>d.totalH>0);

  const thisMonth=todayStr().slice(0,7);
  const lastMonth=(()=>{const d=new Date();d.setMonth(d.getMonth()-1);return d.toISOString().slice(0,7);})();

  const toggleLock=mk=>{
    const wasLocked=lockedMonths.includes(mk);
    if(setLocked) setLocked(p=>wasLocked?p.filter(m=>m!==mk):[...p,mk]);
    addAudit(user.id,user.name,wasLocked?"UNLOCK_MONTH":"LOCK_MONTH",`Month: ${monthLabel(mk)}`);
  };

  // ── Download detailed log ──
  const downloadLog = (mode) => {
    // mode = "engagement" or "staff"
    let rows = [];
    const header = ["Date","Staff","Role","Project Code","Client","Engagement","Category","Hours","Billable","Description","Status"];
    rows.push(header);

    let entries = approved;
    if(mode==="engagement"&&selEngId) entries=entries.filter(t=>t.projectId===selEngId);
    if(mode==="staff"&&selUserId) entries=entries.filter(t=>t.userId===selUserId);

    // Sort by date
    entries = [...entries].sort((a,b)=>a.date>b.date?1:-1);

    entries.forEach(t=>{
      const u2=users.find(u=>u.id===t.userId);
      const p=projects.find(p=>p.id===t.projectId);
      rows.push([
        t.date,
        `"${u2?.name||""}"`,
        u2?.role||"",
        p?.code||"Internal",
        `"${p?.clientName||""}"`,
        `"${p?.name||t.internalType||""}"`,
        t.category||"",
        t.hours,
        t.billable?"Yes":"No",
        `"${(t.description||"").replace(/"/g,'""')}"`,
        t.status,
      ]);
    });

    const csv = rows.map(r=>r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url;
    a.download=`MSNA_Timelog_${mode}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDlgOpen(false);
  };

  const totalH=approved.reduce((s,t)=>s+t.hours,0);
  const totalV=approved.filter(t=>t.billable).reduce((s,t)=>s+t.hours*(users.find(u=>u.id===t.userId)?.billingRate||0),0);

  return (
    <div>
      <div className="sh">
        <div><div className="card-title">Reports & Analytics</div><div className="card-sub mt4 ts">Approved entries only</div></div>
        <button className="btn bp" onClick={()=>setDlgOpen(true)}><I n="download" s={15}/>Download Timesheet Log</button>
      </div>

      {/* Date range filter */}
      <div className="card" style={{padding:"14px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--navy)"}}>Date Range</div>
          <div className="fxc g8">
            <label className="fl" style={{marginBottom:0,fontSize:11}}>From</label>
            <input type="date" className="fi" style={{padding:"6px 10px",fontSize:13}} value={fromDate} onChange={e=>setFrom(e.target.value)}/>
          </div>
          <div className="fxc g8">
            <label className="fl" style={{marginBottom:0,fontSize:11}}>To</label>
            <input type="date" className="fi" style={{padding:"6px 10px",fontSize:13}} value={toDate} onChange={e=>setTo(e.target.value)}/>
          </div>
          <div style={{fontSize:12,color:"var(--slate)"}}>Showing <strong>{approved.length}</strong> approved entries · <strong>{totalH.toFixed(1)}h</strong> · <strong>{fmtCurrency(totalV)}</strong></div>
        </div>
      </div>

      <div className="sg3">
        <div className="sc"><div className="sv">{totalH.toFixed(1)}</div><div className="sl">Total Approved Hours</div></div>
        <div className="sc"><div className="sv" style={{color:"var(--gold)"}}>{fmtCurrency(totalV)}</div><div className="sl">Total Billing Value</div></div>
        <div className="sc"><div className="sv">{projects.filter(p=>p.status==="active").length}</div><div className="sl">Active Engagements</div></div>
      </div>

      <div className="tabs">
        <div className={`tab ${tab==="engagement"?"active":""}`} onClick={()=>setTab("engagement")}>By Engagement</div>
        <div className={`tab ${tab==="staff"?"active":""}`} onClick={()=>setTab("staff")}>By Staff</div>
        <div className={`tab ${tab==="approvals"?"active":""}`} onClick={()=>setTab("approvals")}>Approval Status</div>
        {isAdmin&&<div className={`tab ${tab==="months"?"active":""}`} onClick={()=>setTab("months")}>Month Lock</div>}
      </div>

      <div className="card">
        {tab==="engagement"&&(engData.length===0?<div className="es">No approved data in this date range.</div>:(<>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)"}}>Type</span>
            <TypePills value={engType} onChange={setEngType} counts={engTypeCounts}/>
            <span className="tx tsl" style={{fontSize:12,marginLeft:"auto"}}>{engRows.length} engagement{engRows.length!==1?"s":""}</span>
          </div>
          {engRows.length===0?<div className="es">No {engType==="retainer"?"retainer":"fixed fee"} engagements in this date range.</div>:
          <div className="tw"><table>
            <thead><tr><th>Code</th><th>Engagement</th><th>Client</th><th>Status</th><th>Total Hrs</th><th>Billable Hrs</th><th>Budget</th><th>Billing Value</th></tr></thead>
            <tbody>{engRows.map(d=>{
              const pct=d.p.budgetHours?Math.min(Math.round(d.totalH/d.p.budgetHours*100),100):null;
              return <tr key={d.p.id}>
                <td className="fw6 mono">{d.p.code}</td><td>{d.p.name}</td><td>{d.p.clientName}</td>
                <td><span className={`bdg ${d.p.status==="active"?"bac":d.p.status==="closed"?"bcl":"br"}`}>{d.p.status}</span></td>
                <td className="fw6">{fmtHrs(d.totalH)}h</td><td>{fmtHrs(d.billH)}h</td>
                <td>{pct!=null?<><span className={pct>=100?"tdn":pct>=80?"tam":"tsc"}>{pct}%</span><span className="tx tsl"> of {d.p.budgetHours}h</span></>:"—"}</td>
                <td className="fw6 tgo">{fmtCurrency(d.val)}</td>
              </tr>;
            })}</tbody>
          </table></div>}
        </>))}
        {tab==="staff"&&(staffData.length===0?<div className="es">No approved data in this date range.</div>:(
          <div className="tw"><table>
            <thead><tr><th>Staff</th><th>Role</th><th>Rate/hr</th><th>Total Hrs</th><th>Billable Hrs</th><th>Billing Value</th></tr></thead>
            <tbody>{staffData.map(d=><tr key={d.u.id}>
              <td className="fw6">{d.u.name}</td>
              <td><span className={`bdg ${d.u.role==="partner"?"rpa":d.u.role==="manager"?"rma":"ria"}`}>{d.u.role}</span></td>
              <td>{fmtCurrency(d.u.billingRate)}</td><td>{fmtHrs(d.totalH)}h</td><td>{fmtHrs(d.billH)}h</td><td className="fw6 tgo">{fmtCurrency(d.val)}</td>
            </tr>)}</tbody>
          </table></div>
        ))}
        {tab==="approvals"&&(
          <div className="tw"><table>
            <thead><tr><th>Staff</th><th>Role</th><th>Pending</th><th>Resubmitted</th><th>Approved</th><th>Rejected</th><th>Total</th></tr></thead>
            <tbody>{users.filter(u=>u.active).map(u=>{
              const all=tss.filter(t=>t.userId===u.id);
              return <tr key={u.id}>
                <td className="fw6">{u.name}</td>
                <td><span className={`bdg ${u.role==="partner"?"rpa":u.role==="manager"?"rma":"ria"}`}>{u.role}</span></td>
                <td><span className="bdg bp2">{all.filter(t=>t.status==="pending").length}</span></td>
                <td><span className="bdg brs">{all.filter(t=>t.status==="resubmitted").length}</span></td>
                <td><span className="bdg ba">{all.filter(t=>t.status==="approved").length}</span></td>
                <td><span className="bdg br">{all.filter(t=>t.status==="rejected").length}</span></td>
                <td className="fw6">{all.length}</td>
              </tr>;
            })}</tbody>
          </table></div>
        )}
        {tab==="months"&&isAdmin&&(
          <div>
            <div className="al al-i mb16"><I n="info" s={15}/><div>Locking a month prevents adding or editing entries in that period. Only Admin can lock/unlock.</div></div>
            {[lastMonth,thisMonth].map(mk=>(
              <div key={mk} className="fxb" style={{padding:"15px 0",borderBottom:"1px solid var(--border)"}}>
                <div><div className="fw6">{monthLabel(mk)}</div><div className="tx tsl">{mk}</div></div>
                <div className="fxc g8">
                  {lockedMonths.includes(mk)
                    ?<><span className="bdg blk"><I n="lock" s={12}/>Locked</span><button className="btn bsc bsm" onClick={()=>toggleLock(mk)}>Unlock</button></>
                    :<button className="btn bd bsm" onClick={()=>toggleLock(mk)}><I n="lock" s={12}/>Lock Month</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Download Dialog ── */}
      {dlgOpen&&(
        <div className="mo" onClick={()=>setDlgOpen(false)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:500}}>
            <div className="md-title">Download Timesheet Log</div>
            <div className="al al-i mb16"><I n="info" s={14}/><div>Downloads a date-wise log with staff name, project, hours, description and more. Date range: <strong>{fmtDate(fromDate)}</strong> to <strong>{fmtDate(toDate)}</strong>.</div></div>

            <div className="fg">
              <label className="fl">Report Type</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                <div style={{padding:"14px 16px",borderRadius:10,border:"1.5px solid var(--border)",background:"var(--cream)"}}>
                  <div style={{fontWeight:600,fontSize:13,color:"var(--navy)",marginBottom:4}}>By Engagement</div>
                  <div style={{fontSize:12,color:"var(--slate)"}}>Filter by one engagement or download all. Date-wise entries with description.</div>
                  <div style={{marginTop:10}}>
                    <select className="fs" style={{fontSize:12}} value={selEngId} onChange={e=>setSelEngId(e.target.value)}>
                      <option value="">All Engagements</option>
                      {projects.filter(p=>approved.some(t=>t.projectId===p.id)).slice().sort((a,b)=>a.code.localeCompare(b.code)).map(p=><option key={p.id} value={p.id}>{p.code} — {p.clientName}</option>)}
                    </select>
                  </div>
                  <button className="btn bp" style={{width:"100%",marginTop:10,justifyContent:"center"}} onClick={()=>downloadLog("engagement")}><I n="download" s={14}/>Download</button>
                </div>
                <div style={{padding:"14px 16px",borderRadius:10,border:"1.5px solid var(--border)",background:"var(--cream)"}}>
                  <div style={{fontWeight:600,fontSize:13,color:"var(--navy)",marginBottom:4}}>By Staff</div>
                  <div style={{fontSize:12,color:"var(--slate)"}}>Filter by one person or download all. Date-wise entries with description.</div>
                  <div style={{marginTop:10}}>
                    <select className="fs" style={{fontSize:12}} value={selUserId} onChange={e=>setSelUserId(e.target.value)}>
                      <option value="">All Staff</option>
                      {users.filter(u=>approved.some(t=>t.userId===u.id)).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                  <button className="btn bp" style={{width:"100%",marginTop:10,justifyContent:"center"}} onClick={()=>downloadLog("staff")}><I n="download" s={14}/>Download</button>
                </div>
              </div>
            </div>

            <div style={{fontSize:12,color:"var(--slate)",marginTop:4}}>
              CSV file — opens directly in Excel. Columns: Date, Staff, Role, Project Code, Client, Engagement, Category, Hours, Billable, Description, Status.
            </div>
            <div className="md-actions" style={{marginTop:16}}>
              <button className="btn bgh" onClick={()=>setDlgOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AUDIT TRAIL
// ══════════════════════════════════════════════════════════════
function AuditTrail({ audit=[] }) {
  const auditSorted = [...audit].sort((a,b)=>b.ts>a.ts?1:-1).slice(0,100);
  const color=a=>a.includes("APPROVE")?"var(--green)":a.includes("REJECT")||a.includes("DELETE")?"var(--red)":a.includes("LOCK")?"var(--amber)":"var(--gold)";
  return (
    <div>
      <div className="sh"><div><div className="card-title">Audit Trail</div><div className="card-sub mt4 ts">Last 100 system actions, newest first</div></div></div>
      <div className="card">
        {auditSorted.length===0?<div className="es"><div className="es-icon"><I n="history" s={36}/></div>No audit records yet.</div>:
          auditSorted.map(a=>(
            <div key={a.id} className="audit-row">
              <div style={{width:8,height:8,borderRadius:"50%",background:color(a.action),flexShrink:0,marginTop:5}}/>
              <div style={{flex:1}}>
                <div className="fxc g8"><span className="fw6 ts">{a.userName}</span><span className="tx" style={{background:"var(--cream)",padding:"2px 8px",borderRadius:20,fontFamily:"monospace"}}>{a.action}</span></div>
                <div className="ts tsl mt4">{a.detail}</div>
                <div className="audit-ts mt4">{fmtDate(a.ts)} at {new Date(a.ts).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// USER MANAGEMENT
// ══════════════════════════════════════════════════════════════
function UserManagement({ user, users=[], setUsers, isPartner=false }) {
  const [showM,setSM]     =useState(false);
  const [editU,setEU]     =useState(null);
  const [form,setF]       =useState({name:"",email:"",role:"intern",dateOfJoining:"",billingRate:"",billingRateEffectiveDate:"",actualRate:"",actualRateEffectiveDate:"",password:""});
  const [ferr,setFerr]    =useState("");
  const [filterRole,setFR]=useState("");
  const [filterStatus,setFS]=useState("");
  const [search,setSrch]  =useState("");
  const [umPage,setUMPage]=useState(1);
  const [deactivateFor,setDeactivateFor]=useState(null); // { id, name, date }
  const [saving,setSaving]=useState(false);
  const [resetMsg,setResetMsg]=useState("");
  const UM_PAGE = 10;
  const isAdmin = user.email===ADMIN_EMAIL;
  const isPartnerUser = user.role==="partner";

  const openAdd=()=>{setEU(null);setF({name:"",email:"",role:"intern",dateOfJoining:todayStr(),billingRate:"",billingRateEffectiveDate:todayStr(),actualRate:"",actualRateEffectiveDate:todayStr(),password:""});setFerr("");setResetMsg("");setSM(true);};
  const openEdit=u=>{setEU(u);setF({name:u.name,email:u.email,role:u.role,dateOfJoining:u.dateOfJoining||"",billingRate:u.billingRate,billingRateEffectiveDate:u.billingRateEffectiveDate||todayStr(),actualRate:u.actualRate||"",actualRateEffectiveDate:u.actualRateEffectiveDate||todayStr(),password:""});setFerr("");setResetMsg("");setSM(true);};

  // Cost rates are written to the partner-only "rates" collection, never to the user doc.
  const writeRates = (userId) => setDoc(doc(db, "rates", userId), {
    actualRate: form.actualRate ? Number(form.actualRate) : null,
    actualRateEffectiveDate: form.actualRateEffectiveDate || todayStr(),
  });

  // Firebase emails the staff member a secure link to set a new password. Nobody else ever sees it.
  const sendReset = async (email) => {
    setResetMsg("");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg(`Password reset link sent to ${email}. Ask them to check spam if it does not arrive in a few minutes.`);
      addAudit(user.id,user.name,"SEND_PASSWORD_RESET",`Sent password reset link to ${email}`);
    } catch(e) {
      console.error("Reset email error", e);
      setResetMsg("Could not send the reset link. Please try again.");
    }
  };

  const save= async ()=>{
    if(saving) return;
    setFerr("");
    if(!form.name||!form.email||!form.billingRate){setFerr("Name, email and billing rate are required.");return;}
    if(!editU && !form.dateOfJoining){setFerr("Date of joining is required for new staff.");return;}
    if(!roleKey(form.email).endsWith("@msna.co.in")){setFerr("Must be an @msna.co.in address.");return;}
    if(editU){
      setSaving(true);
      try { await writeRates(editU.id); }
      catch(e){ console.error("Rates save error",e); setFerr("Could not save the cost rate. Please try again."); setSaving(false); return; }
      setUsers(p=>p.map(u=>u.id===editU.id?stripRates({...u,
        name:form.name,role:form.role,
        dateOfJoining:form.dateOfJoining||u.dateOfJoining||null,
        billingRate:Number(form.billingRate),
        billingRateEffectiveDate:form.billingRateEffectiveDate||todayStr(),
      }):u));
      addAudit(user.id,user.name,"EDIT_USER",`Updated ${form.email}`);
      setSaving(false);
    } else {
      if(!form.password){setFerr("Initial password is required for new users.");return;}
      if(form.password.length<6){setFerr("Initial password must be at least 6 characters.");return;}
      if(users.find(u=>roleKey(u.email)===roleKey(form.email))){setFerr("Email already exists.");return;}
      setSaving(true);
      // 1. Create the login first, on the secondary Auth instance, so this partner stays signed in.
      let alreadyHadLogin = false;
      try {
        await createUserWithEmailAndPassword(secondaryAuth, roleKey(form.email), form.password);
      } catch(e){
        if(e.code==="auth/email-already-in-use") alreadyHadLogin = true;
        else {
          console.error("Auth create error",e);
          setFerr(e.code==="auth/weak-password"?"Initial password is too weak.":"Could not create the login. Please try again.");
          setSaving(false); return;
        }
      } finally {
        await signOut(secondaryAuth).catch(()=>{});
      }
      // 2. Then the staff profile and their cost rate. The password itself is never stored anywhere.
      const newId = genId();
      try { await writeRates(newId); }
      catch(e){ console.error("Rates save error",e); }
      setUsers(p=>[...p,{id:newId,name:form.name,email:form.email.trim(),role:form.role,
        dateOfJoining:form.dateOfJoining,
        billingRate:Number(form.billingRate),
        billingRateEffectiveDate:form.billingRateEffectiveDate||todayStr(),
        active:true}]);
      addAudit(user.id,user.name,"CREATE_USER",`Created ${form.email} as ${form.role}`);
      setSaving(false);
      if(alreadyHadLogin){
        alert(`${form.email} already had a login from before, so the password you typed was NOT applied. Their old password still works. Use "Send password reset link" on their profile if they need a new one.`);
      }
    }
    setSM(false);
  };

  const requestDeactivate = u => setDeactivateFor({ id:u.id, name:u.name, date:todayStr() });
  const confirmDeactivate = () => {
    setUsers(p=>p.map(u=>u.id===deactivateFor.id?{...u,active:false,inactiveFrom:deactivateFor.date}:u));
    addAudit(user.id,user.name,"TOGGLE_USER",`Deactivated ${deactivateFor.id} effective ${deactivateFor.date}`);
    setDeactivateFor(null);
  };
  const reactivate = id => {
    setUsers(p=>p.map(u=>u.id===id?{...u,active:true,reactivatedFrom:todayStr(),inactiveFrom:null}:u));
    addAudit(user.id,user.name,"TOGGLE_USER",`Reactivated ${id}`);
  };
  const deleteUser=id=>{
    const target=users.find(u=>u.id===id);
    if(!target) return;
    if(target.email===ADMIN_EMAIL){ alert("The Admin account cannot be deleted."); return; }
    if(!window.confirm(`Delete ${target.name}? Their timesheets will be kept for records but they will no longer be able to log in.`)) return;
    setUsers(p=>p.filter(u=>u.id!==id));
    addAudit(user.id,user.name,"DELETE_USER",`Deleted user ${target.email}`);
  };

  // Filter + search (default sort: by name)
  const filtered = users.slice().sort((a,b)=>a.name.localeCompare(b.name)).filter(u=>{
    if(filterRole&&u.role!==filterRole) return false;
    if(filterStatus==="active"&&!u.active) return false;
    if(filterStatus==="inactive"&&u.active) return false;
    const q=search.toLowerCase();
    if(q&&!u.name.toLowerCase().includes(q)&&!u.email.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div>
      <div className="sh"><div><div className="card-title">User Management</div><div className="card-sub mt4 ts">Manage staff accounts, billing and cost rates</div></div>
        {isAdmin&&<button className="btn bp" onClick={openAdd}><I n="plus" s={15}/>Add Staff</button>}
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
        <input className="fi" placeholder="Search name or email..." value={search}
          style={{flex:1,minWidth:180,padding:"8px 12px",fontSize:13}}
          onChange={e=>{setSrch(e.target.value);setUMPage(1);}}/>
        <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterRole} onChange={e=>{setFR(e.target.value);setUMPage(1);}}>
          <option value="">All Roles</option>
          <option value="partner">Partner</option>
          <option value="manager">Manager</option>
          <option value="intern">Intern</option>
        </select>
        <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={filterStatus} onChange={e=>{setFS(e.target.value);setUMPage(1);}}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {(filterRole||filterStatus||search)&&<button className="btn bgh bsm" onClick={()=>{setFR("");setFS("");setSrch("");setUMPage(1);}}>✕ Clear</button>}
        <span className="tx tsl" style={{fontSize:12}}>{filtered.length} user{filtered.length!==1?"s":""}</span>
      </div>

      <div className="card">
        <div className="tw"><table>
          <thead><tr><th style={{width:36}}>#</th><th>Name</th><th>Email</th><th>Role</th><th>Date of Joining</th><th>Date of Leaving</th><th>Billing Rate</th><th>Actual Cost</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{filtered.slice((umPage-1)*UM_PAGE, umPage*UM_PAGE).map((u,idx)=>(
            <tr key={u.id}>
              <td className="tx tsl" style={{fontSize:12}}>{(umPage-1)*UM_PAGE+idx+1}</td>
              <td className="fw6">{u.name}{u.email===ADMIN_EMAIL&&<span className="tx tgo"> ★ Admin</span>}</td>
              <td className="ts tsl">{u.email}</td>
              <td><span className={`bdg ${u.role==="partner"?"rpa":u.role==="manager"?"rma":"ria"}`}>{u.role.charAt(0).toUpperCase()+u.role.slice(1)}</span></td>
              <td className="ts">{u.dateOfJoining?fmtDate(u.dateOfJoining):<span className="tx tsl">—</span>}</td>
              <td className="ts">{!u.active&&u.inactiveFrom?fmtDate(u.inactiveFrom):<span className="tx tsl">—</span>}</td>
              <td>
                <div className="fw6">{fmtCurrency(u.billingRate)}<span className="tx tsl">/hr</span></div>
                {u.billingRateEffectiveDate&&<div className="tx tsl" style={{fontSize:11}}>w.e.f. {fmtDate(u.billingRateEffectiveDate)}</div>}
              </td>
              <td>
                {u.actualRate
                  ?<><div className="fw6">{fmtCurrency(u.actualRate)}<span className="tx tsl">/hr</span></div>
                    {u.actualRateEffectiveDate&&<div className="tx tsl" style={{fontSize:11}}>w.e.f. {fmtDate(u.actualRateEffectiveDate)}</div>}</>
                  :<span className="tx tsl">—</span>}
              </td>
              <td><span className={`bdg ${u.active?"bac":"bcl"}`}>{u.active?"Active":"Inactive"}</span></td>
              <td><div className="fx g8">
                {isPartnerUser&&<button className="btn bgh bic bsm" onClick={()=>openEdit(u)}><I n="edit" s={14}/></button>}
                {u.id!==user.id&&<button className={`btn bsm ${u.active?"bd":"bsc"}`} onClick={()=>u.active?requestDeactivate(u):reactivate(u.id)}>{u.active?"Deactivate":"Activate"}</button>}
                {u.id!==user.id&&u.email!==ADMIN_EMAIL&&<button className="btn bd bic bsm" title="Delete user" onClick={()=>deleteUser(u.id)}><I n="trash" s={14}/></button>}
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
        {Math.ceil(filtered.length/UM_PAGE)>1&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
            <button className="btn bgh bsm" disabled={umPage===1} onClick={()=>setUMPage(p=>p-1)}>← Prev</button>
            <span className="ts tsl">Page {umPage} of {Math.ceil(filtered.length/UM_PAGE)} · {filtered.length} users</span>
            <button className="btn bgh bsm" disabled={umPage===Math.ceil(filtered.length/UM_PAGE)} onClick={()=>setUMPage(p=>p+1)}>Next →</button>
          </div>
        )}
      </div>

      {showM&&(
        <div className="mo" onClick={()=>setSM(false)}>
          <div className="md" onClick={e=>e.stopPropagation()}>
            <div className="md-title">{editU?"Edit Staff Member":"Add Staff Member"}</div>
            {ferr&&<div className="err">{ferr}</div>}
            <div className="fg"><label className="fl">Full Name</label><input className="fi" placeholder="e.g. Rahul Mehta" value={form.name} onChange={e=>setF(f=>({...f,name:e.target.value}))}/></div>
            <div className="fg"><label className="fl">Email</label><input className="fi" placeholder="rahul@msna.co.in" value={form.email} onChange={e=>setF(f=>({...f,email:e.target.value}))} disabled={!!editU}/></div>
            <div className="fg"><label className="fl">Role</label>
              <select className="fs" value={form.role} onChange={e=>setF(f=>({...f,role:e.target.value}))}>
                <option value="intern">Intern</option><option value="manager">Manager</option><option value="partner">Partner</option>
              </select>
            </div>
            <div className="fg"><label className="fl">Date of Joining{!editU&&" *"}</label><input type="date" className="fi" value={form.dateOfJoining} onChange={e=>setF(f=>({...f,dateOfJoining:e.target.value}))}/></div>
            {/* Billing Rate */}
            <div style={{background:"var(--cream)",borderRadius:8,padding:"14px 16px",border:"1px solid var(--border)"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--navy)",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>Billing Rate (charged to client)</div>
              <div className="g2">
                <div className="fg"><label className="fl">Rate (₹/hr)</label><input type="number" className="fi" placeholder="e.g. 5000" value={form.billingRate} onChange={e=>setF(f=>({...f,billingRate:e.target.value}))}/></div>
                <div className="fg"><label className="fl">With Effect From</label><input type="date" className="fi" value={form.billingRateEffectiveDate} onChange={e=>setF(f=>({...f,billingRateEffectiveDate:e.target.value}))}/></div>
              </div>
            </div>
            {/* Actual Cost Rate */}
            <div style={{background:"#fef9ec",borderRadius:8,padding:"14px 16px",border:"1px solid #fde68a"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#92400e",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>Actual Cost Rate (firm's internal cost)</div>
              <div className="g2">
                <div className="fg"><label className="fl">Rate (₹/hr)</label><input type="number" className="fi" placeholder="e.g. 1500" value={form.actualRate} onChange={e=>setF(f=>({...f,actualRate:e.target.value}))}/></div>
                <div className="fg"><label className="fl">With Effect From</label><input type="date" className="fi" value={form.actualRateEffectiveDate} onChange={e=>setF(f=>({...f,actualRateEffectiveDate:e.target.value}))}/></div>
              </div>
              <div className="tx tsl mt4" style={{fontSize:11}}>Used for internal profitability calculation. Not visible to staff.</div>
            </div>
            {editU?(
              <div className="fg">
                <label className="fl">Password</label>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <button type="button" className="btn bgh bsm" onClick={()=>sendReset(editU.email)}><I n="send" s={13}/>Send password reset link</button>
                  <span className="tx tsl">They set their own new password from the email. No one else sees it.</span>
                </div>
                {resetMsg&&<div className="al al-i" style={{marginTop:10,marginBottom:0}}><I n="info" s={14}/><div>{resetMsg}</div></div>}
              </div>
            ):(
              <div className="fg"><label className="fl">Initial Password *</label><input className="fi" type="password" placeholder="Min 6 characters" value={form.password} onChange={e=>setF(f=>({...f,password:e.target.value}))}/>
                <div className="tx tsl mt4">Share it with them privately. They should change it after first sign-in. It is not saved anywhere in the app.</div>
              </div>
            )}
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setSM(false)}>Cancel</button>
              <button className="btn bp" onClick={save} disabled={saving}><I n="check" s={15}/>{saving?"Saving...":editU?"Save Changes":"Create Account"}</button>
            </div>
          </div>
        </div>
      )}

      {deactivateFor&&(
        <div className="mo" onClick={()=>setDeactivateFor(null)}>
          <div className="md" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="md-title">Deactivate {deactivateFor.name}?</div>
            <p className="ts tsl mb16">This date determines which quarters they still appear in for appraisals and goal setting — earlier quarters remain visible, later ones won't require them.</p>
            <div className="fg"><label className="fl">Last working day / inactive from</label>
              <input type="date" className="fi" value={deactivateFor.date} onChange={e=>setDeactivateFor(d=>({...d,date:e.target.value}))}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setDeactivateFor(null)}>Cancel</button>
              <button className="btn bd" onClick={confirmDeactivate}>Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PROFITABILITY
// ══════════════════════════════════════════════════════════════
const PROF_CSS = `
.prof-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:22px;}
.prof-hero{background:var(--navy);border-radius:var(--rl);padding:22px 24px;color:#fff;position:relative;overflow:hidden;}
.prof-hero::after{content:'';position:absolute;right:-30px;top:-30px;width:140px;height:140px;border-radius:50%;border:30px solid rgba(201,168,76,.15);}
.prof-hero-val{font-family:'Playfair Display',serif;font-size:36px;color:var(--gold);}
.prof-hero-lbl{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,.5);margin-top:6px;}
.prof-hero-sub{font-size:13px;color:rgba(255,255,255,.6);margin-top:4px;}
.signal{display:inline-flex;align-items:center;gap:6px;padding:5px 14px;border-radius:20px;font-size:13px;font-weight:600;}
.sig-profit{background:#d1fae5;color:#065f46;}
.sig-risk{background:#fef3c7;color:#92400e;}
.sig-loss{background:#fee2e2;color:#991b1b;}
.sig-nofee{background:#f1f5f9;color:#64748b;}
.proj-filter{display:flex;gap:10px;align-items:center;margin-bottom:20px;flex-wrap:wrap;}
.proj-pill{padding:7px 16px;border-radius:20px;font-size:13px;cursor:pointer;border:1.5px solid var(--border);background:#fff;color:var(--slate);transition:all .15s;font-weight:500;}
.proj-pill.active{border-color:var(--navy);background:var(--navy);color:#fff;}
.waterfall{display:flex;flex-direction:column;gap:10px;margin-top:16px;}
.wf-row{display:flex;align-items:center;gap:12px;}
.wf-label{width:120px;font-size:13px;color:var(--slate);text-align:right;flex-shrink:0;}
.wf-bar-wrap{flex:1;background:var(--cream);border-radius:6px;height:28px;overflow:hidden;position:relative;}
.wf-bar{height:100%;border-radius:6px;display:flex;align-items:center;padding:0 10px;font-size:12px;font-weight:600;color:#fff;transition:width .4s;}
.wf-bar-fee{background:var(--navy);}
.wf-bar-cost{background:var(--red);}
.wf-bar-margin{background:var(--green);}
.wf-bar-over{background:var(--red);}
.wf-val{width:110px;font-size:13px;font-weight:600;text-align:right;flex-shrink:0;}
.staff-cost-row{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--border);}
.staff-cost-row:last-child{border-bottom:none;}
.cost-bar-wrap{flex:1;margin:0 16px;background:var(--cream);border-radius:4px;height:6px;}
.cost-bar-fill{height:100%;border-radius:4px;background:var(--navy-mid);}
.realization-ring{text-align:center;padding:20px 0;}
.ring-val{font-family:'Playfair Display',serif;font-size:42px;}
.ring-lbl{font-size:12px;color:var(--slate);text-transform:uppercase;letter-spacing:1px;margin-top:4px;}
.proj-header-card{background:var(--navy);border-radius:var(--rl);padding:20px 24px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.phc-left{color:#fff;}
.phc-code{font-family:'Playfair Display',serif;font-size:24px;color:var(--gold);}
.phc-name{font-size:13px;color:rgba(255,255,255,.6);margin-top:3px;}
.monthly-row{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--border);}
.monthly-row:last-child{border-bottom:none;}
`;

function Profitability({ users=[], projects=[], tss=[] }) {
  const [selected, setSelected] = useState(null);
  const [profPage, setProfPage] = useState(1);
  const PROF_PAGE = 10;
  const [profView, setProfView] = useState("actual");
  const [profSearch, setProfSearch] = useState("");
  const [profFilterStatus, setProfFilterStatus] = useState("");
  const [profFilterSignal, setProfFilterSignal] = useState("");
  const [profFilterType, setProfFilterType] = useState(""); // v38: narrows the table only, not the firm-wide cards
  const [profSortCol, setProfSortCol] = useState("margin_pct");
  const [profSortDir, setProfSortDir] = useState("desc");
  const [staffBreakdownMonth, setStaffBreakdownMonth] = useState("all"); // "all" or "YYYY-MM"
  const toggleProfSort = col => { if(profSortCol===col){setProfSortDir(d=>d==="asc"?"desc":"asc");}else{setProfSortCol(col);setProfSortDir("desc");} setProfPage(1); };
  const profSortIcon = col => profSortCol===col?(profSortDir==="asc"?" ↑":" ↓"):"";

  const approved = tss.filter(t => t.status === "approved");

  // Compute profitability for one project
  const calcProfit = (p) => {
    const sheets = approved.filter(t => t.projectId === p.id);
    const totalHrs = sheets.reduce((s,t) => s + t.hours, 0);

    // Get cost rate for a user on a given date — uses actualRate if date >= effectiveDate, else billingRate
    const getCostRate = (u, date) => {
      if(u.actualRate && u.actualRateEffectiveDate && date >= u.actualRateEffectiveDate) {
        return u.actualRate;
      }
      return u.billingRate||0;
    };

    // Billing rate cost (what we charge the client per hour)
    const staffCostBilling = sheets.reduce((s,t) => {
      const u2 = users.find(u=>u.id===t.userId);
      return s + t.hours * (u2?.billingRate||0);
    }, 0);
    // Actual cost (what the firm actually incurs — prospective from effectiveDate)
    const staffCostActual = sheets.reduce((s,t) => {
      const u2 = users.find(u=>u.id===t.userId);
      return s + t.hours * getCostRate(u2||{}, t.date);
    }, 0);
    // Use actual if available, else billing
    const hasActual = users.some(u=>u.actualRate&&u.actualRate>0);
    const staffCost = staffCostActual; // primary for signal/margin

    const fee = p.engagementFee || 0;
    const isRetainer = p.feeType === "retainer";
    const months = isRetainer ? (p.retainerMonths||1) : 1;
    const totalFee = fee;

    const marginActual = totalFee - staffCostActual;
    const marginBilling = totalFee - staffCostBilling;
    const marginPctActual = totalFee > 0 ? Math.round((marginActual/totalFee)*100) : null;
    const marginPctBilling = totalFee > 0 ? Math.round((marginBilling/totalFee)*100) : null;
    const margin = marginActual;
    const marginPct = marginPctActual;
    // Signal based on ACTIVE method (actual if actual rates exist, else billing)
    const signalActual = !fee ? "nofee" : marginActual >= 0 && (marginPctActual||0) >= 20 ? "profit" : marginActual >= 0 ? "risk" : "loss";
    const signalBilling = !fee ? "nofee" : marginBilling >= 0 && (marginPctBilling||0) >= 20 ? "profit" : marginBilling >= 0 ? "risk" : "loss";
    const signal = signalActual; // default; overridden at render time by profView

    // Staff breakdown — both rates
    const staffBreakdown = users.map(u => {
      const uSheets = sheets.filter(t=>t.userId===u.id);
      const hrs = uSheets.reduce((s,t)=>s+t.hours,0);
      const costActual = uSheets.reduce((s,t)=>s+t.hours*getCostRate(u,t.date),0);
      const costBilling = hrs * (u.billingRate||0);
      return {u, hrs, cost:costActual, costBilling};
    }).filter(d=>d.hrs>0).sort((a,b)=>b.cost-a.cost);

    // Monthly breakdown — both rates
    const byMonth = {};
    sheets.forEach(t => {
      const u2 = users.find(u=>u.id===t.userId);
      const mk = monthKey(t.date);
      if(!byMonth[mk]) byMonth[mk]={hrs:0,cost:0,costBilling:0};
      byMonth[mk].hrs += t.hours;
      byMonth[mk].cost += t.hours * getCostRate(u2||{}, t.date);
      byMonth[mk].costBilling += t.hours * (u2?.billingRate||0);
    });
    return {totalHrs, staffCost, staffCostBilling, staffCostActual, hasActual,
            totalFee, margin, marginActual, marginBilling, marginPct, marginPctActual, marginPctBilling,
            signal, signalActual, signalBilling, staffBreakdown, byMonth, months};
  };

  const allProfitRaw = projects
    .filter(p=>p.status==="active"||p.status==="closed")
    .map(p=>({p, ...calcProfit(p)}));

  // Apply search + filters
  const allProfit = allProfitRaw.filter(({p,signal})=>{
    const q = profSearch.toLowerCase();
    if(q&&!p.code.toLowerCase().includes(q)&&!p.clientName.toLowerCase().includes(q)&&!p.name.toLowerCase().includes(q)) return false;
    if(profFilterStatus&&p.status!==profFilterStatus) return false;
    if(profFilterSignal&&signal!==profFilterSignal) return false;
    return true;
  }).sort((a,b)=>{
    const getVal = (d,col) => {
      const margin = profView==="actual"?d.marginActual:d.marginBilling;
      const mp = profView==="actual"?d.marginPctActual:d.marginPctBilling;
      const cost = profView==="actual"?d.staffCostActual:d.staffCostBilling;
      if(col==="code") return d.p.code;
      if(col==="client") return d.p.clientName;
      if(col==="fee") return d.totalFee||0;
      if(col==="cost") return cost||0;
      if(col==="margin") return margin||0;
      if(col==="margin_pct") return mp??-999;
      return mp??-999;
    };
    const va=getVal(a,profSortCol), vb=getVal(b,profSortCol);
    if(va<vb) return profSortDir==="asc"?-1:1;
    if(va>vb) return profSortDir==="asc"?1:-1;
    return 0;
  });

  const sigLabel = s => s==="profit"?"● Profitable":s==="risk"?"● At Risk":s==="loss"?"● Loss Making":"● No Fee Set";
  const sigClass = s => `signal sig-${s}`;
  const firmFee          = allProfit.reduce((s,d)=>s+d.totalFee,0);
  const firmCostActual   = allProfit.reduce((s,d)=>s+d.staffCostActual,0);
  const firmCostBilling  = allProfit.reduce((s,d)=>s+d.staffCostBilling,0);
  const firmCost         = profView==="actual" ? firmCostActual : firmCostBilling;
  const firmMarginActual = firmFee - firmCostActual;
  const firmMarginBilling= firmFee - firmCostBilling;
  const firmMargin       = profView==="actual" ? firmMarginActual : firmMarginBilling;
  const firmMarginPct    = firmFee>0 ? Math.round((firmMargin/firmFee)*100) : 0;
  const hasAnyActual     = users.some(u=>u.actualRate&&u.actualRate>0);

  // v38: type filter applies to the engagement table only; firm-wide cards above keep using allProfit.
  const profTypeCounts = { "":allProfit.length, fixed:allProfit.filter(d=>engTypeOf(d.p)==="fixed").length, retainer:allProfit.filter(d=>engTypeOf(d.p)==="retainer").length };
  const profRows = profFilterType ? allProfit.filter(d=>engTypeOf(d.p)===profFilterType) : allProfit;

  const selData = selected ? allProfit.find(d=>d.p.id===selected) : null;

  return (
    <div>
      <style>{PROF_CSS}</style>
      <div className="sh">
        <div><div className="card-title">Profitability</div><div className="card-sub mt4 ts">Fee vs staff cost analysis — approved hours only</div></div>
        {selected && <button className="btn bgh bsm" onClick={()=>{setSelected(null);setStaffBreakdownMonth("all");}}>← All Engagements</button>}
      </div>

      {/* ── FIRM-WIDE VIEW ── */}
      {!selected && <>
        {/* Method toggle */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap"}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--navy)"}}>Profitability Method:</div>
          <div style={{display:"flex",background:"var(--cream)",borderRadius:10,padding:3,border:"1px solid var(--border)"}}>
            <button onClick={()=>setProfView("actual")} style={{padding:"7px 18px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:500,background:profView==="actual"?"var(--navy)":"transparent",color:profView==="actual"?"#fff":"var(--slate)",transition:"all .15s"}}>Actual Cost Basis</button>
            <button onClick={()=>setProfView("billing")} style={{padding:"7px 18px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:500,background:profView==="billing"?"var(--navy)":"transparent",color:profView==="billing"?"#fff":"var(--slate)",transition:"all .15s"}}>Billing Rate Basis</button>
          </div>
          <div style={{fontSize:12,color:"var(--slate)",background:"var(--cream)",padding:"5px 12px",borderRadius:20,border:"1px solid var(--border)"}}>
            {profView==="actual"?"Using actual cost incurred by firm":"Using billing rate charged to client"}
          </div>
          {!hasAnyActual&&profView==="actual"&&(
            <div style={{fontSize:12,background:"#fef3c7",color:"#92400e",padding:"5px 12px",borderRadius:20,border:"1px solid #fde68a"}}>
              ⚠ No actual cost rates set yet — showing billing rate as fallback. Set actual rates in User Management.
            </div>
          )}
        </div>

        <div className="prof-grid">
          <div className="prof-hero">
            <div className="prof-hero-val">{fmtCurrency(firmFee)}</div>
            <div className="prof-hero-lbl">Total Fee Value</div>
            <div className="prof-hero-sub">{allProfit.filter(d=>d.totalFee>0).length} engagements with fees</div>
          </div>
          <div className="prof-hero" style={{background:"var(--navy-mid)"}}>
            <div className="prof-hero-val">{fmtCurrency(firmCost)}</div>
            <div className="prof-hero-lbl">Staff Cost ({profView==="actual"?"Actual":"Billing Rate"})</div>
            <div className="prof-hero-sub">{approved.reduce((s,t)=>s+t.hours,0).toFixed(1)} approved hours</div>
          </div>
          <div className="prof-hero" style={{background:firmMargin>=0?"#064e3b":"#7f1d1d"}}>
            <div className="prof-hero-val" style={{color:firmMargin>=0?"#6ee7b7":"#fca5a5"}}>{fmtCurrency(firmMargin)}</div>
            <div className="prof-hero-lbl">Net Margin</div>
            <div className="prof-hero-sub">{firmFee>0?firmMarginPct+"% gross margin":"Set fees to see margin"}</div>
          </div>
        </div>

        {/* Side-by-side comparison */}
        {hasAnyActual&&firmFee>0&&(
          <div className="card mb22" style={{padding:"18px 22px"}}>
            <div style={{fontWeight:600,fontSize:14,color:"var(--navy)",marginBottom:14}}>Comparison — Both Methods</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {[
                {label:"Actual Cost Basis",cost:firmCostActual,margin:firmMarginActual,isActive:profView==="actual"},
                {label:"Billing Rate Basis",cost:firmCostBilling,margin:firmMarginBilling,isActive:profView==="billing"},
              ].map(m=>(
                <div key={m.label} style={{background:"var(--cream)",borderRadius:10,padding:"16px 18px",border:"2px solid",borderColor:m.isActive?"var(--navy)":"var(--border)"}}>
                  <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"1px",color:"var(--navy)",marginBottom:12}}>
                    {m.label}{m.isActive&&<span style={{fontSize:10,background:"var(--navy)",color:"#fff",padding:"2px 8px",borderRadius:20,marginLeft:8}}>Active</span>}
                  </div>
                  {[
                    {label:"Fee Earned",val:firmFee,color:"var(--navy)"},
                    {label:"Staff Cost",val:m.cost,color:"var(--red)"},
                    {label:"Net Margin",val:m.margin,color:m.margin>=0?"var(--green)":"var(--red)"},
                    {label:"Margin %",val:null,pct:firmFee>0?Math.round(m.margin/firmFee*100):0},
                  ].map(r=>{
                    const pctColor=!r.val&&r.pct<0?"var(--red)":!r.val&&r.pct<20?"var(--amber)":"var(--green)";
                    return (
                      <div key={r.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid var(--border)"}}>
                        <span style={{fontSize:13,color:"var(--slate)"}}>{r.label}</span>
                        <span style={{fontSize:14,fontWeight:600,color:r.val!=null?(r.val<0?"var(--red)":r.color):pctColor}}>
                          {r.val!=null?fmtCurrency(r.val):r.pct+"%"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {firmFee>0&&(
          <div className="card mb22">
            <div className="card-title mb16">Firm-wide Waterfall — {profView==="actual"?"Actual Cost":"Billing Rate"} Basis</div>
            <div className="waterfall">
              {[
                {label:"Fee Earned",val:firmFee,pct:100,cls:"wf-bar-fee"},
                {label:"Staff Cost",val:firmCost,pct:Math.min(Math.round(firmCost/firmFee*100),100),cls:"wf-bar-cost"},
                {label:"Net Margin",val:firmMargin,pct:Math.max(0,firmMarginPct),cls:firmMargin>=0?"wf-bar-margin":"wf-bar-over"},
              ].map(row=>(
                <div key={row.label} className="wf-row">
                  <div className="wf-label">{row.label}</div>
                  <div className="wf-bar-wrap">
                    <div className={`wf-bar ${row.cls}`} style={{width:Math.max(row.pct,2)+"%"}}>{row.pct>15&&row.label}</div>
                  </div>
                  <div className="wf-val" style={{color:row.val<0?"var(--red)":"var(--navy)"}}>{fmtCurrency(row.val)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search + Filters */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
          <input className="fi" placeholder="Search code, client, engagement..." value={profSearch}
            style={{flex:"0 1 300px",minWidth:180,padding:"8px 12px",fontSize:13}}
            onChange={e=>{setProfSearch(e.target.value);setProfPage(1);}}/>
          <TypePills value={profFilterType} onChange={v=>{setProfFilterType(v);setProfPage(1);}} counts={profTypeCounts}/>
          <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={profFilterStatus} onChange={e=>{setProfFilterStatus(e.target.value);setProfPage(1);}}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
          <select className="fs" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={profFilterSignal} onChange={e=>{setProfFilterSignal(e.target.value);setProfPage(1);}}>
            <option value="">All Signals</option>
            <option value="profit">Profitable</option>
            <option value="risk">At Risk</option>
            <option value="loss">Loss Making</option>
            <option value="nofee">No Fee Set</option>
          </select>
          {(profSearch||profFilterStatus||profFilterSignal||profFilterType)&&
            <button className="btn bgh bsm" onClick={()=>{setProfSearch("");setProfFilterStatus("");setProfFilterSignal("");setProfFilterType("");setProfPage(1);}}>✕ Clear</button>}
          <span className="tx tsl" style={{fontSize:12,marginLeft:"auto"}}>{profRows.length} engagement{profRows.length!==1?"s":""}</span>
        </div>

        <div className="card">
          {allProfitRaw.length===0
            ? <div className="es"><div className="es-icon"><I n="target" s={36}/></div>No engagement data yet.</div>
            : profRows.length===0
            ? <div className="es">No engagements match your filters.</div>
            : <>
              <div className="tw"><table>
                <thead><tr>
                  <th style={{width:32}}>#</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleProfSort("code")}>Code{profSortIcon("code")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleProfSort("client")}>Client{profSortIcon("client")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleProfSort("fee")}>Fee{profSortIcon("fee")}</th>
                  <th style={{cursor:"pointer",color:"var(--red)"}} onClick={()=>toggleProfSort("cost")}>Cost ({profView==="actual"?"Actual":"Billing"}){profSortIcon("cost")}</th>
                  {hasAnyActual&&<th style={{color:"var(--slate)",fontSize:11}}>Alt. Cost</th>}
                  <th style={{cursor:"pointer"}} onClick={()=>toggleProfSort("margin")}>Margin{profSortIcon("margin")}</th>
                  <th style={{cursor:"pointer"}} onClick={()=>toggleProfSort("margin_pct")}>Margin %{profSortIcon("margin_pct")}</th>
                  <th>Status</th><th>Signal</th><th></th>
                </tr></thead>
                <tbody>{profRows.slice((profPage-1)*PROF_PAGE, profPage*PROF_PAGE).map(({p,totalFee,staffCostActual,staffCostBilling,marginActual,marginBilling,marginPctActual,marginPctBilling,signal,signalActual,signalBilling},idx)=>{
                  const cost=profView==="actual"?staffCostActual:staffCostBilling;
                  const otherCost=profView==="actual"?staffCostBilling:staffCostActual;
                  const margin=profView==="actual"?marginActual:marginBilling;
                  const mp=profView==="actual"?marginPctActual:marginPctBilling;
                  return (
                    <tr key={p.id} style={{cursor:"pointer"}} onClick={()=>{setSelected(p.id);setStaffBreakdownMonth("all");}}>
                      <td className="tx tsl" style={{fontSize:12}}>{(profPage-1)*PROF_PAGE+idx+1}</td>
                      <td><span className="fw6 mono">{p.code}</span></td>
                      <td>{p.clientName}<div className="tx tsl">{p.name}</div></td>
                      <td className="fw6">{totalFee>0?fmtCurrency(totalFee):<span className="tsl tx">Not set</span>}
                        {p.feeType==="retainer"&&p.retainerMonths&&<div className="tx tsl">{fmtCurrency(p.monthlyFee||0)}/mo × {p.retainerMonths}m</div>}
                      </td>
                      <td className="fw6">{fmtCurrency(cost)}</td>
                      {hasAnyActual&&<td className="tx tsl" style={{fontSize:12}}>{fmtCurrency(otherCost)}</td>}
                      <td className={margin>=0?"tsc fw6":"tdn fw6"}>{totalFee>0?fmtCurrency(margin):"—"}</td>
                      <td>{mp!=null?<span className={margin<0?"tdn":mp<20?"tam":"tsc"}>{mp}%</span>:<span className="tsl tx">—</span>}</td>
                      <td><span className={`bdg ${p.status==="active"?"bac":"bcl"}`}>{p.status}</span></td>
                      <td style={{whiteSpace:"nowrap"}}><span className={sigClass(profView==="actual"?signalActual:signalBilling)}>{sigLabel(profView==="actual"?signalActual:signalBilling)}</span></td>
                      <td><button className="btn bgh bxs" onClick={e=>{e.stopPropagation();setSelected(p.id);setStaffBreakdownMonth("all");}}>Detail →</button></td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
              {Math.ceil(profRows.length/PROF_PAGE)>1&&(
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                  <button className="btn bgh bsm" disabled={profPage===1} onClick={()=>setProfPage(p=>p-1)}>← Prev</button>
                  <span className="ts tsl">Page {profPage} of {Math.ceil(profRows.length/PROF_PAGE)} · {profRows.length} engagements</span>
                  <button className="btn bgh bsm" disabled={profPage===Math.ceil(profRows.length/PROF_PAGE)} onClick={()=>setProfPage(p=>p+1)}>Next →</button>
                </div>
              )}
            </>}
        </div>
      </>}
      {/* ── SINGLE ENGAGEMENT DRILL-DOWN ── */}
      {selData && (() => {
        const {p, totalHrs, totalFee, staffCostActual, staffCostBilling, marginActual, marginBilling,
               marginPctActual, marginPctBilling, signalActual, signalBilling, staffBreakdown, byMonth, hasActual} = selData; // eslint-disable-line no-unused-vars
        const activeSignal = profView==="actual" ? signalActual : signalBilling; // eslint-disable-line no-unused-vars
        const activeCost   = profView==="actual" ? staffCostActual : staffCostBilling;
        const activeMargin = profView==="actual" ? marginActual : marginBilling;
        const activeMPct   = profView==="actual" ? marginPctActual : marginPctBilling;
        const budgetPct = p.budgetHours ? Math.min(Math.round(totalHrs/p.budgetHours*100),100) : null;
        const partner = users.find(u=>u.id===p.assignedPartnerId);
        return (
          <div>
            {/* Header */}
            <div className="proj-header-card">
              <div className="phc-left">
                <div className="phc-code">{p.code}</div>
                <div className="phc-name">{p.name} · {p.clientName}</div>
                <div style={{marginTop:10,display:"flex",gap:8,flexWrap:"wrap"}}>
                  <span className={sigClass(signalActual)}>{sigLabel(signalActual)} (Actual)</span>
                  <span className={sigClass(signalBilling)}>{sigLabel(signalBilling)} (Billing)</span>
                </div>
              </div>
              <div style={{textAlign:"right",color:"rgba(255,255,255,.7)",fontSize:13}}>
                <div>Partner: {partner?.name||"—"}</div>
                <div style={{marginTop:4}}>Fee type: {p.feeType==="retainer"?"Monthly Retainer":"Fixed Fee"}</div>
                {p.feeType==="retainer"&&<div style={{marginTop:2}}>{p.retainerMonths} month(s) · {fmtCurrency(p.monthlyFee||0)}/month</div>}
              </div>
            </div>

            {/* Dual KPI comparison */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
              {[
                {label:"Actual Cost Basis",cost:staffCostActual,margin:marginActual,mp:marginPctActual,signal:signalActual,active:profView==="actual"},
                {label:"Billing Rate Basis",cost:staffCostBilling,margin:marginBilling,mp:marginPctBilling,signal:signalBilling,active:profView==="billing"},
              ].map(m=>(
                <div key={m.label} style={{background:"var(--cream)",borderRadius:12,padding:"16px 18px",border:"2px solid",borderColor:m.active?"var(--navy)":"var(--border)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"1px",color:"var(--navy)"}}>{m.label}</div>
                    {m.active&&<span style={{fontSize:10,background:"var(--navy)",color:"#fff",padding:"2px 8px",borderRadius:20}}>Active</span>}
                  </div>
                  {[
                    {label:"Fee",val:totalFee,color:"var(--navy)"},
                    {label:"Staff Cost",val:m.cost,color:"var(--red)"},
                    {label:"Net Margin",val:m.margin,color:m.margin>=0?"var(--green)":"var(--red)"},
                    {label:"Margin %",val:null,pct:m.mp},
                  ].map(r=>{
                    const pctColor = r.pct==null?"var(--slate)":r.pct<0?"var(--red)":r.pct<20?"var(--amber)":"var(--green)";
                    return (
                      <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
                        <span style={{fontSize:13,color:"var(--slate)"}}>{r.label}</span>
                        <span style={{fontWeight:600,fontSize:13,color:r.val!=null?(r.val<0?"var(--red)":r.color):pctColor}}>
                          {r.val!=null?fmtCurrency(r.val):r.pct!=null?r.pct+"%":"—"}
                        </span>
                      </div>
                    );
                  })}
                  <div style={{marginTop:10}}><span className={sigClass(m.signal)}>{sigLabel(m.signal)}</span></div>
                </div>
              ))}
            </div>

            <div className="g2" style={{gap:16,marginBottom:22}}>
              {/* Waterfall for active method */}
              <div className="card">
                <div className="card-title mb16">Fee vs Cost — {profView==="actual"?"Actual Cost":"Billing Rate"}</div>
                {totalFee>0?(
                  <div className="waterfall">
                    {[
                      {label:"Fee Earned", val:totalFee, pct:100, cls:"wf-bar-fee"},
                      {label:"Staff Cost", val:activeCost, pct:Math.min(Math.round(activeCost/totalFee*100),100), cls:"wf-bar-cost"},
                      {label:activeMargin>=0?"Margin":"Overrun", val:activeMargin, pct:Math.max(0,activeMPct||0), cls:activeMargin>=0?"wf-bar-margin":"wf-bar-over"},
                    ].map(row=>(
                      <div key={row.label} className="wf-row">
                        <div className="wf-label">{row.label}</div>
                        <div className="wf-bar-wrap">
                          <div className={`wf-bar ${row.cls}`} style={{width:Math.max(row.pct,2)+"%"}}>{row.pct>18&&row.label}</div>
                        </div>
                        <div className="wf-val" style={{color:row.val<0?"var(--red)":"inherit"}}>{fmtCurrency(row.val)}</div>
                      </div>
                    ))}
                  </div>
                ):<div className="es" style={{padding:"30px 0"}}>No fee set for this engagement.</div>}
                {p.budgetHours&&(
                  <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--border)"}}>
                    <div className="fxb mb8">
                      <span className="ts fw6">Hours Budget</span>
                      <span className="ts tsl">{fmtHrs(totalHrs)}h / {p.budgetHours}h</span>
                    </div>
                    <div className="pbw" style={{height:10}}><div className={`pbf ${budgetPct>=100?"pbov":budgetPct>=80?"pbwn":"pbok"}`} style={{width:budgetPct+"%"}}/></div>
                    <div className="tx tsl mt4">{budgetPct}% of budget consumed</div>
                  </div>
                )}
              </div>

              {/* Effective rate */}
              <div className="card" style={{display:"flex",flexDirection:"column"}}>
                <div className="card-title mb16">Effective Rate Analysis</div>
                {totalHrs>0&&totalFee>0?(
                  <>
                    <div style={{textAlign:"center",padding:"16px 0"}}>
                      <div className="ring-val" style={{color:totalFee/totalHrs>=(activeCost/totalHrs)?"var(--green)":"var(--red)"}}>{fmtCurrency(Math.round(totalFee/totalHrs))}</div>
                      <div className="ring-lbl">Fee / Hour</div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                      <div style={{background:"var(--cream)",borderRadius:8,padding:"12px",textAlign:"center"}}>
                        <div className="fw6 ts">{fmtCurrency(Math.round(activeCost/totalHrs))}</div>
                        <div className="tx tsl">Cost / Hour</div>
                        <div className="tx tsl" style={{fontSize:10}}>{profView==="actual"?"Actual":"Billing"}</div>
                      </div>
                      <div style={{background:"var(--cream)",borderRadius:8,padding:"12px",textAlign:"center"}}>
                        <div className="fw6 ts" style={{color:activeMargin>=0?"var(--green)":"var(--red)"}}>{fmtCurrency(Math.round(activeMargin/totalHrs))}</div>
                        <div className="tx tsl">Margin / Hour</div>
                      </div>
                    </div>
                    {hasActual&&staffCostActual!==staffCostBilling&&(
                      <div style={{background:"#fef9ec",borderRadius:8,padding:"10px 12px",border:"1px solid #fde68a",fontSize:12}}>
                        <div style={{fontWeight:600,color:"#92400e",marginBottom:6}}>Rate Comparison</div>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{color:"var(--slate)"}}>Actual cost/hr</span>
                          <span style={{fontWeight:600}}>{fmtCurrency(Math.round(staffCostActual/totalHrs))}</span>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          <span style={{color:"var(--slate)"}}>Billing rate/hr</span>
                          <span style={{fontWeight:600}}>{fmtCurrency(Math.round(staffCostBilling/totalHrs))}</span>
                        </div>
                      </div>
                    )}
                    {activeMargin<0&&(
                      <div className="al al-d" style={{marginTop:10,marginBottom:0}}>
                        <I n="alert" s={14}/>
                        <div>This engagement is loss-making on {profView==="actual"?"actual cost":"billing rate"} basis.</div>
                      </div>
                    )}
                  </>
                ):<div className="es" style={{padding:"30px 0"}}>Log approved hours to see rate analysis.</div>}
              </div>
            </div>

            {/* Staff cost breakdown */}
            {(()=>{
              // Available months from approved sheets for this project (for retainer filter)
              const projSheets = approved.filter(t=>t.projectId===p.id);
              const availMonths = [...new Set(projSheets.map(t=>monthKey(t.date)).filter(Boolean))].sort();
              const isRetainerProj = p.feeType==="retainer";

              // Filter staffBreakdown by selected month (retainer only)
              const getCostRate2 = (u, date) => {
                if(u.actualRate && u.actualRateEffectiveDate && date >= u.actualRateEffectiveDate) return u.actualRate;
                return u.billingRate||0;
              };
              let filteredBreakdown = staffBreakdown;
              let filteredTotalFee = totalFee;
              if(isRetainerProj && staffBreakdownMonth !== "all") {
                // Recompute breakdown for just the selected month's sheets
                const mSheets = projSheets.filter(t=>monthKey(t.date)===staffBreakdownMonth);
                filteredBreakdown = users.map(u=>{
                  const uSheets = mSheets.filter(t=>t.userId===u.id);
                  const hrs = uSheets.reduce((s,t)=>s+t.hours,0);
                  const costActual = uSheets.reduce((s,t)=>s+t.hours*getCostRate2(u,t.date),0);
                  const costBilling = hrs*(u.billingRate||0);
                  return {u,hrs,cost:costActual,costBilling};
                }).filter(d=>d.hrs>0).sort((a,b)=>b.cost-a.cost);
                filteredTotalFee = p.monthlyFee||0; // use monthly fee for % of fee column
              }

              return (
                <div className="card mb22">
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:isRetainerProj&&availMonths.length>0?12:16,flexWrap:"wrap",gap:10}}>
                    <div className="card-title">Staff Cost Breakdown</div>
                    <div style={{fontSize:11,color:"var(--slate)"}}>Both methods shown side by side</div>
                  </div>

                  {/* Month filter — retainer projects only */}
                  {isRetainerProj && availMonths.length > 0 && (
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                      <span style={{fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)",marginRight:4}}>Period:</span>
                      <button
                        onClick={()=>setStaffBreakdownMonth("all")}
                        style={{
                          padding:"4px 13px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:"1.5px solid",transition:"all .15s",
                          background:staffBreakdownMonth==="all"?"var(--navy)":"transparent",
                          color:staffBreakdownMonth==="all"?"#fff":"var(--slate)",
                          borderColor:staffBreakdownMonth==="all"?"var(--navy)":"var(--border)",
                        }}
                      >Till Date</button>
                      {availMonths.map(mk=>(
                        <button
                          key={mk}
                          onClick={()=>setStaffBreakdownMonth(mk)}
                          style={{
                            padding:"4px 13px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:"1.5px solid",transition:"all .15s",
                            background:staffBreakdownMonth===mk?"var(--navy)":"transparent",
                            color:staffBreakdownMonth===mk?"#fff":"var(--slate)",
                            borderColor:staffBreakdownMonth===mk?"var(--navy)":"var(--border)",
                          }}
                        >{monthLabel(mk)}</button>
                      ))}
                    </div>
                  )}

                  {/* Active period label */}
                  {isRetainerProj && staffBreakdownMonth !== "all" && (
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14,padding:"7px 12px",background:"var(--gold-pale)",borderRadius:8,fontSize:12,color:"#92400e",border:"1px solid #fde68a"}}>
                      <I n="calendar" s={13}/>
                      <span>Showing <strong>{monthLabel(staffBreakdownMonth)}</strong> — monthly fee used for % of fee calculation</span>
                    </div>
                  )}

                  {filteredBreakdown.length===0
                    ?<div className="es" style={{padding:"24px 0"}}>{staffBreakdownMonth!=="all"?"No approved hours for this month.":"No approved hours yet."}</div>
                    :<div className="tw"><table>
                      <thead>
                        <tr style={{background:"var(--cream)"}}>
                          <th rowSpan="2">Staff</th>
                          <th rowSpan="2" style={{textAlign:"right"}}>Hours</th>
                          <th colSpan="2" style={{textAlign:"center",borderBottom:"1px solid var(--border)"}}>Staff Cost</th>
                          <th colSpan="2" style={{textAlign:"center",borderBottom:"1px solid var(--border)"}}>% of Fee</th>
                        </tr>
                        <tr style={{background:"var(--cream)"}}>
                          <th style={{textAlign:"right",color:"var(--red)",fontSize:11}}>Actual</th>
                          <th style={{textAlign:"right",color:"var(--slate)",fontSize:11}}>Billing</th>
                          <th style={{textAlign:"right",color:"var(--red)",fontSize:11}}>Actual</th>
                          <th style={{textAlign:"right",color:"var(--slate)",fontSize:11}}>Billing</th>
                        </tr>
                      </thead>
                      <tbody>
                      {filteredBreakdown.map(({u,hrs,cost,costBilling})=>{
                        const actualRate = u.actualRate || u.billingRate || 0;
                        return (
                          <tr key={u.id}>
                            <td>
                              <div className="fw6 ts">{u.name}</div>
                              <div className="tx tsl" style={{fontSize:11}}>
                                {u.role} · Actual {fmtCurrency(actualRate)}/hr · Billing {fmtCurrency(u.billingRate||0)}/hr
                              </div>
                            </td>
                            <td style={{textAlign:"right"}} className="ts">{fmtHrs(hrs)}h</td>
                            <td style={{textAlign:"right"}} className="fw6">{fmtCurrency(cost)}</td>
                            <td style={{textAlign:"right"}} className="tx tsl">{fmtCurrency(costBilling)}</td>
                            <td style={{textAlign:"right"}} className="fw6">{filteredTotalFee>0?Math.round(cost/filteredTotalFee*100)+"%":"—"}</td>
                            <td style={{textAlign:"right"}} className="tx tsl">{filteredTotalFee>0?Math.round(costBilling/filteredTotalFee*100)+"%":"—"}</td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table></div>}
                </div>
              );
            })()}

            {/* Monthly trend — retainer only (not useful for fixed fee) */}
            {p.feeType==="retainer"&&Object.keys(byMonth).length>0&&(
              <div className="card" style={{padding:0,overflow:"hidden"}}>
                {/* Card header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 24px",borderBottom:"1px solid var(--border)"}}>
                  <div className="card-title">Monthly Cost Trend</div>
                  <div style={{fontSize:12,color:"var(--slate-light)",letterSpacing:"0.3px"}}>Showing both methods side by side</div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      {/* Group header row */}
                      <tr style={{background:"var(--cream)"}}>
                        <th style={{padding:"10px 24px",textAlign:"left",fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)",borderBottom:"none",width:"22%"}}>Month</th>
                        <th style={{padding:"10px 16px",textAlign:"right",fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)",borderBottom:"none",width:"10%"}}>Hours</th>
                        <th colSpan="2" style={{padding:"10px 16px",textAlign:"center",fontSize:10,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:"var(--slate)",borderBottom:"2px solid var(--border)",borderLeft:"1px solid var(--border)"}}>Staff Cost</th>
                        {p.feeType==="retainer"&&<th style={{padding:"10px 16px",textAlign:"right",fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)",borderBottom:"none",borderLeft:"1px solid var(--border)",width:"14%"}}>Monthly Fee</th>}
                        {p.feeType==="retainer"&&<th colSpan="2" style={{padding:"10px 16px",textAlign:"center",fontSize:10,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:"var(--slate)",borderBottom:"2px solid var(--border)",borderLeft:"1px solid var(--border)"}}>Monthly Margin</th>}
                      </tr>
                      {/* Sub-header row */}
                      <tr style={{background:"var(--cream)"}}>
                        <th style={{padding:"6px 24px 10px",borderBottom:"1px solid var(--border)"}}></th>
                        <th style={{padding:"6px 16px 10px",borderBottom:"1px solid var(--border)"}}></th>
                        <th style={{padding:"6px 16px 10px",textAlign:"right",fontSize:10,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--red)",borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)"}}>Actual</th>
                        <th style={{padding:"6px 16px 10px",textAlign:"right",fontSize:10,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate-light)",borderBottom:"1px solid var(--border)"}}>Billing</th>
                        {p.feeType==="retainer"&&<th style={{padding:"6px 16px 10px",borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)"}}></th>}
                        {p.feeType==="retainer"&&<th style={{padding:"6px 16px 10px",textAlign:"right",fontSize:10,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--green)",borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)"}}>Actual</th>}
                        {p.feeType==="retainer"&&<th style={{padding:"6px 16px 10px",textAlign:"right",fontSize:10,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate-light)",borderBottom:"1px solid var(--border)"}}>Billing</th>}
                      </tr>
                    </thead>
                    <tbody>{Object.entries(byMonth).sort(([a],[b])=>a>b?1:-1).map(([mk,d],i,arr)=>{
                      const mFee = p.monthlyFee||0;
                      const mMarginActual  = p.feeType==="retainer" ? mFee - d.cost : null;
                      const mMarginBilling = p.feeType==="retainer" ? mFee - d.costBilling : null;
                      const isLast = i===arr.length-1;
                      const tdBase = {padding:"14px 16px",borderBottom:isLast?"none":"1px solid var(--border)",verticalAlign:"middle"};
                      return <tr key={mk} style={{transition:"background .12s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#fcfcfc"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td style={{...tdBase,padding:"14px 24px"}}>
                          <span style={{fontWeight:600,fontSize:14,color:"var(--navy)"}}>{monthLabel(mk)}</span>
                        </td>
                        <td style={{...tdBase,textAlign:"right"}}>
                          <span style={{fontWeight:500,color:"var(--slate)"}}>{fmtHrs(d.hrs)}h</span>
                        </td>
                        <td style={{...tdBase,textAlign:"right",borderLeft:"1px solid var(--border)"}}>
                          <span style={{fontWeight:700,fontSize:14,color:"var(--navy)"}}>{fmtCurrency(d.cost)}</span>
                        </td>
                        <td style={{...tdBase,textAlign:"right"}}>
                          <span style={{fontSize:13,color:"var(--slate-light)"}}>{fmtCurrency(d.costBilling)}</span>
                        </td>
                        {p.feeType==="retainer"&&<td style={{...tdBase,textAlign:"right",borderLeft:"1px solid var(--border)"}}>
                          <span style={{fontWeight:600,fontSize:14,color:"var(--gold)"}}>{fmtCurrency(mFee)}</span>
                        </td>}
                        {p.feeType==="retainer"&&<td style={{...tdBase,textAlign:"right",borderLeft:"1px solid var(--border)"}}>
                          <span style={{fontWeight:700,fontSize:14,color:mMarginActual>=0?"var(--green)":"var(--red)"}}>{fmtCurrency(mMarginActual)}</span>
                        </td>}
                        {p.feeType==="retainer"&&<td style={{...tdBase,textAlign:"right"}}>
                          <span style={{fontSize:13,color:mMarginBilling>=0?"var(--green)":"var(--red)",opacity:0.7}}>{fmtCurrency(mMarginBilling)}</span>
                        </td>}
                      </tr>;
                    })}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}



// ══════════════════════════════════════════════════════════════
// TIMESHEET COMPLIANCE
// ══════════════════════════════════════════════════════════════
const COMPLIANCE_CSS = `
.comp-wrap { display:flex; flex-direction:column; gap:20px; }
.comp-controls { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.comp-week-btn { display:flex; align-items:center; gap:6px; padding:8px 16px; border-radius:8px; border:1.5px solid var(--border); background:#fff; font-family:'DM Sans',sans-serif; font-size:13px; color:var(--slate); cursor:pointer; transition:all .15s; }
.comp-week-btn:hover { border-color:var(--navy); color:var(--navy); }
.comp-week-btn.active { background:var(--navy); color:#fff; border-color:var(--navy); }
.comp-week-label { font-size:14px; font-weight:600; color:var(--navy); min-width:200px; }
.comp-summary { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
.comp-stat { background:#fff; border-radius:12px; padding:18px 20px; border:1px solid var(--border); }
.comp-stat-val { font-family:'Playfair Display',serif; font-size:28px; font-weight:600; }
.comp-stat-lbl { font-size:11px; color:var(--slate); text-transform:uppercase; letter-spacing:1px; margin-top:4px; }
.comp-grid { display:flex; flex-direction:column; gap:12px; }
.comp-card { background:#fff; border-radius:12px; border:1.5px solid var(--border); overflow:hidden; transition:border-color .15s; }
.comp-card.all-ok { border-color:#10b981; }
.comp-card.has-issues { border-color:#ef4444; }
.comp-card.partial { border-color:#f59e0b; }
.comp-card-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; cursor:pointer; }
.comp-card-header:hover { background:var(--cream); }
.comp-user-info { display:flex; align-items:center; gap:12px; }
.comp-avatar { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; flex-shrink:0; }
.comp-avatar.ok { background:#d1fae5; color:#065f46; }
.comp-avatar.warn { background:#fee2e2; color:#991b1b; }
.comp-avatar.partial { background:#fef3c7; color:#92400e; }
.comp-name { font-size:14px; font-weight:600; color:var(--navy); }
.comp-role { font-size:11px; color:var(--slate); text-transform:uppercase; letter-spacing:.5px; margin-top:2px; }
.comp-pills { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
.comp-pill { width:32px; height:32px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:600; flex-direction:column; gap:1px; }
.comp-pill.ok { background:#d1fae5; color:#065f46; }
.comp-pill.low { background:#fee2e2; color:#991b1b; }
.comp-pill.zero { background:#f1f5f9; color:#94a3b8; }
.comp-pill.weekend { background:#f8f6f1; color:#cbd5e1; }
.comp-pill.future { background:transparent; color:#e2e8f0; border:1px dashed #e2e8f0; }
.comp-pill-day { font-size:9px; font-weight:400; opacity:.8; }
.comp-pill-hrs { font-size:12px; font-weight:700; }
.comp-status-badge { display:flex; align-items:center; gap:6px; padding:5px 12px; border-radius:20px; font-size:12px; font-weight:500; }
.comp-status-badge.ok { background:#d1fae5; color:#065f46; }
.comp-status-badge.warn { background:#fee2e2; color:#991b1b; }
.comp-status-badge.partial { background:#fef3c7; color:#92400e; }
.comp-detail { padding:12px 18px 16px; border-top:1px solid var(--border); background:var(--cream); }
.comp-detail-row { display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--border); font-size:13px; }
.comp-detail-row:last-child { border-bottom:none; }
.comp-missing-tag { background:#fee2e2; color:#991b1b; font-size:11px; padding:2px 8px; border-radius:20px; font-weight:500; }
.comp-ok-tag { background:#d1fae5; color:#065f46; font-size:11px; padding:2px 8px; border-radius:20px; font-weight:500; }
.comp-toggle { font-size:11px; color:var(--slate); }
`;

function Compliance({ user, users=[], tss=[], projects=[] }) {
  const [weekOffset, setWeekOffset] = useState(-1); // default = last week
  const [expanded, setExpanded] = useState({});
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const MIN_HOURS = 8;

  // Get week dates for given offset
  const getWeek = (offset) => {
    const d = new Date(); d.setDate(d.getDate() + offset*7);
    const day = d.getDay(); const mon = new Date(d);
    mon.setDate(d.getDate()-(day===0?6:day-1));
    return Array.from({length:7},(_,i)=>{ const x=new Date(mon); x.setDate(mon.getDate()+i); return x.toISOString().slice(0,10); });
  };

  const week = getWeek(weekOffset);
  const today = todayStr();
  const workdays = week.slice(0,5).filter(d=>d<=today); // eslint-disable-line no-unused-vars
  const dayNames = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  // Partners see interns + managers; managers see only interns (their team)
  const isCompPartner = user.role==="partner";
  const allowedRoles = isCompPartner ? ["intern","manager"] : ["intern"];
  const staff = users.filter(u=>u.active&&allowedRoles.includes(u.role))
    .filter(u=>filterRole==="all"||u.role===filterRole);

  // For each staff member compute compliance
  const staffData = staff.map(u=>{
    const days = week.map((d,i)=>{
      const dayTss = tss.filter(t=>t.userId===u.id&&t.date===d&&["approved","pending","resubmitted"].includes(t.status)); // includes internal entries
      const hrs = dayTss.reduce((s,t)=>s+t.hours,0);
      const isPast = d<=today;
      const isWeekend = i>=5;
      const isFuture = d>today;
      const isBeforeTracking = d<"2026-04-01";
      return { date:d, dayName:dayNames[i], hrs, isPast, isWeekend, isFuture, isBeforeTracking,
        status: isFuture||isWeekend||isBeforeTracking?"na": hrs>=MIN_HOURS?"ok": hrs>0?"low":"zero" };
    });
    const workdayResults = days.filter(d=>!d.isWeekend&&d.isPast&&!d.isBeforeTracking);
    const okDays = workdayResults.filter(d=>d.status==="ok").length;
    const lowDays = workdayResults.filter(d=>d.status==="low").length;
    const zeroDays = workdayResults.filter(d=>d.status==="zero").length;
    // eslint-disable-next-line no-unused-vars
    const totalOk = workdayResults.length>0;
    const overallStatus = workdayResults.length===0?"na": zeroDays===0&&lowDays===0?"ok": zeroDays===workdayResults.length?"zero":"partial";
    return { u, days, okDays, lowDays, zeroDays, workdayResults, overallStatus };
  }).filter(d=>{
    if(filterStatus==="ok") return d.overallStatus==="ok";
    if(filterStatus==="issues") return d.overallStatus!=="ok"&&d.overallStatus!=="na";
    return true;
  }).sort((a,b)=>{
    // Sort: most issues first
    const score = x => x.zeroDays*2 + x.lowDays;
    return score(b)-score(a);
  });

  const totalIssues = staffData.filter(d=>d.overallStatus!=="ok"&&d.overallStatus!=="na").length; // eslint-disable-line no-unused-vars
  const totalOk = staffData.filter(d=>d.overallStatus==="ok").length; // eslint-disable-line no-unused-vars
  const totalZero = staffData.filter(d=>d.zeroDays>0).length;
  const totalLow = staffData.filter(d=>d.lowDays>0&&d.zeroDays===0).length;

  const weekLabel = weekOffset===0?"This Week":weekOffset===-1?"Last Week":`${Math.abs(weekOffset)} Weeks Ago`;

  const toggle = id => setExpanded(e=>({...e,[id]:!e[id]}));

  return (
    <div className="comp-wrap">
      <style>{COMPLIANCE_CSS}</style>

      {/* Controls */}
      <div className="comp-controls">
        <div className="fxc g8">
          <button className="comp-week-btn" onClick={()=>setWeekOffset(o=>o-1)}>← Prev</button>
          <div className="comp-week-label">{weekLabel}: {fmtDate(week[0])} — {fmtDate(week[4])}</div>
          {weekOffset<0&&<button className="comp-week-btn" onClick={()=>setWeekOffset(o=>o+1)}>Next →</button>}
        </div>
        <div style={{flex:1}}/>
        <select className="fs" style={{width:"auto",fontSize:13,padding:"7px 12px"}} value={filterRole} onChange={e=>setFilterRole(e.target.value)}>
          <option value="all">All Staff</option>
          {isCompPartner&&<option value="manager">Managers Only</option>}
          <option value="intern">Interns Only</option>
        </select>
        <select className="fs" style={{width:"auto",fontSize:13,padding:"7px 12px"}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="issues">Issues Only</option>
          <option value="ok">Fully Compliant</option>
        </select>
      </div>

      {/* Summary stats */}
      <div className="comp-summary">
        <div className="comp-stat">
          <div className="comp-stat-val" style={{color:"var(--navy)"}}>{staffData.length}</div>
          <div className="comp-stat-lbl">Staff Checked</div>
        </div>
        <div className="comp-stat">
          <div className="comp-stat-val" style={{color:"var(--green)"}}>{totalOk}</div>
          <div className="comp-stat-lbl">Fully Compliant</div>
        </div>
        <div className="comp-stat">
          <div className="comp-stat-val" style={{color:"var(--amber)"}}>{totalLow}</div>
          <div className="comp-stat-lbl">Under 8h Days</div>
        </div>
        <div className="comp-stat">
          <div className="comp-stat-val" style={{color:"var(--red)"}}>{totalZero}</div>
          <div className="comp-stat-lbl">Missing Days</div>
        </div>
      </div>

      {/* Staff cards */}
      {staffData.length===0
        ?<div className="es"><div className="es-icon"><I n="check" s={40}/></div>No staff to display for this period.</div>
        :<div className="comp-grid">
          {staffData.map(({u,days,okDays,lowDays,zeroDays,workdayResults,overallStatus})=>{
            const isExp = expanded[u.id];
            const cardClass = overallStatus==="ok"?"all-ok":overallStatus==="zero"?"has-issues":"partial";
            const avatarClass = overallStatus==="ok"?"ok":overallStatus==="zero"?"warn":"partial";
            const initials = u.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
            const missingDays = workdayResults.filter(d=>d.status!=="ok"); // eslint-disable-line no-unused-vars

            return (
              <div key={u.id} className={`comp-card ${cardClass}`}>
                <div className="comp-card-header" onClick={()=>toggle(u.id)}>
                  <div className="comp-user-info">
                    <div className={`comp-avatar ${avatarClass}`}>{initials}</div>
                    <div>
                      <div className="comp-name">{u.name}</div>
                      <div className="comp-role">{u.role}</div>
                    </div>
                  </div>

                  {/* Day pills */}
                  <div className="comp-pills">
                    {days.map((d,i)=>(
                      <div key={d.date} className={`comp-pill ${d.isWeekend?"weekend":d.isFuture?"future":d.status}`}>
                        <span className="comp-pill-day">{d.dayName}</span>
                        <span className="comp-pill-hrs">{d.isWeekend||d.isFuture||d.isBeforeTracking?"—":d.hrs>0?(Math.round(d.hrs*10)/10)+"h":"✕"}</span>
                      </div>
                    ))}
                  </div>

                  <div className="fxc g8">
                    <div className={`comp-status-badge ${overallStatus==="ok"?"ok":overallStatus==="zero"?"warn":"partial"}`}>
                      {overallStatus==="ok"
                        ?<><I n="check" s={13}/>Compliant</>
                        :overallStatus==="zero"
                        ?<><I n="x" s={13}/>{zeroDays} day{zeroDays>1?"s":""} missing</>
                        :<><I n="alert" s={13}/>{lowDays} under 8h</>}
                    </div>
                    <span className="comp-toggle">{isExp?"▲":"▼"}</span>
                  </div>
                </div>

                {isExp&&(
                  <div className="comp-detail">
                    <div style={{fontSize:12,fontWeight:600,color:"var(--slate)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>
                      Day-by-day breakdown
                    </div>
                    {workdayResults.map(d=>(
                      <div key={d.date} className="comp-detail-row">
                        <div style={{width:100,fontWeight:500}}>{fmtDate(d.date)}</div>
                        <div style={{width:60,color:"var(--slate)",fontSize:12}}>{d.dayName}</div>
                        <div style={{flex:1}}>
                          {d.status==="ok"
                            ?<span style={{color:"var(--green)",fontWeight:600}}>{(Math.round(d.hrs*10)/10)}h logged</span>
                            :d.status==="low"
                            ?<span style={{color:"var(--amber)",fontWeight:600}}>{(Math.round(d.hrs*10)/10)}h logged</span>
                            :<span style={{color:"var(--slate)"}}>No entries</span>}
                        </div>
                        <div>
                          {d.status==="ok"
                            ?<span className="comp-ok-tag">✓ Complete</span>
                            :d.status==="low"
                            ?<span className="comp-missing-tag">⚠ Under {MIN_HOURS}h</span>
                            :<span className="comp-missing-tag">✕ Missing</span>}
                        </div>
                        <div style={{width:80,textAlign:"right",fontSize:12,color:"var(--slate)"}}>
                          {d.status!=="ok"&&d.hrs<MIN_HOURS?`${MIN_HOURS-d.hrs}h short`:""}
                        </div>
                      </div>
                    ))}
                    <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid var(--border)",display:"flex",gap:24,fontSize:12}}>
                      <span><b style={{color:"var(--green)"}}>{okDays}</b> <span style={{color:"var(--slate)"}}>complete days</span></span>
                      <span><b style={{color:"var(--amber)"}}>{lowDays}</b> <span style={{color:"var(--slate)"}}>under 8h</span></span>
                      <span><b style={{color:"var(--red)"}}>{zeroDays}</b> <span style={{color:"var(--slate)"}}>missing</span></span>
                      <span><b style={{color:"var(--navy)"}}>{workdayResults.reduce((s,d)=>s+d.hrs,0).toFixed(1)}h</b> <span style={{color:"var(--slate)"}}>total logged</span></span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// LEAVE MANAGEMENT
// ══════════════════════════════════════════════════════════════
const LEAVE_CSS = `
.lv-wrap{display:flex;flex-direction:column;gap:20px;}
.lv-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
.lv-stat{background:#fff;border-radius:12px;padding:16px 18px;border:1px solid var(--border);}
.lv-stat-val{font-size:26px;font-weight:700;color:var(--navy);}
.lv-stat-lbl{font-size:11px;color:var(--slate);text-transform:uppercase;letter-spacing:1px;margin-top:3px;}
.lv-cal-wrap{background:#fff;border-radius:12px;border:1px solid var(--border);padding:20px;}
.lv-cal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;flex-wrap:wrap;gap:10px;}
.lv-cal-nav{display:flex;align-items:center;gap:10px;}
.lv-cal-nav button{background:var(--cream);border:1px solid var(--border);border-radius:6px;padding:5px 12px;cursor:pointer;font-size:13px;color:var(--navy);}
.lv-cal-title{font-size:15px;font-weight:600;color:var(--navy);}
.lv-cal-legend{display:flex;gap:14px;flex-wrap:wrap;}
.lv-leg{display:flex;align-items:center;gap:5px;font-size:12px;color:var(--slate);}
.lv-leg-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}
.lv-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;}
.lv-day-hdr{text-align:center;font-size:11px;color:var(--slate);padding:6px 0;font-weight:600;text-transform:uppercase;letter-spacing:.5px;}
.lv-day{min-height:56px;border-radius:8px;padding:5px 6px;border:1px solid var(--border);background:#fff;}
.lv-day.today{border-color:var(--navy);background:#f0f4ff;}
.lv-day.has-one{background:#FEF9EC;border-color:#FDE68A;}
.lv-day.has-multi{background:#FFF0F0;border-color:#FECACA;}
.lv-day.other-month{opacity:.3;}
.lv-day.weekend{background:var(--cream);}
.lv-day-num{font-size:12px;font-weight:600;color:var(--navy);}
.lv-day.today .lv-day-num{color:#1d4ed8;}
.lv-day-names{display:flex;flex-wrap:wrap;gap:2px;margin-top:3px;}
.lv-tag{font-size:9px;font-weight:600;padding:1px 5px;border-radius:10px;white-space:nowrap;}
.lv-tag.intern{background:#e0e7ff;color:#3730a3;}
.lv-tag.manager{background:#fef3c7;color:#92400e;}
.lv-tag.partner{background:#d1fae5;color:#065f46;}
.lv-today-card{background:#fff;border-radius:12px;border:1px solid var(--border);padding:16px 20px;}
.lv-req-card{background:#fff;border-radius:12px;border:1.5px solid var(--border);padding:14px 18px;display:flex;align-items:flex-start;gap:14px;}
.lv-req-card.pending-m{border-left:3px solid var(--amber);}
.lv-req-card.pending-p{border-left:3px solid var(--navy);}
.lv-req-card.approved{border-left:3px solid var(--green);}
.lv-req-card.rejected{border-left:3px solid var(--red);}
.lv-av{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;flex-shrink:0;}
.lv-av.intern{background:#e0e7ff;color:#3730a3;}
.lv-av.manager{background:#fef3c7;color:#92400e;}
.lv-av.partner{background:#d1fae5;color:#065f46;}
.lv-info{flex:1;min-width:0;}
.lv-name{font-size:13px;font-weight:600;color:var(--navy);}
.lv-dates{font-size:12px;color:var(--slate);margin-top:2px;}
.lv-flow{display:flex;align-items:center;gap:5px;margin-top:5px;flex-wrap:wrap;}
.lv-fstep{padding:2px 8px;border-radius:20px;font-size:10px;font-weight:500;}
.lv-fstep.done{background:#d1fae5;color:#065f46;}
.lv-fstep.active{background:#dbeafe;color:#1e40af;}
.lv-fstep.waiting{background:var(--cream);color:var(--slate);}
.lv-badge{padding:3px 10px;border-radius:20px;font-size:11px;font-weight:500;white-space:nowrap;}
.lv-badge.pending-m{background:#fef3c7;color:#92400e;}
.lv-badge.pending-p{background:#e0e7ff;color:#3730a3;}
.lv-badge.approved{background:#d1fae5;color:#065f46;}
.lv-badge.rejected{background:#fee2e2;color:#991b1b;}
.lv-form{display:flex;flex-direction:column;gap:14px;}
.lv-notice{border-radius:8px;padding:10px 14px;font-size:12px;}
.lv-notice.el{background:#fef3c7;border:1px solid #fde68a;color:#92400e;}
.lv-notice.sl{background:#fee2e2;border:1px solid #fecaca;color:#991b1b;}
.lv-approver-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
.lv-approver-item{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;border:1.5px solid var(--border);cursor:pointer;transition:all .15s;}
.lv-approver-item.selected{border-color:var(--navy);background:var(--cream);}
.lv-empty{text-align:center;padding:40px 20px;color:var(--slate);font-size:14px;}
.lv-type-sel{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:4px;}
.lv-type-btn{padding:12px 16px;border-radius:10px;border:2px solid var(--border);cursor:pointer;transition:all .15s;text-align:left;}
.lv-type-btn.active-pl{border-color:#d97706;background:#fffbeb;}
.lv-type-btn.active-sl{border-color:#dc2626;background:#fff5f5;}
`;

function Leave({ user, users=[], leaves=[], setLeaves, tss=[] }) {
  // Filters & modify modal
  const [filtStaff,setFiltStaff]=useState("");
  const [filtStatus,setFiltStatus]=useState("");
  const [filtType,setFiltType]=useState("");
  const [filtMonth,setFiltMonth]=useState("");
  const [modifyM,setModifyM]=useState(null);
  const [modStart,setModStart]=useState("");
  const [modEnd,setModEnd]=useState("");
  const [modReason,setModReason]=useState("");
  const [modErr,setModErr]=useState("");

  const [activeTab, setActiveTab] = useState("calendar");
  const [calOffset, setCalOffset] = useState(0);
  const [rejectM, setRejectM] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0,7));
  const [form, setForm] = useState({
    leaveType:"planned", startDate:"", endDate:"", halfDay:false, reason:"",
    approverManagers:[], approverPartners:[]
  });
  const [ferr, setFerr] = useState("");

  const isPartner = user.role==="partner";
  const isManager = user.role==="manager";
  const isIntern  = user.role==="intern";
  const canSeeCalendar = isPartner || isManager;

  // All managers and partners for approver selection
  const allManagers = users.filter(u=>u.role==="manager"&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const allPartners = users.filter(u=>u.role==="partner"&&u.active).slice().sort((a,b)=>a.name.localeCompare(b.name));

  const getDatesInRange = (start, end) => {
    const dates=[]; const cur=new Date(start); const last=new Date(end);
    while(cur<=last){dates.push(cur.toISOString().slice(0,10));cur.setDate(cur.getDate()+1);}
    return dates;
  };
  const countWorkdays = (start, end) => {
    if(!start||!end||end<start) return 0;
    return getDatesInRange(start,end).filter(d=>{const wd=new Date(d).getDay();return wd!==0&&wd!==6;}).length;
  };
  const workdays = form.halfDay ? 0.5 : countWorkdays(form.startDate, form.endDate);

  const toggleApprover = (id, type) => {
    const key = type==="manager"?"approverManagers":"approverPartners";
    setForm(f=>({...f,[key]:f[key].includes(id)?f[key].filter(x=>x!==id):[...f[key],id]}));
  };

  // Compute status label and class
  const statusLabel = s => {
    if(s==="pending_manager") return "Pending Manager";
    if(s==="pending_partner") return "Pending Partner";
    if(s==="approved") return "Approved";
    return "Rejected";
  };
  const statusClass = s => s==="pending_manager"?"pending-m":s==="pending_partner"?"pending-p":s==="approved"?"approved":"rejected";

  // Submit
  const submitRequest = () => {
    setFerr("");
    if(!form.startDate||!form.endDate){setFerr("Please select start and end dates.");return;}
    if(form.endDate<form.startDate){setFerr("End date cannot be before start date.");return;}
    if(workdays===0){setFerr("No working days in selected range.");return;}
    if(isIntern&&form.approverManagers.length===0){setFerr("Please select at least one manager approver.");return;}
    if(!isPartner&&form.approverPartners.length===0){setFerr("Please select at least one partner approver.");return;}

    // Managers skip the manager approval step — go straight to partner
    // Interns start at manager approval
    const initStatus = (isManager||isPartner) ? "pending_partner" : "pending_manager";

    const newLeave = {
      id:genId(), userId:user.id, userName:user.name, userRole:user.role,
      leaveType:form.leaveType,
      startDate:form.startDate, endDate:form.endDate, days:workdays, halfDay:form.halfDay||false,
      reason:form.reason,
      status:isPartner?"approved":initStatus,
      approverManagers:form.approverManagers,
      approverPartners:form.approverPartners,
      managerApprovals:[], // list of manager ids who approved
      partnerApprovals:[], // list of partner ids who approved
      createdAt:new Date().toISOString(),
    };
    setLeaves(prev=>[...prev,newLeave]);
    addAudit(user.id,user.name,"LEAVE_REQUEST",`Requested ${workdays}d ${form.leaveType} leave: ${form.startDate} to ${form.endDate}`);
    setForm({leaveType:"planned",startDate:"",endDate:"",halfDay:false,reason:"",approverManagers:[],approverPartners:[]});
    setFerr("");
    setActiveTab("history");
  };

  // Approve — handles sequential multi-approver flow
  const approve = (leave) => {
    let managerApprovals = [...(leave.managerApprovals||[])];
    let partnerApprovals = [...(leave.partnerApprovals||[])];
    let newStatus = leave.status;

    if(leave.status==="pending_manager"&&isManager){
      managerApprovals = [...new Set([...managerApprovals, user.id])];
      // Check if ALL required managers have approved
      const allMgrsDone = (leave.approverManagers||[]).every(id=>managerApprovals.includes(id));
      if(allMgrsDone) newStatus = "pending_partner";
    } else if(leave.status==="pending_partner"&&isPartner){
      partnerApprovals = [...new Set([...partnerApprovals, user.id])];
      // Check if ALL required partners have approved
      const allPtnrsDone = (leave.approverPartners||[]).every(id=>partnerApprovals.includes(id));
      if(allPtnrsDone) newStatus = "approved";
    }

    setLeaves(prev=>prev.map(l=>l.id===leave.id?{...l,status:newStatus,managerApprovals,partnerApprovals}:l));
    addAudit(user.id,user.name,"LEAVE_APPROVE",`Approved leave for ${leave.userName} (${newStatus})`);
  };

  const confirmReject = () => {
    if(!rejectReason.trim()) return;
    setLeaves(prev=>prev.map(l=>l.id===rejectM.id?{...l,status:"rejected",rejectedBy:user.id,rejectReason}:l));
    addAudit(user.id,user.name,"LEAVE_REJECT",`Rejected leave for ${rejectM.userName}: ${rejectReason}`);
    setRejectM(null); setRejectReason("");
  };

  // Cancel leave (own, not yet fully approved)
  const cancelLeave = (l) => {
    if(!window.confirm(`Cancel your leave request from ${fmtDate(l.startDate)} to ${fmtDate(l.endDate)}?`)) return;
    setLeaves(prev=>prev.filter(x=>x.id!==l.id));
    addAudit(user.id,user.name,"LEAVE_CANCEL",`Cancelled own leave ${fmtDate(l.startDate)} to ${fmtDate(l.endDate)}`);
  };

  // Partner action: delete any leave (mistakes/corrections)
  const deleteLeave = (l) => {
    const msg = l.status==="approved"
      ? `Delete APPROVED leave for ${l.userName} (${fmtDate(l.startDate)} to ${fmtDate(l.endDate)})?\n\nThis cannot be undone. Ask the staff to re-submit the correct leave.`
      : `Delete leave for ${l.userName} (${fmtDate(l.startDate)} to ${fmtDate(l.endDate)})?`;
    if(!window.confirm(msg)) return;
    setLeaves(prev=>prev.filter(x=>x.id!==l.id));
    addAudit(user.id,user.name,"LEAVE_DELETE",`Deleted ${l.status} leave of ${l.userName}: ${fmtDate(l.startDate)} to ${fmtDate(l.endDate)}`);
  };

  // Partner action: modify dates of any leave
  const openModify = (l) => {
    setModifyM(l);
    setModStart(l.startDate);
    setModEnd(l.endDate);
    setModReason("");
    setModErr("");
  };
  const saveModify = () => {
    if(!modStart||!modEnd){setModErr("Please select valid start and end dates.");return;}
    if(modEnd<modStart){setModErr("End date cannot be before start date.");return;}
    if(!modReason.trim()){setModErr("Please provide a reason for the modification.");return;}
    const days = Math.round((new Date(modEnd)-new Date(modStart))/86400000)+1;
    const actualDays = modifyM.halfDay ? days*0.5 : days;
    setLeaves(prev=>prev.map(l=>l.id===modifyM.id?{
      ...l,
      startDate: modStart,
      endDate: modEnd,
      days: actualDays,
      modifiedBy: user.id,
      modifiedByName: user.name,
      modifiedAt: new Date().toISOString(),
      modifyReason: modReason,
      modifyHistory: [
        ...(l.modifyHistory||[]),
        {at: new Date().toISOString(), by: user.name, from: `${l.startDate} to ${l.endDate}`, to: `${modStart} to ${modEnd}`, reason: modReason}
      ]
    }:l));
    addAudit(user.id,user.name,"LEAVE_MODIFY",
      `Modified ${modifyM.userName}'s leave: ${fmtDate(modifyM.startDate)}-${fmtDate(modifyM.endDate)} → ${fmtDate(modStart)}-${fmtDate(modEnd)}. Reason: ${modReason}`);
    setModifyM(null);
  };

  // Pending leaves for current user to action
  const pendingForMe = leaves.filter(l=>{
    if(l.status==="pending_manager"&&isManager) return (l.approverManagers||[]).includes(user.id)&&!(l.managerApprovals||[]).includes(user.id);
    if(l.status==="pending_partner"&&isPartner) return (l.approverPartners||[]).includes(user.id)&&!(l.partnerApprovals||[]).includes(user.id);
    return false;
  });

  const myRequests = leaves.filter(l=>l.userId===user.id).slice().reverse();
  const allRequests = (isPartner||isManager)?leaves.slice().reverse():myRequests;

  // ── Calendar ──
  const getCalMonth = (offset) => {
    const d=new Date(); d.setDate(1); d.setMonth(d.getMonth()+offset); return d;
  };
  const calDate=getCalMonth(calOffset);
  const calYear=calDate.getFullYear(), calMonth=calDate.getMonth();
  const calMonthLabel=calDate.toLocaleString("default",{month:"long",year:"numeric"});
  const firstDay=new Date(calYear,calMonth,1).getDay();
  const daysInMonth=new Date(calYear,calMonth+1,0).getDate();
  const startPad=firstDay===0?6:firstDay-1;
  const cells=[];
  const prevDays=new Date(calYear,calMonth,0).getDate();
  for(let i=startPad-1;i>=0;i--) cells.push({day:prevDays-i,thisMonth:false,date:null});
  for(let d=1;d<=daysInMonth;d++){
    const date=`${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    cells.push({day:d,thisMonth:true,date});
  }
  while(cells.length%7!==0) cells.push({day:cells.length-startPad-daysInMonth+1,thisMonth:false,date:null});

  // Calendar shows all non-rejected leave requests (pending + approved) so verbal/off-app
  // approvals still surface on the calendar instead of waiting on every digital sign-off.
  const calendarLeaves=leaves.filter(l=>l.status!=="rejected");
  const getLeavesOnDate=(date)=>{
    if(!date) return [];
    return calendarLeaves.filter(l=>date>=l.startDate&&date<=l.endDate)
      .map(l=>({...l,role:users.find(u=>u.id===l.userId)?.role||"intern"}));
  };
  const today2=todayStr();
  const onLeaveToday=calendarLeaves.filter(l=>today2>=l.startDate&&today2<=l.endDate);
  // Pending (not yet approved) leave requests that fall anywhere within the displayed month —
  // shown as a separate list so it's easy to see who's still awaiting sign-off, not just today.
  const calMoStart=`${calYear}-${String(calMonth+1).padStart(2,"0")}-01`;
  const calMoEnd=`${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(daysInMonth).padStart(2,"0")}`;
  const monthPendingLeaves=leaves
    .filter(l=>l.status!=="approved"&&l.status!=="rejected"&&l.startDate<=calMoEnd&&l.endDate>=calMoStart)
    .sort((a,b)=>a.startDate<b.startDate?-1:a.startDate>b.startDate?1:0);
  const initials=(name)=>name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()||"??";

  // ── Excel Attendance Matrix Download ──
  const downloadReport = () => {
    const [yr,mo] = reportMonth.split("-").map(Number);
    const daysInMo = new Date(yr,mo,0).getDate();
    const moStr = reportMonth;

    // Staff = interns and managers only
    // Sort by category (manager before intern), then by date of joining ascending
    const roleOrder = {manager:0,intern:1};
    const staff = users.filter(u=>u.active&&["intern","manager"].includes(u.role))
      .sort((a,b)=>{
        const ro = (roleOrder[a.role]??99)-(roleOrder[b.role]??99);
        if(ro!==0) return ro;
        const da = a.dateOfJoining||"9999-99-99";
        const db = b.dateOfJoining||"9999-99-99";
        if(da!==db) return da<db?-1:1;
        return a.name.localeCompare(b.name);
      });

    // Get approved leaves for the month
    const moStart = `${moStr}-01`;
    const moEnd = new Date(yr,mo,0).toISOString().slice(0,10);
    const approvedLeavesInMo = leaves.filter(l=>l.status==="approved"&&l.startDate<=moEnd&&l.endDate>=moStart);

    // Helper: get attendance mark for a staff member on a date
    const getMark = (userId, dayNum) => {
      const dateStr = `${yr}-${String(mo).padStart(2,"0")}-${String(dayNum).padStart(2,"0")}`;
      const dow = new Date(dateStr).getDay(); // 0=Sun, 6=Sat
      if(dow===0||dow===6) return "WO";

      // Check approved leaves for this person on this date
      const leave = approvedLeavesInMo.find(l=>l.userId===userId&&dateStr>=l.startDate&&dateStr<=l.endDate);
      if(leave){
        if(leave.halfDay) return leave.leaveType==="planned"?"HPL":"HSL";
        return leave.leaveType==="planned"?"PL":"SL";
      }

      // Check if date is in the future — leave blank
      const todayDate = new Date(); todayDate.setHours(0,0,0,0);
      const checkDate = new Date(dateStr);
      if(checkDate>todayDate) return "";

      // Check timesheets — any approved/pending/resubmitted entry = Present
      const hasEntry = tss.some(t=>t.userId===userId&&t.date===dateStr&&["approved","pending","resubmitted"].includes(t.status));
      return hasEntry ? "P" : "A";
    };

    // Build header rows
    const monthLabel = new Date(yr,mo-1,1).toLocaleString("default",{month:"long",year:"numeric"});
    // eslint-disable-next-line no-unused-vars
    const dayHeaders = Array.from({length:daysInMo},(_,i)=>`${i+1}`);
    const ordinals = Array.from({length:daysInMo},(_,i)=>{
      const n=i+1; const s=["th","st","nd","rd"]; const v=n%100;
      return n+(s[(v-20)%10]||s[v]||s[0]);
    });

    // Build rows
    const headerRow1 = ["Staff Name","Category","Date of Joining",...ordinals];
    const dataRows = staff.map(u=>{
      const marks = Array.from({length:daysInMo},(_,i)=>getMark(u.id,i+1));
      return [u.name, u.role.charAt(0).toUpperCase()+u.role.slice(1), u.dateOfJoining?fmtDate(u.dateOfJoining):"—", ...marks];
    });

    // Summary row — count PL, SL, HPL, HSL per person
    const summaryRows = staff.map(u=>{
      const marks = Array.from({length:daysInMo},(_,i)=>getMark(u.id,i+1));
      const pl=marks.filter(m=>m==="PL").length;
      const sl=marks.filter(m=>m==="SL").length;
      const hpl=marks.filter(m=>m==="HPL").length;
      const hsl=marks.filter(m=>m==="HSL").length;
      const absent=marks.filter(m=>m==="A").length;
      return [u.name, u.role.charAt(0).toUpperCase()+u.role.slice(1), u.dateOfJoining?fmtDate(u.dateOfJoining):"—", pl, sl, hpl, hsl, absent];
    });

    // Legend
    const legend = [
      ["Legend:","","P = Present","PL = Planned Leave","SL = Sick Leave","HPL = Half-day Planned","HSL = Half-day Sick","WO = Week Off","A = Absent"],
    ];

    // Build CSV
    const allRows = [
      [`MSNA & Associates LLP — Attendance Report — ${monthLabel}`],
      [],
      headerRow1,
      ...dataRows,
      [],
      ["SUMMARY"],
      ["Staff Name","Category","Date of Joining","PL Days","SL Days","Half-day PL","Half-day SL","Absent"],
      ...summaryRows,
      [],
      ...legend,
    ];

    const csv = allRows.map(r=>r.map(c=>`"${String(c===undefined?"":c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download=`MSNA_Attendance_${reportMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="lv-wrap">
      <style>{LEAVE_CSS}</style>

      {/* Stats */}
      <div className="lv-stats">
        <div className="lv-stat"><div className="lv-stat-val" style={{color:"var(--amber)"}}>{pendingForMe.length}</div><div className="lv-stat-lbl">Pending My Action</div></div>
        <div className="lv-stat"><div className="lv-stat-val" style={{color:"var(--green)"}}>{leaves.filter(l=>l.status==="approved").length}</div><div className="lv-stat-lbl">Approved</div></div>
        <div className="lv-stat"><div className="lv-stat-val" style={{color:"var(--navy)"}}>{onLeaveToday.length}</div><div className="lv-stat-lbl">On Leave Today</div></div>
        <div className="lv-stat"><div className="lv-stat-val" style={{color:"var(--red)"}}>{myRequests.filter(l=>l.status==="rejected").length}</div><div className="lv-stat-lbl">My Rejected</div></div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {canSeeCalendar&&<div className={`tab ${activeTab==="calendar"?"active":""}`} onClick={()=>setActiveTab("calendar")}>Leave Calendar</div>}
        {(isManager||isPartner)&&<div className={`tab ${activeTab==="approvals"?"active":""}`} onClick={()=>setActiveTab("approvals")}>
          Pending Approvals{pendingForMe.length>0&&<span style={{background:"var(--amber)",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,marginLeft:5}}>{pendingForMe.length}</span>}
        </div>}
        <div className={`tab ${activeTab==="request"?"active":""}`} onClick={()=>setActiveTab("request")}>Request Leave</div>
        <div className={`tab ${activeTab==="history"?"active":""}`} onClick={()=>setActiveTab("history")}>{isPartner||isManager?"All Requests":"My Requests"}</div>
        {isPartner&&<div className={`tab ${activeTab==="report"?"active":""}`} onClick={()=>setActiveTab("report")}>Download Report</div>}
      </div>

      {/* ── CALENDAR ── */}
      {activeTab==="calendar"&&canSeeCalendar&&(<>
        <div className="lv-cal-wrap">
          <div className="lv-cal-head">
            <div className="lv-cal-nav">
              <button onClick={()=>setCalOffset(o=>o-1)}>← Prev</button>
              <span className="lv-cal-title">{calMonthLabel}</span>
              <button onClick={()=>setCalOffset(o=>o+1)}>Next →</button>
            </div>
            <div className="lv-cal-legend">
              <div className="lv-leg"><div className="lv-leg-dot" style={{background:"#6366f1"}}></div>Intern</div>
              <div className="lv-leg"><div className="lv-leg-dot" style={{background:"#f59e0b"}}></div>Manager</div>
              <div className="lv-leg"><div className="lv-leg-dot" style={{background:"#10b981"}}></div>Partner</div>
              <div className="lv-leg"><div style={{width:12,height:12,background:"#FFF0F0",border:"1px solid #FECACA",borderRadius:3}}></div>Multiple on leave</div>
            </div>
          </div>
          <div className="lv-cal-grid">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=><div key={d} className="lv-day-hdr">{d}</div>)}
            {cells.map((cell,i)=>{
              const isWeekend=(i%7)>=5;
              const onLeave=cell.date?getLeavesOnDate(cell.date):[];
              const isToday=cell.date===today2;
              let cls="lv-day";
              if(!cell.thisMonth) cls+=" other-month";
              else if(isToday) cls+=" today";
              else if(onLeave.length>1) cls+=" has-multi";
              else if(onLeave.length===1) cls+=" has-one";
              else if(isWeekend) cls+=" weekend";
              return (
                <div key={i} className={cls}>
                  <div className="lv-day-num">{cell.day}</div>
                  <div className="lv-day-names">
                    {onLeave.map(l=><div key={l.id} className={`lv-tag ${l.role}`} title={`${l.userName} — ${l.leaveType==="planned"?"Planned":"Sick"} Leave${l.status!=="approved"?" (Pending approval)":""}`}>{initials(l.userName)}</div>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="lv-today-card">
          <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>On leave today — {fmtDate(today2)}</div>
          {onLeaveToday.length===0
            ?<div style={{color:"var(--slate)",fontSize:13}}>No staff on leave today.</div>
            :<div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {onLeaveToday.map(l=>{
                const u2=users.find(u=>u.id===l.userId);
                const isPending=l.status!=="approved";
                return <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"var(--cream)",borderRadius:8,border:isPending?"1px dashed var(--amber)":"1px solid var(--border)"}}>
                  <div className={`lv-av ${u2?.role||"intern"}`}>{initials(l.userName)}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                      {l.userName}
                      {isPending&&<span style={{fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:20,background:"#fef3c7",color:"#92400e"}}>Pending approval</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--slate)"}}>{u2?.role} · {l.leaveType==="planned"?"Planned":"Sick"} Leave</div>
                  </div>
                </div>;
              })}
            </div>}
        </div>
        <div className="lv-today-card">
          <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>Pending approval — {calMonthLabel}</div>
          {monthPendingLeaves.length===0
            ?<div style={{color:"var(--slate)",fontSize:13}}>No pending leave requests this month.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:8}}>
              {monthPendingLeaves.map(l=>{
                const u2=users.find(u=>u.id===l.userId);
                const stageLabel=l.status==="pending_manager"?"Awaiting manager":"Awaiting partner";
                return <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",background:"var(--cream)",borderRadius:8,border:"1px dashed var(--amber)"}}>
                  <div className={`lv-av ${u2?.role||"intern"}`}>{initials(l.userName)}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                      {l.userName}
                      <span style={{fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:20,background:"#fef3c7",color:"#92400e"}}>{stageLabel}</span>
                    </div>
                    <div style={{fontSize:11,color:"var(--slate)"}}>{u2?.role} · {l.leaveType==="planned"?"Planned":"Sick"} Leave · {fmtDate(l.startDate)}{l.startDate!==l.endDate?` — ${fmtDate(l.endDate)}`:""}</div>
                  </div>
                </div>;
              })}
            </div>}
        </div>
      </>)}

      {/* ── APPROVALS ── */}
      {activeTab==="approvals"&&(isManager||isPartner)&&(
        <div className="card">
          <div style={{fontWeight:600,fontSize:13,marginBottom:14}}>Awaiting your approval</div>
          {pendingForMe.length===0
            ?<div className="lv-empty">All clear — no pending leave requests.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {pendingForMe.map(l=>{
                const req=users.find(u=>u.id===l.userId);
                const mgrsDone=(l.managerApprovals||[]).length;
                const mgrsNeeded=(l.approverManagers||[]).length;
                const ptnrsDone=(l.partnerApprovals||[]).length;
                const ptnrsNeeded=(l.approverPartners||[]).length;
                return (
                  <div key={l.id} className={`lv-req-card ${statusClass(l.status)}`}>
                    <div className={`lv-av ${req?.role||"intern"}`}>{initials(l.userName)}</div>
                    <div className="lv-info">
                      <div className="lv-name">{l.userName} <span style={{fontSize:11,color:"var(--slate)",fontWeight:400}}>{req?.role}</span>
                        <span style={{marginLeft:6,fontSize:11,padding:"2px 7px",borderRadius:20,background:l.leaveType==="sick"?"#fee2e2":"#fef3c7",color:l.leaveType==="sick"?"#991b1b":"#92400e",fontWeight:500}}>{l.leaveType==="planned"?"Planned Leave":"Sick Leave"}</span>
                      </div>
                      <div className="lv-dates">{fmtDate(l.startDate)} — {fmtDate(l.endDate)} · {l.halfDay?"Half day":l.days+" working day"+(l.days!==1?"s":"")}</div>
                      {l.reason&&<div style={{fontSize:12,color:"var(--slate)",marginTop:2}}>"{l.reason}"</div>}
                      <div className="lv-flow" style={{marginTop:6}}>
                        <span className={`lv-fstep ${l.status==="pending_manager"?"active":l.status==="pending_partner"||l.status==="approved"?"done":"waiting"}`}>
                          Managers {mgrsDone}/{mgrsNeeded} ✓
                        </span>
                        <span style={{color:"var(--slate)",fontSize:11}}>→</span>
                        <span className={`lv-fstep ${l.status==="pending_partner"?"active":l.status==="approved"?"done":"waiting"}`}>
                          Partners {ptnrsDone}/{ptnrsNeeded} ✓
                        </span>
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                      <span className={`lv-badge ${statusClass(l.status)}`}>{statusLabel(l.status)}</span>
                      <div style={{display:"flex",gap:6}}>
                        <button className="btn bsc bsm" onClick={()=>approve(l)}><I n="check" s={13}/>Approve</button>
                        <button className="btn bd bsm" onClick={()=>{setRejectM(l);setRejectReason("");}}><I n="x" s={13}/>Reject</button>
                        {isPartner&&<button className="btn bgh bic bsm" title="Modify dates before approving" onClick={()=>openModify(l)}><I n="edit" s={13}/></button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>}
        </div>
      )}

      {/* ── REQUEST FORM ── */}
      {activeTab==="request"&&(
        <div className="card" style={{maxWidth:580}}>
          <div style={{fontWeight:600,fontSize:15,marginBottom:16}}>Request Leave</div>
          <div className="lv-form">
            {ferr&&<div className="err">{ferr}</div>}

            {/* Leave type selection */}
            <div>
              <label className="fl">Leave Type</label>
              <div className="lv-type-sel">
                <div className={`lv-type-btn ${form.leaveType==="planned"?"active-pl":""}`} onClick={()=>setForm(f=>({...f,leaveType:"planned"}))}>
                  <div style={{fontWeight:600,fontSize:13,color:"#d97706"}}>Planned Leave</div>
                  <div style={{fontSize:11,color:"var(--slate)",marginTop:2}}>Planned leave · future dates</div>
                </div>
                <div className={`lv-type-btn ${form.leaveType==="sick"?"active-sl":""}`} onClick={()=>setForm(f=>({...f,leaveType:"sick"}))}>
                  <div style={{fontWeight:600,fontSize:13,color:"#dc2626"}}>Sick Leave</div>
                  <div style={{fontSize:11,color:"var(--slate)",marginTop:2}}>Unplanned · backdating allowed</div>
                </div>
              </div>
            </div>

            {/* Date range */}
            <div className={form.halfDay?"fg":"g2"}>
              <div className="fg"><label className="fl">{form.halfDay?"Date":"Start Date"}</label>
                <input type="date" className="fi" value={form.startDate}
                  max={form.leaveType==="planned"?undefined:todayStr()}
                  onChange={e=>setForm(f=>({...f,startDate:e.target.value,...(f.halfDay?{endDate:e.target.value}:{})}))}/>
              </div>
              {!form.halfDay&&<div className="fg"><label className="fl">End Date</label>
                <input type="date" className="fi" value={form.endDate}
                  min={form.startDate||undefined} max={form.leaveType==="planned"?undefined:todayStr()}
                  onChange={e=>setForm(f=>({...f,endDate:e.target.value}))}/>
              </div>}
            </div>

            {/* Half-day toggle */}
            <div className="fg">
              <label className="fl">Duration</label>
              <div style={{display:"flex",gap:10}}>
                <label style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",borderRadius:8,border:"1.5px solid",borderColor:!form.halfDay?"var(--navy)":"var(--border)",background:!form.halfDay?"var(--cream)":"#fff",cursor:"pointer",flex:1}}>
                  <input type="radio" checked={!form.halfDay} onChange={()=>setForm(f=>({...f,halfDay:false}))} style={{accentColor:"var(--navy)"}}/>
                  <div><div style={{fontSize:13,fontWeight:!form.halfDay?600:400,color:"var(--navy)"}}>Full Day(s)</div><div style={{fontSize:11,color:"var(--slate)"}}>One or more complete days</div></div>
                </label>
                <label style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",borderRadius:8,border:"1.5px solid",borderColor:form.halfDay?"var(--navy)":"var(--border)",background:form.halfDay?"var(--cream)":"#fff",cursor:"pointer",flex:1}}>
                  <input type="radio" checked={form.halfDay} onChange={()=>setForm(f=>({...f,halfDay:true,endDate:f.startDate}))} style={{accentColor:"var(--navy)"}}/>
                  <div><div style={{fontSize:13,fontWeight:form.halfDay?600:400,color:"var(--navy)"}}>Half Day</div><div style={{fontSize:11,color:"var(--slate)"}}>Morning or afternoon only</div></div>
                </label>
              </div>
            </div>

            {/* Working days notice */}
            {form.startDate&&form.endDate&&workdays>0&&(
              <div className={`lv-notice ${form.leaveType==="sick"?"sl":"el"}`}>
                <strong>{form.halfDay?"Half day":workdays+" working day"+(workdays!==1?"s":"")}</strong> selected.
                {form.leaveType==="sick"&&" Sick leave — backdating permitted. Requires all selected approvers."}
                {form.leaveType==="planned"&&isIntern&&(form.halfDay||workdays===1)&&" Requires manager approval only."}
                {form.leaveType==="planned"&&isIntern&&!form.halfDay&&workdays>1&&" 2+ days — manager then partner approval required."}
                {isManager&&" Requires all selected partner approvers."}
              </div>
            )}

            {/* Approver selection — managers */}
            {!isPartner&&(
              <div className="fg">
                <label className="fl">Select Manager Approver(s)</label>
                <div className="lv-approver-grid">
                  {allManagers.map(m=>(
                    <div key={m.id} className={`lv-approver-item ${form.approverManagers.includes(m.id)?"selected":""}`}
                      onClick={()=>toggleApprover(m.id,"manager")}>
                      <input type="checkbox" checked={form.approverManagers.includes(m.id)} readOnly style={{accentColor:"var(--navy)"}}/>
                      <div><div style={{fontSize:13,fontWeight:500}}>{m.name}</div><div style={{fontSize:11,color:"var(--slate)"}}>Manager</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approver selection — partners */}
            {!isPartner&&(
              <div className="fg">
                <label className="fl">Select Partner Approver(s)</label>
                <div className="lv-approver-grid">
                  {allPartners.map(p=>(
                    <div key={p.id} className={`lv-approver-item ${form.approverPartners.includes(p.id)?"selected":""}`}
                      onClick={()=>toggleApprover(p.id,"partner")}>
                      <input type="checkbox" checked={form.approverPartners.includes(p.id)} readOnly style={{accentColor:"var(--navy)"}}/>
                      <div><div style={{fontSize:13,fontWeight:500}}>{p.name}</div><div style={{fontSize:11,color:"var(--slate)"}}>Partner</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="fg"><label className="fl">Reason</label>
              <textarea className="fta" placeholder={form.leaveType==="sick"?"Brief description of illness...":"Reason for leave..."} value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setForm({leaveType:"planned",startDate:"",endDate:"",halfDay:false,reason:"",approverManagers:[],approverPartners:[]})}>Clear</button>
              <button className="btn bp" onClick={submitRequest}><I n="send" s={14}/>Submit Request</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY ── */}
      {activeTab==="history"&&(
        <div className="card">
          <div style={{fontWeight:600,fontSize:13,marginBottom:14}}>{isPartner||isManager?"All Leave Requests":"My Leave Requests"}</div>
          {/* Filters */}
          {(isPartner||isManager)&&allRequests.length>0&&(
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
              <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filtStaff} onChange={e=>setFiltStaff(e.target.value)}>
                <option value="">All Staff</option>
                {[...new Set(allRequests.map(l=>l.userId))]
                  .map(uid=>users.find(u=>u.id===uid)).filter(Boolean)
                  .sort((a,b)=>a.name.localeCompare(b.name))
                  .map(u2=><option key={u2.id} value={u2.id}>{u2.name}</option>)}
              </select>
              <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filtStatus} onChange={e=>setFiltStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="pending_manager">Pending Manager</option>
                <option value="pending_partner">Pending Partner</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select className="fs" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filtType} onChange={e=>setFiltType(e.target.value)}>
                <option value="">All Types</option>
                <option value="planned">Planned Leave</option>
                <option value="sick">Sick Leave</option>
              </select>
              <input type="month" className="fi" style={{fontSize:12,padding:"7px 10px",width:"auto"}} value={filtMonth} onChange={e=>setFiltMonth(e.target.value)}/>
              {(filtStaff||filtStatus||filtType||filtMonth)&&
                <button className="btn bgh bsm" onClick={()=>{setFiltStaff("");setFiltStatus("");setFiltType("");setFiltMonth("");}}>✕ Clear</button>}
              <span className="tx tsl" style={{fontSize:12,marginLeft:"auto"}}>
                {allRequests.filter(l=>{
                  if(filtStaff&&l.userId!==filtStaff) return false;
                  if(filtStatus&&l.status!==filtStatus) return false;
                  if(filtType&&l.leaveType!==filtType) return false;
                  if(filtMonth){const mEnd=new Date(filtMonth.slice(0,4),filtMonth.slice(5,7),0).toISOString().slice(0,10);if(l.startDate>mEnd||l.endDate<`${filtMonth}-01`) return false;}
                  return true;
                }).length} request(s)
              </span>
            </div>
          )}
          {allRequests.length===0
            ?<div className="lv-empty">No leave requests yet.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {allRequests.filter(l=>{
                if(filtStaff&&l.userId!==filtStaff) return false;
                if(filtStatus&&l.status!==filtStatus) return false;
                if(filtType&&l.leaveType!==filtType) return false;
                if(filtMonth){const mEnd=new Date(filtMonth.slice(0,4),filtMonth.slice(5,7),0).toISOString().slice(0,10);if(l.startDate>mEnd||l.endDate<`${filtMonth}-01`) return false;}
                return true;
              }).map(l=>{
                const req=users.find(u=>u.id===l.userId);
                const mgrsDone=(l.managerApprovals||[]).length;
                const mgrsNeeded=(l.approverManagers||[]).length;
                const ptnrsDone=(l.partnerApprovals||[]).length;
                const ptnrsNeeded=(l.approverPartners||[]).length;
                return (
                  <div key={l.id} className={`lv-req-card ${statusClass(l.status)}`}>
                    <div className={`lv-av ${req?.role||"intern"}`}>{initials(l.userName)}</div>
                    <div className="lv-info">
                      <div className="lv-name">{l.userName}
                        <span style={{fontSize:11,color:"var(--slate)",fontWeight:400,marginLeft:5}}>{req?.role}</span>
                        <span style={{marginLeft:6,fontSize:11,padding:"2px 7px",borderRadius:20,background:l.leaveType==="sick"?"#fee2e2":"#fef3c7",color:l.leaveType==="sick"?"#991b1b":"#92400e",fontWeight:500}}>{l.leaveType==="planned"?"Planned":"Sick"} Leave</span>
                      </div>
                      <div className="lv-dates">{fmtDate(l.startDate)} — {fmtDate(l.endDate)} · {l.days} day{l.days!==1?"s":""}</div>
                      {(mgrsNeeded>0||ptnrsNeeded>0)&&<div style={{fontSize:11,color:"var(--slate)",marginTop:3}}>
                        Mgrs: {mgrsDone}/{mgrsNeeded} · Partners: {ptnrsDone}/{ptnrsNeeded}
                      </div>}
                      {l.status==="rejected"&&l.rejectReason&&<div style={{fontSize:11,color:"var(--red)",marginTop:3}}>Rejected: {l.rejectReason}</div>}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                      <span className={`lv-badge ${statusClass(l.status)}`}>{statusLabel(l.status)}</span>
                      <div style={{display:"flex",gap:6}}>
                        {l.userId===user.id&&["pending_manager","pending_partner"].includes(l.status)&&
                          <button className="btn bd bsm" title="Cancel this request" onClick={()=>cancelLeave(l)}><I n="x" s={12}/>Cancel</button>}
                        {isPartner&&l.userId!==user.id&&<>
                          <button className="btn bgh bic bsm" title="Modify leave dates" onClick={()=>openModify(l)}><I n="edit" s={13}/></button>
                          <button className="btn bd bic bsm" title="Delete leave" onClick={()=>deleteLeave(l)}><I n="trash" s={13}/></button>
                        </>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>}
        </div>
      )}

      {/* ── REPORT (Partners only) ── */}
      {activeTab==="report"&&isPartner&&(
        <div className="card" style={{maxWidth:440}}>
          <div style={{fontWeight:600,fontSize:15,marginBottom:4}}>Monthly Leave Report</div>
          <div style={{fontSize:13,color:"var(--slate)",marginBottom:20}}>Downloads a monthly attendance matrix for all interns and managers. Days marked as P/PL/SL/HPL/HSL/WO/A.</div>
          <div className="fg" style={{marginBottom:16}}>
            <label className="fl">Select Month</label>
            <input type="month" className="fi" value={reportMonth} onChange={e=>setReportMonth(e.target.value)}/>
          </div>
          <div style={{background:"var(--cream)",borderRadius:8,padding:"12px 14px",fontSize:12,color:"var(--slate)",marginBottom:16}}>
            {leaves.filter(l=>{
              const [yr,mo]=reportMonth.split("-").map(Number);
              const moEnd=new Date(yr,mo,0).toISOString().slice(0,10);
              return l.startDate<=moEnd&&l.endDate>=`${reportMonth}-01`;
            }).length} leave request(s) found for {new Date(reportMonth+"-01").toLocaleString("default",{month:"long",year:"numeric"})}
          </div>
          <button className="btn bp" onClick={downloadReport}><I n="chart" s={15}/>Download Attendance Report (.csv)</button>
        </div>
      )}

      {/* ── MODIFY LEAVE MODAL ── */}
      {modifyM&&(
        <div className="mo" onClick={()=>setModifyM(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:500}}>
            <div className="md-title">Modify Leave Dates</div>
            <div className="al al-i mb16"><I n="info" s={14}/>
              <div>Modifying leave for <strong>{modifyM.userName}</strong>
                {modifyM.status==="approved"&&<div style={{marginTop:4,fontSize:12,color:"#92400e"}}>This leave is already approved — changes will update immediately.</div>}
              </div>
            </div>
            {modErr&&<div className="err">{modErr}</div>}
            <div style={{background:"var(--cream)",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12}}>
              <div style={{color:"var(--slate)",marginBottom:4}}>Current Dates</div>
              <div style={{fontWeight:600}}>{fmtDate(modifyM.startDate)} — {fmtDate(modifyM.endDate)} · {modifyM.days} day{modifyM.days!==1?"s":""}</div>
            </div>
            <div className="g2">
              <div className="fg"><label className="fl">New Start Date</label>
                <input type="date" className="fi" value={modStart} onChange={e=>setModStart(e.target.value)}/>
              </div>
              <div className="fg"><label className="fl">New End Date</label>
                <input type="date" className="fi" value={modEnd} onChange={e=>setModEnd(e.target.value)}/>
              </div>
            </div>
            <div className="fg"><label className="fl">Reason for Modification</label>
              <textarea className="fta" placeholder="e.g. Wrong date selected by staff, date extension requested etc." value={modReason} onChange={e=>setModReason(e.target.value)}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setModifyM(null)}>Cancel</button>
              <button className="btn bp" onClick={saveModify}><I n="check" s={15}/>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT MODAL ── */}
      {rejectM&&(
        <div className="mo" onClick={()=>setRejectM(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <div className="md-title">Reject Leave Request</div>
            <div className="al al-w mb16"><I n="alert" s={14}/><div>Rejecting leave for <strong>{rejectM.userName}</strong> — {fmtDate(rejectM.startDate)} to {fmtDate(rejectM.endDate)}</div></div>
            <div className="fg"><label className="fl">Reason for Rejection</label>
              <textarea className="fta" placeholder="Please give a reason..." value={rejectReason} onChange={e=>setRejectReason(e.target.value)}/>
            </div>
            <div className="md-actions">
              <button className="btn bgh" onClick={()=>setRejectM(null)}>Cancel</button>
              <button className="btn bd" onClick={confirmReject}><I n="x" s={14}/>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CHANGE PASSWORD
// ══════════════════════════════════════════════════════════════
function ChangePassword({ user, setTab }) {
  const [curPw,  setCurPw]  = useState("");
  const [newPw,  setNewPw]  = useState("");
  const [confPw, setConfPw] = useState("");
  const [err,    setErr]    = useState("");
  const [success,setSuccess]= useState(false);

  const save = async () => {
    setErr(""); setSuccess(false);
    if(!curPw||!newPw||!confPw){ setErr("All fields are required."); return; }
    if(newPw.length < 6){ setErr("New password must be at least 6 characters."); return; }
    if(newPw !== confPw){ setErr("New passwords do not match."); return; }
    try {
      // Re-authenticate with Firebase Auth before changing password
      const credential = EmailAuthProvider.credential(user.email, curPw);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // Update password in Firebase Auth
      await updatePassword(auth.currentUser, newPw);
      addAudit(user.id, user.name, "CHANGE_PASSWORD", `Password changed by ${user.email}`);
      setSuccess(true);
      setCurPw(""); setNewPw(""); setConfPw("");
    } catch(e) {
      if(e.code==="auth/invalid-credential"||e.code==="auth/wrong-password"){
        setErr("Current password is incorrect.");
      } else {
        setErr("Failed to update password. Please try again.");
      }
    }
  };

  return (
    <div style={{maxWidth:460}}>
      <div className="sh"><div><div className="card-title">Change Password</div><div className="card-sub mt4 ts">Update your login password</div></div></div>
      <div className="card">
        <div style={{padding:4}}>
          {err&&<div className="err">{err}</div>}
          {success&&<div className="al al-s mb16"><I n="check" s={15}/><div>Password updated successfully!</div></div>}
          <div className="fg"><label className="fl">Current Password</label><input className="fi" type="password" placeholder="Enter current password" value={curPw} onChange={e=>setCurPw(e.target.value)}/></div>
          <div className="fg"><label className="fl">New Password</label><input className="fi" type="password" placeholder="Min 6 characters" value={newPw} onChange={e=>setNewPw(e.target.value)}/></div>
          <div className="fg"><label className="fl">Confirm New Password</label><input className="fi" type="password" placeholder="Re-enter new password" value={confPw} onChange={e=>setConfPw(e.target.value)}/></div>
          <div className="fx g8" style={{justifyContent:"flex-end",marginTop:8}}>
            <button className="btn bgh" onClick={()=>setTab("dashboard")}>Cancel</button>
            <button className="btn bp" onClick={save}><I n="lock" s={15}/>Update Password</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PRODUCTIVITY DASHBOARD (Partner only)
// ══════════════════════════════════════════════════════════════
function ProductivityDashboard({ users=[], projects=[], tss=[] }) {
  // Productive = engagement work + Reading (self-development counts)
  // Non-productive = Leave, Holiday, Idle only
  const PRODUCTIVE_CATS = ["Assurance","Virtual CFO","Compliance","Consulting","Reading"];
  const NON_BILLABLE_CATS = ["Leave","Holiday","Idle","Reading"]; // Reading: productive but never billable
  const NON_PRODUCTIVE_CATS = ["Leave","Holiday","Idle"]; // Idle/Leave card only — excludes Reading, which is productive

  // ── Period filter ──
  const allMonths = [...new Set(tss.map(t=>monthKey(t.date)).filter(Boolean))].sort().reverse();
  const [selMonths, setSelMonths] = useState(allMonths.slice(0,1));
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [staffTab, setStaffTab] = useState("table"); // table | billable | productive
  const [sortCol, setSortCol] = useState("total");
  const [sortDir, setSortDir] = useState(-1);

  const toggleMonth = mk => {
    setSelMonths(prev => prev.includes(mk) ? (prev.length>1?prev.filter(m=>m!==mk):prev) : [...prev, mk]);
  };

  // ── Filtered timesheets ──
  const filtered = tss.filter(t => {
    if(!selMonths.includes(monthKey(t.date))) return false;
    if(t.status!=="approved") return false;
    return true;
  });

  // Helper: internal categories are NEVER billable regardless of stored flag
  // (fixes legacy data where internal entries were incorrectly saved as billable:true)
  const isActuallyBillable = t => {
    if(t.isInternal) return false;
    const cat = t.category || "";
    if(NON_BILLABLE_CATS.includes(cat)) return false;
    return t.billable;
  };

  // ── Summary metrics ──
  const totalHrs     = filtered.reduce((s,t)=>s+t.hours,0);
  const billableHrs  = filtered.filter(t=>isActuallyBillable(t)).reduce((s,t)=>s+t.hours,0);
  const productiveHrs= filtered.filter(t=>PRODUCTIVE_CATS.includes(t.category||t.type)).reduce((s,t)=>s+t.hours,0);
  const billPct      = totalHrs>0?Math.round(billableHrs/totalHrs*1000)/10:0;
  const prodPct      = totalHrs>0?Math.round(productiveHrs/totalHrs*1000)/10:0;
  const idleLeaveHrs = filtered.filter(t=>NON_PRODUCTIVE_CATS.includes(t.category||t.type)).reduce((s,t)=>s+t.hours,0);
  const activeClients= [...new Set(filtered.map(t=>{const p=projects.find(px=>px.id===t.projectId); return p?.clientName;}).filter(Boolean))].length;

  // ── By category ──
  const byCategory = {};
  filtered.forEach(t=>{
    const cat = t.category||t.type||"Other";
    byCategory[cat]=(byCategory[cat]||0)+t.hours;
  });

  // ── By role ──
  const byRole = { partner:0, manager:0, intern:0 };
  const byRoleBillable = { partner:0, manager:0, intern:0 };
  filtered.forEach(t=>{
    const u=users.find(u=>u.id===t.userId);
    if(!u) return;
    byRole[u.role]=(byRole[u.role]||0)+t.hours;
    if(isActuallyBillable(t)) byRoleBillable[u.role]=(byRoleBillable[u.role]||0)+t.hours;
  });

  // ── Top clients ──
  const clientHrs = {};
  filtered.forEach(t=>{
    if(!isActuallyBillable(t)) return;
    const p=projects.find(px=>px.id===t.projectId);
    if(!p?.clientName) return;
    clientHrs[p.clientName]=(clientHrs[p.clientName]||0)+t.hours;
  });
  const topClients = Object.entries(clientHrs).sort((a,b)=>b[1]-a[1]).slice(0,10);

  // ── Daily trend ──
  const dailyHrs = {};
  filtered.forEach(t=>{ dailyHrs[t.date]=(dailyHrs[t.date]||0)+t.hours; });
  const dailyEntries = Object.entries(dailyHrs).sort((a,b)=>a[0].localeCompare(b[0]));

  // ── Staff performance ──
  const staffMap = {};
  filtered.forEach(t=>{
    const u=users.find(u=>u.id===t.userId);
    if(!u) return;
    if(!staffMap[u.id]) staffMap[u.id]={ id:u.id, name:u.name, role:u.role, total:0, billable:0, productive:0 };
    staffMap[u.id].total+=t.hours;
    if(isActuallyBillable(t)) staffMap[u.id].billable+=t.hours;
    const cat=t.category||t.type||"";
    if(PRODUCTIVE_CATS.includes(cat)) staffMap[u.id].productive+=t.hours;
  });
  const allStaff = Object.values(staffMap).map(s=>({
    ...s,
    billable_pct: s.total>0?Math.round(s.billable/s.total*1000)/10:0,
    productive_pct: s.total>0?Math.round(s.productive/s.total*1000)/10:0,
  }));

  // Apply role + search filter for table
  const visibleStaff = allStaff.filter(s=>{
    if(roleFilter!=="all"&&s.role!==roleFilter) return false;
    if(search&&!s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a,b)=>{
    const va=a[sortCol], vb=b[sortCol];
    if(typeof va==="string") return sortDir*va.localeCompare(vb);
    return sortDir*(va-vb);
  });

  const handleSort = col => {
    if(sortCol===col) setSortDir(d=>d*-1);
    else { setSortCol(col); setSortDir(-1); }
  };

  // ── pct color helper ──
  const pctColor = v => v>=90?"var(--green)":v>=70?"var(--amber)":"var(--red)";

  const CAT_COLORS = {
    "Assurance":"#0f6e56","Virtual CFO":"#185fa5","Compliance":"#534ab7",
    "Consulting":"#ba7517","Reading":"#888780","Leave":"#d85a30","Idle":"#b4b2a9","Holiday":"#d4537e"
  };

  // ── Canvas chart refs ──
  const refDaily  = useRef(null);
  const refCat    = useRef(null);
  const refClients= useRef(null);
  const refRole   = useRef(null);
  const refBillR  = useRef(null);
  const refProdR  = useRef(null);
  const chartInst = useRef({});

  // Destroy and redraw charts when data changes
  useEffect(()=>{
    const Script = document.getElementById("chartjs-cdn");
    const draw = () => {
      if(!window.Chart) return;
      Object.values(chartInst.current).forEach(c=>{ try{c.destroy();}catch(e){} });
      chartInst.current={};
      if(refDaily.current && dailyEntries.length>0){
        chartInst.current.daily = new window.Chart(refDaily.current,{
          type:"line",
          data:{ labels:dailyEntries.map(([d])=>d.slice(5)),
            datasets:[{label:"Hours",data:dailyEntries.map(([,v])=>v),
              borderColor:"var(--gold)",backgroundColor:"rgba(201,168,76,.12)",fill:true,tension:0.35,
              pointRadius:3,pointBackgroundColor:"var(--gold)",borderWidth:2}]},
          options:{ responsive:true,maintainAspectRatio:false,
            plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.parsed.y+"h"}}},
            scales:{x:{grid:{display:false},ticks:{font:{size:11},maxRotation:45,color:"#64748b"}},
              y:{grid:{color:"#e2e8f0"},ticks:{font:{size:11},color:"#64748b"}}}}
        });
      }
      if(refCat.current && Object.keys(byCategory).length>0){
        const cats=Object.keys(byCategory), vals=Object.values(byCategory);
        chartInst.current.cat = new window.Chart(refCat.current,{
          type:"doughnut",
          data:{ labels:cats,
            datasets:[{data:vals,backgroundColor:cats.map(c=>CAT_COLORS[c]||"#888"),borderWidth:2,borderColor:"#fff"}]},
          options:{ responsive:true,maintainAspectRatio:false,cutout:"62%",
            plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.label+": "+ctx.parsed+"h"}}}}
        });
      }
      if(refClients.current && topClients.length>0){
        chartInst.current.clients = new window.Chart(refClients.current,{
          type:"bar",
          data:{ labels:topClients.map(([c])=>c.length>30?c.slice(0,28)+"…":c),
            datasets:[{label:"Hrs",data:topClients.map(([,v])=>v),backgroundColor:"#185fa5",borderRadius:4}]},
          options:{ indexAxis:"y",responsive:true,maintainAspectRatio:false,
            plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.parsed.x+"h"}}},
            scales:{x:{grid:{color:"#e2e8f0"},ticks:{font:{size:11},color:"#64748b"}},
              y:{grid:{display:false},ticks:{font:{size:11},color:"#0f2044"}}}}
        });
      }
      if(refRole.current){
        const roles=["intern","manager","partner"];
        chartInst.current.role = new window.Chart(refRole.current,{
          type:"bar",
          data:{ labels:roles.map(r=>r.charAt(0).toUpperCase()+r.slice(1)),
            datasets:[
              {label:"Billable",data:roles.map(r=>Math.round((byRoleBillable[r]||0)*10)/10),backgroundColor:"#10b981",borderRadius:4},
              {label:"Non-billable",data:roles.map(r=>Math.round(((byRole[r]||0)-(byRoleBillable[r]||0))*10)/10),backgroundColor:"#ef4444",borderRadius:4}
            ]},
          options:{ responsive:true,maintainAspectRatio:false,
            plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.dataset.label+": "+ctx.parsed.y+"h"}}},
            scales:{x:{stacked:true,grid:{display:false},ticks:{font:{size:12},color:"#0f2044"}},
              y:{stacked:true,grid:{color:"#e2e8f0"},ticks:{font:{size:11},color:"#64748b"}}}}
        });
      }
    };
    if(!window.Chart){
      if(!Script){
        const s=document.createElement("script");
        s.id="chartjs-cdn"; s.src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
        s.onload=draw; document.head.appendChild(s);
      } else { Script.addEventListener("load",draw); }
    } else { draw(); }
    return ()=>{ Object.values(chartInst.current).forEach(c=>{ try{c.destroy();}catch(e){} }); chartInst.current={}; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selMonths, tss.length, projects.length, users.length]);

  // Ranking charts drawn separately
  useEffect(()=>{
    if(!window.Chart) return;
    if(staffTab==="billable"&&refBillR.current){
      if(chartInst.current.billR){ try{chartInst.current.billR.destroy();}catch(e){} }
      const sorted=[...allStaff].sort((a,b)=>b.billable_pct-a.billable_pct);
      chartInst.current.billR = new window.Chart(refBillR.current,{
        type:"bar",
        data:{ labels:sorted.map(s=>s.name),
          datasets:[{label:"Billable %",data:sorted.map(s=>s.billable_pct),
            backgroundColor:sorted.map(s=>s.billable_pct>=90?"#10b981":s.billable_pct>=70?"#f59e0b":"#ef4444"),borderRadius:4}]},
        options:{ indexAxis:"y",responsive:true,maintainAspectRatio:false,
          plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.parsed.x+"%"}}},
          scales:{x:{min:0,max:100,grid:{color:"#e2e8f0"},ticks:{font:{size:11},color:"#64748b",callback:v=>v+"%"}},
            y:{grid:{display:false},ticks:{font:{size:11},color:"#0f2044"}}}}
      });
    }
    if(staffTab==="productive"&&refProdR.current){
      if(chartInst.current.prodR){ try{chartInst.current.prodR.destroy();}catch(e){} }
      const sorted=[...allStaff].sort((a,b)=>b.productive_pct-a.productive_pct);
      chartInst.current.prodR = new window.Chart(refProdR.current,{
        type:"bar",
        data:{ labels:sorted.map(s=>s.name),
          datasets:[{label:"Productive %",data:sorted.map(s=>s.productive_pct),
            backgroundColor:sorted.map(s=>s.productive_pct>=90?"#10b981":s.productive_pct>=70?"#f59e0b":"#ef4444"),borderRadius:4}]},
        options:{ indexAxis:"y",responsive:true,maintainAspectRatio:false,
          plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>ctx.parsed.x+"%"}}},
          scales:{x:{min:0,max:100,grid:{color:"#e2e8f0"},ticks:{font:{size:11},color:"#64748b",callback:v=>v+"%"}},
            y:{grid:{display:false},ticks:{font:{size:11},color:"#0f2044"}}}}
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[staffTab, allStaff.length]);

  const periodLabel = selMonths.length===1 ? monthLabel(selMonths[0]) : selMonths.length+" months selected";

  return (
    <div>
      {/* ── Period Selector ── */}
      <div className="card mb22">
        <div className="fxb mb16">
          <div>
            <div className="card-title">Period</div>
            <div className="card-sub mt4 ts">{periodLabel} · approved timesheets only</div>
          </div>
          {selMonths.length>1&&<button className="btn bgh bsm" onClick={()=>setSelMonths(allMonths.slice(0,1))}>Reset</button>}
        </div>
        {allMonths.length===0
          ? <div className="tsl ts">No approved timesheet data found. Log and approve timesheets first.</div>
          : <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {allMonths.map(mk=>(
                <div key={mk} onClick={()=>toggleMonth(mk)}
                  style={{padding:"6px 16px",borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:500,transition:"all .15s",
                    background:selMonths.includes(mk)?"var(--navy)":"var(--cream)",
                    color:selMonths.includes(mk)?"#fff":"var(--slate)",
                    border:`1.5px solid ${selMonths.includes(mk)?"var(--navy)":"var(--border)"}`}}>
                  {monthLabel(mk)}
                </div>
              ))}
            </div>
        }
      </div>

      {filtered.length===0 ? (
        <div className="es"><div className="es-icon"><I n="chart" s={44}/></div><div>No approved data for selected period. Try selecting a different month.</div></div>
      ) : (<>

      {/* ── KPI Row ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:14,marginBottom:22}}>
        {[
          {label:"Total Hours",      value:Math.round(totalHrs*10)/10,     sub:`${allStaff.length} contributors`, color:"var(--navy)"},
          {label:"Billable Hours",   value:Math.round(billableHrs*10)/10,  sub:`${billPct}% of total`,             color:"var(--gold)"},
          {label:"Billable %",       value:billPct+"%",                    sub:billPct>=90?"Excellent":billPct>=70?"Good":"Needs attention", color:pctColor(billPct)},
          {label:"Productive Hours", value:Math.round(productiveHrs*10)/10,sub:`${prodPct}% utilisation`,          color:"var(--purple)"},
          {label:"Active Clients",   value:activeClients,                  sub:"billed this period",               color:"#185fa5"},
          {label:"Idle / Leave",     value:Math.round(idleLeaveHrs*10)/10, sub:"hrs non-productive",               color:"var(--slate)"},
        ].map(k=>(
          <div key={k.label} className="sc">
            <div style={{fontSize:11,fontWeight:600,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--slate)",marginBottom:6}}>{k.label}</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,color:k.color,lineHeight:1}}>{k.value}</div>
            <div style={{fontSize:12,color:"var(--slate-light)",marginTop:5}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Daily trend + Category donut ── */}
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16,marginBottom:16}}>
        <div className="card">
          <div className="card-title mb8">Daily hours logged</div>
          <div className="card-sub">Team-wide hours per working day · approved only</div>
          <div style={{position:"relative",height:220,marginTop:12}}>
            <canvas ref={refDaily}/>
          </div>
        </div>
        <div className="card">
          <div className="card-title mb8">Hours by category</div>
          <div className="card-sub">Service line split</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"6px 12px",marginBottom:10,marginTop:8}}>
            {Object.entries(byCategory).sort((a,b)=>b[1]-a[1]).map(([cat,hrs])=>(
              <span key={cat} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--slate)"}}>
                <span style={{width:9,height:9,borderRadius:2,background:CAT_COLORS[cat]||"#888",flexShrink:0,display:"inline-block"}}/>
                {cat} ({fmtHrs(hrs)}h)
              </span>
            ))}
          </div>
          <div style={{position:"relative",height:180}}>
            <canvas ref={refCat}/>
          </div>
        </div>
      </div>

      {/* ── Top Clients + Role breakdown ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div className="card">
          <div className="card-title mb8">Top 10 clients by billable hours</div>
          <div className="card-sub">Total engagement hours this period</div>
          <div style={{position:"relative",height:Math.max(topClients.length,5)*38+60,marginTop:12}}>
            <canvas ref={refClients}/>
          </div>
        </div>
        <div className="card">
          <div className="card-title mb8">Billable vs Non-billable by role</div>
          <div className="card-sub">Partner · Manager · Intern breakdown</div>
          <div style={{display:"flex",gap:16,marginBottom:10,marginTop:8}}>
            <span style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--slate)"}}>
              <span style={{width:10,height:10,borderRadius:2,background:"var(--green)",display:"inline-block"}}/>Billable
            </span>
            <span style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--slate)"}}>
              <span style={{width:10,height:10,borderRadius:2,background:"var(--red)",display:"inline-block"}}/>Non-billable
            </span>
          </div>
          <div style={{position:"relative",height:240}}>
            <canvas ref={refRole}/>
          </div>
        </div>
      </div>

      {/* ── Staff Performance ── */}
      <div className="card">
        <div className="fxb mb16">
          <div>
            <div className="card-title">Staff Performance</div>
            <div className="card-sub mt4 ts">{allStaff.length} contributors · approved entries</div>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="tabs" style={{marginBottom:16}}>
          {[["table","Full Table"],["billable","Billable % Ranking"],["productive","Productive % Ranking"]].map(([id,lbl])=>(
            <div key={id} className={`tab ${staffTab===id?"active":""}`} onClick={()=>setStaffTab(id)}>{lbl}</div>
          ))}
        </div>

        {staffTab==="table" && (<>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:12,color:"var(--slate)"}}>Filter:</span>
            <input className="fi" placeholder="Search name…" value={search} onChange={e=>setSearch(e.target.value)}
              style={{width:180,padding:"6px 12px",fontSize:13}}/>
            <select className="fs" value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={{padding:"6px 12px",fontSize:13}}>
              <option value="all">All roles</option>
              <option value="partner">Partners</option>
              <option value="manager">Managers</option>
              <option value="intern">Interns</option>
            </select>
          </div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  {[["name","Name"],["role","Role"],["total","Total hrs"],["billable","Billable hrs"],["billable_pct","Billable %"],["productive_pct","Productive %"]].map(([col,lbl])=>(
                    <th key={col} onClick={()=>handleSort(col)} style={{cursor:"pointer",userSelect:"none"}}>
                      {lbl} {sortCol===col?(sortDir===-1?"↓":"↑"):"↕"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleStaff.map(s=>(
                  <tr key={s.id}>
                    <td className="fw6">{s.name}</td>
                    <td>
                      <span className={`bdg ${s.role==="partner"?"rpa":s.role==="manager"?"rma":"ria"}`}>{s.role}</span>
                    </td>
                    <td>{fmtHrs(s.total)}h</td>
                    <td>{fmtHrs(s.billable)}h</td>
                    <td>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{flex:1,height:6,background:"var(--cream)",borderRadius:3,overflow:"hidden",minWidth:60}}>
                          <div style={{height:"100%",borderRadius:3,background:pctColor(s.billable_pct),width:Math.min(s.billable_pct,100)+"%"}}/>
                        </div>
                        <span style={{fontSize:12,fontWeight:600,color:pctColor(s.billable_pct),minWidth:40,textAlign:"right"}}>{s.billable_pct}%</span>
                      </div>
                    </td>
                    <td>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{flex:1,height:6,background:"var(--cream)",borderRadius:3,overflow:"hidden",minWidth:60}}>
                          <div style={{height:"100%",borderRadius:3,background:pctColor(s.productive_pct),width:Math.min(s.productive_pct,100)+"%"}}/>
                        </div>
                        <span style={{fontSize:12,fontWeight:600,color:pctColor(s.productive_pct),minWidth:40,textAlign:"right"}}>{s.productive_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleStaff.length===0&&<tr><td colSpan={6} style={{textAlign:"center",padding:"32px 0",color:"var(--slate)"}}>No staff match your filter.</td></tr>}
              </tbody>
            </table>
          </div>
        </>)}

        {staffTab==="billable" && (
          <div style={{position:"relative",height:Math.max(allStaff.length,4)*38+60}}>
            <canvas ref={refBillR}/>
          </div>
        )}
        {staffTab==="productive" && (
          <div style={{position:"relative",height:Math.max(allStaff.length,4)*38+60}}>
            <canvas ref={refProdR}/>
          </div>
        )}
      </div>

      {/* ── Legend: productivity definition ── */}
      <div className="al al-i" style={{marginTop:16}}>
        <I n="info" s={16}/>
        <div><strong>Productivity definition:</strong> Productive hours = Assurance + Virtual CFO + Compliance + Consulting + Reading. Leave, Holiday and Idle are excluded. Billable hours = productive/engagement hours explicitly marked billable (Reading is always non-billable). Only approved timesheet entries are counted.</div>
      </div>

      </>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// PERFORMANCE APPRAISAL
// ══════════════════════════════════════════════════════════════
function AppraisalMetricInput({ m, value, onChange, disabled }) {
  const v = value || { score:null, na:false, remark:"" };
  return (
    <div style={{padding:"14px 0",borderBottom:"1px solid var(--border)"}}>
      <div className="fw6" style={{fontSize:14}}>{m.label}</div>
      <div className="tx tsl mt4">{m.traits}</div>
      <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,flexWrap:"wrap"}}>
        {[1,2,3,4,5].map(n=>(
          <button key={n} type="button" disabled={disabled||v.na}
            className={`btn bsm ${v.score===n?"bp":"bgh"}`}
            style={{width:36,padding:0,justifyContent:"center"}}
            onClick={()=>onChange({...v,score:n})}>{n}</button>
        ))}
        <label style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--slate)",marginLeft:10,cursor:disabled?"default":"pointer"}}>
          <input type="checkbox" checked={!!v.na} disabled={disabled}
            onChange={e=>onChange({...v,na:e.target.checked,score:e.target.checked?null:v.score})}/>
          Not applicable
        </label>
      </div>
      <textarea className="fta" style={{marginTop:8,minHeight:52}} disabled={disabled}
        placeholder="Remark — basis for this score (mandatory)"
        value={v.remark||""} onChange={e=>onChange({...v,remark:e.target.value})}/>
    </div>
  );
}

function AppraisalForm({ user, users, req, existing, onSave, onBack }) {
  const isPartnerViewer = user.role==="partner";
  const isAppraiser = existing ? existing.appraiserId===user.id : (req?.eligibleAppraisers||[]).includes(user.id);
  const staff = users.find(u=>u.id===(existing?existing.staffId:req.staffId));

  const blank = () => { const o={}; APPRAISAL_METRICS.forEach(m=>o[m.key]={score:null,na:false,remark:""}); return o; };
  const [metrics, setMetrics] = useState(()=>({ ...blank(), ...(existing?.metrics||{}) }));
  const [valueAddition, setValueAddition] = useState(existing?.valueAddition?.remark || "");
  const [notApplicable, setNotApplicable] = useState(!!existing?.notApplicable);
  const [naReason, setNaReason] = useState(existing?.naReason || "");
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [editNote, setEditNote] = useState("");
  const [partnerFeedback, setPartnerFeedback] = useState(existing?.partnerFeedback || "");
  const savePartnerFeedback = () => onSave({ ...existing, partnerFeedback, partnerFeedbackBy:user.id, partnerFeedbackAt:new Date().toISOString() });

  const submitted = existing?.status==="submitted";
  // Authorization first: only the assigned appraiser (or a partner) may ever edit this form.
  // Everyone else — most importantly the staff member being appraised — is read-only, always.
  const canEditNow = isPartnerViewer
    ? (!submitted || overrideMode)
    : (isAppraiser && (!submitted || editUnlocked));
  const locked = !canEditNow;

  const { sum, max, pct } = calcAppraisalScore(metrics);
  const allRemarksFilled = APPRAISAL_METRICS.every(m => metrics[m.key].na || (metrics[m.key].remark||"").trim().length>0) && valueAddition.trim().length>0;
  const allScored = APPRAISAL_METRICS.every(m => metrics[m.key].na || metrics[m.key].score);

  const save = (status) => {
    if(status==="submitted" && notApplicable && !naReason.trim()) { alert("Give a reason for marking this appraisal as not applicable."); return; }
    if(status==="submitted" && !notApplicable && (!allScored || !allRemarksFilled)) { alert("Every metric needs a score (or N/A) and a mandatory remark, and Value Addition needs a note, before submitting."); return; }
    const base = existing || { id:genId(), type:req.type, fy:req.fy, quarter:req.quarter||null, projectId:req.type==="retainer"?null:(req.projectId||null), clientNames:req.clientNames||[], ...(req.type==="retainer"?{ groupKey:req.key, projectIds:req.projectIds||[], groupLeadId:req.groupLeadId||null }:{}), staffId:req.staffId, appraiserRole:req.appraiserRole, appraiserId:user.id, createdAt:new Date().toISOString() };
    let rec = { ...base, metrics, valueAddition:{ remark:valueAddition }, notApplicable, naReason: notApplicable ? naReason.trim() : "", status };
    if(rec.type==="retainer" && req?.key) { rec.groupKey = req.key; rec.clientNames = req.clientNames||rec.clientNames; rec.projectIds = req.projectIds||rec.projectIds||[]; }
    if(status==="submitted") {
      rec.submittedAt = new Date().toISOString();
      // Pin a fixed appraisal to the quarter it is submitted under; edits/overrides and later pushes don't move it.
      if(rec.type==="fixed") {
        const wasSub = existing?.status==="submitted";
        rec.periodFy = existing?.periodFy || (wasSub ? req.baseFy : req.fy);
        rec.periodQuarter = existing?.periodQuarter || (wasSub ? req.baseQuarter : req.quarter);
      }
    }
    if(isPartnerViewer && overrideMode && existing) {
      rec.overrideHistory = [...(existing.overrideHistory||[]), { metrics:existing.metrics, valueAddition:existing.valueAddition, notApplicable:!!existing.notApplicable, naReason:existing.naReason||"", replacedAt:new Date().toISOString(), replacedBy:existing.overriddenBy||existing.appraiserId }];
      rec.overriddenBy = user.id; rec.overriddenAt = new Date().toISOString();
    }
    if(isAppraiser && editUnlocked && existing) {
      rec.editHistory = [...(existing.editHistory||[]), { metrics:existing.metrics, valueAddition:existing.valueAddition, notApplicable:!!existing.notApplicable, naReason:existing.naReason||"", replacedAt:new Date().toISOString() }];
      rec.editRequestStatus = "used";
    }
    onSave(rec);
  };

  const requestEdit = () => onSave({ ...existing, editRequestStatus:"requested", editRequestedAt:new Date().toISOString(), editRequestNote:editNote });
  const approveEdit = () => onSave({ ...existing, editRequestStatus:"approved", editApprovedBy:user.id, editApprovedAt:new Date().toISOString() });
  const denyEdit    = () => onSave({ ...existing, editRequestStatus:"denied" });

  const fxPeriod = req ? appraisalPeriod(req, existing) : null;
  const scopeLabel = (req?.type||existing?.type)==="fixed"
    ? `Fixed engagement${fxPeriod?` · ${fxPeriod.quarter} FY ${fxPeriod.fy}`:""}`
    : `Retainer · ${existing?.quarter||req?.quarter} FY ${existing?.fy||req?.fy}`;

  return (
    <div>
      <button className="btn bgh bsm" style={{marginBottom:14}} onClick={onBack}><I n="arrowleft" s={13}/>Back</button>
      <div className="card">
        <div className="fxb" style={{alignItems:"flex-start"}}>
          <div>
            <div className="card-title">{staff?.name}</div>
            <div className="card-sub mt4">{scopeLabel} · {(req?.clientNames||existing?.clientNames||[]).join(", ")}</div>
            {(req?.type||existing?.type)==="retainer" && req?.groupLeadName && (
              <div className="card-sub mt4" style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <span className="bdg bac">Appraised by {req.groupLeadName}</span>
                {(req.projects||[]).map(pj=><span key={pj.id} className="mono" style={{fontSize:11,background:"var(--cream)",border:"1px solid var(--border)",borderRadius:5,padding:"1px 6px"}}>{pj.code}</span>)}
              </div>
            )}
            {(req?.type||existing?.type)==="retainer" && (req?.projects||[]).length>1 && (
              <div className="tx tsl mt4">One combined appraisal covering all {req.projects.length} retainer engagements above.</div>
            )}
            {(req?.type||existing?.type)==="fixed" && (req?.projectName||req?.projectCode) && (
              <div className="card-sub mt4" style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:8}}>
                {req?.projectName && <b style={{color:"var(--navy)"}}>{req.projectName}</b>}
                {req?.projectCode && <span className="mono" style={{fontSize:11,background:"var(--cream)",border:"1px solid var(--border)",borderRadius:5,padding:"1px 6px"}}>{req.projectCode}</span>}
              </div>
            )}
          </div>
          <span className={`bdg ${submitted?"bac":"brs"}`}>{submitted?"Submitted":"Draft"}</span>
        </div>

        {(req?.pushes||[]).length>0 && (
          <div style={{marginTop:14,padding:"10px 12px",background:"var(--cream)",borderRadius:8,border:"1px solid var(--border)"}}>
            <div className="fw6" style={{fontSize:13}}>Quarter push history</div>
            {req.pushes.map((h,i)=>(
              <div key={i} className="tx mt4">
                {h.action==="pullback"?"Pulled back":"Pushed"} {h.fromQuarter} FY {h.fromFy} → {h.toQuarter} FY {h.toFy} by {users.find(u=>u.id===h.by)?.name||"—"} on {fmtDate(h.at)}{h.reason?<span className="tsl"> · {h.reason}</span>:null}
              </div>
            ))}
          </div>
        )}
        {isPartnerViewer && !isAppraiser && submitted && (
          <div className="al al-i" style={{marginTop:14,flexWrap:"wrap"}}>
            <I n="info" s={16}/>
            <div>Given by {users.find(u=>u.id===existing.appraiserId)?.name||"—"}. As a partner you can override any score below.
              {!overrideMode && <button className="btn bgh bxs" style={{marginLeft:8}} onClick={()=>setOverrideMode(true)}>Override score</button>}
            </div>
          </div>
        )}
        {existing?.overriddenBy && (
          <div className="al al-w" style={{marginTop:14}}><I n="alert" s={16}/><div>This score was overridden by {users.find(u=>u.id===existing.overriddenBy)?.name}. {isPartnerViewer?"Earlier version(s) shown below (partner-visible only).":""}</div></div>
        )}
        {isPartnerViewer && existing?.overrideHistory?.length>0 && (
          <div style={{marginTop:14}}>
            <div className="fw6" style={{fontSize:13}}>Override history</div>
            {existing.overrideHistory.map((h,i) => {
              const { sum:hSum, max:hMax, pct:hPct } = calcAppraisalScore(h.metrics);
              return (
                <div key={i} style={{marginTop:8,padding:"10px 12px",background:"var(--cream)",borderRadius:8,border:"1px solid var(--border)"}}>
                  <div className="tx tsl">Replaced by {users.find(u=>u.id===h.replacedBy)?.name||"—"} on {fmtDate(h.replacedAt)} · previous score {h.notApplicable?"Not applicable":hMax?`${hSum}/${hMax} (${Math.round(hPct)}%)`:"All N/A"}</div>
                  {h.notApplicable && <div className="tx" style={{marginTop:6,fontSize:12}}><b>Reason:</b> {h.naReason||"—"}</div>}
                  {!h.notApplicable && APPRAISAL_METRICS.map(m => {
                    const v = h.metrics?.[m.key];
                    return (
                      <div key={m.key} style={{marginTop:6}}>
                        <div className="tx" style={{fontWeight:600,fontSize:12}}>{m.label}: {v?.na?"N/A":(v?.score||"—")}</div>
                        {!v?.na && v?.remark && <div className="tx tsl" style={{fontSize:12}}>{v.remark}</div>}
                      </div>
                    );
                  })}
                  {!h.notApplicable && <div style={{marginTop:6}}>
                    <div className="tx" style={{fontWeight:600,fontSize:12}}>Value addition</div>
                    <div className="tx tsl" style={{fontSize:12}}>{h.valueAddition?.remark||"—"}</div>
                  </div>}
                </div>
              );
            })}
          </div>
        )}
        {existing?.editRequestStatus==="requested" && isPartnerViewer && (
          <div className="al al-w" style={{marginTop:14}}>
            <I n="alert" s={16}/>
            <div>{users.find(u=>u.id===existing.appraiserId)?.name} has requested to edit this submitted appraisal{existing.editRequestNote?`: "${existing.editRequestNote}"`:"."}
              <div style={{marginTop:8,display:"flex",gap:8}}>
                <button className="btn bsc bxs" onClick={approveEdit}>Approve edit</button>
                <button className="btn bgh bxs" onClick={denyEdit}>Deny</button>
              </div>
            </div>
          </div>
        )}
        {existing?.editRequestStatus==="requested" && isAppraiser && (
          <div className="al al-w" style={{marginTop:14}}><I n="alert" s={16}/><div>Edit request sent — waiting for a partner to approve.</div></div>
        )}
        {existing?.editRequestStatus==="approved" && isAppraiser && !editUnlocked && (
          <div className="al al-s" style={{marginTop:14}}><I n="check" s={16}/><div>Your edit request was approved.<button className="btn bgo bxs" style={{marginLeft:8}} onClick={()=>setEditUnlocked(true)}>Edit now</button></div></div>
        )}

        <div style={{marginTop:14}}>
          {!existing && !canEditNow && (
            <div className="al al-i"><I n="info" s={16}/><div>Not given yet. This appraisal will appear here once your {req.appraiserRole==="partner"?"partner":"manager"} submits it.</div></div>
          )}
          {(canEditNow || notApplicable) && (
            <div style={{padding:"12px 14px",marginBottom:4,borderRadius:10,border:`1.5px solid ${notApplicable?"var(--gold)":"var(--border)"}`,background:notApplicable?"var(--gold-pale)":"transparent"}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,cursor:locked?"default":"pointer"}}>
                <input type="checkbox" checked={notApplicable} disabled={locked} onChange={e=>setNotApplicable(e.target.checked)}/>
                Appraisal not applicable for this {(req?.type||existing?.type)==="fixed"?"engagement":"quarter"}
              </label>
              <div className="tx tsl mt4">Counts as complete, but is left out of the quarter average score.</div>
              {notApplicable && (
                <textarea className="fta" style={{marginTop:8,minHeight:52}} disabled={locked} value={naReason} onChange={e=>setNaReason(e.target.value)}
                  placeholder="Reason — e.g. only a few hours booked, not enough exposure to assess (mandatory)"/>
              )}
            </div>
          )}
          {!notApplicable && APPRAISAL_METRICS.map(m => (
            <AppraisalMetricInput key={m.key} m={m} value={metrics[m.key]} disabled={locked}
              onChange={v=>setMetrics(prev=>({...prev,[m.key]:v}))}/>
          ))}
          {!notApplicable && <div style={{padding:"14px 0"}}>
            <div className="fw6" style={{fontSize:14}}>Value addition</div>
            <div className="tx tsl mt4">Standout feature in this engagement — descriptive, not scored</div>
            <textarea className="fta" style={{marginTop:8}} disabled={locked} value={valueAddition} onChange={e=>setValueAddition(e.target.value)}
              placeholder="What stood out — an effort, piece of work, or trait that benefited both the client and the firm (mandatory)"/>
          </div>}
        </div>

        <div className="fxb" style={{borderTop:"1px solid var(--border)",paddingTop:14,marginTop:4}}>
          <span className="ts tsl">Overall score</span>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:22}}>{notApplicable?"Not applicable":max?`${sum}/${max} (${Math.round(pct)}%)`:"All N/A"}</span>
        </div>

        {existing && (
          <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid var(--border)"}}>
            <div className="fw6" style={{fontSize:14}}>Partner feedback <span className="tx tsl" style={{fontWeight:400}}>(optional — any partner can add this)</span></div>
            {isPartnerViewer ? (
              <>
                <textarea className="fta" style={{marginTop:8}} value={partnerFeedback} onChange={e=>setPartnerFeedback(e.target.value)} placeholder="Add any additional feedback for this person — visible to them"/>
                <button className="btn bgh bsm" style={{marginTop:8}} onClick={savePartnerFeedback}>Save feedback</button>
                {existing.partnerFeedbackBy && <p className="tx tsl mt8">Last updated by {users.find(u=>u.id===existing.partnerFeedbackBy)?.name} on {fmtDate(existing.partnerFeedbackAt)}</p>}
              </>
            ) : existing.partnerFeedback ? (
              <>
                <p className="tx mt8">{existing.partnerFeedback}</p>
                <p className="tx tsl mt4">— {users.find(u=>u.id===existing.partnerFeedbackBy)?.name}, {fmtDate(existing.partnerFeedbackAt)}</p>
              </>
            ) : <p className="tx tsl mt8">No additional feedback yet.</p>}
          </div>
        )}

        {canEditNow && (
          <div className="md-actions" style={{justifyContent:"flex-start",marginTop:16}}>
            <button className="btn bgh" onClick={()=>save("draft")}>Save draft</button>
            <button className="btn bp" onClick={()=>save("submitted")}>Submit</button>
          </div>
        )}
        {!canEditNow && submitted && isAppraiser && !existing.editRequestStatus && (
          <div style={{marginTop:16}}>
            <textarea className="fta" style={{minHeight:44}} placeholder="Reason for the edit request (optional)" value={editNote} onChange={e=>setEditNote(e.target.value)}/>
            <button className="btn bgh bsm" style={{marginTop:8}} onClick={requestEdit}><I n="edit" s={13}/>Request edit</button>
          </div>
        )}
        {!canEditNow && submitted && <p className="tx tsl mt8">This appraisal is locked. {isAppraiser?"Request an edit above if a correction is needed.":"Only the appraiser, or a partner override, can change it."}</p>}
      </div>
    </div>
  );
}

function FYSelector({ fy, setFy, options }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
      <span className="ts tsl">Financial year</span>
      <select className="fs" style={{width:150}} value={fy} onChange={e=>setFy(e.target.value)}>
        {options.map(o=><option key={o} value={o}>FY {o}</option>)}
      </select>
    </div>
  );
}

function AppraisalListRow({ r, ex, onClick, actions }) {
  const sc = appraisalScoreOf(ex);
  const isFixed = r.type==="fixed";
  const label = isFixed ? (r.clientNames||[]).join(", ") : `Retainers · ${(r.clientNames||[]).join(", ")} · ${r.quarter}`;
  // A client can have several engagements, so show which one this appraisal is for.
  const engName = (r.projectName||"").trim();
  const engCode = r.projectCode||"";
  const showEngName = !!engName && !(r.clientNames||[]).some(c=>(c||"").trim().toLowerCase()===engName.toLowerCase());
  return (
    <div className="card" style={{cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",gap:16,padding:"14px 18px"}} onClick={onClick}>
      <div style={{minWidth:0}}>
        <div className="fw6" style={{fontSize:14}}>{label}</div>
        {(showEngName || engCode) && (
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:8,marginTop:4}}>
            {showEngName && <span style={{fontSize:13,color:"var(--navy-mid)"}}>{engName}</span>}
            {engCode && <span className="mono" style={{fontSize:11,color:"var(--slate)",background:"var(--cream)",border:"1px solid var(--border)",borderRadius:5,padding:"1px 6px"}}>{engCode}</span>}
          </div>
        )}
        {!isFixed && (r.projects||[]).length>0 && (
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:6,marginTop:4}}>
            {r.projects.map(pj=><span key={pj.id} className="mono" style={{fontSize:11,color:"var(--slate)",background:"var(--cream)",border:"1px solid var(--border)",borderRadius:5,padding:"1px 6px"}}>{pj.code}</span>)}
          </div>
        )}
        <div className="tx tsl mt4" style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:6}}>
          <span>{isFixed?"Fixed engagement":"Retainer"} · {r.appraiserRole==="partner"?"Partner appraisal":"Manager appraisal"}</span>
          {!isFixed && r.groupLeadName && <span className="bdg bac" style={{fontSize:11}}>Appraised by {r.groupLeadName}</span>}
        </div>
        {actions}
      </div>
      <div style={{flexShrink:0}}>
        {sc.kind==="score" ? <span className="bdg bac">{Math.round(sc.pct)}%</span>
          : sc.kind==="na" ? <span className="bdg bcl" title={ex?.naReason||"All metrics marked N/A"}>N/A · excluded</span>
          : ex?.status==="draft" ? <span className="bdg brs">Draft</span> : <span className="bdg bcl">Pending</span>}
      </div>
    </div>
  );
}

// What to show for one quarter: the average (partners / own view), or progress (manager's team view).
// A quarter stays Provisional while any appraisal in it is still open — even after the quarter has ended.
// v53: with showAvg, the number shown is the OVERALL score (appraisal 70% + goals 30%); goalFn(fy,q) gives the goal score.
function quarterDisplay(fy, quarter, items, showAvg, goalFn) {
  const qItems = items.filter(i=>i.fy===fy && i.quarter===quarter);
  const s = quarterAverage(qItems);
  const gs = showAvg && goalFn ? goalFn(fy, quarter) : null;
  const c = combinedScore(s, gs);
  const [qStart] = quarterDateRange(fy, quarter);
  const upcoming = qStart>todayStr();
  if(upcoming && s.scored===0) return { s, c, gs, main:"Upcoming", muted:true, upcoming, provisional:false };
  if(!showAvg) {
    if(s.total===0) return { s, c, gs, main:"N/A", muted:true, provisional:false };
    return { s, c, gs, main:`${s.total-s.pending}/${s.total}`, muted:s.pending===s.total, provisional:false };
  }
  if(c.mode==="none") return { s, c, gs, main:"N/A", muted:true, provisional:false };
  if(c.pct!=null) return { s, c, gs, main:`${Math.round(c.pct)}%`, muted:false, provisional:c.provisional };
  return { s, c, gs, main: c.provisional ? "Pending" : "N/A", muted:true, provisional:false };
}
// Overall score of a quarter for one staff member (null when there is nothing to score yet).
function overallOf(fy, quarter, items, goalFn) {
  const s = quarterAverage(items.filter(i=>i.fy===fy && i.quarter===quarter));
  return combinedScore(s, goalFn ? goalFn(fy, quarter) : null).pct;
}

const ProvisionalTag = ({ small }) => (
  <span className="bdg brs" style={small?{fontSize:9.5,padding:"1px 6px"}:{}} title="Some appraisals or goal self-assessments in this quarter are still open, so this score can change">Provisional</span>
);

// ── Name-card quarter row: whole-number scores, coloured against the previous scored quarter ──
const TREND_UP = "#15803d", TREND_DOWN = "#b91c1c", PROV_GOLD = "#d69e2e";
function prevQuarterOf(fy, quarter) {
  const i = GOAL_QUARTER_ORDER.indexOf(quarter);
  if(i>0) return { fy, quarter:GOAL_QUARTER_ORDER[i-1] };
  const y = Number(fy.split("-")[0])-1;
  return { fy:`${y}-${String((y+1)%100).padStart(2,"0")}`, quarter:"Q4" };
}
// Rounded average of the most recent earlier quarter that has a score (crosses into the previous FY).
function priorRoundedScore(fy, quarter, history, goalFn) {
  let cur = { fy, quarter };
  for(let n=0; n<12; n++) {
    cur = prevQuarterOf(cur.fy, cur.quarter);
    if(quarterDateRange(cur.fy, cur.quarter)[1] < APPRAISAL_START_DATE) return null;
    const pct = overallOf(cur.fy, cur.quarter, history, goalFn);
    if(pct!=null) return Math.round(pct);
  }
  return null;
}
const ProvDot = () => <span style={{display:"inline-block",width:5,height:5,borderRadius:"50%",background:PROV_GOLD,marginLeft:3,alignSelf:"flex-start",marginTop:3}}/>;

function TrendLegend() {
  const sw = c => <i style={{width:8,height:8,borderRadius:2,background:c,display:"inline-block"}}/>;
  const item = { display:"inline-flex", alignItems:"center", gap:6 };
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:"6px 18px",fontSize:12,color:"var(--slate)",alignItems:"center",marginBottom:16}}>
      <span style={item}>{sw(TREND_UP)}Improved on last quarter</span>
      <span style={item}>{sw(TREND_DOWN)}Dipped</span>
      <span style={item}>{sw("var(--navy)")}Unchanged / first score</span>
      <span style={item}><i style={{width:5,height:5,borderRadius:"50%",background:PROV_GOLD,display:"inline-block"}}/>Provisional</span>
      <span style={item}>— Not started</span>
    </div>
  );
}

function QuarterStrip({ fy, items, showAvg, history=[], goalFn }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",columnGap:10,marginTop:12,paddingTop:10,borderTop:"1px solid var(--border)"}}>
      {GOAL_QUARTER_ORDER.map(q=>{
        const d = quarterDisplay(fy, q, items, showAvg, goalFn);
        let cell;
        if(d.upcoming && d.s.scored===0) cell = <span style={{color:"#b3bac6"}}>—</span>;
        else if(showAvg && d.c.pct!=null) {
          const cur = Math.round(d.c.pct);
          const prev = priorRoundedScore(fy, q, history, goalFn);
          const color = prev==null || prev===cur ? "var(--navy)" : cur>prev ? TREND_UP : TREND_DOWN;
          cell = <><span style={{color,fontWeight:600}}>{cur}%</span>{d.provisional && <ProvDot/>}</>;
        }
        else if(!showAvg && d.s.total>0) cell = <span style={{fontWeight:600,color:"var(--navy)"}}>{d.main}</span>;
        else cell = <span style={{color:"#b3bac6",fontSize:12.5}}>{d.main}</span>;
        return (
          <div key={q} title={quarterLabel(fy,q)} style={{minWidth:0}}>
            <div style={{fontSize:11,color:"var(--slate)",letterSpacing:.5}}>{q}</div>
            <div style={{height:20,marginTop:3,display:"flex",alignItems:"baseline",whiteSpace:"nowrap",fontSize:14.5,fontVariantNumeric:"tabular-nums"}}>{cell}</div>
          </div>
        );
      })}
    </div>
  );
}

// Four quarter cards inside a staff member; click one to see its engagements
function QuarterTiles({ fy, items, showAvg, onPick, goalFn }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:14}}>
      {GOAL_QUARTER_ORDER.map(q=>{
        const d = quarterDisplay(fy, q, items, showAvg, goalFn);
        const { s } = d;
        const clickable = s.total>0 || (showAvg && !!d.gs?.applicable);
        const parts = [];
        if(s.total>0) {
          if(d.upcoming && s.scored===0) parts.push(`${s.total} engagement${s.total>1?"s":""} pushed here`);
          else if(showAvg) {
            parts.push(`${s.scored} scored`);
            if(s.na) parts.push(`${s.na} N/A`);
            if(s.pending) parts.push(`${s.pending} open`);
          } else parts.push(`${s.total-s.pending} of ${s.total} submitted`);
        }
        return (
          <div key={q} className="card" onClick={clickable?()=>onPick(q):undefined}
            style={{cursor:clickable?"pointer":"default",opacity:clickable?1:.7}}>
            <div className="fxb" style={{alignItems:"baseline"}}>
              <span className="fw6" style={{fontSize:15}}>{q}</span>
              <span className="tx tsl">{quarterLabel(fy,q)}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10}}>
              <span style={{fontFamily:"'Playfair Display',serif",fontSize:28,color:d.muted?"var(--slate)":"var(--navy)"}}>{d.main}</span>
              {d.provisional && <ProvisionalTag/>}
            </div>
            <div className="tx tsl mt4">{[
              showAvg && d.c.pct!=null ? "Overall score" : null,
              ...parts,
              !parts.length ? (d.upcoming ? "Quarter not started" : showAvg && d.gs?.applicable ? "Goals only, no appraisals" : "No appraisals this quarter") : null,
            ].filter(Boolean).join(" · ")}</div>
            {showAvg && !d.upcoming && d.c.mode!=="none" && <QuarterMiniSplit c={d.c} s={s} gs={d.gs}/>}
          </div>
        );
      })}
    </div>
  );
}

function QuarterAppraisalList({ fy, quarter, items, showAvg, onOpen, rowActions, goalFn, afterScore }) {
  const qItems = items.filter(i=>i.fy===fy && i.quarter===quarter);
  const s = quarterAverage(qItems);
  const gs = showAvg && goalFn ? goalFn(fy, quarter) : null;
  return (
    <div>
      {showAvg && <OverallBreakdown quarter={quarter} s={s} gs={gs} c={combinedScore(s, gs)}/>}
      {afterScore}
      {qItems.length>0 && showAvg && <div style={{fontSize:11,fontWeight:600,letterSpacing:"1px",textTransform:"uppercase",color:"var(--slate)",margin:"18px 0 10px"}}>Engagement appraisals</div>}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {qItems.map(i=><AppraisalListRow key={i.r.key} r={i.r} ex={i.ex} onClick={()=>onOpen(i.r)} actions={rowActions?rowActions(i):null}/>)}
        {qItems.length===0 && <div className="es" style={{padding:"28px 24px"}}>No engagement appraisals in this quarter.</div>}
      </div>
    </div>
  );
}

// ── v55: collapsible sections. Open/closed is remembered in this browser only (a viewing convenience). ──
function useCollapsed(key, defaultCollapsed=true) {
  const lsKey = `msna_collapse_${key}`;
  const [collapsed, setCollapsed] = useState(()=>{
    try { const v = localStorage.getItem(lsKey); return v===null ? defaultCollapsed : v==="1"; } catch(e) { return defaultCollapsed; }
  });
  const toggle = () => setCollapsed(c=>{ const n=!c; try { localStorage.setItem(lsKey, n?"1":"0"); } catch(e) {} return n; });
  return [collapsed, toggle, setCollapsed];
}
// Clickable header row: whole row toggles, Enter/Space work too. Buttons inside should stopPropagation.
function CollapseHead({ collapsed, onToggle, children }) {
  return (
    <div role="button" tabIndex={0} aria-expanded={!collapsed} onClick={onToggle}
      onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); onToggle(); } }}
      style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",userSelect:"none",outline:"none"}}>
      <span title={collapsed?"Expand":"Collapse"} style={{flexShrink:0,marginTop:1,color:"var(--slate)",display:"inline-flex",transition:"transform .15s",transform:collapsed?"rotate(-90deg)":"none"}}><I n="chevron" s={20}/></span>
      <div style={{flex:1,minWidth:0}}>{children}</div>
    </div>
  );
}

// ── v55: PARTNERS' ONE-ON-ONE DISCUSSION ──
// Optional, held only in some cases after the quarter's appraisals and self-assessment. One record per staff member
// per quarter in the "one_on_ones" collection (id staffId-fy-quarter). Confidential: Firestore rules let only partners
// and that staff member read it, and only partners write it. Any partner can edit; each earlier version is kept in history.
// Not part of any score or gate.
const oneOnOneId = (staffId, fy, quarter) => `${staffId}-${fy}-${quarter}`;
function OneOnOneBox({ user, users=[], staffId, fy, quarter, record }) {
  const isP = user.role==="partner";
  const staff = users.find(u=>u.id===staffId);
  const nameOf = id => users.find(u=>u.id===id)?.name || "A partner";
  const [, qEnd] = quarterDateRange(fy, quarter);
  const ended = todayStr() > qEnd;
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [showHist, setShowHist] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, toggleCollapsed, setCollapsed] = useCollapsed("oneonone", true);
  useEffect(()=>{ setEditing(false); setErr(""); setShowHist(false); }, [staffId, fy, quarter]);

  if(!isP && !record) return null;                       // staff see the box only when a discussion was recorded
  if(isP && !record && !ended) return null;              // opens once the quarter is over

  const startEdit = () => { setDate(record?.date || todayStr()); setNotes(record?.notes || ""); setErr(""); setEditing(true); setCollapsed(false); };
  const save = async () => {
    if(!date) { setErr("Enter the date of the discussion."); return; }
    if(date>todayStr()) { setErr("The date can't be in the future."); return; }
    if(!notes.trim()) { setErr("Write what was discussed."); return; }
    if(record && record.date===date && record.notes===notes.trim()) { setEditing(false); return; }
    const now = new Date().toISOString();
    const id = oneOnOneId(staffId, fy, quarter);
    const next = record
      ? { ...record, date, notes:notes.trim(), updatedBy:user.id, updatedAt:now,
          history:[...(record.history||[]), { date:record.date, notes:record.notes, by:record.updatedBy||record.createdBy, at:record.updatedAt||record.createdAt }] }
      : { id, staffId, fy, quarter, date, notes:notes.trim(), createdBy:user.id, createdAt:now, updatedBy:user.id, updatedAt:now, history:[] };
    setSaving(true);
    await fsSet("one_on_ones", id, next);
    setSaving(false);
    // Audit records the action only, never the confidential notes.
    addAudit(user.id, user.name, record?"EDIT_ONE_ON_ONE":"ADD_ONE_ON_ONE", `${record?"Edited":"Recorded"} partners' one-on-one discussion for ${staff?.name||staffId}, ${quarter} FY ${fy}`);
    setEditing(false);
  };

  const edited = record && record.updatedAt && record.updatedAt!==record.createdAt;
  const hist = [...(record?.history||[])].reverse();
  return (
    <div className="card" style={{marginTop:14,padding:"16px 20px",borderColor:record?"var(--gold)":"var(--border)",borderStyle:record||editing?"solid":"dashed"}}>
      {(()=>{
        const head = (
          <div className="fxb" style={{alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
            <div style={{minWidth:0}}>
              <div className="card-title" style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><I n="users" s={17}/>Partners' one-on-one discussion
                {record&&<span className="bdg rpa" style={{fontWeight:500}}><I n="calendar" s={11}/>{fmtDate(record.date)}</span>}
              </div>
              <div className="tx tsl mt4" style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                <I n="lock" s={11}/>Confidential · visible only to partners and {isP?(staff?.name||"the staff member"):"you"}
                {isP&&!record&&!editing&&<span> · Record only if a one-on-one was held. It does not affect the score.</span>}
              </div>
              {record && collapsed && !editing && <div className="ts" style={{marginTop:8,color:"var(--slate)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{record.notes.split("\n")[0]}</div>}
            </div>
            {isP && !editing && <button className="btn bgh bsm" onClick={e=>{e.stopPropagation();startEdit();}}><I n={record?"edit":"plus"} s={13}/>{record?"Edit":"Record discussion"}</button>}
          </div>
        );
        return record && !editing ? <CollapseHead collapsed={collapsed} onToggle={toggleCollapsed}>{head}</CollapseHead> : head;
      })()}

      {editing ? (
        <div style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:14}}>
          {err&&<div className="err">{err}</div>}
          <div className="fg" style={{maxWidth:220}}><label className="fl">Date of discussion *</label>
            <input type="date" className="fi" value={date} max={todayStr()} onChange={e=>{setDate(e.target.value);setErr("");}}/>
          </div>
          <div className="fg"><label className="fl">What was discussed *</label>
            <textarea className="fta" style={{minHeight:140}} placeholder="Points discussed, feedback given, and anything agreed" value={notes} onChange={e=>{setNotes(e.target.value);setErr("");}}/>
          </div>
          {record&&<div className="tx tsl" style={{marginTop:-8,marginBottom:12}}>The current version is kept in the edit history.</div>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
            <button className="btn bgh" onClick={()=>setEditing(false)}>Cancel</button>
            <button className="btn bp" disabled={saving} onClick={save}><I n="check" s={15}/>{saving?"Saving...":"Save discussion"}</button>
          </div>
        </div>
      ) : record && !collapsed ? (
        <div style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10}}>
            <span className="tx tsl">Recorded by {nameOf(record.createdBy)}{edited?` · last edited by ${nameOf(record.updatedBy)} on ${fmtDate(record.updatedAt)}`:""}</span>
          </div>
          <div style={{fontSize:13.5,lineHeight:1.65,color:"var(--navy)",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{record.notes}</div>
          {isP && hist.length>0 && (
            <div style={{marginTop:12}}>
              <button className="btn bgh bxs" onClick={()=>setShowHist(h=>!h)}><I n="history" s={11}/>{showHist?"Hide":"Show"} edit history ({hist.length})</button>
              {showHist && (
                <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                  {hist.map((h,i)=>(
                    <div key={i} style={{background:"var(--cream)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px"}}>
                      <div className="tx tsl" style={{marginBottom:4}}>Version by {nameOf(h.by)}{h.at?` · ${fmtDate(h.at)}`:""} · discussion dated {fmtDate(h.date)}</div>
                      <div style={{fontSize:13,whiteSpace:"pre-wrap",wordBreak:"break-word",color:"var(--slate)"}}>{h.notes}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── v53: overall score building blocks ──
const OV_BLUE = "#1e5fa8", OV_GREEN = "#0f8a5f";
const fmtPts = x => { const r = Math.round(x*10)/10; return Number.isInteger(r) ? String(r) : r.toFixed(1); };
// Thin two-colour bar on a quarter tile: appraisal points and goal points.
function QuarterMiniSplit({ c, s, gs }) {
  const total = c.wA + c.wG || 100;
  const aw = c.decA ? (s.avg*c.wA/100) : 0;
  const gw = c.goalsApplicable ? gs.points : 0;
  return (
    <div style={{marginTop:10}}>
      <div style={{display:"flex",height:6,borderRadius:4,overflow:"hidden",background:"var(--cream)"}}>
        <div style={{width:`${aw/total*100}%`,background:OV_BLUE}}/>
        <div style={{width:`${gw/total*100}%`,background:OV_GREEN}}/>
      </div>
      <div className="tx tsl" style={{marginTop:5,display:"flex",gap:10,flexWrap:"wrap"}}>
        {c.apprApplicable && <span>Appraisal {s.avg!=null?`${Math.round(s.avg)}%`:"pending"}</span>}
        {c.goalsApplicable && <span>Goals {gs.achieved}/{gs.n}</span>}
      </div>
    </div>
  );
}

const GOAL_ROW_PILL = r => {
  if(!r.scored) return { cls:"bcl", label:"Optional · not scored" };
  if(r.state==="notset") return { cls:"bp2", label:"Not added yet" };
  if(r.state==="running") return { cls:"bcl", label:"Assessed after quarter end" };
  if(r.state==="toassess") return { cls:"brs", label:"Self-assessment pending" };
  if(r.state==="rejected") return { cls:"bp2", label:"Sent back to resubmit" };
  if(r.state==="review") return { cls:"brs", label:`${r.selfStatus==="Completed"?"Achieved":r.selfStatus} · awaiting partner` };
  if(r.selfStatus===GOAL_ACHIEVED) return { cls:"ba", label:"Achieved" };
  if(r.selfStatus==="WIP") return { cls:"brs", label:"WIP" };
  return { cls:"br", label:"Not achieved" };
};

// "How it's arrived at" panel for one staff member and quarter.
function OverallBreakdown({ quarter, s, gs, c }) {
  const [collapsed, toggleCollapsed] = useCollapsed("overallscore", true); // v55
  const both = c.mode==="both";
  const total = c.wA + c.wG || 100;
  const apprW = c.mode==="appraisalOnly" ? 100 : OVERALL_W_APPRAISAL;
  const goalW = c.mode==="goalsOnly" ? 100 : OVERALL_W_GOALS;
  const apprBar = c.decA ? s.avg*c.wA/100 : 0;
  const goalBar = c.goalsApplicable ? gs.points : 0;
  const Panel = ({ children }) => <div style={{background:"var(--cream)",borderRadius:10,padding:"12px 14px",border:"1px solid var(--border)",minWidth:0}}>{children}</div>;
  const Row = ({ l, r, strong, muted }) => (
    <div style={{display:"flex",justifyContent:"space-between",gap:10,padding:"7px 0",borderTop:"1px solid var(--border)",fontSize:13,fontWeight:strong?600:400,color:muted?"var(--slate-light)":"var(--navy)"}}>
      <span style={{minWidth:0}}>{l}</span><span style={{flexShrink:0,fontVariantNumeric:"tabular-nums"}}>{r}</span>
    </div>
  );
  const Head = ({ title, w, color }) => (
    <div className="fxb" style={{marginBottom:6}}>
      <span style={{display:"flex",alignItems:"center",gap:7,fontWeight:600,fontSize:13}}><i style={{width:9,height:9,borderRadius:2,background:color,display:"inline-block"}}/>{title}</span>
      <span className="bdg" style={{background:"#fff",border:"1px solid var(--border)",color:"var(--navy)"}}>{w}% weight</span>
    </div>
  );
  let note = null;
  if(c.mode==="appraisalOnly") note = `No goals apply this quarter (${gs?.reason||"not applicable"}), so the appraisal average counts 100%.`;
  else if(c.mode==="goalsOnly") note = "No engagement appraisals this quarter, so goals count 100%.";
  if(c.partial) note = `${note?note+" ":""}Until every appraisal and self-assessment is in, the score is worked out on the parts available so far, so open items don't pull it down.`;
  return (
    <div className="card" style={{padding:"16px 20px"}}>
      <CollapseHead collapsed={collapsed} onToggle={toggleCollapsed}>
      <div className="fxb" style={{alignItems:"flex-start",gap:16,flexWrap:"wrap",marginBottom:12}}>
        <div>
          <div className="card-title">{quarter} overall score: how it's arrived at</div>
          <div className="tx tsl mt4">Performance appraisal {OVERALL_W_APPRAISAL}% + goals {OVERALL_W_GOALS}%{collapsed?" · click to see the breakdown":""}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {c.pct!=null && (c.provisional ? <ProvisionalTag/> : <span className="bdg bac">Final</span>)}
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:30,lineHeight:1}}>{c.pct!=null?`${Math.round(c.pct)}%`:c.provisional?"Pending":"N/A"}</span>
        </div>
      </div>

      {c.mode!=="none" && <>
        <div style={{display:"flex",height:12,borderRadius:6,overflow:"hidden",background:"var(--cream)",border:"1px solid var(--border)"}}>
          <div style={{width:`${apprBar/total*100}%`,background:OV_BLUE}}/>
          <div style={{width:`${goalBar/total*100}%`,background:OV_GREEN}}/>
        </div>
        <div className="tx tsl" style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:6,marginBottom:14}}>
          {c.apprApplicable && <span>Appraisal {c.decA?fmtPts(c.apprPts):"pending"} of {OVERALL_W_APPRAISAL}</span>}
          {c.goalsApplicable && <span>Goals {fmtPts(c.goalPts)} of {OVERALL_W_GOALS}</span>}
        </div>
      </>}
      </CollapseHead>

      {!collapsed && <>

      <div style={{display:"grid",gridTemplateColumns:c.apprApplicable&&c.goalsApplicable?"minmax(0,1fr) minmax(0,1fr)":"minmax(0,1fr)",gap:12}}>
        {c.apprApplicable && (
          <Panel>
            <Head title="Performance appraisal" w={apprW} color={OV_BLUE}/>
            <Row l={`Engagements scored`} r={s.scored}/>
            {s.na>0 && <Row l="Marked N/A (left out)" r={s.na} muted/>}
            {s.pending>0 && <Row l="Still open" r={s.pending} muted/>}
            <Row l="Quarter average" r={s.avg!=null?`${fmtPts(s.avg)}%`:"Pending"} strong/>
            {both && <Row l={s.avg!=null?`${fmtPts(s.avg)}% × ${OVERALL_W_APPRAISAL}%`:`Average × ${OVERALL_W_APPRAISAL}%`} r={s.avg!=null?fmtPts(c.apprPts):"—"} strong/>}
          </Panel>
        )}
        {c.goalsApplicable && (
          <Panel>
            <Head title="Goals" w={goalW} color={OV_GREEN}/>
            {gs.rows.map(r=>{
              const pill = GOAL_ROW_PILL(r);
              return (
                <div key={r.sl} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"7px 0",borderTop:"1px solid var(--border)",fontSize:13}}>
                  <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:r.scored?"var(--navy)":"var(--slate-light)"}} title={r.desc}>{r.sl}. {r.desc||"Goal not set"}</span>
                  <span style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                    <span className={`bdg ${pill.cls}`}>{pill.label}</span>
                    <span title={r.scored&&r.decided&&!r.approved?"Counted from the self-assessment, awaiting partner approval":undefined} style={{width:30,textAlign:"right",fontVariantNumeric:"tabular-nums",color:r.scored&&r.approved?"var(--navy)":"var(--slate-light)",fontStyle:r.scored&&r.decided&&!r.approved?"italic":"normal"}}>{r.scored&&r.decided?fmtPts(r.points):"—"}</span>
                  </span>
                </div>
              );
            })}
            <Row l={`Each goal worth (${gs.n} scored goal${gs.n>1?"s":""})`} r={fmtPts(gs.each)}/>
            <Row l={`${gs.achieved} of ${gs.n} achieved${gs.final?"":" (provisional)"}`} r={`${fmtPts(gs.points)} of ${OVERALL_W_GOALS}`} strong/>
          </Panel>
        )}
      </div>

      {c.mode!=="none" && c.pct!=null && (
        <div style={{marginTop:12,padding:"10px 12px",borderRadius:8,background:"var(--cream)",fontSize:13,border:"1px solid var(--border)"}}>
          <span className="tsl">Overall = </span>
          {both && !c.partial && <span>{fmtPts(c.apprPts)} + {fmtPts(c.goalPts)} = {fmtPts(c.apprPts+c.goalPts)}, shown as <strong>{Math.round(c.pct)}%</strong></span>}
          {both && c.partial && <span>({c.decA?fmtPts(c.apprPts):"0"} + {fmtPts(c.goalPts)}) ÷ {fmtPts(c.decA+c.decG)} points counted so far × 100 = {fmtPts(c.pct)}, shown as <strong>{Math.round(c.pct)}%</strong></span>}
          {c.mode==="appraisalOnly" && <span>appraisal average {fmtPts(s.avg)}%, shown as <strong>{Math.round(c.pct)}%</strong></span>}
          {c.mode==="goalsOnly" && <span>{fmtPts(c.goalPts)} of {fmtPts(c.decG)} goal points{c.partial?" counted so far":""} = {fmtPts(c.pct)}%, shown as <strong>{Math.round(c.pct)}%</strong></span>}
        </div>
      )}
      {note && <div className="tx tsl" style={{marginTop:10,lineHeight:1.5}}>{note}</div>}
      {c.goalsApplicable && <div className="tx tsl" style={{marginTop:6,lineHeight:1.5}}>Goals count from the submitted self-assessment: Completed earns the goal's points, WIP and Zero progress earn 0. Points in grey italics are provisional until a partner approves that entry. A rejected entry drops out until it is resubmitted.{gs.rows.some(r=>!r.scored)?" The optional third goal is not scored.":""}</div>}
      </>}
    </div>
  );
}

function PerformanceAppraisal({ user, users=[], projects=[], setProjects, tss=[], appraisals=[], setAppraisals, goals=[], oneOnOnes=[] }) {
  const isP = user.role==="partner";
  const isMgr = user.role==="manager";
  const rawRequired = useMemo(()=>getRequiredAppraisals(users, projects, tss), [users, projects, tss]);
  const required = useMemo(()=>gateInactiveAppraisals(rawRequired, appraisals, users), [rawRequired, appraisals, users]);
  const allItems = useMemo(()=>appraisalItems(required, appraisals), [required, appraisals]);

  const fyList = [...new Set([currentFY(), ...allItems.map(i=>i.fy)])].sort().reverse();
  const [fy, setFy] = useState(currentFY());
  const [subView, setSubView] = useState("received");
  const [selStaff, setSelStaff] = useState(null);
  const [selQuarter, setSelQuarter] = useState(null);
  const [selReq, setSelReq] = useState(null);
  const [pushM, setPushM] = useState(null);   // { r } — engagement being pushed
  const [pushReason, setPushReason] = useState("");

  // ── Push a running fixed engagement to the next quarter (whole engagement, all staff on it) ──
  const canPush = r => r.type==="fixed" && (isP || (r.managers||[]).includes(user.id));
  const openOnEngagement = pid => allItems.filter(i=>i.r.type==="fixed" && i.r.projectId===pid && i.ex?.status!=="submitted");
  const doPush = () => {
    const r = pushM.r; const reason = pushReason.trim(); if(!reason) return;
    const to = nextQuarterOf(r.fy, r.quarter);
    setProjects(ps=>ps.map(p=>p.id!==r.projectId?p:{...p, appraisalQuarter:to,
      appraisalPushes:[...(p.appraisalPushes||[]), { action:"push", fromFy:r.fy, fromQuarter:r.quarter, toFy:to.fy, toQuarter:to.quarter, reason, by:user.id, at:new Date().toISOString() }]}));
    addAudit(user.id, user.name, "APPRAISAL_PUSH", `${r.projectCode||r.projectName}: ${r.quarter} FY ${r.fy} → ${to.quarter} FY ${to.fy} · ${reason}`);
    setPushM(null); setPushReason(""); setSelQuarter(null);
  };
  const doPullBack = r => {
    const last = [...(r.pushes||[])].reverse().find(h=>h.action==="push" && h.toFy===r.fy && h.toQuarter===r.quarter);
    if(!last) return;
    if(!window.confirm(`Move ${r.projectCode||r.projectName} back to ${last.fromQuarter} FY ${last.fromFy}? This applies to every staff member on the engagement.`)) return;
    setProjects(ps=>ps.map(p=>p.id!==r.projectId?p:{...p, appraisalQuarter:{fy:last.fromFy, quarter:last.fromQuarter},
      appraisalPushes:[...(p.appraisalPushes||[]), { action:"pullback", fromFy:r.fy, fromQuarter:r.quarter, toFy:last.fromFy, toQuarter:last.fromQuarter, by:user.id, at:new Date().toISOString() }]}));
    addAudit(user.id, user.name, "APPRAISAL_PULLBACK", `${r.projectCode||r.projectName}: ${r.quarter} FY ${r.fy} → ${last.fromQuarter} FY ${last.fromFy}`);
    setSelQuarter(null);
  };
  const rowActions = i => {
    const r = i.r;
    if(!canPush(r) || i.ex?.status==="submitted") return null;
    const pushedHere = (r.pushes||[]).some(h=>h.action==="push" && h.toFy===r.fy && h.toQuarter===r.quarter) && !(r.fy===r.baseFy && r.quarter===r.baseQuarter);
    const nq = nextQuarterOf(r.fy, r.quarter);
    const stop = fn => e => { e.stopPropagation(); fn(); };
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:8}}>
        <button className="btn bgh bxs" onClick={stop(()=>{setPushM({r});setPushReason("");})} title="Engagement still running? Move its appraisals to the next quarter">
          <I n="arrowright" s={11}/>Push to {nq.quarter}{nq.fy!==r.fy?` FY ${nq.fy}`:""}
        </button>
        {pushedHere && <button className="btn bgh bxs" onClick={stop(()=>doPullBack(r))}>Pull back</button>}
        {pushedHere && <span className="tx tsl">Pushed here from an earlier quarter</span>}
      </div>
    );
  };
  const pushModal = pushM && (() => {
    const r = pushM.r; const to = nextQuarterOf(r.fy, r.quarter);
    const moving = openOnEngagement(r.projectId);
    const names = [...new Set(moving.map(i=>users.find(u=>u.id===i.r.staffId)?.name).filter(Boolean))];
    const stayCount = allItems.filter(i=>i.r.type==="fixed" && i.r.projectId===r.projectId && i.ex?.status==="submitted").length;
    return (
      <div className="mo" onClick={()=>setPushM(null)}>
        <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:460}}>
          <div className="md-title">Push engagement to {to.quarter} FY {to.fy}</div>
          <div className="al al-i mb16"><I n="info" s={14}/><div>
            <strong>{r.projectName}</strong>{r.projectCode?` (${r.projectCode})`:""} moves from {r.quarter} FY {r.fy} to {to.quarter} FY {to.fy} for every staff member still to be appraised: {names.join(", ")||"—"}.
            {stayCount>0 && ` ${stayCount} appraisal${stayCount>1?"s":""} already submitted stay in the quarter they were submitted under.`}
          </div></div>
          <div className="fg"><label className="fl">Reason (mandatory)</label>
            <textarea className="fta" placeholder="e.g. Fieldwork continues till mid-November" value={pushReason} onChange={e=>setPushReason(e.target.value)}/>
          </div>
          <div className="md-actions">
            <button className="btn bgh" onClick={()=>setPushM(null)}>Cancel</button>
            <button className="btn bp" disabled={!pushReason.trim()} onClick={doPush}><I n="arrowright" s={14}/>Push to {to.quarter}</button>
          </div>
        </div>
      </div>
    );
  })();

  const fyItems = allItems.filter(i=>i.fy===fy);
  // v53: goal score for a staff member, used for the overall score (appraisal 70% + goals 30%)
  const goalFnFor = sid => (gfy, q) => goalScoreFor(gfy, q, sid, goals, users);
  const hasGoalQuarter = (sid, gfy) => GOAL_QUARTER_ORDER.some(q => goalScoreFor(gfy, q, sid, goals, users).applicable);
  // v55: partners' one-on-one discussion record for a staff member and quarter
  const oneOnOneFor = (sid, q) => oneOnOnes.find(o=>o.id===oneOnOneId(sid, fy, q));
  const receivedItems = fyItems.filter(i=>i.r.staffId===user.id);
  const givenItems = fyItems.filter(i=>i.r.staffId!==user.id && i.r.eligibleAppraisers.includes(user.id));

  const changeFy = v => { setFy(v); setSelStaff(null); setSelQuarter(null); };
  const onSave = (rec) => {
    setAppraisals(prev => prev.some(a=>a.id===rec.id) ? prev.map(a=>a.id===rec.id?rec:a) : [...prev, rec]);
    setSelReq(null);
  };

  if(selReq) {
    const existing = findAppraisalFor(appraisals, selReq);
    return <AppraisalForm user={user} users={users} req={selReq} existing={existing} onSave={onSave} onBack={()=>setSelReq(null)}/>;
  }

  // Breadcrumb: FY › Staff › Quarter
  const Crumbs = ({ staffName, rootLabel }) => (
    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:16}}>
      <button className="btn bgh bsm" onClick={()=>{ if(selQuarter) setSelQuarter(null); else setSelStaff(null); }}><I n="arrowleft" s={13}/>Back</button>
      <span className="ts tsl" style={{cursor:"pointer"}} onClick={()=>{setSelStaff(null);setSelQuarter(null);}}>{rootLabel} · FY {fy}</span>
      {staffName && <><span className="ts tsl">›</span><span className="ts" style={{cursor:selQuarter?"pointer":"default",fontWeight:selQuarter?400:600}} onClick={()=>setSelQuarter(null)}>{staffName}</span></>}
      {selQuarter && <><span className="ts tsl">›</span><span className="ts fw6">{selQuarter} · {quarterLabel(fy,selQuarter)}</span></>}
    </div>
  );

  // Staff → quarters → appraisals (used by partners, and by managers for their team without averages)
  const staffDrill = (items, showAvg, rootLabel) => {
    const u = users.find(x=>x.id===selStaff);
    const inactive = u?.active===false;
    const mine = items.filter(i=>i.r.staffId===selStaff);
    return (
      <div>
        <Crumbs staffName={<>{u?.name}{inactive&&<span className="bdg bcl" style={{marginLeft:8}}>Inactive</span>}</>} rootLabel={rootLabel}/>
        <div style={{opacity:inactive?.6:1}}>
          {!selQuarter
            ? <QuarterTiles fy={fy} items={mine} showAvg={showAvg} onPick={setSelQuarter} goalFn={goalFnFor(selStaff)}/>
            : <QuarterAppraisalList fy={fy} quarter={selQuarter} items={mine} showAvg={showAvg} onOpen={setSelReq} rowActions={rowActions} goalFn={goalFnFor(selStaff)}
                afterScore={isP ? <OneOnOneBox user={user} users={users} staffId={selStaff} fy={fy} quarter={selQuarter} record={oneOnOneFor(selStaff, selQuarter)}/> : null}/>}
        </div>
        {pushModal}
      </div>
    );
  };

  // v41: cards split into Managers and Interns & others, each sorted A–Z by name, separated by a line.
  const staffCards = (items, showAvg, showRole, history=[], extraIds=[]) => {
    const nameOf = sid => users.find(x=>x.id===sid)?.name || "";
    const staffIds = [...new Set([...items.map(i=>i.r.staffId), ...extraIds])].sort((a,b)=>nameOf(a).localeCompare(nameOf(b), undefined, {sensitivity:"base"}));
    const groups = [
      { key:"mgr",   label:"Managers",          ids: staffIds.filter(sid=>users.find(x=>x.id===sid)?.role==="manager") },
      { key:"other", label:"Interns & others",  ids: staffIds.filter(sid=>users.find(x=>x.id===sid)?.role!=="manager") },
    ].filter(g=>g.ids.length>0);
    const showHeaders = groups.length>1 || showRole;
    const card = sid => {
      const u = users.find(x=>x.id===sid);
      const inactive = u?.active===false;
      const mine = items.filter(i=>i.r.staffId===sid);
      const done = mine.filter(i=>i.ex?.status==="submitted").length;
      return (
        <div key={sid} className="card" style={{cursor:"pointer",opacity:inactive?.55:1,padding:"16px 18px"}} onClick={()=>{setSelStaff(sid);setSelQuarter(null);}}>
          <div className="fw6" style={{fontSize:15}}>{u?.name}{inactive&&<span className="bdg bcl" style={{marginLeft:8}}>Inactive</span>}</div>
          <div className="tsl" style={{fontSize:12.5,marginTop:3}}>{showRole&&u?.role?`${u.role.charAt(0).toUpperCase()+u.role.slice(1)} · `:""}{mine.length?`${done} of ${mine.length} complete`:"Goals only"}</div>
          <QuarterStrip fy={fy} items={mine} showAvg={showAvg} history={history.filter(i=>i.r.staffId===sid)} goalFn={showAvg?goalFnFor(sid):null}/>
        </div>
      );
    };
    if(staffIds.length===0) return <div className="es">{isP?"No appraisals required yet for this financial year.":"You have no team appraisals to give this financial year."}</div>;
    return (
      <div>
        {groups.map((g,gi)=>(
          <div key={g.key}>
            {gi>0 && <div style={{height:1,background:"var(--border)",margin:"26px 0 20px"}}/>}
            {showHeaders && (
              <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:12}}>
                <span style={{fontSize:11,fontWeight:600,letterSpacing:"1px",textTransform:"uppercase",color:"var(--slate)"}}>{g.label}</span>
                <span className="tx tsl">{g.ids.length}</span>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:14}}>
              {g.ids.map(card)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if(isP) {
    if(selStaff) return staffDrill(fyItems, true, "All staff");
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",columnGap:16}}>
          <FYSelector fy={fy} setFy={changeFy} options={fyList}/>
          <TrendLegend/>
        </div>
        {staffCards(fyItems, true, true, allItems, users.filter(u=>u.role!=="partner" && hasGoalQuarter(u.id, fy)).map(u=>u.id))}
      </div>
    );
  }

  if(isMgr && subView==="given" && selStaff) return staffDrill(givenItems, false, "My team");

  return (
    <div>
      <FYSelector fy={fy} setFy={changeFy} options={fyList}/>
      {isMgr && (
        <div className="tabs">
          <div className={`tab ${subView==="received"?"active":""}`} onClick={()=>{setSubView("received");setSelStaff(null);setSelQuarter(null);}}>Feedback I've received</div>
          <div className={`tab ${subView==="given"?"active":""}`} onClick={()=>{setSubView("given");setSelStaff(null);setSelQuarter(null);}}>Feedback I've given</div>
        </div>
      )}
      {subView==="received" && (
        !selQuarter ? (
          receivedItems.length===0 && !hasGoalQuarter(user.id, fy)
            ? <div className="es">No appraisals for this financial year yet.</div>
            : <QuarterTiles fy={fy} items={receivedItems} showAvg={true} onPick={setSelQuarter} goalFn={goalFnFor(user.id)}/>
        ) : (
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
              <button className="btn bgh bsm" onClick={()=>setSelQuarter(null)}><I n="arrowleft" s={13}/>All quarters</button>
              <span className="ts fw6">{selQuarter} · {quarterLabel(fy,selQuarter)}</span>
            </div>
            <QuarterAppraisalList fy={fy} quarter={selQuarter} items={receivedItems} showAvg={true} onOpen={setSelReq} goalFn={goalFnFor(user.id)}
              afterScore={<OneOnOneBox user={user} users={users} staffId={user.id} fy={fy} quarter={selQuarter} record={oneOnOneFor(user.id, selQuarter)}/>}/>
          </div>
        )
      )}
      {isMgr && subView==="given" && staffCards(givenItems, false, false)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// GOAL SETTING
// ══════════════════════════════════════════════════════════════
function GoalQuarterCard({ fy, quarter, staffId, viewer, users, record, onSave }) {
  const [qStart, qEnd] = quarterDateRange(fy, quarter);
  const today = todayStr();
  const started = today >= qStart;
  const ended = today > qEnd;
  const isPartnerViewer = viewer.role==="partner";
  const isSelf = viewer.id===staffId;

  const rec = record || blankGoalRecord(fy, quarter, staffId);
  const goalSubmitted = rec.goalStatus==="submitted";
  const assessSubmitted = rec.assessStatus==="submitted";
  const goalPhase = phaseReviewStatus(rec.goals, goalSubmitted);     // null | "submitted" | "resubmission" | "approved"
  const assessPhase = phaseReviewStatus(rec.assess, assessSubmitted);

  const [goals, setGoals] = useState(rec.goals);
  const [assess, setAssess] = useState(rec.assess);
  const [goalRejectNotes, setGoalRejectNotes] = useState({});
  const [assessRejectNotes, setAssessRejectNotes] = useState({});
  const [goalChanging, setGoalChanging] = useState({});
  const [assessChanging, setAssessChanging] = useState({});
  const [goalErr, setGoalErr] = useState("");
  const staffUser = users.find(u=>u.id===staffId);
  const goalRole = goalRoleOf(rec, staffUser);
  const reqGoals = requiredGoalCount(fy, quarter, goalRole);
  const hasOptionalGoal = reqGoals<3;
  const goalWeight = Math.round(OVERALL_W_GOALS/reqGoals); // v53: points each scored goal carries in the overall score
  const missingGoals = goalMissingCount(rec, staffUser);   // v53: manager submitted 2 goals before goal 3 became mandatory
  const [newGoal, setNewGoal] = useState({ desc:"", steps:"" });
  const [newGoalErr, setNewGoalErr] = useState("");
  const isFilled = g => !!(g.desc||"").trim() && !!(g.steps||"").trim();
  const isBlank  = g => !(g.desc||"").trim() && !(g.steps||"").trim();

  // v45: reset local edits only when the SAVED content of this card changes. The Firestore listener hands back
  // new objects on every snapshot (anyone's save, metadata updates), and resetting on object identity was wiping
  // text people were still typing. Goals and self-assessment are tracked separately so one doesn't reset the other.
  const savedGoalsKey = JSON.stringify(rec.goals||[]);
  const savedAssessKey = JSON.stringify(rec.assess||[]);
  useEffect(()=>{ setGoals(rec.goals); setGoalErr(""); }, [savedGoalsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ setAssess(rec.assess); }, [savedAssessKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if(!started) {
    return (
      <div className="card" style={{opacity:.55}}>
        <div className="fxb"><span className="fw6">{quarter} · {quarterLabel(fy,quarter)}</span><span className="tx tsl">Opens {fmtDate(qStart)}</span></div>
      </div>
    );
  }

  const submitInitialGoals = (status) => {
    setGoalErr("");
    if(status==="submitted") {
      const missing = goals.filter(g=>g.sl<=reqGoals && !isFilled(g)).map(g=>g.sl);
      if(missing.length) { setGoalErr(`Goal ${missing.join(" and ")} ${missing.length>1?"need":"needs"} a description and steps before submitting.`); return; }
      const partial = goals.filter(g=>g.sl>reqGoals && !isFilled(g) && !isBlank(g)).map(g=>g.sl);
      if(partial.length) { setGoalErr(`Goal ${partial.join(", ")} is optional. Fill in both the goal and its steps, or clear it completely.`); return; }
    }
    // Blank optional goals are dropped on submission (and their self-assessment slots with them).
    const kept = status==="submitted" ? goals.filter(g=>g.sl<=reqGoals || isFilled(g)) : goals;
    const nextGoals = status==="submitted" ? kept.map(g=>({...g, reviewStatus:"pending", reviewRemark:null})) : kept;
    const keptSl = nextGoals.map(g=>g.sl);
    const nextAssess = status==="submitted" ? (rec.assess||[]).filter(a=>keptSl.includes(a.sl)) : rec.assess;
    onSave({ ...rec, goals:nextGoals, assess:nextAssess, goalStatus:status, goalSubmittedAt: status==="submitted" ? new Date().toISOString() : rec.goalSubmittedAt,
      ...(status==="submitted" ? { goalRole } : {}) });
  };
  // v53: add the missing mandatory goal(s) to an already-submitted record. Goes to the partner for review like any goal.
  const addMissingGoal = () => {
    setNewGoalErr("");
    if(!newGoal.desc.trim() || !newGoal.steps.trim()) { setNewGoalErr("Fill in both the goal and the steps to achieve it."); return; }
    const used = (rec.goals||[]).filter(g=>(g.desc||"").trim()).map(g=>g.sl);
    const sl = [1,2,3].find(x=>!used.includes(x));
    if(!sl) return;
    const goalsNext = [...(rec.goals||[]).filter(g=>g.sl!==sl), { sl, desc:newGoal.desc.trim(), steps:newGoal.steps.trim(), reviewStatus:"pending", reviewRemark:null, addedLater:true, addedAt:new Date().toISOString() }].sort((a,b)=>a.sl-b.sl);
    const assessNext = (rec.assess||[]).some(a=>a.sl===sl) ? rec.assess : [...(rec.assess||[]), { sl, status:"", remark:"" }].sort((a,b)=>a.sl-b.sl);
    onSave({ ...rec, goals:goalsNext, assess:assessNext, goalRole });
    addAudit(viewer.id, viewer.name, "GOAL_ADDED", `Added goal ${sl} for ${quarter} FY ${fy} (now mandatory for managers)`);
    setNewGoal({ desc:"", steps:"" });
  };
  const resubmitGoal = (sl) => {
    const item = goals.find(g=>g.sl===sl);
    if(!item.desc.trim()||!item.steps.trim()) { alert("This goal needs a description and steps before resubmitting."); return; }
    const persisted = rec.goals.find(g=>g.sl===sl);
    const history = [...(persisted.reviewHistory||[]), { status:persisted.reviewStatus, remark:persisted.reviewRemark, reviewedBy:persisted.reviewedBy, reviewedAt:persisted.reviewedAt }];
    onSave({ ...rec, goals: rec.goals.map(g=>g.sl===sl ? { ...item, reviewStatus:"pending", reviewRemark:null, reviewHistory:history } : g) });
  };
  // Partner decisions (approve/reject) are reversible — any prior decision is preserved in history, never overwritten silently.
  const decideGoal = (sl, decision) => {
    const note = (goalRejectNotes[sl]||"").trim();
    if(decision==="rejected" && !note) { alert("A remark is required when rejecting a goal."); return; }
    const persisted = rec.goals.find(g=>g.sl===sl);
    const prevWasDecided = persisted.reviewStatus==="approved" || persisted.reviewStatus==="rejected";
    const history = prevWasDecided ? [...(persisted.reviewHistory||[]), { status:persisted.reviewStatus, remark:persisted.reviewRemark, reviewedBy:persisted.reviewedBy, reviewedAt:persisted.reviewedAt }] : (persisted.reviewHistory||[]);
    onSave({ ...rec, goals: rec.goals.map(g=>g.sl===sl ? { ...g, reviewStatus:decision, reviewedBy:viewer.id, reviewedAt:new Date().toISOString(), reviewRemark:note||null, reviewHistory:history } : g) });
    setGoalRejectNotes(prev=>({...prev,[sl]:""}));
    setGoalChanging(prev=>({...prev,[sl]:false}));
  };

  const submitInitialAssess = () => {
    if(assess.some(a=>!a.status)) { alert("Please select a status for each goal before submitting your self-assessment."); return; }
    onSave({ ...rec, assess: assess.map(a=>({...a, reviewStatus:"pending", reviewRemark:null})), assessStatus:"submitted", assessSubmittedAt:new Date().toISOString() });
  };
  const resubmitAssess = (sl) => {
    const item = assess.find(a=>a.sl===sl);
    if(!item.status) { alert("Please select a status before resubmitting."); return; }
    const persisted = rec.assess.find(a=>a.sl===sl) || { sl };
    const wasDecided = persisted.reviewStatus==="approved" || persisted.reviewStatus==="rejected";
    const history = wasDecided ? [...(persisted.reviewHistory||[]), { status:persisted.reviewStatus, remark:persisted.reviewRemark||null, reviewedBy:persisted.reviewedBy||null, reviewedAt:persisted.reviewedAt||null }] : (persisted.reviewHistory||[]);
    const nextItem = { ...item, reviewStatus:"pending", reviewRemark:null, reviewHistory:history };
    onSave({ ...rec, assess: rec.assess.some(a=>a.sl===sl) ? rec.assess.map(a=>a.sl===sl ? nextItem : a) : [...rec.assess, nextItem] });
  };
  const decideAssess = (sl, decision) => {
    const note = (assessRejectNotes[sl]||"").trim();
    if(decision==="rejected" && !note) { alert("A remark is required when rejecting a self-assessment entry."); return; }
    const persisted = rec.assess.find(a=>a.sl===sl);
    const prevWasDecided = persisted.reviewStatus==="approved" || persisted.reviewStatus==="rejected";
    const history = prevWasDecided ? [...(persisted.reviewHistory||[]), { status:persisted.reviewStatus, remark:persisted.reviewRemark, reviewedBy:persisted.reviewedBy, reviewedAt:persisted.reviewedAt }] : (persisted.reviewHistory||[]);
    onSave({ ...rec, assess: rec.assess.map(a=>a.sl===sl ? { ...a, reviewStatus:decision, reviewedBy:viewer.id, reviewedAt:new Date().toISOString(), reviewRemark:note||null, reviewHistory:history } : a) });
    setAssessRejectNotes(prev=>({...prev,[sl]:""}));
    setAssessChanging(prev=>({...prev,[sl]:false}));
  };

  const goalBadge = !goalSubmitted ? {cls:"brs",label:"Goals in draft"} : goalPhase==="resubmission" ? {cls:"bp2",label:"Resubmission needed"} : goalPhase==="submitted" ? {cls:"brs",label:"Pending review"} : {cls:"bac",label:"Goals approved"};
  const assessBadge = !assessSubmitted ? {cls:"brs",label:"Not submitted"} : assessPhase==="resubmission" ? {cls:"bp2",label:"Resubmission needed"} : assessPhase==="submitted" ? {cls:"brs",label:"Pending review"} : {cls:"bac",label:"Approved"};

  return (
    <div className="card">
      <div className="fxb mb16">
        <span className="fw6">{quarter} · {quarterLabel(fy,quarter)}</span>
        <span className={`bdg ${goalBadge.cls}`}>{goalBadge.label}</span>
      </div>

      {isPartnerViewer && !goalSubmitted && <p className="tx tsl mb8">Waiting for {users.find(u=>u.id===rec.staffId)?.name} to set and submit these goals.</p>}

      {isSelf && !goalSubmitted && hasOptionalGoal && (
        <div className="al al-i">
          <I n="info" s={15}/>
          <div><strong>Tip:</strong> Goals 1 and 2 are mandatory and carry {goalWeight}% each of your overall score. Goal 3 is optional and is not scored: fill in both the goal and its steps, or leave it fully blank.</div>
        </div>
      )}
      {isSelf && !goalSubmitted && !hasOptionalGoal && isQuarterOnOrAfter(fy, quarter, GOAL_OPTIONAL_THIRD_FROM) && (
        <div className="al al-i">
          <I n="info" s={15}/>
          <div><strong>Tip:</strong> All 3 goals are mandatory for managers and carry {goalWeight}% each of your overall score.</div>
        </div>
      )}
      {!goalSubmitted && (
        <div className="tw">
          <table>
            <thead><tr><th style={{width:36}}>#</th><th>Goal</th><th>Steps to achieve it</th></tr></thead>
            <tbody>
              {goals.map(g=>{
                const optional = g.sl>reqGoals;
                return (
                <tr key={g.sl}>
                  <td style={{verticalAlign:"top",paddingTop:16}}>
                    {g.sl}{hasOptionalGoal&&!optional&&<span className="tdn" title="Mandatory"> *</span>}
                    {optional&&<div className="tx tsl" style={{fontSize:10,marginTop:2}}>Optional · not scored</div>}
                  </td>
                  <td>{isSelf
                    ? <textarea className="fta" style={{minHeight:44}} placeholder={optional?"Optional":""} value={g.desc} onChange={e=>{setGoalErr("");setGoals(prev=>prev.map(x=>x.sl===g.sl?{...x,desc:e.target.value}:x));}}/>
                    : <div style={{padding:"6px 0"}}>{g.desc||(optional?<span className="tsl">Not set (optional)</span>:"—")}</div>}</td>
                  <td>{isSelf
                    ? <textarea className="fta" style={{minHeight:44}} placeholder={optional?"Optional":""} value={g.steps} onChange={e=>{setGoalErr("");setGoals(prev=>prev.map(x=>x.sl===g.sl?{...x,steps:e.target.value}:x));}}/>
                    : <div style={{padding:"6px 0"}}>{g.steps||(optional?"":"—")}</div>}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isSelf && !goalSubmitted && (
        <>
          {goalErr && <div className="err" style={{marginTop:12,marginBottom:0}}>{goalErr}</div>}
          <div className="fxb" style={{marginTop:12,flexWrap:"wrap",gap:10}}>
            <span className="tx tsl">
              {goals.filter(g=>g.sl<=reqGoals&&isFilled(g)).length} of {reqGoals} required goals filled
              {hasOptionalGoal && goals.some(g=>g.sl>reqGoals&&isFilled(g)) && " · optional goal 3 added"}
            </span>
            <div className="fx g8">
              <button className="btn bgh bsm" onClick={()=>submitInitialGoals("draft")}>Save draft</button>
              <button className="btn bp bsm" onClick={()=>submitInitialGoals("submitted")}>Submit & lock</button>
            </div>
          </div>
        </>
      )}

      {goalSubmitted && (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {goals.map(g=>{
            const persisted = rec.goals.find(x=>x.sl===g.sl) || g;
            const status = persisted.reviewStatus || "pending";
            const rowEditable = isSelf && status==="rejected";
            return (
              <div key={g.sl} style={{padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
                <div className="ts fw6">Goal {g.sl}{g.sl>reqGoals&&<span className="bdg bcl" style={{marginLeft:8,fontWeight:400}}>Optional · not scored</span>}{persisted.addedLater&&<span className="bdg bac" style={{marginLeft:8,fontWeight:400}}>Added later</span>}</div>
                {rowEditable ? (
                  <>
                    <textarea className="fta" style={{marginTop:6,minHeight:44}} value={g.desc} onChange={e=>setGoals(prev=>prev.map(x=>x.sl===g.sl?{...x,desc:e.target.value}:x))} placeholder="Goal description"/>
                    <textarea className="fta" style={{marginTop:6,minHeight:44}} value={g.steps} onChange={e=>setGoals(prev=>prev.map(x=>x.sl===g.sl?{...x,steps:e.target.value}:x))} placeholder="Steps to achieve it"/>
                  </>
                ) : (
                  <>
                    <div className="tx" style={{marginTop:4}}>{g.desc||"—"}</div>
                    <div className="tx tsl" style={{marginTop:2}}>{g.steps||"—"}</div>
                  </>
                )}
                <div style={{marginTop:8}}>
                  {status==="approved" && (
                    <div>
                      <span className="bdg bac">Approved</span>
                      {persisted.reviewRemark && <span className="tx tsl" style={{marginLeft:8}}>{persisted.reviewRemark}</span>}
                      {isPartnerViewer && !goalChanging[g.sl] && <button className="btn bgh bxs" style={{marginLeft:8}} onClick={()=>setGoalChanging(p=>({...p,[g.sl]:true}))}><I n="edit" s={12}/>Change decision</button>}
                    </div>
                  )}
                  {status==="rejected" && (
                    <div>
                      <span className="bdg bp2">Rejected</span>
                      <span className="tx tsl" style={{marginLeft:8}}>{persisted.reviewRemark}</span>
                      {isSelf && <div style={{marginTop:8}}><button className="btn bp bxs" onClick={()=>resubmitGoal(g.sl)}>Resubmit</button></div>}
                      {isPartnerViewer && !goalChanging[g.sl] && <div style={{marginTop:6}}><button className="btn bgh bxs" onClick={()=>setGoalChanging(p=>({...p,[g.sl]:true}))}><I n="edit" s={12}/>Change decision</button></div>}
                    </div>
                  )}
                  {status==="pending" && isSelf && <span className="tx tsl">Awaiting partner review</span>}
                  {((status==="pending" && isPartnerViewer) || (isPartnerViewer && goalChanging[g.sl])) && (
                    <div>
                      <textarea className="fta" style={{minHeight:34}} placeholder="Remark — optional if approving, required if rejecting"
                        value={goalRejectNotes[g.sl]||""} onChange={e=>setGoalRejectNotes(prev=>({...prev,[g.sl]:e.target.value}))}/>
                      <div style={{display:"flex",gap:8,marginTop:6}}>
                        <button className="btn bsc bxs" onClick={()=>decideGoal(g.sl,"approved")}>Approve</button>
                        <button className="btn bd bxs" onClick={()=>decideGoal(g.sl,"rejected")}>Reject</button>
                        {goalChanging[g.sl] && <button className="btn bgh bxs" onClick={()=>setGoalChanging(p=>({...p,[g.sl]:false}))}>Cancel</button>}
                      </div>
                    </div>
                  )}
                  {persisted.reviewHistory?.length>0 && (
                    <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid var(--border)"}}>
                      {persisted.reviewHistory.map((h,i)=>(
                        <div key={i} className="tx tsl" style={{fontSize:12,marginTop:2}}>
                          Previously {h.status} by {users.find(u=>u.id===h.reviewedBy)?.name||"—"}{h.remark?`: "${h.remark}"`:""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {hasOptionalGoal && goals.length<3 && <div className="tx tsl">Goal 3 not set (optional).</div>}
          {missingGoals>0 && (
            <div style={{padding:"12px 14px",borderRadius:10,border:"1.5px dashed #c4b5fd",background:"#faf5ff"}}>
              <div className="ts fw6" style={{color:"#6d28d9"}}>Goal 3 is now mandatory for managers</div>
              <div className="tx tsl" style={{marginTop:4,lineHeight:1.5}}>From {GOAL_OPTIONAL_THIRD_FROM.quarter} FY {GOAL_OPTIONAL_THIRD_FROM.fy}, managers set 3 goals at {goalWeight}% each of the overall score. {isSelf?"Add your third goal below. It goes to a partner for review like the others.":`Waiting for ${staffUser?.name||"them"} to add a third goal. Until then it scores 0.`}</div>
              {isSelf && <>
                {newGoalErr && <div className="err" style={{marginTop:10,marginBottom:0}}>{newGoalErr}</div>}
                <textarea className="fta" style={{marginTop:10,minHeight:44,background:"#fff"}} placeholder="Goal 3" value={newGoal.desc} onChange={e=>{setNewGoalErr("");setNewGoal(v=>({...v,desc:e.target.value}));}}/>
                <textarea className="fta" style={{marginTop:6,minHeight:44,background:"#fff"}} placeholder="Steps to achieve it" value={newGoal.steps} onChange={e=>{setNewGoalErr("");setNewGoal(v=>({...v,steps:e.target.value}));}}/>
                <button className="btn bp bsm" style={{marginTop:8}} onClick={addMissingGoal}><I n="send" s={13}/>Submit goal 3 for review</button>
              </>}
            </div>
          )}
        </div>
      )}

      {!ended && <p className="tx tsl mt16">Self-assessment opens once this quarter ends ({fmtDate(qEnd)}).</p>}
      {ended && (
        <div style={{marginTop:18,paddingTop:14,borderTop:"1px solid var(--border)"}}>
          <div className="fxb mb8">
            <span className="fw6" style={{fontSize:13}}>Self-assessment</span>
            <span className={`bdg ${assessBadge.cls}`}>{assessBadge.label}</span>
          </div>
          {isPartnerViewer && !assessSubmitted && <p className="tx tsl mb8">Waiting for {users.find(u=>u.id===rec.staffId)?.name} to complete their self-assessment.</p>}

          {goals.map(g=>{
            const a = assess.find(x=>x.sl===g.sl) || {sl:g.sl,status:"",remark:""};
            const persistedA = rec.assess.find(x=>x.sl===g.sl) || a;
            const aStatus = assessSubmitted ? (persistedA.reviewStatus || "pending") : null;
            const unanswered = assessSubmitted && !persistedA.status; // v53: goal added after the self-assessment went in
            const rowEditable = !assessSubmitted || (isSelf && (aStatus==="rejected" || unanswered));
            return (
              <div key={g.sl} style={{padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
                <div className="ts fw6">{g.sl}. {g.desc||"—"}{g.sl>reqGoals&&<span className="bdg bcl" style={{marginLeft:8,fontWeight:400}}>Not scored</span>}</div>
                <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                  {["Completed","WIP","Zero progress"].map(s=>(
                    <button key={s} type="button" disabled={!rowEditable} className={`btn bxs ${a.status===s?"bp":"bgh"}`}
                      onClick={()=>setAssess(prev=>{
                        const exists = prev.find(x=>x.sl===g.sl);
                        return exists ? prev.map(x=>x.sl===g.sl?{...x,status:s}:x) : [...prev,{sl:g.sl,status:s,remark:""}];
                      })}>{s}</button>
                  ))}
                </div>
                <textarea className="fta" style={{marginTop:8,minHeight:36}} disabled={!rowEditable} placeholder="Remark (optional)"
                  value={a.remark||""} onChange={e=>setAssess(prev=>{
                    const exists = prev.find(x=>x.sl===g.sl);
                    return exists ? prev.map(x=>x.sl===g.sl?{...x,remark:e.target.value}:x) : [...prev,{sl:g.sl,status:"",remark:e.target.value}];
                  })}/>
                {assessSubmitted && (
                  <div style={{marginTop:8}}>
                    {aStatus==="approved" && (
                      <div>
                        <span className="bdg bac">Approved</span>
                        {persistedA.reviewRemark && <span className="tx tsl" style={{marginLeft:8}}>{persistedA.reviewRemark}</span>}
                        {isPartnerViewer && !assessChanging[g.sl] && <button className="btn bgh bxs" style={{marginLeft:8}} onClick={()=>setAssessChanging(p=>({...p,[g.sl]:true}))}><I n="edit" s={12}/>Change decision</button>}
                      </div>
                    )}
                    {aStatus==="rejected" && (
                      <div>
                        <span className="bdg bp2">Rejected</span>
                        <span className="tx tsl" style={{marginLeft:8}}>{persistedA.reviewRemark}</span>
                        {isSelf && <div style={{marginTop:8}}><button className="btn bp bxs" onClick={()=>resubmitAssess(g.sl)}>Resubmit</button></div>}
                        {isPartnerViewer && !assessChanging[g.sl] && <div style={{marginTop:6}}><button className="btn bgh bxs" onClick={()=>setAssessChanging(p=>({...p,[g.sl]:true}))}><I n="edit" s={12}/>Change decision</button></div>}
                      </div>
                    )}
                    {aStatus==="pending" && isSelf && !unanswered && <span className="tx tsl">Awaiting partner review</span>}
                    {unanswered && isSelf && <div><button className="btn bp bxs" onClick={()=>resubmitAssess(g.sl)}>Submit</button></div>}
                    {unanswered && isPartnerViewer && <span className="tx tsl">Waiting for a status on this goal</span>}
                    {((aStatus==="pending" && isPartnerViewer && !unanswered) || (isPartnerViewer && assessChanging[g.sl])) && (
                      <div>
                        <textarea className="fta" style={{minHeight:34}} placeholder="Remark — optional if approving, required if rejecting"
                          value={assessRejectNotes[g.sl]||""} onChange={e=>setAssessRejectNotes(prev=>({...prev,[g.sl]:e.target.value}))}/>
                        <div style={{display:"flex",gap:8,marginTop:6}}>
                          <button className="btn bsc bxs" onClick={()=>decideAssess(g.sl,"approved")}>Approve</button>
                          <button className="btn bd bxs" onClick={()=>decideAssess(g.sl,"rejected")}>Reject</button>
                          {assessChanging[g.sl] && <button className="btn bgh bxs" onClick={()=>setAssessChanging(p=>({...p,[g.sl]:false}))}>Cancel</button>}
                        </div>
                      </div>
                    )}
                    {persistedA.reviewHistory?.length>0 && (
                      <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid var(--border)"}}>
                        {persistedA.reviewHistory.map((h,i)=>(
                          <div key={i} className="tx tsl" style={{fontSize:12,marginTop:2}}>
                            Previously {h.status} by {users.find(u=>u.id===h.reviewedBy)?.name||"—"}{h.remark?`: "${h.remark}"`:""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {isSelf && !assessSubmitted && (
            <div className="md-actions" style={{justifyContent:"flex-start",marginTop:12}}>
              <button className="btn bp bsm" onClick={submitInitialAssess}>Submit self-assessment</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GoalSetting({ user, users=[], goals=[], setGoals }) {
  const isP = user.role==="partner";
  const fyList = [...new Set([currentFY(), ...goals.map(g=>g.fy)])].sort().reverse();
  const [fy, setFy] = useState(currentFY());
  const [selStaff, setSelStaff] = useState(null);

  const quartersForFY = GOAL_QUARTER_ORDER.filter(q=>isQuarterInGoalScheme(fy,q));
  const onSave = (rec) => setGoals(prev => prev.some(g=>g.id===rec.id) ? prev.map(g=>g.id===rec.id?rec:g) : [...prev, rec]);

  const renderStaffPage = (staffId) => {
    const su = users.find(x=>x.id===staffId);
    const quarters = quartersForFY.filter(q => !goalQuarterExclusion(su, fy, q, goals));
    const graceQ = quartersForFY.find(q => goalQuarterExclusion(su, fy, q, goals)==="grace");
    const jd = effectiveJoinDate(su);
    const nextQ = graceQ ? quarters.find(q => GOAL_QUARTER_ORDER.indexOf(q) > GOAL_QUARTER_ORDER.indexOf(graceQ)) : null;
    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {graceQ && (
          <div className="card" style={{padding:"10px 14px",borderLeft:"3px solid var(--border)"}}>
            <span className="tx tsl">{staffId===user.id?"You":"They"} joined on {fmtDate(jd)}, within the last {GOAL_GRACE_DAYS} days of {graceQ} ({quarterLabel(fy,graceQ)}), so goal setting for {graceQ} is skipped.{nextQ ? ` Goals start from ${nextQ}.` : " Goals start from the next quarter."}</span>
          </div>
        )}
        {quarters.length===0 && <div className="es">No applicable quarters for this staff member in FY {fy}.</div>}
        {quarters.map(q => {
          const rec = goals.find(g=>g.fy===fy&&g.quarter===q&&g.staffId===staffId);
          return <GoalQuarterCard key={q} fy={fy} quarter={q} staffId={staffId} viewer={user} users={users} record={rec} onSave={onSave}/>;
        })}
      </div>
    );
  };

  if(isP) {
    const staffList = users.filter(u => {
      if(u.role==="partner") return false;
      const hasApplicableQuarter = quartersForFY.some(q => !goalQuarterExclusion(u, fy, q, goals));
      if(hasApplicableQuarter) return true;
      return goals.some(g=>g.fy===fy && g.staffId===u.id); // no quarter left this FY, but keep if a record exists (historical)
    });
    if(!selStaff) {
      // v43: same arrangement as Performance Appraisal: Managers, then Interns & others,
      // each sorted A–Z, 4 cards per row, separated by a line.
      const byName = (a,b) => (a.name||"").localeCompare(b.name||"", undefined, {sensitivity:"base"});
      const groups = [
        { key:"mgr",   label:"Managers",         list: staffList.filter(u=>u.role==="manager").sort(byName) },
        { key:"other", label:"Interns & others", list: staffList.filter(u=>u.role!=="manager").sort(byName) },
      ].filter(g=>g.list.length>0);
      const recentQs = twoRecentQuartersForFY(fy);
      const card = u => {
        const inactive = u.active===false;
        return (
          <div key={u.id} className="card" style={{cursor:"pointer",opacity:inactive?.55:1,padding:"16px 18px",minWidth:0}} onClick={()=>setSelStaff(u.id)}>
            <div className="fw6" style={{fontSize:15}}>{u.name}{inactive&&<span className="bdg bcl" style={{marginLeft:8}}>Inactive</span>}</div>
            <div className="tsl" style={{fontSize:12.5,marginTop:3}}>{u.role?u.role.charAt(0).toUpperCase()+u.role.slice(1):""}</div>
            <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:7}}>
              {recentQs.length===0
                ? <div className="tx tsl">Not started yet</div>
                : recentQs.map(q => {
                    const st = goalQuarterStatus(fy, q, u.id, goals, users);
                    return (
                      <div key={q} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,minWidth:0}}>
                        <span style={{fontSize:11,color:"var(--slate)",letterSpacing:.5,flexShrink:0}} title={quarterLabel(fy,q)}>{q}</span>
                        <span className={`bdg ${st.cls}`} title={st.label} style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{st.label}</span>
                      </div>
                    );
                  })}
            </div>
          </div>
        );
      };
      return (
        <div>
          <FYSelector fy={fy} setFy={setFy} options={fyList}/>
          {staffList.length===0 && <div className="es">No staff yet.</div>}
          {groups.map((g,gi)=>(
            <div key={g.key}>
              {gi>0 && <div style={{height:1,background:"var(--border)",margin:"26px 0 20px"}}/>}
              <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:12}}>
                <span style={{fontSize:11,fontWeight:600,letterSpacing:"1px",textTransform:"uppercase",color:"var(--slate)"}}>{g.label}</span>
                <span className="tx tsl">{g.list.length}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:14}}>
                {g.list.map(card)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    const u = users.find(x=>x.id===selStaff);
    return (
      <div>
        <button className="btn bgh bsm mb16" onClick={()=>setSelStaff(null)}><I n="arrowleft" s={13}/>All staff</button>
        <FYSelector fy={fy} setFy={setFy} options={fyList}/>
        <p className="card-title mb16">{u?.name}{u?.active===false&&<span className="bdg bcl" style={{marginLeft:8}}>Inactive</span>}</p>
        <div style={{opacity:u?.active===false?.7:1}}>{renderStaffPage(selStaff)}</div>
      </div>
    );
  }

  return (
    <div>
      <FYSelector fy={fy} setFy={setFy} options={fyList}/>
      {renderStaffPage(user.id)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PARTNER ACTIONABLES (v37): partners only
// Meetings amongst partners → actionables, each with one owner, tracked to closure.
// Data: "partner_meetings" and "partner_actions" (partner-only in Firestore rules).
// ══════════════════════════════════════════════════════════════
const PA_CATEGORIES = ["Staffing","Business Development","Client","Operations","Finance","Risk & Compliance","Other"];
const PA_PRIORITIES = ["High","Medium","Low"];
const PA_STATUSES = ["open","in_progress","done","dropped"];
const PA_STATUS_LABEL = { open:"Open", in_progress:"In progress", done:"Done", dropped:"Dropped" };
const PA_STATUS_CLASS = { open:"bp2", in_progress:"bac", done:"ba", dropped:"bcl" };
const PA_CAT_STYLE = {
  "Staffing":             { background:"#e0e7ff", color:"#3730a3" },
  "Business Development": { background:"#fef3c7", color:"#92400e" },
  "Client":               { background:"#dbeafe", color:"#1e40af" },
  "Operations":           { background:"#f1f5f9", color:"#475569" },
  "Finance":              { background:"#d1fae5", color:"#065f46" },
  "Risk & Compliance":    { background:"#fee2e2", color:"#991b1b" },
  "Other":                { background:"#f3e8ff", color:"#6d28d9" },
};
const PA_PRIO_COLOR = { High:"#b91c1c", Medium:"#b45309", Low:"#64748b" };
const PA_DUE_COLOR  = { over:"#b91c1c", soon:"#b45309", ok:"var(--navy)", closed:"var(--slate)" };

const paIsOpen = a => a.status==="open" || a.status==="in_progress";
const paDayDiff = (from, to) => {
  const [y1,m1,d1] = from.split("-").map(Number); const [y2,m2,d2] = to.split("-").map(Number);
  return Math.round((Date.UTC(y2,m2-1,d2) - Date.UTC(y1,m1-1,d1)) / 86400000);
};
const paIsOverdue = a => paIsOpen(a) && !!a.dueDate && a.dueDate < todayStr();
function paDueInfo(a) {
  if(!paIsOpen(a)) {
    const when = a.closedAt ? fmtDate(a.closedAt.slice(0,10)) : "";
    return { kind:"closed", sub: a.status==="done" ? `Closed ${when}` : `Dropped ${when}` };
  }
  if(!a.dueDate) return { kind:"ok", sub:"No due date" };
  const d = paDayDiff(todayStr(), a.dueDate);
  if(d<0)  return { kind:"over", sub:`${-d} day${d===-1?"":"s"} overdue` };
  if(d===0) return { kind:"soon", sub:"Due today" };
  if(d<=7) return { kind:"soon", sub:`Due in ${d} day${d===1?"":"s"}` };
  return { kind:"ok", sub:`In ${d} days` };
}
const paRank = a => { const k = paDueInfo(a).kind; return k==="over"?0 : k==="soon"?1 : 2; };
const paSort = (a,b) => paRank(a)-paRank(b) || (a.dueDate||"9999").localeCompare(b.dueDate||"9999");
const paBlankRow = () => ({ key:genId(), title:"", category:"", ownerId:"", supportIds:[], dueDate:"", priority:"Medium" });
const paFirstName = n => (n||"").split(" ")[0];
// Overdue items the partner OWNS: drives the sidebar badge
const paMyOverdueCount = (actions, userId) => actions.filter(a=>a.ownerId===userId && paIsOverdue(a)).length;

// v46: update visibility.
// Latest real update on an actionable (the "created" entry does not count).
const paLatestUpdate = a => {
  const h = a.history||[];
  for(let i=h.length-1;i>=0;i--) if(h[i].type!=="created") return h[i];
  return null;
};
// Updates written before this go-live are treated as already seen, so v46 does not flood every row with "New".
const PA_SEEN_START = "2026-10-07T00:00:00+05:30";
// "New" for this partner = someone ELSE updated it after this partner last opened it.
const paIsNewFor = (a, userId) => {
  const h = paLatestUpdate(a);
  if(!h || h.byId===userId) return false;
  const at = new Date(h.at).getTime();
  if(at < new Date(PA_SEEN_START).getTime()) return false;
  const seen = a.seenBy?.[userId];
  return !seen || at > new Date(seen).getTime();
};
// Record that this partner has now seen the item. Field-level write, so it never overwrites a
// concurrent update from another partner. The real-time listener brings the change back to every screen.
const paMarkSeen = (a, userId) => {
  if(!paIsNewFor(a, userId)) return;
  updateDoc(doc(db, "partner_actions", a.id), { [`seenBy.${userId}`]: new Date().toISOString() })
    .catch(e=>console.error("paMarkSeen error", e));
};
const paTimeAgo = iso => {
  const d = paDayDiff(iso.slice(0,10), todayStr());
  if(d<=0) return `today, ${new Date(iso).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}`;
  if(d===1) return "yesterday";
  if(d<7) return `${d} days ago`;
  return fmtDate(iso.slice(0,10));
};
function PANewPill() {
  return <span className="pa-new" title="Updated by another partner since you last opened it">New</span>;
}
// One-line summary of the latest update, shown under the title in lists.
function PALatestLine({ a, clamp=1 }) {
  const h = paLatestUpdate(a);
  if(!h) return <div className="tx" style={{color:"var(--slate-light)",marginTop:4}}>No updates yet</div>;
  const text = h.note || h.change;
  return (
    <div className="pa-up" style={{WebkitLineClamp:clamp}}>
      <span className="fw6" style={{color:"var(--navy)"}}>{h.byName}</span> · {paTimeAgo(h.at)}: {h.change&&h.change!=="Note"&&h.note?<span style={{color:"var(--navy-mid)"}}>{h.change}. </span>:null}{text}
    </div>
  );
}

const PA_CSS = `
.pa-row{display:grid;grid-template-columns:minmax(0,2.4fr) 150px 130px 125px 110px minmax(0,1.4fr) 84px;gap:14px;align-items:center;padding:13px 18px;border-bottom:1px solid var(--border);}
.pa-row:last-child{border-bottom:none;}
.pa-row.hd{background:var(--cream);padding-top:10px;padding-bottom:10px;}
.pa-hd{font-size:11px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:var(--slate);}
.pa-chip{display:inline-flex;padding:3px 10px;border-radius:20px;font-size:11.5px;font-weight:500;white-space:nowrap;}
.pa-you{font-size:11px;font-weight:600;color:#7a5a12;background:var(--gold-pale);padding:1px 7px;border-radius:20px;}
.pa-drawer-bg{position:fixed;inset:0;background:rgba(15,32,68,.45);z-index:1000;}
.pa-drawer{position:fixed;right:0;top:0;bottom:0;width:600px;max-width:100vw;background:#fff;box-shadow:-12px 0 40px rgba(15,32,68,.2);padding:26px 30px;display:flex;flex-direction:column;gap:18px;overflow-y:auto;z-index:1001;}
.pa-seg{display:flex;gap:3px;background:var(--cream);border-radius:10px;padding:4px;border:1px solid var(--border);}
.pa-seg button{flex:1;padding:9px 10px;border-radius:7px;border:none;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;background:transparent;color:var(--slate);}
.pa-seg button.on{background:#fff;color:var(--navy);box-shadow:var(--sh);}
.pa-grid{display:grid;grid-template-columns:24px minmax(0,1fr) 165px 135px 200px 145px 100px 28px;gap:8px;align-items:center;}
.pa-grid .fi,.pa-grid .fs{padding:8px 10px;font-size:13px;}
.pa-pchip{padding:5px 9px;border-radius:20px;font-size:11.5px;font-weight:500;cursor:pointer;border:1.5px solid var(--border);background:#fff;color:var(--slate);font-family:inherit;}
.pa-pchip.on{background:var(--navy);color:#fff;border-color:var(--navy);}
.pa-meta{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px 18px;background:var(--cream);border:1px solid var(--border);border-radius:12px;padding:16px 18px;}
.pa-k{font-size:11px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:var(--slate);}
.pa-v{font-size:13.5px;margin-top:4px;}
.pa-mi{display:grid;grid-template-columns:minmax(0,1fr) 150px 120px 125px 110px;gap:14px;align-items:center;padding:10px 0;border-bottom:1px solid var(--cream);cursor:pointer;}
.pa-mi:hover{background:#fcfcfc;}
.pa-ri{display:grid;grid-template-columns:minmax(0,1fr) 150px 135px 110px 300px;gap:14px;align-items:center;padding:12px 20px;border-bottom:1px solid var(--cream);}
.pa-ri:last-child{border-bottom:none;}
.pa-new{display:inline-flex;align-items:center;font-size:10.5px;font-weight:700;letter-spacing:.3px;color:#7a4b06;background:#fde68a;padding:1px 8px;border-radius:20px;margin-left:8px;vertical-align:2px;white-space:nowrap;}
.pa-up{font-size:12px;color:var(--slate);margin-top:5px;line-height:1.5;padding-left:9px;border-left:2px solid var(--gold);overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;}
.pa-mi.is-new{background:#fffbeb;}
.pa-mi.is-new:hover{background:#fef6dc;}
.pa-latest{background:#fef9ec;border:1px solid #fde68a;border-left:4px solid var(--gold);border-radius:0 12px 12px 0;padding:14px 16px;}
`;

function PACatChip({ cat }) {
  return <span className="pa-chip" style={PA_CAT_STYLE[cat]||PA_CAT_STYLE.Other}>{cat||"—"}</span>;
}
function PAStatusBadge({ status }) {
  return <span className={`bdg ${PA_STATUS_CLASS[status]||"bcl"}`}>{PA_STATUS_LABEL[status]||status}</span>;
}
function PADue({ a }) {
  const info = paDueInfo(a);
  const c = PA_DUE_COLOR[info.kind];
  return <div>
    <div style={{fontSize:13,fontWeight:info.kind==="over"||info.kind==="soon"?600:400,color:c}}>{a.dueDate?fmtDate(a.dueDate):"—"}</div>
    <div style={{fontSize:11.5,color:info.kind==="ok"?"var(--slate)":c}}>{info.sub}</div>
  </div>;
}
// Toggle chips for picking supporting partners (owner is excluded)
function PASupportPicker({ partners, ownerId, value=[], onChange }) {
  const opts = partners.filter(p=>p.id!==ownerId);
  if(!opts.length) return <span className="tx tsl">—</span>;
  return <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
    {opts.map(p=>{
      const on = value.includes(p.id);
      return <button key={p.id} type="button" className={`pa-pchip ${on?"on":""}`} aria-pressed={on}
        onClick={()=>onChange(on?value.filter(x=>x!==p.id):[...value,p.id])}>{paFirstName(p.name)}</button>;
    })}
  </div>;
}

// ── Log / edit meeting ──────────────────────────────────────────
function PAMeetingModal({ partners, existing, onClose, onSave }) {
  const isEdit = !!existing;
  const [date,setDate]       = useState(existing?.date || todayStr());
  const [title,setTitle]     = useState(existing?.title || "Partners' meeting");
  const [attendees,setAtt]   = useState(existing?.attendees || partners.map(p=>p.id));
  const [notes,setNotes]     = useState(existing?.notes || "");
  const [rows,setRows]       = useState([paBlankRow(),paBlankRow(),paBlankRow()]);
  const [err,setErr]         = useState("");

  const setRow = (key, patch) => setRows(rs=>rs.map(r=>r.key===key?{...r,...patch}:r));
  const addRow = () => setRows(rs=>[...rs,paBlankRow()]);
  const removeRow = key => setRows(rs=>rs.length>1?rs.filter(r=>r.key!==key):[paBlankRow()]);
  const isBlank = r => !r.title.trim() && !r.ownerId && !r.dueDate && !r.category;
  const filled = rows.filter(r=>!isBlank(r));

  const save = () => {
    setErr("");
    if(!date){ setErr("Meeting date is required."); return; }
    if(!title.trim()){ setErr("Meeting title is required."); return; }
    if(!isEdit){
      for(let i=0;i<rows.length;i++){
        const r = rows[i]; if(isBlank(r)) continue;
        const miss = [!r.title.trim()&&"actionable",!r.category&&"category",!r.ownerId&&"owner",!r.dueDate&&"due date"].filter(Boolean);
        if(miss.length){ setErr(`Row ${i+1} needs: ${miss.join(", ")}.`); return; }
      }
    }
    onSave({ date, title:title.trim(), attendees, notes:notes.trim() }, isEdit?[]:filled);
  };

  return (
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:isEdit?620:1240}}>
        <div className="md-title">{isEdit?"Edit meeting":"Log partners' meeting"}</div>
        {!isEdit&&<div className="ts tsl" style={{marginTop:-12,marginBottom:18}}>Record the meeting once, then add every actionable in one go.</div>}
        {err&&<div className="err">{err}</div>}
        <div style={{display:"grid",gridTemplateColumns:isEdit?"1fr 1fr":"180px 300px minmax(0,1fr)",gap:16}}>
          <div className="fg"><label className="fl" htmlFor="pa-md">Meeting date</label><input id="pa-md" type="date" className="fi" value={date} max={todayStr()} onChange={e=>setDate(e.target.value)}/></div>
          <div className="fg"><label className="fl" htmlFor="pa-mt">Title</label><input id="pa-mt" className="fi" value={title} onChange={e=>setTitle(e.target.value)}/></div>
          <div className="fg" style={isEdit?{gridColumn:"1 / -1"}:undefined}>
            <div className="fl">Present</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {partners.map(p=>{
                const on = attendees.includes(p.id);
                return <button key={p.id} type="button" aria-pressed={on} className={`pa-pchip ${on?"on":""}`} style={{padding:"8px 14px",fontSize:13}}
                  onClick={()=>setAtt(a=>on?a.filter(x=>x!==p.id):[...a,p.id])}>{p.name}</button>;
              })}
            </div>
          </div>
        </div>
        <div className="fg"><label className="fl" htmlFor="pa-mn">Short notes (optional)</label>
          <textarea id="pa-mn" className="fta" style={{minHeight:56}} placeholder="Topics discussed..." value={notes} onChange={e=>setNotes(e.target.value)}/>
        </div>

        {!isEdit&&(
          <div style={{borderTop:"1.5px solid var(--border)",paddingTop:16}}>
            <div className="fxb mb8">
              <div className="fw6" style={{fontSize:15}}>Actionables <span className="tsl" style={{fontWeight:500}}>({filled.length})</span></div>
              <div className="tx tsl">Each needs an actionable, category, one owner and a due date. Blank rows are ignored.</div>
            </div>
            <div className="pa-grid" style={{marginBottom:6}}>
              <div className="pa-hd">#</div><div className="pa-hd">Actionable</div><div className="pa-hd">Category</div><div className="pa-hd">Owner</div><div className="pa-hd">Supporting</div><div className="pa-hd">Due date</div><div className="pa-hd">Priority</div><div/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {rows.map((r,idx)=>(
                <div key={r.key} className="pa-grid">
                  <div className="ts tsl fw6">{idx+1}</div>
                  <input className="fi" aria-label={`Actionable ${idx+1}`} placeholder="What needs to happen?" value={r.title}
                    onChange={e=>setRow(r.key,{title:e.target.value})}
                    onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); if(idx===rows.length-1) addRow(); } }}/>
                  <select className="fs" aria-label="Category" value={r.category} onChange={e=>setRow(r.key,{category:e.target.value})}>
                    <option value="">Select</option>{PA_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                  <select className="fs" aria-label="Owner" value={r.ownerId} onChange={e=>setRow(r.key,{ownerId:e.target.value,supportIds:r.supportIds.filter(x=>x!==e.target.value)})}>
                    <option value="">Select</option>{partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <PASupportPicker partners={partners} ownerId={r.ownerId} value={r.supportIds} onChange={v=>setRow(r.key,{supportIds:v})}/>
                  <input type="date" className="fi" aria-label="Due date" value={r.dueDate} onChange={e=>setRow(r.key,{dueDate:e.target.value})}/>
                  <select className="fs" aria-label="Priority" value={r.priority} onChange={e=>setRow(r.key,{priority:e.target.value})}>
                    {PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}
                  </select>
                  <button type="button" className="btn bgh bic bsm" aria-label="Remove row" title="Remove row" onClick={()=>removeRow(r.key)}><I n="x" s={13}/></button>
                </div>
              ))}
            </div>
            <button type="button" className="btn bsm" onClick={addRow} style={{marginTop:10,background:"transparent",border:"1.5px dashed var(--gold)",color:"var(--navy)"}}><I n="plus" s={13}/>Add another row</button>
            <div className="tx tsl mt8">Tip: press Enter in the last row's actionable box to add a new row.</div>
          </div>
        )}

        <div className="md-actions" style={{justifyContent:"space-between",alignItems:"center"}}>
          <div className="tx tsl">{isEdit?"Actionables are edited individually from their own panel.":"Open items from earlier meetings stay on the Meeting Review list. No need to re-enter them."}</div>
          <div className="fx g8">
            <button className="btn bgh" onClick={onClose}>Cancel</button>
            <button className="btn bp" onClick={save}><I n="check" s={15}/>{isEdit?"Save meeting":`Save meeting${filled.length?` and ${filled.length} actionable${filled.length===1?"":"s"}`:""}`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Quick add: one actionable, optionally linked to a meeting ──
function PAQuickAddModal({ partners, meetings, defaultMeetingId, defaultOwnerId, onClose, onSave }) {
  const [r,setR] = useState({ ...paBlankRow(), ownerId:defaultOwnerId||"" });
  const [meetingId,setMeetingId] = useState(defaultMeetingId||"");
  const [err,setErr] = useState("");
  const save = () => {
    const miss = [!r.title.trim()&&"actionable",!r.category&&"category",!r.ownerId&&"owner",!r.dueDate&&"due date"].filter(Boolean);
    if(miss.length){ setErr(`Please fill in: ${miss.join(", ")}.`); return; }
    onSave(r, meetingId||null);
  };
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
        <div className="md-title">Add actionable</div>
        {err&&<div className="err">{err}</div>}
        <div className="fg"><label className="fl" htmlFor="pa-qt">Actionable</label>
          <textarea id="pa-qt" className="fta" style={{minHeight:60}} placeholder="What needs to happen?" value={r.title} onChange={e=>setR(x=>({...x,title:e.target.value}))}/>
        </div>
        <div className="g2">
          <div className="fg"><label className="fl" htmlFor="pa-qc">Category</label>
            <select id="pa-qc" className="fs" value={r.category} onChange={e=>setR(x=>({...x,category:e.target.value}))}><option value="">Select</option>{PA_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select>
          </div>
          <div className="fg"><label className="fl" htmlFor="pa-qp">Priority</label>
            <select id="pa-qp" className="fs" value={r.priority} onChange={e=>setR(x=>({...x,priority:e.target.value}))}>{PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}</select>
          </div>
          <div className="fg"><label className="fl" htmlFor="pa-qo">Owner</label>
            <select id="pa-qo" className="fs" value={r.ownerId} onChange={e=>setR(x=>({...x,ownerId:e.target.value,supportIds:x.supportIds.filter(s=>s!==e.target.value)}))}><option value="">Select</option>{partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
          </div>
          <div className="fg"><label className="fl" htmlFor="pa-qd">Due date</label>
            <input id="pa-qd" type="date" className="fi" value={r.dueDate} onChange={e=>setR(x=>({...x,dueDate:e.target.value}))}/>
          </div>
        </div>
        <div className="fg"><div className="fl">Supporting partners (optional)</div>
          <PASupportPicker partners={partners} ownerId={r.ownerId} value={r.supportIds} onChange={v=>setR(x=>({...x,supportIds:v}))}/>
        </div>
        <div className="fg"><label className="fl" htmlFor="pa-qm">From meeting</label>
          <select id="pa-qm" className="fs" value={meetingId} onChange={e=>setMeetingId(e.target.value)}>
            <option value="">Not from a meeting</option>
            {meetings.map(m=><option key={m.id} value={m.id}>{m.title} · {fmtDate(m.date)}</option>)}
          </select>
        </div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bp" onClick={save}><I n="check" s={15}/>Add actionable</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail drawer: update status, add a note, revise due date, edit details ──
function PADetailDrawer({ user, a, users, partners, meeting, canDelete, wasNew=false, onClose, onSave, onDelete }) {
  const [status,setStatus] = useState(a.status);
  const [note,setNote]     = useState("");
  const [due,setDue]       = useState(a.dueDate||"");
  const [editing,setEditing] = useState(false);
  const [showForm,setShowForm] = useState(false); // v46: update form stays collapsed until asked for
  const [d,setD] = useState({ title:a.title, category:a.category, ownerId:a.ownerId, supportIds:a.supportIds||[], priority:a.priority||"Medium" });
  const [err,setErr] = useState("");
  const nameOf = id => users.find(u=>u.id===id)?.name || "—";
  const info = paDueInfo(a);
  const willBeOpen = status==="open"||status==="in_progress";
  const ownerOptions = partners.some(p=>p.id===a.ownerId) ? partners : [...partners, users.find(u=>u.id===a.ownerId)].filter(Boolean);

  const save = () => {
    setErr("");
    const changes = [];
    if(status!==a.status) changes.push(`${PA_STATUS_LABEL[a.status]} → ${PA_STATUS_LABEL[status]}`);
    const newDue = willBeOpen ? due : a.dueDate;
    if(willBeOpen && !due){ setErr("Due date is required."); return; }
    if(newDue!==a.dueDate) changes.push(`Due ${fmtDate(a.dueDate)} → ${fmtDate(newDue)}`);
    let details = {};
    if(editing){
      if(!d.title.trim()){ setErr("Actionable text is required."); return; }
      if(!d.category||!d.ownerId){ setErr("Category and owner are required."); return; }
      const sup = d.supportIds.filter(x=>x!==d.ownerId);
      if(d.title.trim()!==a.title) changes.push("Text edited");
      if(d.category!==a.category) changes.push(`Category ${a.category} → ${d.category}`);
      if(d.ownerId!==a.ownerId) changes.push(`Owner ${nameOf(a.ownerId)} → ${nameOf(d.ownerId)}`);
      if([...sup].sort().join()!==[...(a.supportIds||[])].sort().join()) changes.push(`Supporting: ${sup.length?sup.map(nameOf).join(", "):"none"}`);
      if(d.priority!==(a.priority||"Medium")) changes.push(`Priority ${a.priority||"Medium"} → ${d.priority}`);
      details = { title:d.title.trim(), category:d.category, ownerId:d.ownerId, supportIds:sup, priority:d.priority };
    }
    if(status==="dropped" && a.status!=="dropped" && !note.trim()){ setErr("A reason is required to drop an actionable."); return; }
    if(!changes.length && !note.trim()){ setErr("Nothing to save. Change the status, add a note or revise the due date."); return; }
    const now = new Date().toISOString();
    const wasOpen = paIsOpen(a);
    const closedAt = willBeOpen ? null : (wasOpen || status!==a.status ? now : (a.closedAt||now));
    const entry = { at:now, byId:user.id, byName:user.name, type:changes.length?"update":"note", change:changes.join(" · ")||"Note", note:note.trim() };
    onSave({ ...a, ...details, status, dueDate:newDue, closedAt, updatedAt:now, updatedBy:user.id, history:[...(a.history||[]), entry] }, entry.change);
  };

  const noteLabel = status==="dropped"&&a.status!=="dropped" ? "Reason for dropping (required)" : status==="done"&&a.status!=="done" ? "Closure note (optional)" : "Update note";
  const notePh = status==="dropped" ? "Why is this no longer needed?" : status==="done" ? "What was the outcome?" : "What has happened since the last update?";
  const history = (a.history||[]).slice().reverse();
  const latest = paLatestUpdate(a);
  const cancelForm = () => {
    setShowForm(false); setEditing(false); setErr(""); setNote(""); setStatus(a.status); setDue(a.dueDate||"");
    setD({ title:a.title, category:a.category, ownerId:a.ownerId, supportIds:a.supportIds||[], priority:a.priority||"Medium" });
  };
  const dotColor = h => h.type==="created" ? "#94a3b8" : /→ Done/.test(h.change) ? "var(--green)" : /→ Dropped/.test(h.change) ? "var(--slate)" : /Due /.test(h.change) ? "var(--amber)" : h.type==="note" ? "var(--gold)" : "#1e40af";

  return (
    <>
      <div className="pa-drawer-bg" onClick={onClose}/>
      <div className="pa-drawer" role="dialog" aria-label="Actionable details">
        <div className="fxb">
          <div className="fx g8" style={{flexWrap:"wrap"}}>
            <PACatChip cat={a.category}/>
            <PAStatusBadge status={a.status}/>
            {info.kind==="over"&&<span className="pa-chip" style={{background:"#fee2e2",color:"#b91c1c",fontWeight:600}}>{info.sub}</span>}
          </div>
          <button className="btn bgh bic bsm" aria-label="Close" onClick={onClose}><I n="x" s={16}/></button>
        </div>
        {!editing
          ? <div style={{fontFamily:"'Playfair Display',serif",fontSize:23,lineHeight:1.3}}>{a.title}</div>
          : <div className="fg" style={{marginBottom:0}}><label className="fl" htmlFor="pa-et">Actionable</label><textarea id="pa-et" className="fta" style={{minHeight:60}} value={d.title} onChange={e=>setD(x=>({...x,title:e.target.value}))}/></div>}

        {/* v46: latest update sits right under the title, so nobody has to scroll to find it */}
        {latest
          ? <div className="pa-latest">
              <div className="fxb" style={{gap:10}}>
                <div className="pa-k" style={{color:"#92400e"}}>Latest update{wasNew&&<span className="pa-new">New for you</span>}</div>
                <div className="tx" style={{color:"#92400e"}}>{paTimeAgo(latest.at)}</div>
              </div>
              <div className="ts" style={{marginTop:6,color:"var(--navy)"}}>
                <strong>{latest.byName}</strong>
                {latest.change&&latest.change!=="Note"&&<span style={{color:"var(--navy-mid)",fontWeight:600}}> · {latest.change}</span>}
              </div>
              {latest.note&&<div style={{fontSize:14,color:"#334155",marginTop:6,lineHeight:1.55,whiteSpace:"pre-wrap"}}>{latest.note}</div>}
            </div>
          : <div className="pa-latest" style={{background:"var(--cream)",borderColor:"var(--border)",borderLeftColor:"var(--slate-light)"}}>
              <div className="pa-k">Latest update</div>
              <div className="ts tsl" style={{marginTop:6}}>No updates yet since this was raised.</div>
            </div>}

        {!editing?(
          <div className="pa-meta">
            <div><div className="pa-k">Owner</div><div className="pa-v fw6">{nameOf(a.ownerId)}</div></div>
            <div><div className="pa-k">Supporting</div><div className="pa-v" style={{color:(a.supportIds||[]).length?"var(--navy)":"var(--slate)"}}>{(a.supportIds||[]).length?(a.supportIds||[]).map(nameOf).join(", "):"None"}</div></div>
            <div><div className="pa-k">Priority</div><div className="pa-v fw6" style={{color:PA_PRIO_COLOR[a.priority]||"var(--slate)"}}>{a.priority||"Medium"}</div></div>
            <div><div className="pa-k">Due</div><div className="pa-v fw6" style={{color:PA_DUE_COLOR[info.kind]}}>{a.dueDate?fmtDate(a.dueDate):"—"}</div>
              {a.originalDueDate&&a.originalDueDate!==a.dueDate&&<div className="tx tsl">Originally {fmtDate(a.originalDueDate)}</div>}</div>
            <div><div className="pa-k">Raised in</div><div className="pa-v">{meeting?`${meeting.title}, ${fmtDate(meeting.date)}`:"Not from a meeting"}</div></div>
            <div><div className="pa-k">Created by</div><div className="pa-v">{a.createdByName||nameOf(a.createdBy)}</div></div>
          </div>
        ):(
          <div className="pa-meta" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div><label className="fl" htmlFor="pa-ec">Category</label><select id="pa-ec" className="fs" value={d.category} onChange={e=>setD(x=>({...x,category:e.target.value}))}>{PA_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
            <div><label className="fl" htmlFor="pa-ep">Priority</label><select id="pa-ep" className="fs" value={d.priority} onChange={e=>setD(x=>({...x,priority:e.target.value}))}>{PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
            <div><label className="fl" htmlFor="pa-eo">Owner</label><select id="pa-eo" className="fs" value={d.ownerId} onChange={e=>setD(x=>({...x,ownerId:e.target.value,supportIds:x.supportIds.filter(s=>s!==e.target.value)}))}>{ownerOptions.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
            <div><div className="fl">Supporting</div><PASupportPicker partners={partners} ownerId={d.ownerId} value={d.supportIds} onChange={v=>setD(x=>({...x,supportIds:v}))}/></div>
          </div>
        )}
        <div className="fx g8" style={{flexWrap:"wrap"}}>
          {!showForm&&<button className="btn bp bsm" onClick={()=>setShowForm(true)}><I n="plus" s={13}/>Add an update</button>}
          <button className="btn bgh bsm" onClick={()=>{const on=!editing;setEditing(on);if(on)setShowForm(true);setD({ title:a.title, category:a.category, ownerId:a.ownerId, supportIds:a.supportIds||[], priority:a.priority||"Medium" });}}><I n="edit" s={13}/>{editing?"Cancel editing details":"Edit details"}</button>
          {canDelete&&<button className="btn bd bsm" onClick={()=>onDelete(a)}><I n="trash" s={13}/>Delete</button>}
        </div>

        {showForm&&<div style={{display:"flex",flexDirection:"column",gap:14,border:"1.5px solid var(--navy)",borderRadius:12,padding:18}}>
          <div className="fw6" style={{fontSize:14.5}}>{editing?"Save changes":"Add an update"}</div>
          {err&&<div className="err" style={{marginBottom:0}}>{err}</div>}
          <div>
            <div className="fl">Status</div>
            <div className="pa-seg" role="group" aria-label="Status">
              {PA_STATUSES.map(s=><button key={s} type="button" className={status===s?"on":""} aria-pressed={status===s} onClick={()=>setStatus(s)}>{PA_STATUS_LABEL[s]}</button>)}
            </div>
          </div>
          <div>
            <label className="fl" htmlFor="pa-note">{noteLabel}</label>
            <textarea id="pa-note" className="fta" placeholder={notePh} value={note} onChange={e=>setNote(e.target.value)}/>
          </div>
          {willBeOpen&&(
            <div style={{display:"grid",gridTemplateColumns:"190px minmax(0,1fr)",gap:12,alignItems:"end"}}>
              <div><label className="fl" htmlFor="pa-due">Due date</label><input id="pa-due" type="date" className="fi" value={due} onChange={e=>setDue(e.target.value)}/></div>
              <div className="tx tsl" style={{paddingBottom:12}}>Changing it keeps the original date in the history, so repeated slips stay visible.</div>
            </div>
          )}
          <div className="fx g8" style={{justifyContent:"flex-end"}}>
            <button className="btn bgh" onClick={cancelForm}>Cancel</button>
            <button className="btn bp" onClick={save}><I n="check" s={15}/>Save update</button>
          </div>
        </div>}

        <div>
          <div className="fw6" style={{fontSize:14.5,marginBottom:12}}>History <span className="tx tsl" style={{fontWeight:400}}>({history.length})</span></div>
          {history.map((h,i)=>(
            <div key={i} style={{display:"flex",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:12}}>
                <div style={{width:12,height:12,borderRadius:"50%",flexShrink:0,background:dotColor(h)}}/>
                {i<history.length-1&&<div style={{width:2,flexGrow:1,background:"var(--border)",margin:"4px 0"}}/>}
              </div>
              <div style={{paddingBottom:16,flex:1}}>
                <div className="ts"><strong>{h.byName}</strong> <span className="tsl">· {fmtDate(h.at.slice(0,10))} at {new Date(h.at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span></div>
                {h.change&&h.change!=="Note"&&<div style={{fontSize:12.5,color:"var(--navy-mid)",fontWeight:600,marginTop:3}}>{h.change}</div>}
                {h.note&&<div className="ts" style={{color:"#334155",marginTop:3,lineHeight:1.45,whiteSpace:"pre-wrap"}}>{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────
function PartnerActions({ user, users=[], meetings=[], setMeetings, actions=[], setActions, loaded=true }) {
  const [tab,setTab]         = useState("mine");
  const [fOwner,setFOwner]   = useState("");
  const [fCat,setFCat]       = useState("");
  const [fStatus,setFStatus] = useState("open");
  const [meetingM,setMeetingM] = useState(null); // null | {existing?}
  const [quickM,setQuickM]   = useState(null);   // null | {meetingId}
  const [detailId,setDetailId] = useState(null);
  const [detailNew,setDetailNew] = useState(false); // was the opened item "New" at the moment it was opened
  const isAdmin = user.email===ADMIN_EMAIL;
  // v46: opening an item marks its latest update as seen by this partner
  const isNew = a => paIsNewFor(a, user.id);
  const openDetail = a => { setDetailNew(isNew(a)); setDetailId(a.id); paMarkSeen(a, user.id); };

  const partners = users.filter(u=>u.role==="partner"&&u.active!==false).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const nameOf = id => users.find(u=>u.id===id)?.name || "—";
  const meetingOf = id => meetings.find(m=>m.id===id);
  const meetingsSorted = meetings.slice().sort((a,b)=>(b.date||"").localeCompare(a.date||"")||(b.createdAt||"").localeCompare(a.createdAt||""));
  const lastMeeting = meetingsSorted[0];

  const openAll   = actions.filter(paIsOpen).sort(paSort);
  const mineOwned = openAll.filter(a=>a.ownerId===user.id);
  const mineSupp  = openAll.filter(a=>a.ownerId!==user.id&&(a.supportIds||[]).includes(user.id));
  const mine      = [...mineOwned,...mineSupp].sort(paSort);
  const myOverdue = mineOwned.filter(paIsOverdue).length;
  const firmOverdue = openAll.filter(paIsOverdue).length;

  const allFiltered = actions.filter(a=>{
    if(fOwner&&a.ownerId!==fOwner) return false;
    if(fCat&&a.category!==fCat) return false;
    if(fStatus==="open"&&!paIsOpen(a)) return false;
    if(fStatus==="overdue"&&!paIsOverdue(a)) return false;
    if(fStatus==="done"&&a.status!=="done") return false;
    if(fStatus==="dropped"&&a.status!=="dropped") return false;
    return true;
  }).sort((a,b)=>paIsOpen(a)!==paIsOpen(b)?(paIsOpen(a)?-1:1):paIsOpen(a)?paSort(a,b):(b.closedAt||"").localeCompare(a.closedAt||""));

  const raisedLabel = a => { const m=meetingOf(a.meetingId); return m?`Raised ${fmtDate(m.date)}`:`Added ${fmtDate((a.createdAt||"").slice(0,10))}`; };
  const latestNote = a => {
    const h = a.history||[]; const last = h[h.length-1];
    if(!last||last.type==="created") return "No update yet";
    return `${fmtDate(last.at.slice(0,10))}: ${last.note||last.change}`;
  };

  // ── writes ──
  const newAction = (r, meetingId, now) => ({
    id:genId(), meetingId:meetingId||null, title:r.title.trim(), category:r.category,
    ownerId:r.ownerId, supportIds:(r.supportIds||[]).filter(x=>x!==r.ownerId),
    dueDate:r.dueDate, originalDueDate:r.dueDate, priority:r.priority||"Medium",
    status:"open", closedAt:null,
    createdBy:user.id, createdByName:user.name, createdAt:now, updatedAt:now, updatedBy:user.id,
    history:[{ at:now, byId:user.id, byName:user.name, type:"created",
      change: meetingId?"Created from partners' meeting":"Created",
      note:`Owner ${nameOf(r.ownerId)} · due ${fmtDate(r.dueDate)} · ${r.priority||"Medium"} priority` }],
  });
  const saveMeeting = (m, rows) => {
    const now = new Date().toISOString();
    if(meetingM?.existing){
      const id = meetingM.existing.id;
      setMeetings(prev=>prev.map(x=>x.id===id?{...x,...m,updatedAt:now,updatedBy:user.id}:x));
      addAudit(user.id,user.name,"PA_EDIT_MEETING",`Edited partners' meeting of ${m.date}`);
    } else {
      const mid = genId();
      setMeetings(prev=>[...prev,{ id:mid, ...m, createdBy:user.id, createdByName:user.name, createdAt:now }]);
      if(rows.length) setActions(prev=>[...prev,...rows.map(r=>newAction(r,mid,now))]);
      addAudit(user.id,user.name,"PA_LOG_MEETING",`Logged partners' meeting of ${m.date} with ${rows.length} actionable(s)`);
      setTab("meet");
    }
    setMeetingM(null);
  };
  const saveQuick = (r, meetingId) => {
    const now = new Date().toISOString();
    setActions(prev=>[...prev,newAction(r,meetingId,now)]);
    addAudit(user.id,user.name,"PA_ADD_ACTION",`Added actionable for ${nameOf(r.ownerId)}: ${r.title.trim()}`);
    setQuickM(null);
  };
  const saveDetail = (updated, summary) => {
    setActions(prev=>prev.map(x=>x.id===updated.id?updated:x));
    addAudit(user.id,user.name,"PA_UPDATE_ACTION",`${updated.title}: ${summary}`);
    setDetailId(null);
  };
  const deleteAction = a => {
    if(!window.confirm(`Delete this actionable permanently?\n\n"${a.title}"\n\nUse "Dropped" instead if it is simply no longer needed.`)) return;
    setActions(prev=>prev.filter(x=>x.id!==a.id));
    addAudit(user.id,user.name,"PA_DELETE_ACTION",`Deleted actionable: ${a.title}`);
    setDetailId(null);
  };
  const deleteMeeting = m => {
    const linked = actions.filter(a=>a.meetingId===m.id);
    if(!window.confirm(`Delete the meeting of ${fmtDate(m.date)}${linked.length?` and its ${linked.length} actionable${linked.length===1?"":"s"}`:""}? This cannot be undone.`)) return;
    setMeetings(prev=>prev.filter(x=>x.id!==m.id));
    if(linked.length) setActions(prev=>prev.filter(a=>a.meetingId!==m.id));
    addAudit(user.id,user.name,"PA_DELETE_MEETING",`Deleted partners' meeting of ${m.date} with ${linked.length} actionable(s)`);
  };
  const markDone = a => {
    if(!window.confirm(`Mark as done?\n\n"${a.title}"`)) return;
    const now = new Date().toISOString();
    setActions(prev=>prev.map(x=>x.id===a.id?{...x,status:"done",closedAt:now,updatedAt:now,updatedBy:user.id,
      history:[...(x.history||[]),{at:now,byId:user.id,byName:user.name,type:"update",change:`${PA_STATUS_LABEL[x.status]} → Done`,note:"Closed during meeting review"}]}:x));
    addAudit(user.id,user.name,"PA_UPDATE_ACTION",`${a.title}: marked done in meeting review`);
  };

  const detail = detailId ? actions.find(a=>a.id===detailId) : null;

  // ── pieces ──
  const Row = ({ a }) => {
    const role = a.ownerId===user.id ? "Owner" : (a.supportIds||[]).includes(user.id) ? "Supporting" : "";
    return (
      <div className="pa-row">
        <div style={{minWidth:0}}>
          <div className="fw6" style={{fontSize:14,lineHeight:1.35}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
          <div className="fxc g8 mt4" style={{flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:PA_PRIO_COLOR[a.priority]||"var(--slate)"}}>{a.priority||"Medium"}</span>
            <span className="tx tsl">{raisedLabel(a)}</span>
            {role&&<span className="pa-you">You: {role}</span>}
          </div>
        </div>
        <div><PACatChip cat={a.category}/></div>
        <div>
          <div style={{fontWeight:500,fontSize:13.5}}>{nameOf(a.ownerId)}</div>
          {(a.supportIds||[]).length>0&&<div className="tx tsl">+ {(a.supportIds||[]).map(id=>paFirstName(nameOf(id))).join(", ")}</div>}
        </div>
        <PADue a={a}/>
        <div><PAStatusBadge status={a.status}/></div>
        <div className="tx tsl" style={{lineHeight:1.4,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{latestNote(a)}</div>
        <div><button className="btn bgh bsm" onClick={()=>openDetail(a)}>Update</button></div>
      </div>
    );
  };
  const Table = ({ list, empty }) => (
    <div className="card" style={{padding:0,overflow:"hidden"}}>
      {list.length===0 ? <div className="es"><div className="es-icon"><I n="task" s={36}/></div>{empty}</div> : <>
        <div className="pa-row hd"><div className="pa-hd">Actionable</div><div className="pa-hd">Category</div><div className="pa-hd">Owner</div><div className="pa-hd">Due</div><div className="pa-hd">Status</div><div className="pa-hd">Latest update</div><div/></div>
        {list.map(a=><Row key={a.id} a={a}/>)}
      </>}
    </div>
  );

  const newAll = actions.filter(isNew).length;
  const tabs = [
    ["mine",`My Actionables (${mine.length})`],
    ["all",`All (${openAll.length} open)`],
    ["meet",newAll?`By Meeting · ${newAll} new`:"By Meeting"],
    ["review","Meeting Review"],
  ];

  // Review groups: open items by owner, overdue first
  const ownerIds = [...new Set(openAll.map(a=>a.ownerId))].sort((x,y)=>nameOf(x).localeCompare(nameOf(y)));
  const groups = ownerIds.map(id=>({ id, items:openAll.filter(a=>a.ownerId===id) }));
  const closedSince = lastMeeting ? actions.filter(a=>!paIsOpen(a)&&a.closedAt&&a.closedAt.slice(0,10)>=lastMeeting.date).sort((a,b)=>(b.closedAt||"").localeCompare(a.closedAt||"")) : [];
  const unlinked = actions.filter(a=>!a.meetingId||!meetingOf(a.meetingId));

  const MeetingCard = ({ m, items, title, sub }) => {
    const done = items.filter(a=>a.status==="done").length;
    const dropped = items.filter(a=>a.status==="dropped").length;
    const closed = done+dropped; const open = items.length-closed;
    const pct = items.length ? Math.round(closed/items.length*100) : 0;
    const sorted = items.slice().sort((a,b)=>paIsOpen(a)!==paIsOpen(b)?(paIsOpen(a)?-1:1):paSort(a,b));
    const canDel = m && (isAdmin || m.createdBy===user.id);
    const newCount = items.filter(isNew).length;
    return (
      <div className="card" style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:20}}>
          <div style={{minWidth:0}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:19}}>{title}</div>
            <div className="ts tsl mt4">{sub}</div>
            {m?.notes&&<div className="ts" style={{color:"#334155",marginTop:8,maxWidth:760,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{m.notes}</div>}
            {m&&<div className="fx g8" style={{marginTop:10}}>
              <button className="btn bgh bxs" onClick={()=>setQuickM({meetingId:m.id})}><I n="plus" s={12}/>Add actionable</button>
              <button className="btn bgh bxs" onClick={()=>setMeetingM({existing:m})}><I n="edit" s={12}/>Edit meeting</button>
              {canDel&&<button className="btn bgh bxs" style={{color:"var(--red)"}} onClick={()=>deleteMeeting(m)}><I n="trash" s={12}/>Delete</button>}
            </div>}
          </div>
          <div style={{width:220,flexShrink:0,textAlign:"right"}}>
            {newCount>0&&<div style={{marginBottom:6}}><span className="pa-new" style={{marginLeft:0,fontSize:11.5,padding:"3px 10px"}}>{newCount} new update{newCount===1?"":"s"}</span></div>}
            <div className="ts fw6">{closed} of {items.length} closed</div>
            <div className="pbw" style={{marginTop:7}}><div className="pbf pbok" style={{width:Math.max(pct,items.length?2:0)+"%"}}/></div>
            <div className="tx tsl mt4">{open} open · {done} done · {dropped} dropped</div>
          </div>
        </div>
        {sorted.length>0&&<div style={{borderTop:"1px solid var(--border)"}}>
          {sorted.map(a=>(
            <div key={a.id} className={`pa-mi${isNew(a)?" is-new":""}`} role="button" tabIndex={0} onClick={()=>openDetail(a)} onKeyDown={e=>{if(e.key==="Enter")openDetail(a);}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:500}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
                <PALatestLine a={a}/>
              </div>
              <div><PACatChip cat={a.category}/></div>
              <div className="ts">{nameOf(a.ownerId)}</div>
              <PADue a={a}/>
              <div><PAStatusBadge status={a.status}/></div>
            </div>
          ))}
        </div>}
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <style>{PA_CSS}</style>

      <div className="sh" style={{marginBottom:0}}>
        <div>
          <div className="card-title">Decisions from partners' meetings, tracked to closure</div>
          <div className="card-sub mt4 ts">
            {lastMeeting?`Last meeting: ${fmtDate(lastMeeting.date)} · ${actions.filter(a=>a.meetingId===lastMeeting.id).length} actionables raised`:"No meetings logged yet"}
            <span style={{marginLeft:10}} className="pa-you"><I n="lock" s={10}/> Visible to partners only</span>
          </div>
        </div>
        <div className="fxc g8">
          <button className="btn bgh" onClick={()=>setQuickM({meetingId:""})}><I n="plus" s={15}/>Quick add</button>
          <button className="btn bp" onClick={()=>setMeetingM({})}><I n="calendar" s={15}/>Log meeting</button>
        </div>
      </div>

      <div className="sg" style={{marginBottom:0}}>
        <div className="sc"><div className="sv">{mineOwned.length}</div><div className="sl">My open{mineSupp.length?` · +${mineSupp.length} supporting`:""}</div></div>
        <div className="sc" style={myOverdue?{borderColor:"#fecaca"}:undefined}><div className="sv" style={{color:myOverdue?"#b91c1c":"var(--green)"}}>{myOverdue}</div><div className="sl">My overdue</div></div>
        <div className="sc"><div className="sv">{openAll.length}</div><div className="sl">Firm-wide open</div></div>
        <div className="sc"><div className="sv" style={{color:firmOverdue?"#b45309":"var(--green)"}}>{firmOverdue}</div><div className="sl">Firm-wide overdue</div></div>
      </div>

      <div className="tabs" style={{marginBottom:0,alignSelf:"flex-start"}}>
        {tabs.map(([id,label])=><div key={id} className={`tab ${tab===id?"active":""}`} role="button" tabIndex={0} onClick={()=>setTab(id)} onKeyDown={e=>{if(e.key==="Enter")setTab(id);}}>{label}</div>)}
      </div>

      {!loaded&&<div className="al al-i"><I n="info" s={15}/><div>Loading actionables...</div></div>}

      {tab==="mine"&&<>
        <div className="ts tsl">Items you own or support. Overdue first.</div>
        <Table list={mine} empty="Nothing open on your plate."/>
      </>}

      {tab==="all"&&<>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <select className="fs" aria-label="Owner" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fOwner} onChange={e=>setFOwner(e.target.value)}>
            <option value="">All partners</option>{[...new Set([...partners.map(p=>p.id),...actions.map(a=>a.ownerId)])].map(id=><option key={id} value={id}>{nameOf(id)}</option>)}
          </select>
          <select className="fs" aria-label="Category" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fCat} onChange={e=>setFCat(e.target.value)}>
            <option value="">All categories</option>{PA_CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <select className="fs" aria-label="Status" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="open">Open + In progress</option><option value="overdue">Overdue only</option><option value="done">Done</option><option value="dropped">Dropped</option><option value="all">All statuses</option>
          </select>
          {(fOwner||fCat||fStatus!=="open")&&<button className="btn bgh bsm" onClick={()=>{setFOwner("");setFCat("");setFStatus("open");}}>✕ Clear</button>}
          <span className="tx tsl" style={{marginLeft:"auto",fontSize:12}}>{allFiltered.length} actionable{allFiltered.length===1?"":"s"}</span>
        </div>
        <Table list={allFiltered} empty="No actionables match these filters."/>
      </>}

      {tab==="meet"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        {meetingsSorted.length===0&&unlinked.length===0&&<div className="card"><div className="es"><div className="es-icon"><I n="calendar" s={36}/></div>No meetings logged yet. Use "Log meeting" after your next partners' meeting.</div></div>}
        {meetingsSorted.map(m=><MeetingCard key={m.id} m={m} items={actions.filter(a=>a.meetingId===m.id)}
          title={m.title} sub={`${fmtDate(m.date)} · Present: ${(m.attendees||[]).map(nameOf).join(", ")||"—"}`}/>)}
        {unlinked.length>0&&<MeetingCard m={null} items={unlinked} title="Not from a meeting" sub="Added directly with Quick add"/>}
      </div>}

      {tab==="review"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:"var(--navy)",color:"#fff",borderRadius:"var(--rl)",padding:"18px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:20}}>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"var(--gold-light)"}}>Review list for the next partners' meeting</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,.75)",marginTop:4}}>Everything still open, grouped by owner, overdue first. Go through this at the start of the meeting.</div>
          </div>
          <div style={{textAlign:"right",fontSize:13,color:"rgba(255,255,255,.8)",flexShrink:0}}>
            <div><strong style={{color:"#fff"}}>{openAll.length}</strong> open · <strong style={{color:"#fca5a5"}}>{firmOverdue}</strong> overdue</div>
            {lastMeeting&&<div style={{marginTop:4}}>{closedSince.length} closed since {fmtDate(lastMeeting.date)}</div>}
          </div>
        </div>
        {groups.length===0&&<div className="card"><div className="es"><div className="es-icon"><I n="check" s={36}/></div>All clear. Nothing open.</div></div>}
        {groups.map(g=>{
          const over = g.items.filter(paIsOverdue).length;
          const nm = nameOf(g.id);
          return (
            <div key={g.id} className="card" style={{padding:0,overflow:"hidden"}}>
              <div className="fxc g12" style={{padding:"14px 20px",background:"var(--cream)",borderBottom:"1px solid var(--border)"}}>
                <div style={{width:34,height:34,borderRadius:"50%",background:"var(--gold-pale)",color:"#7a5a12",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13}}>{nm.split(" ").map(x=>x[0]).join("").slice(0,2).toUpperCase()}</div>
                <div className="fw6" style={{fontSize:14.5}}>{nm}</div>
                <div className="ts tsl">{g.items.length} open{over?` · ${over} overdue`:""}</div>
              </div>
              {g.items.map(a=>(
                <div key={a.id} className="pa-ri">
                  <div style={{minWidth:0}}>
                    <div className="fw6" style={{fontSize:13.5}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
                    <PALatestLine a={a} clamp={2}/>
                  </div>
                  <div><PACatChip cat={a.category}/></div>
                  <PADue a={a}/>
                  <div><PAStatusBadge status={a.status}/></div>
                  <div className="fx g8" style={{justifyContent:"flex-end"}}>
                    <button className="btn bsc bsm" onClick={()=>markDone(a)}><I n="check" s={12}/>Done</button>
                    <button className="btn bgh bsm" onClick={()=>openDetail(a)}>Update or new date</button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        {closedSince.length>0&&(
          <div className="card">
            <div className="card-title mb8">Closed since the last meeting</div>
            {closedSince.map(a=>(
              <div key={a.id} className="pa-mi" role="button" tabIndex={0} onClick={()=>openDetail(a)} onKeyDown={e=>{if(e.key==="Enter")openDetail(a);}}>
                <div style={{fontSize:13.5,fontWeight:500}}>{a.title}</div>
                <div><PACatChip cat={a.category}/></div>
                <div className="ts">{nameOf(a.ownerId)}</div>
                <PADue a={a}/>
                <div><PAStatusBadge status={a.status}/></div>
              </div>
            ))}
          </div>
        )}
      </div>}

      {meetingM&&<PAMeetingModal partners={partners} existing={meetingM.existing} onClose={()=>setMeetingM(null)} onSave={saveMeeting}/>}
      {quickM&&<PAQuickAddModal partners={partners} meetings={meetingsSorted} defaultMeetingId={quickM.meetingId} defaultOwnerId={user.id} onClose={()=>setQuickM(null)} onSave={saveQuick}/>}
      {detail&&<PADetailDrawer key={detail.id} user={user} a={detail} users={users} partners={partners} meeting={meetingOf(detail.meetingId)}
        canDelete={isAdmin||detail.createdBy===user.id} wasNew={detailNew} onClose={()=>setDetailId(null)} onSave={saveDetail} onDelete={deleteAction}/>}
    </div>
  );
}

// Dashboard card: the signed-in partner's own open actionables
function PADashboardCard({ user, actions=[], onOpen }) {
  const mine = actions.filter(a=>paIsOpen(a)&&a.ownerId===user.id).sort(paSort);
  if(!mine.length) return null;
  const over = mine.filter(paIsOverdue).length;
  return (
    <div className="card mb22">
      <style>{PA_CSS}</style>
      <div className="fxb mb8">
        <div className="fxc g8">
          <div className="card-title">My Partner Actionables</div>
          {over>0&&<span className="pa-chip" style={{background:"#fee2e2",color:"#b91c1c",fontWeight:600}}>{over} overdue</span>}
          <span className="pa-chip" style={{background:"var(--cream)",color:"var(--slate)"}}>{mine.length} open</span>
        </div>
        <button className="btn bgh bsm" onClick={onOpen}>View all <I n="arrowright" s={13}/></button>
      </div>
      {mine.slice(0,5).map(a=>{
        const info = paDueInfo(a);
        return (
          <div key={a.id} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 170px 130px",gap:14,alignItems:"center",padding:"10px 0",borderTop:"1px solid var(--cream)"}}>
            <div style={{fontSize:13.5,fontWeight:500}}>{a.title}{paIsNewFor(a,user.id)&&<PANewPill/>}</div>
            <div className="tx tsl">{a.category}</div>
            <div style={{fontSize:12.5,fontWeight:600,textAlign:"right",color:info.kind==="ok"?"var(--slate)":PA_DUE_COLOR[info.kind]}}>{info.sub}</div>
          </div>
        );
      })}
      {mine.length>5&&<div className="tx tsl mt8">+ {mine.length-5} more</div>}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ASSIGNED WORK (v49): partners and managers
// Firm work (staffing, operations, BD, internal events, training, admin) given by partners to managers.
// Partners assign items, or managers log a meeting with partners and add what came out of it.
// Managers mark items done with a closure note; any partner accepts (closes) or reopens with a reason.
// Data: "work_meetings" and "work_items", kept apart from Partner Actionables so partners' meetings stay private.
// Managers load only items they own, support or created.
// ══════════════════════════════════════════════════════════════
const AW_CATEGORIES = ["Staffing","Operations","Business Development","Internal event","Training","Admin","Other"];
const AW_STATUSES_MGR = ["open","in_progress","submitted"];
const AW_STATUSES_PTR = ["open","in_progress","submitted","done","dropped"];
const AW_STATUS_LABEL = { open:"Open", in_progress:"In progress", submitted:"Awaiting acceptance", done:"Accepted", dropped:"Dropped" };
const AW_STATUS_SEG   = { open:"Open", in_progress:"In progress", submitted:"Done, send for acceptance", done:"Accepted", dropped:"Dropped" };
const AW_STATUS_CLASS = { open:"bp2", in_progress:"bac", submitted:"brs", done:"ba", dropped:"bcl" };
const AW_CAT_STYLE = {
  "Staffing":             { background:"#e0e7ff", color:"#3730a3" },
  "Operations":           { background:"#f1f5f9", color:"#475569" },
  "Business Development": { background:"#fef3c7", color:"#92400e" },
  "Internal event":       { background:"#fce7f3", color:"#9d174d" },
  "Training":             { background:"#d1fae5", color:"#065f46" },
  "Admin":                { background:"#dbeafe", color:"#1e40af" },
  "Other":                { background:"#f3e8ff", color:"#6d28d9" },
};
const AW_SEEN_START = "2026-10-08T00:00:00+05:30";

const awIsWorking = a => a.status==="open" || a.status==="in_progress";        // with the manager
const awIsOpen    = a => awIsWorking(a) || a.status==="submitted";              // not yet closed
const awIsOverdue = a => awIsWorking(a) && !!a.dueDate && a.dueDate < todayStr();
function awDueInfo(a) {
  if(a.status==="submitted") return { kind:"ok", sub:"Awaiting acceptance" };
  if(!awIsOpen(a)) {
    const when = a.closedAt ? fmtDate(a.closedAt.slice(0,10)) : "";
    return { kind:"closed", sub: a.status==="done" ? `Accepted ${when}` : `Dropped ${when}` };
  }
  return paDueInfo(a);
}
const awRank = a => a.status==="submitted" ? 3 : (k => k==="over"?0 : k==="soon"?1 : 2)(awDueInfo(a).kind);
const awSort = (a,b) => awRank(a)-awRank(b) || (a.dueDate||"9999").localeCompare(b.dueDate||"9999");
const awBlankRow = () => ({ key:genId(), title:"", category:"", ownerId:"", supportIds:[], dueDate:"", priority:"Medium" });
const awInvolves = (a, uid) => a.ownerId===uid || (a.supportIds||[]).includes(uid);
// v51: managers on an item are equal. Stored as ownerId (first picked) + supportIds (the rest), so older items still work.
const awMgrIds = a => [a.ownerId, ...(a.supportIds||[])].filter((x,i,arr)=>x&&arr.indexOf(x)===i);
const awSetMgrs = ids => ({ ownerId: ids[0]||"", supportIds: ids.slice(1) });
// Who should see a "New" pill: partners who gave it (or any partner once it awaits acceptance); the owner and supporting managers.
const awRelevant = (a, user) => user.role==="partner"
  ? ((a.fromPartnerIds||[]).includes(user.id) || a.status==="submitted")
  : awInvolves(a, user.id);
const awIsNewFor = (a, user) => {
  const h = a.history||[]; const last = h[h.length-1];
  if(!last || last.byId===user.id || !awRelevant(a,user)) return false;
  const at = new Date(last.at).getTime();
  if(at < new Date(AW_SEEN_START).getTime()) return false;
  const seen = a.seenBy?.[user.id];
  return !seen || at > new Date(seen).getTime();
};
const awMarkSeen = (a, user) => {
  if(!awIsNewFor(a, user)) return;
  updateDoc(doc(db, "work_items", a.id), { [`seenBy.${user.id}`]: new Date().toISOString() })
    .catch(e=>console.error("awMarkSeen error", e));
};
// Sidebar badge: partners → items awaiting acceptance (firm-wide, any partner can accept); managers → their own overdue.
const awBadgeCount = (items, user) => !user ? 0 : user.role==="partner"
  ? items.filter(a=>a.status==="submitted").length
  : items.filter(a=>awInvolves(a,user.id) && awIsOverdue(a)).length;

function AWCatChip({ cat }) {
  return <span className="pa-chip" style={AW_CAT_STYLE[cat]||AW_CAT_STYLE.Other}>{cat||"—"}</span>;
}
function AWStatusBadge({ status }) {
  return <span className={`bdg ${AW_STATUS_CLASS[status]||"bcl"}`}>{AW_STATUS_LABEL[status]||status}</span>;
}
function AWDue({ a }) {
  const info = awDueInfo(a);
  const c = PA_DUE_COLOR[info.kind];
  return <div>
    <div style={{fontSize:13,fontWeight:info.kind==="over"||info.kind==="soon"?600:400,color:c}}>{a.dueDate?fmtDate(a.dueDate):"—"}</div>
    <div style={{fontSize:11.5,color:info.kind==="ok"?"var(--slate)":c}}>{info.sub}</div>
  </div>;
}
// Toggle chips for a list of people (partners present, partners who gave it, supporting managers)
function AWPeopleChips({ people, value=[], onChange, exclude, big=false }) {
  const opts = people.filter(p=>p.id!==exclude);
  if(!opts.length) return <span className="tx tsl">—</span>;
  return <div style={{display:"flex",gap:big?8:4,flexWrap:"wrap"}}>
    {opts.map(p=>{
      const on = value.includes(p.id);
      return <button key={p.id} type="button" aria-pressed={on} className={`pa-pchip ${on?"on":""}`} style={big?{padding:"8px 14px",fontSize:13}:undefined}
        onClick={()=>onChange(on?value.filter(x=>x!==p.id):[...value,p.id])}>{big?p.name:paFirstName(p.name)}</button>;
    })}
  </div>;
}

// ── Log / edit a meeting with partners (partners or managers) ──
function AWMeetingModal({ user, partners, managers, existing, onClose, onSave }) {
  const isEdit = !!existing;
  const [date,setDate]     = useState(existing?.date || todayStr());
  const [title,setTitle]   = useState(existing?.title || (user.role==="partner"?"Meeting with managers":"Meeting with partners"));
  const [present,setPres]  = useState(existing?.partnerIds || (user.role==="partner"?[user.id]:[]));
  const [notes,setNotes]   = useState(existing?.notes || "");
  const [rows,setRows]     = useState([{...awBlankRow(),ownerId:user.role==="manager"?user.id:""},awBlankRow(),awBlankRow()]);
  const [err,setErr]       = useState("");
  const setRow = (key, patch) => setRows(rs=>rs.map(r=>r.key===key?{...r,...patch}:r));
  const addRow = () => setRows(rs=>[...rs,awBlankRow()]);
  const removeRow = key => setRows(rs=>rs.length>1?rs.filter(r=>r.key!==key):[awBlankRow()]);
  const isBlank = r => !r.title.trim() && !r.dueDate && !r.category;
  const filled = rows.filter(r=>!isBlank(r));
  const save = () => {
    setErr("");
    if(!date){ setErr("Meeting date is required."); return; }
    if(!title.trim()){ setErr("Meeting title is required."); return; }
    if(!present.length){ setErr("Select the partners who were present."); return; }
    if(!isEdit){
      for(let i=0;i<rows.length;i++){
        const r = rows[i]; if(isBlank(r)) continue;
        const miss = [!r.title.trim()&&"work",!r.category&&"category",!r.ownerId&&"at least one manager",!r.dueDate&&"due date"].filter(Boolean);
        if(miss.length){ setErr(`Row ${i+1} needs: ${miss.join(", ")}.`); return; }
      }
    }
    onSave({ date, title:title.trim(), partnerIds:present, notes:notes.trim() }, isEdit?[]:filled);
  };
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:isEdit?620:1180}}>
        <div className="md-title">{isEdit?"Edit meeting":user.role==="partner"?"Log meeting with managers":"Log meeting with partners"}</div>
        {!isEdit&&<div className="ts tsl" style={{marginTop:-12,marginBottom:18}}>Record the meeting once, then add every piece of work that came out of it.</div>}
        {err&&<div className="err">{err}</div>}
        <div style={{display:"grid",gridTemplateColumns:isEdit?"1fr 1fr":"180px 300px minmax(0,1fr)",gap:16}}>
          <div className="fg"><label className="fl" htmlFor="aw-md">Meeting date</label><input id="aw-md" type="date" className="fi" value={date} max={todayStr()} onChange={e=>setDate(e.target.value)}/></div>
          <div className="fg"><label className="fl" htmlFor="aw-mt">Title</label><input id="aw-mt" className="fi" value={title} onChange={e=>setTitle(e.target.value)}/></div>
          <div className="fg" style={isEdit?{gridColumn:"1 / -1"}:undefined}>
            <div className="fl">Partners present *</div>
            <AWPeopleChips people={partners} value={present} onChange={setPres} big/>
          </div>
        </div>
        <div className="fg"><label className="fl" htmlFor="aw-mn">Short notes (optional)</label>
          <textarea id="aw-mn" className="fta" style={{minHeight:56}} placeholder="Topics discussed..." value={notes} onChange={e=>setNotes(e.target.value)}/>
        </div>
        {!isEdit&&(
          <div style={{borderTop:"1.5px solid var(--border)",paddingTop:16}}>
            <div className="fxb mb8">
              <div className="fw6" style={{fontSize:15}}>Work <span className="tsl" style={{fontWeight:500}}>({filled.length})</span></div>
              <div className="tx tsl">Each needs the work, a category, at least one manager and a due date. Blank rows are ignored.</div>
            </div>
            <div className="pa-grid" style={{marginBottom:6,gridTemplateColumns:"24px minmax(0,1fr) 165px 300px 145px 100px 28px"}}>
              <div className="pa-hd">#</div><div className="pa-hd">Work</div><div className="pa-hd">Category</div><div className="pa-hd">Managers</div><div className="pa-hd">Due date</div><div className="pa-hd">Priority</div><div/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {rows.map((r,idx)=>(
                <div key={r.key} className="pa-grid" style={{gridTemplateColumns:"24px minmax(0,1fr) 165px 300px 145px 100px 28px"}}>
                  <div className="ts tsl fw6">{idx+1}</div>
                  <input className="fi" aria-label={`Work ${idx+1}`} placeholder="What needs to be done?" value={r.title}
                    onChange={e=>setRow(r.key,{title:e.target.value})}
                    onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); if(idx===rows.length-1) addRow(); } }}/>
                  <select className="fs" aria-label="Category" value={r.category} onChange={e=>setRow(r.key,{category:e.target.value})}>
                    <option value="">Select</option>{AW_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                  <AWPeopleChips people={managers} value={awMgrIds(r)} onChange={v=>setRow(r.key,awSetMgrs(v))}/>
                  <input type="date" className="fi" aria-label="Due date" value={r.dueDate} onChange={e=>setRow(r.key,{dueDate:e.target.value})}/>
                  <select className="fs" aria-label="Priority" value={r.priority} onChange={e=>setRow(r.key,{priority:e.target.value})}>
                    {PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}
                  </select>
                  <button type="button" className="btn bgh bic bsm" aria-label="Remove row" title="Remove row" onClick={()=>removeRow(r.key)}><I n="x" s={13}/></button>
                </div>
              ))}
            </div>
            <button type="button" className="btn bsm" onClick={addRow} style={{marginTop:10,background:"transparent",border:"1.5px dashed var(--gold)",color:"var(--navy)"}}><I n="plus" s={13}/>Add another row</button>
          </div>
        )}
        <div className="md-actions" style={{justifyContent:"space-between",alignItems:"center"}}>
          <div className="tx tsl">{isEdit?"Work items are edited individually from their own panel.":user.role==="manager"?"The partners present will see these marked New and can adjust due dates or priority.":""}</div>
          <div className="fx g8">
            <button className="btn bgh" onClick={onClose}>Cancel</button>
            <button className="btn bp" onClick={save}><I n="check" s={15}/>{isEdit?"Save meeting":`Save meeting${filled.length?` and ${filled.length} item${filled.length===1?"":"s"}`:""}`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Quick add: one item ──
function AWQuickAddModal({ user, partners, managers, meetings, defaultMeetingId, onClose, onSave }) {
  const [r,setR] = useState({ ...awBlankRow(), ownerId:user.role==="manager"?user.id:"" });
  const [from,setFrom] = useState(user.role==="partner"?[user.id]:[]);
  const [meetingId,setMeetingId] = useState(defaultMeetingId||"");
  const [err,setErr] = useState("");
  const pickMeeting = id => {
    setMeetingId(id);
    const m = meetings.find(x=>x.id===id);
    if(m && (m.partnerIds||[]).length) setFrom(m.partnerIds);
  };
  const save = () => {
    const miss = [!r.title.trim()&&"work",!r.category&&"category",!r.ownerId&&"at least one manager",!r.dueDate&&"due date",!from.length&&"given by"].filter(Boolean);
    if(miss.length){ setErr(`Please fill in: ${miss.join(", ")}.`); return; }
    onSave(r, from, meetingId||null);
  };
  return (
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
        <div className="md-title">{user.role==="partner"?"Assign work":"Add work item"}</div>
        {err&&<div className="err">{err}</div>}
        <div className="fg"><label className="fl" htmlFor="aw-qt">Work</label>
          <textarea id="aw-qt" className="fta" style={{minHeight:60}} placeholder="What needs to be done?" value={r.title} onChange={e=>setR(x=>({...x,title:e.target.value}))}/>
        </div>
        <div className="g2">
          <div className="fg"><label className="fl" htmlFor="aw-qc">Category</label>
            <select id="aw-qc" className="fs" value={r.category} onChange={e=>setR(x=>({...x,category:e.target.value}))}><option value="">Select</option>{AW_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select>
          </div>
          <div className="fg"><label className="fl" htmlFor="aw-qp">Priority</label>
            <select id="aw-qp" className="fs" value={r.priority} onChange={e=>setR(x=>({...x,priority:e.target.value}))}>{PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}</select>
          </div>
          <div className="fg" style={{gridColumn:"1 / -1"}}><label className="fl" htmlFor="aw-qd">Due date</label>
            <input id="aw-qd" type="date" className="fi" value={r.dueDate} onChange={e=>setR(x=>({...x,dueDate:e.target.value}))}/>
          </div>
        </div>
        <div className="fg"><div className="fl">Managers * <span className="tsl" style={{textTransform:"none",letterSpacing:0,fontWeight:400}}>(select one or more; each can update it and mark it done)</span></div>
          <AWPeopleChips people={managers} value={awMgrIds(r)} onChange={v=>setR(x=>({...x,...awSetMgrs(v)}))} big/>
        </div>
        <div className="fg"><label className="fl" htmlFor="aw-qm">From meeting</label>
          <select id="aw-qm" className="fs" value={meetingId} onChange={e=>pickMeeting(e.target.value)}>
            <option value="">Not from a meeting</option>
            {meetings.map(m=><option key={m.id} value={m.id}>{m.title} · {fmtDate(m.date)}</option>)}
          </select>
        </div>
        <div className="fg"><div className="fl">Given by *</div>
          <AWPeopleChips people={partners} value={from} onChange={setFrom} big/>
        </div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bp" onClick={save}><I n="check" s={15}/>{user.role==="partner"?"Assign work":"Add item"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Reopen with a reason (partners) ──
function AWReopenModal({ a, onClose, onSave }) {
  const [reason,setReason] = useState("");
  const [err,setErr] = useState("");
  return (
    <div className="mo" style={{zIndex:1100}} onClick={onClose}>
      <div className="md" style={{maxWidth:460}} onClick={e=>e.stopPropagation()}>
        <div className="md-title">Reopen work</div>
        <div className="ts tsl mb16">{a.title}</div>
        {err&&<div className="err">{err}</div>}
        <div className="fg"><label className="fl" htmlFor="aw-rr">What still needs to be done? *</label>
          <textarea id="aw-rr" className="fta" placeholder="The manager will see this." value={reason} onChange={e=>{setReason(e.target.value);setErr("");}}/>
        </div>
        <div className="md-actions">
          <button className="btn bgh" onClick={onClose}>Cancel</button>
          <button className="btn bam" onClick={()=>{ if(!reason.trim()){ setErr("Give a reason so the manager knows what's missing."); return; } onSave(reason.trim()); }}><I n="arrowleft" s={14}/>Reopen</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail drawer ──
function AWDetailDrawer({ user, a, users, partners, managers, meeting, canDelete, wasNew=false, onClose, onSave, onDelete, onAccept, onReopen }) {
  const isP = user.role==="partner";
  const [status,setStatus] = useState(a.status);
  const [note,setNote]     = useState("");
  const [due,setDue]       = useState(a.dueDate||"");
  const [prio,setPrio]     = useState(a.priority||"Medium");
  const [editing,setEditing] = useState(false);
  const [showForm,setShowForm] = useState(false);
  const blankD = () => ({ title:a.title, category:a.category, ownerId:a.ownerId, supportIds:a.supportIds||[], fromPartnerIds:a.fromPartnerIds||[] });
  const [d,setD] = useState(blankD());
  const [err,setErr] = useState("");
  const nameOf = id => users.find(u=>u.id===id)?.name || "—";
  const info = awDueInfo(a);
  const statuses = isP ? AW_STATUSES_PTR : AW_STATUSES_MGR;
  const closed = !awIsOpen(a);
  const canUpdate = isP || (awInvolves(a, user.id) && !closed);
  const willWork = status==="open"||status==="in_progress";
  const mgrOptions = [...managers, ...awMgrIds(a).filter(id=>!managers.some(m=>m.id===id)).map(id=>users.find(u=>u.id===id)).filter(Boolean)];

  const save = () => {
    setErr("");
    const changes = [];
    if(status!==a.status) changes.push(`${AW_STATUS_LABEL[a.status]} → ${AW_STATUS_LABEL[status]}`);
    const newDue = isP && willWork ? due : a.dueDate;
    if(isP && willWork && !due){ setErr("Due date is required."); return; }
    if(newDue!==a.dueDate) changes.push(`Due ${fmtDate(a.dueDate)} → ${fmtDate(newDue)}`);
    const newPrio = isP ? prio : (a.priority||"Medium");
    if(newPrio!==(a.priority||"Medium")) changes.push(`Priority ${a.priority||"Medium"} → ${newPrio}`);
    let details = {};
    if(editing){
      if(!d.title.trim()){ setErr("The work description is required."); return; }
      if(!d.category||!d.ownerId){ setErr("Category and at least one manager are required."); return; }
      if(!d.fromPartnerIds.length){ setErr("Select at least one partner under Given by."); return; }
      const sup = d.supportIds.filter(x=>x!==d.ownerId);
      if(d.title.trim()!==a.title) changes.push("Text edited");
      if(d.category!==a.category) changes.push(`Category ${a.category} → ${d.category}`);
      const newMgrs = [d.ownerId, ...sup];
      if([...newMgrs].sort().join()!==[...awMgrIds(a)].sort().join()) changes.push(`Managers: ${newMgrs.map(nameOf).join(", ")}`);
      if([...d.fromPartnerIds].sort().join()!==[...(a.fromPartnerIds||[])].sort().join()) changes.push(`Given by: ${d.fromPartnerIds.map(nameOf).join(", ")}`);
      details = { title:d.title.trim(), category:d.category, ownerId:d.ownerId, supportIds:sup, fromPartnerIds:d.fromPartnerIds };
    }
    if(status==="submitted" && a.status!=="submitted" && !note.trim()){ setErr("Add a closure note so the partner sees what was delivered."); return; }
    if(status==="dropped" && a.status!=="dropped" && !note.trim()){ setErr("A reason is required to drop an item."); return; }
    if(!changes.length && !note.trim()){ setErr("Nothing to save. Change the status or add a note."); return; }
    const now = new Date().toISOString();
    const nowOpen = status==="open"||status==="in_progress"||status==="submitted";
    const closedAt = nowOpen ? null : (awIsOpen(a) || status!==a.status ? now : (a.closedAt||now));
    const extra = status==="submitted"&&a.status!=="submitted" ? { submittedAt:now, submittedBy:user.id, submittedByName:user.name }
      : status==="done"&&a.status!=="done" ? { acceptedBy:user.id, acceptedAt:now } : {};
    const entry = { at:now, byId:user.id, byName:user.name, type:changes.length?"update":"note", change:changes.join(" · ")||"Note", note:note.trim() };
    onSave({ ...a, ...details, ...extra, status, dueDate:newDue, priority:newPrio, closedAt, updatedAt:now, updatedBy:user.id, history:[...(a.history||[]), entry] }, entry.change);
  };

  const noteLabel = status==="submitted"&&a.status!=="submitted" ? "Closure note (required)" : status==="dropped"&&a.status!=="dropped" ? "Reason for dropping (required)" : "Update note";
  const notePh = status==="submitted" ? "What was delivered?" : status==="dropped" ? "Why is this no longer needed?" : "What has happened since the last update?";
  const history = (a.history||[]).slice().reverse();
  const latest = paLatestUpdate(a);
  const cancelForm = () => { setShowForm(false); setEditing(false); setErr(""); setNote(""); setStatus(a.status); setDue(a.dueDate||""); setPrio(a.priority||"Medium"); setD(blankD()); };
  const dotColor = h => h.type==="created" ? "#94a3b8" : /→ Accepted/.test(h.change) ? "var(--green)" : /→ Awaiting/.test(h.change) ? "var(--gold)" : /→ Dropped/.test(h.change) ? "var(--slate)" : /Reopened/.test(h.change) ? "var(--amber)" : /Due /.test(h.change) ? "var(--amber)" : h.type==="note" ? "var(--gold)" : "#1e40af";

  return (
    <>
      <div className="pa-drawer-bg" onClick={onClose}/>
      <div className="pa-drawer" role="dialog" aria-label="Work item details">
        <div className="fxb">
          <div className="fx g8" style={{flexWrap:"wrap"}}>
            <AWCatChip cat={a.category}/>
            <AWStatusBadge status={a.status}/>
            {info.kind==="over"&&<span className="pa-chip" style={{background:"#fee2e2",color:"#b91c1c",fontWeight:600}}>{info.sub}</span>}
          </div>
          <button className="btn bgh bic bsm" aria-label="Close" onClick={onClose}><I n="x" s={16}/></button>
        </div>
        {!editing
          ? <div style={{fontFamily:"'Playfair Display',serif",fontSize:23,lineHeight:1.3}}>{a.title}</div>
          : <div className="fg" style={{marginBottom:0}}><label className="fl" htmlFor="aw-et">Work</label><textarea id="aw-et" className="fta" style={{minHeight:60}} value={d.title} onChange={e=>setD(x=>({...x,title:e.target.value}))}/></div>}

        {isP&&a.status==="submitted"&&!showForm&&(
          <div className="al al-w" style={{marginBottom:0,alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",gap:8,alignItems:"center"}}><I n="info" s={15}/><div><strong>{nameOf(a.submittedBy||a.ownerId)}</strong> has marked this done. Accept to close it, or reopen with a reason.</div></div>
            <div className="fx g8" style={{flexShrink:0}}>
              <button className="btn bsc bsm" onClick={()=>onAccept(a)}><I n="check" s={13}/>Accept</button>
              <button className="btn bgh bsm" onClick={()=>onReopen(a)}>Reopen</button>
            </div>
          </div>
        )}

        {latest
          ? <div className="pa-latest">
              <div className="fxb" style={{gap:10}}>
                <div className="pa-k" style={{color:"#92400e"}}>Latest update{wasNew&&<span className="pa-new">New for you</span>}</div>
                <div className="tx" style={{color:"#92400e"}}>{paTimeAgo(latest.at)}</div>
              </div>
              <div className="ts" style={{marginTop:6,color:"var(--navy)"}}>
                <strong>{latest.byName}</strong>
                {latest.change&&latest.change!=="Note"&&<span style={{color:"var(--navy-mid)",fontWeight:600}}> · {latest.change}</span>}
              </div>
              {latest.note&&<div style={{fontSize:14,color:"#334155",marginTop:6,lineHeight:1.55,whiteSpace:"pre-wrap"}}>{latest.note}</div>}
            </div>
          : <div className="pa-latest" style={{background:"var(--cream)",borderColor:"var(--border)",borderLeftColor:"var(--slate-light)"}}>
              <div className="pa-k">Latest update{wasNew&&<span className="pa-new">New for you</span>}</div>
              <div className="ts tsl" style={{marginTop:6}}>No updates yet since this was {a.createdByRole==="manager"?`logged by ${a.createdByName}`:"assigned"}.</div>
            </div>}

        {!editing?(
          <div className="pa-meta">
            <div style={{gridColumn:"span 2"}}><div className="pa-k">Managers</div><div className="pa-v fw6">{awMgrIds(a).map(nameOf).join(", ")||"—"}</div></div>
            <div><div className="pa-k">Priority</div><div className="pa-v fw6" style={{color:PA_PRIO_COLOR[a.priority]||"var(--slate)"}}>{a.priority||"Medium"}</div></div>
            <div><div className="pa-k">Due</div><div className="pa-v fw6" style={{color:PA_DUE_COLOR[info.kind]}}>{a.dueDate?fmtDate(a.dueDate):"—"}</div>
              {a.originalDueDate&&a.originalDueDate!==a.dueDate&&<div className="tx tsl">Originally {fmtDate(a.originalDueDate)}</div>}</div>
            <div><div className="pa-k">Given by</div><div className="pa-v">{(a.fromPartnerIds||[]).map(nameOf).join(", ")||"—"}</div></div>
            <div><div className="pa-k">{meeting?"Raised in":"Created by"}</div><div className="pa-v">{meeting?`${meeting.title}, ${fmtDate(meeting.date)}`:(a.createdByName||nameOf(a.createdBy))}</div></div>
          </div>
        ):(
          <div className="pa-meta" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div><label className="fl" htmlFor="aw-ec">Category</label><select id="aw-ec" className="fs" value={d.category} onChange={e=>setD(x=>({...x,category:e.target.value}))}>{AW_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
            <div style={{gridColumn:"1 / -1"}}><div className="fl">Managers</div><AWPeopleChips people={mgrOptions} value={awMgrIds(d)} onChange={v=>setD(x=>({...x,...awSetMgrs(v)}))}/></div>
            <div><div className="fl">Given by</div><AWPeopleChips people={partners} value={d.fromPartnerIds} onChange={v=>setD(x=>({...x,fromPartnerIds:v}))}/></div>
          </div>
        )}
        <div className="fx g8" style={{flexWrap:"wrap"}}>
          {canUpdate&&!showForm&&<button className="btn bp bsm" onClick={()=>setShowForm(true)}><I n="plus" s={13}/>Add an update</button>}
          {isP&&<button className="btn bgh bsm" onClick={()=>{const on=!editing;setEditing(on);if(on)setShowForm(true);setD(blankD());}}><I n="edit" s={13}/>{editing?"Cancel editing details":"Edit details"}</button>}
          {canDelete&&<button className="btn bd bsm" onClick={()=>onDelete(a)}><I n="trash" s={13}/>Delete</button>}
        </div>

        {showForm&&<div style={{display:"flex",flexDirection:"column",gap:14,border:"1.5px solid var(--navy)",borderRadius:12,padding:18}}>
          <div className="fw6" style={{fontSize:14.5}}>{editing?"Save changes":"Add an update"}</div>
          {err&&<div className="err" style={{marginBottom:0}}>{err}</div>}
          <div>
            <div className="fl">Status</div>
            <div className="pa-seg" role="group" aria-label="Status">
              {statuses.map(s=><button key={s} type="button" className={status===s?"on":""} aria-pressed={status===s} onClick={()=>setStatus(s)}>{isP?AW_STATUS_LABEL[s]:AW_STATUS_SEG[s]}</button>)}
            </div>
          </div>
          <div>
            <label className="fl" htmlFor="aw-note">{noteLabel}</label>
            <textarea id="aw-note" className="fta" placeholder={notePh} value={note} onChange={e=>setNote(e.target.value)}/>
          </div>
          {isP&&willWork&&(
            <div style={{display:"grid",gridTemplateColumns:"190px 150px minmax(0,1fr)",gap:12,alignItems:"end"}}>
              <div><label className="fl" htmlFor="aw-due">Due date</label><input id="aw-due" type="date" className="fi" value={due} onChange={e=>setDue(e.target.value)}/></div>
              <div><label className="fl" htmlFor="aw-pr">Priority</label><select id="aw-pr" className="fs" value={prio} onChange={e=>setPrio(e.target.value)}>{PA_PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
              <div className="tx tsl" style={{paddingBottom:12}}>The original due date stays in the history.</div>
            </div>
          )}
          {!isP&&<div className="tx tsl">Due date and priority are set by partners. If you need more time, say so in the note.</div>}
          <div className="fx g8" style={{justifyContent:"flex-end"}}>
            <button className="btn bgh" onClick={cancelForm}>Cancel</button>
            <button className="btn bp" onClick={save}><I n="check" s={15}/>Save update</button>
          </div>
        </div>}

        <div>
          <div className="fw6" style={{fontSize:14.5,marginBottom:12}}>History <span className="tx tsl" style={{fontWeight:400}}>({history.length})</span></div>
          {history.map((h,i)=>(
            <div key={i} style={{display:"flex",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:12}}>
                <div style={{width:12,height:12,borderRadius:"50%",flexShrink:0,background:dotColor(h)}}/>
                {i<history.length-1&&<div style={{width:2,flexGrow:1,background:"var(--border)",margin:"4px 0"}}/>}
              </div>
              <div style={{paddingBottom:16,flex:1}}>
                <div className="ts"><strong>{h.byName}</strong> <span className="tsl">· {fmtDate(h.at.slice(0,10))} at {new Date(h.at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span></div>
                {h.change&&h.change!=="Note"&&<div style={{fontSize:12.5,color:"var(--navy-mid)",fontWeight:600,marginTop:3}}>{h.change}</div>}
                {h.note&&<div className="ts" style={{color:"#334155",marginTop:3,lineHeight:1.45,whiteSpace:"pre-wrap"}}>{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main page ──
function AssignedWork({ user, users=[], meetings=[], setMeetings, items=[], setItems, loaded=true }) {
  const isP = user.role==="partner";
  const isAdmin = user.email===ADMIN_EMAIL;
  const [tab,setTab]         = useState(isP?"accept":"mine");
  const [fOwner,setFOwner]   = useState("");
  const [fCat,setFCat]       = useState("");
  const [fStatus,setFStatus] = useState("open");
  const [meetingM,setMeetingM] = useState(null);
  const [quickM,setQuickM]   = useState(null);
  const [detailId,setDetailId] = useState(null);
  const [detailNew,setDetailNew] = useState(false);
  const [reopenFor,setReopenFor] = useState(null);

  const partners = users.filter(u=>u.role==="partner"&&u.active!==false).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const managers = users.filter(u=>u.role==="manager"&&u.active!==false).slice().sort((a,b)=>a.name.localeCompare(b.name));
  const nameOf = id => users.find(u=>u.id===id)?.name || "—";
  const meetingOf = id => meetings.find(m=>m.id===id);
  const isNew = a => awIsNewFor(a, user);
  const openDetail = a => { setDetailNew(isNew(a)); setDetailId(a.id); awMarkSeen(a, user); };

  // Managers only see items they own, support or created (the data is already scoped that way).
  const visible = isP ? items : items.filter(a=>awInvolves(a,user.id)||a.createdBy===user.id);
  const openAll = visible.filter(awIsOpen).sort(awSort);
  const awaiting = openAll.filter(a=>a.status==="submitted");
  const givenByMe = openAll.filter(a=>(a.fromPartnerIds||[]).includes(user.id));
  const mine      = openAll.filter(a=>awInvolves(a,user.id) && awIsWorking(a)).sort(awSort);
  const myAwaiting = awaiting.filter(a=>awInvolves(a,user.id));
  const myOverdue = mine.filter(awIsOverdue).length;
  const firmOverdue = openAll.filter(awIsOverdue).length;
  const closedList = visible.filter(a=>!awIsOpen(a)).sort((a,b)=>(b.closedAt||"").localeCompare(a.closedAt||""));
  const myMeetingIds = new Set(visible.map(a=>a.meetingId).filter(Boolean));
  const meetingsSorted = meetings
    .filter(m=>isP || m.createdBy===user.id || myMeetingIds.has(m.id))
    .slice().sort((a,b)=>(b.date||"").localeCompare(a.date||"")||(b.createdAt||"").localeCompare(a.createdAt||""));

  const allFiltered = visible.filter(a=>{
    if(fOwner&&!awInvolves(a,fOwner)) return false;
    if(fCat&&a.category!==fCat) return false;
    if(fStatus==="open"&&!awIsOpen(a)) return false;
    if(fStatus==="overdue"&&!awIsOverdue(a)) return false;
    if(fStatus==="submitted"&&a.status!=="submitted") return false;
    if(fStatus==="done"&&a.status!=="done") return false;
    if(fStatus==="dropped"&&a.status!=="dropped") return false;
    return true;
  }).sort((a,b)=>awIsOpen(a)!==awIsOpen(b)?(awIsOpen(a)?-1:1):awIsOpen(a)?awSort(a,b):(b.closedAt||"").localeCompare(a.closedAt||""));

  // ── writes ──
  const newItem = (r, fromIds, meetingId, now) => ({
    id:genId(), meetingId:meetingId||null, title:r.title.trim(), category:r.category,
    ownerId:r.ownerId, supportIds:(r.supportIds||[]).filter(x=>x!==r.ownerId), fromPartnerIds:fromIds,
    dueDate:r.dueDate, originalDueDate:r.dueDate, priority:r.priority||"Medium",
    status:"open", closedAt:null,
    createdBy:user.id, createdByName:user.name, createdByRole:user.role, createdAt:now, updatedAt:now, updatedBy:user.id,
    history:[{ at:now, byId:user.id, byName:user.name, type:"created",
      change: isP ? (meetingId?"Assigned in meeting":"Assigned") : "Logged by manager",
      note:`Managers ${awMgrIds(r).map(nameOf).join(", ")} · due ${fmtDate(r.dueDate)} · ${r.priority||"Medium"} priority · given by ${fromIds.map(nameOf).join(", ")}` }],
  });
  const saveMeeting = (m, rows) => {
    const now = new Date().toISOString();
    if(meetingM?.existing){
      const id = meetingM.existing.id;
      setMeetings(prev=>prev.map(x=>x.id===id?{...x,...m,updatedAt:now,updatedBy:user.id}:x));
      addAudit(user.id,user.name,"AW_EDIT_MEETING",`Edited work meeting of ${m.date}`);
    } else {
      const mid = genId();
      setMeetings(prev=>[...prev,{ id:mid, ...m, createdBy:user.id, createdByName:user.name, createdByRole:user.role, createdAt:now }]);
      if(rows.length) setItems(prev=>[...prev,...rows.map(r=>newItem(r,m.partnerIds,mid,now))]);
      addAudit(user.id,user.name,"AW_LOG_MEETING",`Logged work meeting of ${m.date} with ${rows.length} item(s)`);
      setTab("meet");
    }
    setMeetingM(null);
  };
  const saveQuick = (r, fromIds, meetingId) => {
    const now = new Date().toISOString();
    setItems(prev=>[...prev,newItem(r,fromIds,meetingId,now)]);
    addAudit(user.id,user.name,"AW_ADD_ITEM",`Work for ${awMgrIds(r).map(nameOf).join(", ")}: ${r.title.trim()}`);
    setQuickM(null);
  };
  const saveDetail = (updated, summary) => {
    setItems(prev=>prev.map(x=>x.id===updated.id?updated:x));
    addAudit(user.id,user.name,"AW_UPDATE_ITEM",`${updated.title}: ${summary}`);
    setDetailId(null);
  };
  const accept = a => {
    const now = new Date().toISOString();
    setItems(prev=>prev.map(x=>x.id===a.id?{...x,status:"done",closedAt:now,acceptedBy:user.id,acceptedAt:now,updatedAt:now,updatedBy:user.id,
      history:[...(x.history||[]),{at:now,byId:user.id,byName:user.name,type:"update",change:"Awaiting acceptance → Accepted",note:""}]}:x));
    addAudit(user.id,user.name,"AW_ACCEPT_ITEM",`Accepted: ${a.title}`);
    setDetailId(null);
  };
  const reopen = (a, reason) => {
    const now = new Date().toISOString();
    setItems(prev=>prev.map(x=>x.id===a.id?{...x,status:"in_progress",closedAt:null,updatedAt:now,updatedBy:user.id,reopenCount:(x.reopenCount||0)+1,
      history:[...(x.history||[]),{at:now,byId:user.id,byName:user.name,type:"update",change:"Reopened · Awaiting acceptance → In progress",note:reason}]}:x));
    addAudit(user.id,user.name,"AW_REOPEN_ITEM",`Reopened: ${a.title}`);
    setReopenFor(null); setDetailId(null);
  };
  const deleteItem = a => {
    if(!window.confirm(`Delete this item permanently?\n\n"${a.title}"${isP?'\n\nUse "Dropped" instead if it is simply no longer needed.':""}`)) return;
    setItems(prev=>prev.filter(x=>x.id!==a.id));
    addAudit(user.id,user.name,"AW_DELETE_ITEM",`Deleted work item: ${a.title}`);
    setDetailId(null);
  };
  const deleteMeeting = m => {
    const linked = items.filter(a=>a.meetingId===m.id);
    if(!window.confirm(`Delete the meeting of ${fmtDate(m.date)}${linked.length?` and its ${linked.length} item${linked.length===1?"":"s"}`:""}? This cannot be undone.`)) return;
    setMeetings(prev=>prev.filter(x=>x.id!==m.id));
    if(linked.length) setItems(prev=>prev.filter(a=>a.meetingId!==m.id));
    addAudit(user.id,user.name,"AW_DELETE_MEETING",`Deleted work meeting of ${m.date} with ${linked.length} item(s)`);
  };

  const detail = detailId ? items.find(a=>a.id===detailId) : null;
  // Delete: admin; a partner who created it; a manager who logged it, while nobody else has touched it.
  const canDeleteItem = a => isAdmin || (a.createdBy===user.id && (isP || (a.history||[]).every(h=>h.byId===user.id)));

  // ── pieces ──
  const sourceLabel = a => {
    const m = meetingOf(a.meetingId);
    const from = (a.fromPartnerIds||[]).map(id=>paFirstName(nameOf(id))).join(", ");
    if(m) return `${fmtDate(m.date)} meeting${a.createdByRole==="manager"?` · logged by ${paFirstName(a.createdByName)}`:""}`;
    return `Given by ${from||"—"}`;
  };
  const Row = ({ a }) => {
    const role = awInvolves(a,user.id) ? "Assigned" : (a.fromPartnerIds||[]).includes(user.id) ? "Given by you" : "";
    return (
      <div className="pa-row">
        <div style={{minWidth:0}}>
          <div className="fw6" style={{fontSize:14,lineHeight:1.35}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
          <div className="fxc g8 mt4" style={{flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:PA_PRIO_COLOR[a.priority]||"var(--slate)"}}>{a.priority||"Medium"}</span>
            <span className="tx tsl">{sourceLabel(a)}</span>
            {role&&<span className="pa-you">You: {role}</span>}
            {(a.reopenCount||0)>0&&<span className="tx" style={{color:"#b45309"}}>Reopened {a.reopenCount}×</span>}
          </div>
        </div>
        <div><AWCatChip cat={a.category}/></div>
        <div>
          {awMgrIds(a).map(id=><div key={id} style={{fontWeight:500,fontSize:13.5,lineHeight:1.35}}>{nameOf(id)}</div>)}
        </div>
        <AWDue a={a}/>
        <div><AWStatusBadge status={a.status}/></div>
        <div style={{minWidth:0}}><PALatestLine a={a} clamp={2}/></div>
        <div>
          {isP&&a.status==="submitted"
            ? <div style={{display:"flex",flexDirection:"column",gap:4}}>
                <button className="btn bsc bxs" style={{justifyContent:"center"}} onClick={()=>accept(a)}>Accept</button>
                <button className="btn bgh bxs" style={{justifyContent:"center"}} onClick={()=>setReopenFor(a)}>Reopen</button>
              </div>
            : <button className="btn bgh bsm" onClick={()=>openDetail(a)}>{awIsOpen(a)?"Update":"View"}</button>}
        </div>
      </div>
    );
  };
  const Table = ({ list, empty }) => (
    <div className="card" style={{padding:0,overflow:"hidden"}}>
      {list.length===0 ? <div className="es"><div className="es-icon"><I n="task" s={36}/></div>{empty}</div> : <>
        <div className="pa-row hd"><div className="pa-hd">Work</div><div className="pa-hd">Category</div><div className="pa-hd">Managers</div><div className="pa-hd">Due</div><div className="pa-hd">Status</div><div className="pa-hd">Latest update</div><div/></div>
        {list.map(a=><Row key={a.id} a={a}/>)}
      </>}
    </div>
  );
  const MeetingCard = ({ m }) => {
    const list = visible.filter(a=>a.meetingId===m.id).sort((a,b)=>awIsOpen(a)!==awIsOpen(b)?(awIsOpen(a)?-1:1):awSort(a,b));
    const closedN = list.filter(a=>!awIsOpen(a)).length;
    const pct = list.length ? Math.round(closedN/list.length*100) : 0;
    const canEdit = isP || m.createdBy===user.id;
    return (
      <div className="card" style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:20}}>
          <div style={{minWidth:0}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:19}}>{m.title}</div>
            <div className="ts tsl mt4">{fmtDate(m.date)} · Partners: {(m.partnerIds||[]).map(nameOf).join(", ")||"—"}{m.createdByRole==="manager"?` · logged by ${m.createdByName}`:""}</div>
            {m.notes&&<div className="ts" style={{color:"#334155",marginTop:8,maxWidth:760,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{m.notes}</div>}
            {canEdit&&<div className="fx g8" style={{marginTop:10}}>
              <button className="btn bgh bxs" onClick={()=>setQuickM({meetingId:m.id})}><I n="plus" s={12}/>Add item</button>
              <button className="btn bgh bxs" onClick={()=>setMeetingM({existing:m})}><I n="edit" s={12}/>Edit meeting</button>
              {(isAdmin||m.createdBy===user.id)&&<button className="btn bgh bxs" style={{color:"var(--red)"}} onClick={()=>deleteMeeting(m)}><I n="trash" s={12}/>Delete</button>}
            </div>}
          </div>
          <div style={{width:200,flexShrink:0,textAlign:"right"}}>
            <div className="ts fw6">{closedN} of {list.length} closed</div>
            <div className="pbw" style={{marginTop:7}}><div className="pbf pbok" style={{width:Math.max(pct,list.length?2:0)+"%"}}/></div>
          </div>
        </div>
        {list.length>0&&<div style={{borderTop:"1px solid var(--border)"}}>
          {list.map(a=>(
            <div key={a.id} className={`pa-mi${isNew(a)?" is-new":""}`} role="button" tabIndex={0} onClick={()=>openDetail(a)} onKeyDown={e=>{if(e.key==="Enter")openDetail(a);}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:500}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
                <PALatestLine a={a}/>
              </div>
              <div><AWCatChip cat={a.category}/></div>
              <div className="ts">{awMgrIds(a).map(id=>paFirstName(nameOf(id))).join(", ")}</div>
              <AWDue a={a}/>
              <div><AWStatusBadge status={a.status}/></div>
            </div>
          ))}
        </div>}
      </div>
    );
  };

  const tabs = isP ? [
    ["accept",`Awaiting acceptance (${awaiting.length})`],
    ["given",`Given by me (${givenByMe.length})`],
    ["bymgr","By manager"],
    ["meet","By meeting"],
    ["all","All"],
  ] : [
    ["mine",`My work (${mine.length})`],
    ["waiting",`Awaiting acceptance (${myAwaiting.length})`],
    ["meet","By meeting"],
    ["closed","Closed"],
  ];
  const mgrGroups = managers.map(m=>({ m, list:openAll.filter(a=>awInvolves(a,m.id)) })).filter(g=>g.list.length);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <style>{PA_CSS}</style>
      <div className="sh" style={{marginBottom:0}}>
        <div>
          <div className="card-title">{isP?"Firm work given to managers, tracked to acceptance":"Work given to you by partners"}</div>
          <div className="card-sub mt4 ts">Staffing, operations, business development, internal events, training and admin. Engagement work stays in timesheets.
            <span style={{marginLeft:10}} className="pa-you"><I n="lock" s={10}/> Partners and managers only</span>
          </div>
        </div>
        <div className="fxc g8">
          <button className="btn bgh" onClick={()=>setQuickM({meetingId:""})}><I n="plus" s={15}/>{isP?"Assign work":"Add item"}</button>
          <button className="btn bp" onClick={()=>setMeetingM({})}><I n="calendar" s={15}/>{isP?"Log meeting":"Log meeting with partners"}</button>
        </div>
      </div>

      <div className="sg" style={{marginBottom:0}}>
        {isP ? <>
          <div className="sc"><div className="sv">{givenByMe.length}</div><div className="sl">Open, given by you</div></div>
          <div className="sc" style={awaiting.length?{borderColor:"#fde68a"}:undefined}><div className="sv" style={{color:awaiting.length?"#b45309":"var(--green)"}}>{awaiting.length}</div><div className="sl">Awaiting acceptance</div></div>
          <div className="sc"><div className="sv" style={{color:firmOverdue?"#b91c1c":"var(--green)"}}>{firmOverdue}</div><div className="sl">Overdue</div></div>
          <div className="sc"><div className="sv">{openAll.length}</div><div className="sl">Firm-wide open</div></div>
        </> : <>
          <div className="sc"><div className="sv">{mine.length}</div><div className="sl">My open</div></div>
          <div className="sc" style={myOverdue?{borderColor:"#fecaca"}:undefined}><div className="sv" style={{color:myOverdue?"#b91c1c":"var(--green)"}}>{myOverdue}</div><div className="sl">My overdue</div></div>
          <div className="sc"><div className="sv" style={{color:myAwaiting.length?"#b45309":"var(--navy)"}}>{myAwaiting.length}</div><div className="sl">Awaiting acceptance</div></div>
          <div className="sc"><div className="sv">{closedList.filter(a=>a.status==="done").length}</div><div className="sl">Accepted</div></div>
        </>}
      </div>

      <div className="tabs" style={{marginBottom:0,alignSelf:"flex-start"}}>
        {tabs.map(([id,label])=><div key={id} className={`tab ${tab===id?"active":""}`} role="button" tabIndex={0} onClick={()=>setTab(id)} onKeyDown={e=>{if(e.key==="Enter")setTab(id);}}>{label}</div>)}
      </div>

      {!loaded&&<div className="al al-i"><I n="info" s={15}/><div>Loading work items...</div></div>}

      {tab==="accept"&&<>
        <div className="ts tsl">Marked done by managers. Any partner can accept or reopen.</div>
        <Table list={awaiting} empty="Nothing waiting for acceptance."/>
      </>}
      {tab==="given"&&<>
        <div className="ts tsl">Open items you gave, overdue first.</div>
        <Table list={givenByMe} empty="Nothing open that you gave."/>
      </>}
      {tab==="mine"&&<>
        <div className="ts tsl">Items you own or support, overdue first.</div>
        <Table list={mine} empty="Nothing open on your plate."/>
      </>}
      {tab==="waiting"&&<>
        <div className="ts tsl">Items you've marked done. A partner will accept or reopen them.</div>
        <Table list={myAwaiting} empty="Nothing waiting for a partner."/>
      </>}
      {tab==="closed"&&<Table list={closedList} empty="Nothing closed yet."/>}

      {tab==="bymgr"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        {mgrGroups.length===0&&<div className="card"><div className="es"><div className="es-icon"><I n="check" s={36}/></div>All clear. Nothing open.</div></div>}
        {mgrGroups.map(({m,list})=>{
          const over = list.filter(awIsOverdue).length;
          const wait = list.filter(a=>a.status==="submitted").length;
          return (
            <div key={m.id} className="card" style={{padding:0,overflow:"hidden"}}>
              <div className="fxc g12" style={{padding:"14px 20px",background:"var(--cream)",borderBottom:"1px solid var(--border)"}}>
                <div style={{width:34,height:34,borderRadius:"50%",background:"#dbeafe",color:"#1e40af",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13}}>{m.name.split(" ").map(x=>x[0]).join("").slice(0,2).toUpperCase()}</div>
                <div className="fw6" style={{fontSize:14.5}}>{m.name}</div>
                <div className="ts tsl">{list.length} open{over?` · ${over} overdue`:""}{wait?` · ${wait} awaiting acceptance`:""}</div>
              </div>
              {list.map(a=>(
                <div key={a.id} className="pa-ri">
                  <div style={{minWidth:0}}>
                    <div className="fw6" style={{fontSize:13.5}}>{a.title}{isNew(a)&&<PANewPill/>}</div>
                    <PALatestLine a={a} clamp={2}/>
                  </div>
                  <div><AWCatChip cat={a.category}/></div>
                  <AWDue a={a}/>
                  <div><AWStatusBadge status={a.status}/></div>
                  <div className="fx g8" style={{justifyContent:"flex-end"}}>
                    {a.status==="submitted"&&<button className="btn bsc bsm" onClick={()=>accept(a)}><I n="check" s={12}/>Accept</button>}
                    {a.status==="submitted"&&<button className="btn bgh bsm" onClick={()=>setReopenFor(a)}>Reopen</button>}
                    {a.status!=="submitted"&&<button className="btn bgh bsm" onClick={()=>openDetail(a)}>Update or new date</button>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>}

      {tab==="meet"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        {meetingsSorted.length===0&&<div className="card"><div className="es"><div className="es-icon"><I n="calendar" s={36}/></div>No meetings logged yet.</div></div>}
        {meetingsSorted.map(m=><MeetingCard key={m.id} m={m}/>)}
      </div>}

      {tab==="all"&&<>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <select className="fs" aria-label="Owner" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fOwner} onChange={e=>setFOwner(e.target.value)}>
            <option value="">All managers</option>{[...new Set([...managers.map(p=>p.id),...items.flatMap(awMgrIds)])].map(id=><option key={id} value={id}>{nameOf(id)}</option>)}
          </select>
          <select className="fs" aria-label="Category" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fCat} onChange={e=>setFCat(e.target.value)}>
            <option value="">All categories</option>{AW_CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <select className="fs" aria-label="Status" style={{width:"auto",fontSize:13,padding:"8px 12px"}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="open">Not yet closed</option><option value="overdue">Overdue only</option><option value="submitted">Awaiting acceptance</option><option value="done">Accepted</option><option value="dropped">Dropped</option><option value="all">All statuses</option>
          </select>
          {(fOwner||fCat||fStatus!=="open")&&<button className="btn bgh bsm" onClick={()=>{setFOwner("");setFCat("");setFStatus("open");}}>✕ Clear</button>}
          <span className="tx tsl" style={{marginLeft:"auto",fontSize:12}}>{allFiltered.length} item{allFiltered.length===1?"":"s"}</span>
        </div>
        <Table list={allFiltered} empty="No items match these filters."/>
      </>}

      {meetingM&&<AWMeetingModal user={user} partners={partners} managers={managers} existing={meetingM.existing} onClose={()=>setMeetingM(null)} onSave={saveMeeting}/>}
      {quickM&&<AWQuickAddModal user={user} partners={partners} managers={managers} meetings={meetingsSorted} defaultMeetingId={quickM.meetingId} onClose={()=>setQuickM(null)} onSave={saveQuick}/>}
      {detail&&<AWDetailDrawer key={detail.id} user={user} a={detail} users={users} partners={partners} managers={managers} meeting={meetingOf(detail.meetingId)}
        canDelete={canDeleteItem(detail)} wasNew={detailNew} onClose={()=>setDetailId(null)} onSave={saveDetail} onDelete={deleteItem}
        onAccept={accept} onReopen={a=>setReopenFor(a)}/>}
      {reopenFor&&<AWReopenModal a={reopenFor} onClose={()=>setReopenFor(null)} onSave={reason=>reopen(reopenFor,reason)}/>}
    </div>
  );
}

// Dashboard card. Managers: their open work. Partners: items awaiting acceptance.
function AWDashboardCard({ user, items=[], onOpen }) {
  if(user.role!=="partner"&&user.role!=="manager") return null;
  const isP = user.role==="partner";
  const list = isP
    ? items.filter(a=>a.status==="submitted").sort((a,b)=>(a.submittedAt||"").localeCompare(b.submittedAt||""))
    : items.filter(a=>awInvolves(a,user.id)&&awIsWorking(a)).sort(awSort);
  if(!list.length) return null;
  const over = isP ? 0 : list.filter(awIsOverdue).length;
  return (
    <div className="card mb22">
      <style>{PA_CSS}</style>
      <div className="fxb mb8">
        <div className="fxc g8">
          <div className="card-title">{isP?"Work awaiting your acceptance":"My assigned work"}</div>
          {over>0&&<span className="pa-chip" style={{background:"#fee2e2",color:"#b91c1c",fontWeight:600}}>{over} overdue</span>}
          <span className="pa-chip" style={{background:"var(--cream)",color:"var(--slate)"}}>{list.length} {isP?"waiting":"open"}</span>
        </div>
        <button className="btn bgh bsm" onClick={onOpen}>{isP?"Review":"View all"} <I n="arrowright" s={13}/></button>
      </div>
      {list.slice(0,5).map(a=>{
        const info = awDueInfo(a);
        return (
          <div key={a.id} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 170px 150px",gap:14,alignItems:"center",padding:"10px 0",borderTop:"1px solid var(--cream)"}}>
            <div style={{fontSize:13.5,fontWeight:500}}>{a.title}{awIsNewFor(a,user)&&<PANewPill/>}</div>
            <div className="tx tsl">{a.category}</div>
            <div style={{fontSize:12.5,fontWeight:600,textAlign:"right",color:isP?"#b45309":info.kind==="ok"?"var(--slate)":PA_DUE_COLOR[info.kind]}}>{isP?`Done by ${paFirstName(a.submittedByName||"")||"manager"}`:info.sub}</div>
          </div>
        );
      })}
      {list.length>5&&<div className="tx tsl mt8">+ {list.length-5} more</div>}
    </div>
  );
}


// ROOT
// ══════════════════════════════════════════════════════════════
export default function App() {
  const [currentUser,setCU]=useState(null);
  const [tab,setTab]       =useState("dashboard");

  // ── Firebase Auth session: data listeners open only once someone is signed in ──
  const [authUser,setAuthUser]=useState(null);
  useEffect(()=>{
    clearLegacyPasswordCache();
    return onAuthStateChanged(auth, u=>setAuthUser(u));
  },[]);
  const signedIn  = !!authUser;
  // Sign Out: finish pending saves, sign out, wipe the browser's copy of firm data, reload.
  const clearLocalDataAndReload = async () => {
    try { await Promise.race([waitForPendingWrites(db), new Promise(r=>setTimeout(r,4000))]); } catch(e) {}
    try { await signOut(auth); } catch(e) {}
    setCU(null);
    try { await terminate(db); await clearIndexedDbPersistence(db); } catch(e) { console.error("Cache clear error", e); }
    window.location.reload();
  };
  const isPartner = !!currentUser && currentUser.role==="partner";
  const isAdminUser = !!currentUser && currentUser.email===ADMIN_EMAIL;

  // ── Load ALL data at root level from Firestore and pass down as props ──
  const [users,   setUsers, usersLoaded] = useLS("users", [], signedIn);
  const [projects,setProjects, projectsLoaded] = useLS("projects", [], signedIn);
  // Timesheets: partners and managers load the full set (approvals, compliance, profitability need it).
  // Interns load only their own entries plus the engagements they work on (v35).
  const isInternUser = !!currentUser && currentUser.role==="intern";
  const internAssignedIds = useMemo(()=> isInternUser
    ? projects.filter(p=>[...(p.assignedStaff||[]),...(p.assignedManagers||[]),...(p.assignedPartners||[])].includes(currentUser.id)).map(p=>p.id).sort()
    : [], [isInternUser, projects, currentUser]);
  const tsScopeKey = isInternUser ? `intern:${currentUser.id}:${internAssignedIds.join(",")}` : "all";
  const tsEnabled  = signedIn && !!currentUser && (!isInternUser || projectsLoaded);
  const [tss,     setTss]      = useLS("timesheets",[], tsEnabled,
    isInternUser ? internTimesheetScope(currentUser.id, internAssignedIds) : null, tsScopeKey);
  const [locked,  setLocked]   = useLS("locked_months",[], signedIn);
  const [audit]                = useLS("audit",    [], isPartner);
  const [leaves,  setLeaves]   = useLS("leaves",   [], signedIn);
  const [appraisals, setAppraisals] = useLS("appraisals", [], signedIn);
  const [goals, setGoals] = useLS("goals", [], signedIn);
  // Partner-only collections
  const [rates, , ratesLoaded] = useLS("rates", [], isPartner);
  const [roles, , rolesLoaded] = useLS("roles", [], isPartner);
  // v37: Partner Actionables (meetings amongst partners and their action items). Partner-only.
  const [paMeetings, setPaMeetings] = useLS("partner_meetings", [], isPartner);
  const [paActions, setPaActions, paActionsLoaded] = useLS("partner_actions", [], isPartner);
  // v49: Assigned Work. Partners load everything; managers only items they own, support or created.
  const isManagerUser = !!currentUser && currentUser.role==="manager";
  const awEnabled = signedIn && (isPartner || isManagerUser);
  const awScope = useMemo(()=> isManagerUser ? ((colName, emit) => {
    const col = collection(db, colName);
    return listenMerged([
      query(col, where("ownerId","==",currentUser.id)),
      query(col, where("supportIds","array-contains",currentUser.id)),
      query(col, where("createdBy","==",currentUser.id)),
    ], emit, colName+" (mine)");
  }) : null, [isManagerUser, currentUser]);
  const [awMeetings, setAwMeetings] = useLS("work_meetings", [], awEnabled);
  // v55: partners' one-on-one discussions. Confidential: partners load all; everyone else only their own records
  // (the Firestore rules refuse anything wider). Passed only to Performance Appraisal, never via db_props.
  const ooEnabled = signedIn && !!currentUser;
  const ooScope = useMemo(()=> currentUser && currentUser.role!=="partner" ? ((colName, emit) =>
    listenMerged([query(collection(db, colName), where("staffId","==",currentUser.id))], emit, colName+" (mine)")
  ) : null, [currentUser]);
  const [oneOnOnes] = useLS("one_on_ones", [], ooEnabled, ooScope, currentUser ? `${currentUser.role}:${currentUser.id}` : "none");
  // v50: show a notice when a save is refused or fails, instead of the screen quietly flipping back
  const [writeErr,setWriteErr] = useState(null);
  useEffect(()=>{
    const h = e => setWriteErr({ ...(e.detail||{}), at:Date.now() });
    window.addEventListener("msna-write-error", h);
    return () => window.removeEventListener("msna-write-error", h);
  },[]);
  const [awItems, setAwItems, awItemsLoaded] = useLS("work_items", [], awEnabled, awScope, isManagerUser?`mgr:${currentUser.id}`:"all");

  // Partners see cost rates merged onto each user (from "rates"); everyone else never gets them.
  const usersView = useMemo(()=>{
    if(!isPartner) return users.map(stripRates);
    return users.map(u=>{
      const r = rates.find(x=>x.id===u.id);
      return r ? { ...u, actualRate:r.actualRate??null, actualRateEffectiveDate:r.actualRateEffectiveDate??null } : u;
    });
  },[users,rates,isPartner]);

  // ── Access list: roles/{email} mirrors every ACTIVE user. Security rules read it to decide who gets in.
  // Kept in sync automatically whenever a partner has the app open. Deactivated or deleted staff drop off it.
  useEffect(()=>{
    if(!isPartner||!usersLoaded||!rolesLoaded) return;
    const me = roleKey(currentUser.email);
    const desired = {};
    users.forEach(u=>{ if(u.active!==false && u.email) desired[roleKey(u.email)] = { role:u.role, userId:u.id }; });
    if(!desired[me]) return; // safety: never touch the list if the signed-in partner isn't on it
    Object.entries(desired).forEach(([email,v])=>{
      const ex = roles.find(r=>r.id===email);
      if(!ex || ex.role!==v.role || ex.userId!==v.userId) fsSet("roles", email, { email, role:v.role, userId:v.userId });
    });
    roles.forEach(r=>{ if(!desired[r.id] && r.id!==me) fsDel("roles", r.id); });
  },[isPartner,usersLoaded,rolesLoaded,users,roles,currentUser]);

  // ── One-time migration (Admin only): move cost rates off user docs into "rates" ──
  // Copies first, then strips. Never overwrites a rate that already exists in "rates".
  const rateMigrationRunning = useRef(false);
  useEffect(()=>{
    if(!isAdminUser||!usersLoaded||!ratesLoaded||rateMigrationRunning.current) return;
    const pending = users.filter(hasRateFields);
    if(!pending.length) return;
    rateMigrationRunning.current = true;
    (async()=>{
      let moved = 0;
      for(const u of pending){
        try {
          if(!rates.some(r=>r.id===u.id)){
            await setDoc(doc(db,"rates",u.id), { actualRate:u.actualRate??null, actualRateEffectiveDate:u.actualRateEffectiveDate??null });
          }
          await setDoc(doc(db,"users",u.id), stripRates(u));
          moved++;
        } catch(e){ console.error("Rate migration error", u.id, e); break; }
      }
      if(moved) addAudit(currentUser.id,currentUser.name,"SECURITY_MIGRATION",`Moved cost rates of ${moved} staff to partner-only storage`);
      rateMigrationRunning.current = false;
    })();
  },[isAdminUser,usersLoaded,ratesLoaded,users,rates,currentUser]);

  const db_props = { users:usersView, setUsers, projects, setProjects, tss, setTss, locked, setLocked, audit, leaves, setLeaves, appraisals, setAppraisals, goals, setGoals };

  // ── Migration: fix existing pending intern entries on projects with no managers ──
  // These entries were stuck — no manager to approve them, not visible to partner either
  useEffect(() => {
    if(!isPartner||!tss.length||!projects.length) return; // v50: partners only (others aren't allowed to write it)
    const toFix = tss.filter(t => {
      if(!["pending","resubmitted"].includes(t.status)) return false;
      const u2 = users.find(u=>u.id===t.userId);
      if(u2?.role!=="intern") return false;
      if(t.isInternal) return false;
      const proj = projects.find(p=>p.id===t.projectId);
      if(!proj) return false;
      const hasManagers = (proj.assignedManagers||[]).length > 0;
      if(hasManagers) return false; // already routed correctly
      return true; // no managers on this project — needs migration flag
    });
    if(toFix.length === 0) return;
    // Mark these entries so partners can see them (no change to status, just flag)
    setTss(prev => prev.map(t => {
      const needsFix = toFix.find(x=>x.id===t.id);
      if(!needsFix) return t;
      if(t.noManagerProject) return t; // already flagged
      return {...t, noManagerProject: true};
    }));
  }, [tss.length, projects.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: exact same logic as Approvals tab pending filter
  const calcPendingCount = (cu) => {
    if(!cu) return 0;
    const isP = cu.role==="partner";
    const myMgrProjIds = !isP ? projects.filter(p=>(p.assignedManagers||[]).includes(cu.id)).map(p=>p.id) : [];

    const reg = tss.filter(t=>{
      const u2=users.find(u=>u.id===t.userId);
      if(!["pending","resubmitted"].includes(t.status)) return false;
      if(isP){
        if(u2?.role!=="manager"&&u2?.role!=="intern") return false;
        if(t.isInternal) return true; // badge count: visible to all partners, same as pending
        const proj = projects.find(px=>px.id===t.projectId);
        if(!proj) return false;
        const isMyProj = proj.assignedPartnerId===cu.id||(proj.assignedPartners||[]).includes(cu.id);
        if(!isMyProj) return false;
        if(u2?.role==="manager") return true;
        if(u2?.role==="intern") return (proj.assignedManagers||[]).length===0;
        return false;
      }
      if(u2?.role!=="intern") return false;
      if(t.isInternal) return (t.internalApprovers||[]).includes(cu.id);
      return myMgrProjIds.includes(t.projectId);
    }).length;

    const onBehalf = isP ? tss.filter(t=>t.status==="pending_partner"&&t.userId===cu.id).length : 0;
    return reg + onBehalf;
  };
  const pendingCount = calcPendingCount(currentUser);

  // Appraisals this user still needs to GIVE, current FY, not yet submitted
  const appraisalPendingCount = currentUser ? (() => {
    const required = getRequiredAppraisals(users, projects, tss);
    return required.filter(r => { if(!r.eligibleAppraisers.includes(currentUser.id)) return false; const ex=findAppraisalFor(appraisals,r); if(ex?.status==="submitted") return false; const per=appraisalPeriod(r,ex); return quarterDateRange(per.fy,per.quarter)[0]<=todayStr(); }).length;
  })() : 0;

  // Goals/self-assessment this user still needs to act on, current FY (partners have no badge — firm-wide would be noisy)
  const goalPendingCount = (currentUser && currentUser.role!=="partner") ? (() => {
    const fy = currentFY(); const today = todayStr(); let cnt = 0;
    GOAL_QUARTER_ORDER.filter(q=>isQuarterInGoalScheme(fy,q)).forEach(q => {
      const [s,e] = quarterDateRange(fy,q);
      if(today<s) return;
      if(goalQuarterExclusion(users.find(x=>x.id===currentUser.id)||currentUser, fy, q, goals)) return;
      const rec = goals.find(g=>g.fy===fy&&g.quarter===q&&g.staffId===currentUser.id);
      if(!rec || rec.goalStatus!=="submitted") { cnt++; return; }
      if(phaseReviewStatus(rec.goals,true)==="resubmission") { cnt++; return; }
      if(goalMissingCount(rec, users.find(x=>x.id===currentUser.id)||currentUser)>0) { cnt++; return; } // v53
      if(today>e) {
        if(rec.assessStatus!=="submitted") { cnt++; return; }
        if(phaseReviewStatus(rec.assess,true)==="resubmission") cnt++;
      }
    });
    return cnt;
  })() : 0;

  const titles={dashboard:"Dashboard",week:"My Week",timesheets:"Timesheets",projects:"Projects",approvals:"Approvals",reports:"Reports",profitability:"Profitability",productivity:"Productivity Dashboard",compliance:"Compliance",leave:"Leave",appraisal:"Performance Appraisal",goals:"Goal Setting",audit:"Audit Trail",users:"User Management",changepassword:"Change Password",partneractions:"Partner Actionables",assignedwork:"Assigned Work"};
  const paOverdueCount = (isPartner && currentUser) ? paMyOverdueCount(paActions, currentUser.id) : 0;

  if(!currentUser) return <><style>{CSS}</style><Login onLogin={u=>{setCU(u);setTab("dashboard");}}/></>;

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <Sidebar user={currentUser} tab={tab} setTab={setTab} onLogout={clearLocalDataAndReload} pendingCount={pendingCount}
          leavePendingCount={leaves.filter(l=>{
            if(currentUser.role==="manager") return l.status==="pending_manager"&&(l.approverManagers||[]).includes(currentUser.id)&&!(l.managerApprovals||[]).includes(currentUser.id);
            if(currentUser.role==="partner") return l.status==="pending_partner"&&(l.approverPartners||[]).includes(currentUser.id)&&!(l.partnerApprovals||[]).includes(currentUser.id);
            return false;
          }).length}
          projPendingCount={currentUser.role==="partner" ? projects.filter(p=>p.status==="pending_approval").length : 0}
          projClosurePendingCount={currentUser.role==="partner" ? projects.filter(p=>p.status==="pending_closure").length : 0}
          appraisalPendingCount={appraisalPendingCount}
          goalPendingCount={goalPendingCount}
          paOverdueCount={paOverdueCount}
          awCount={awBadgeCount(awItems, currentUser)}/>
        <div className="main">
          <div className="topbar">
            <div className="tb-title">{titles[tab]}</div>
            <div className="tb-right">
              {pendingCount>0&&<div className="tb-pill"><I n="bell" s={14}/><strong>{pendingCount}</strong> pending</div>}
              <div className="ts tsl">{fmtDate(todayStr())}</div>
            </div>
          </div>
          <div className="content">
            {writeErr&&(
              <div className="al al-d" style={{alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",gap:8,alignItems:"flex-start"}}><I n="alert" s={16}/><div>
                  <strong>Your last change was not saved.</strong> {writeErr.code==="permission-denied"?"Your access does not allow this change.":"The connection or server refused it."} Refresh the page and try again. If it keeps happening, tell the Admin and mention: <span className="mono">{writeErr.col}{writeErr.code?` · ${writeErr.code}`:""}</span>
                </div></div>
                <button className="btn bgh bsm" onClick={()=>setWriteErr(null)}>Dismiss</button>
              </div>
            )}
            {tab==="dashboard"  &&<Dashboard    user={currentUser} {...db_props} paActions={isPartner?paActions:[]} onOpenActions={()=>setTab("partneractions")} onOpenProjects={()=>setTab("projects")} awItems={awItems} onOpenWork={()=>setTab("assignedwork")}/>}
            {tab==="assignedwork"&&(isPartner||currentUser.role==="manager")&&<AssignedWork user={currentUser} users={usersView} meetings={awMeetings} setMeetings={setAwMeetings} items={awItems} setItems={setAwItems} loaded={awItemsLoaded}/>}
            {tab==="partneractions"&&isPartner&&<PartnerActions user={currentUser} users={usersView} meetings={paMeetings} setMeetings={setPaMeetings} actions={paActions} setActions={setPaActions} loaded={paActionsLoaded}/>}
            {tab==="week"       &&<WeekView     user={currentUser} {...db_props}/>}
            {tab==="timesheets" &&<Timesheets   user={currentUser} {...db_props}/>}
            {tab==="projects"   &&<Projects     user={currentUser} {...db_props}/>}
            {tab==="appraisal" &&<PerformanceAppraisal user={currentUser} {...db_props} oneOnOnes={oneOnOnes}/>}
            {tab==="goals"     &&<GoalSetting user={currentUser} {...db_props}/>}
            {tab==="approvals"  &&["partner","manager"].includes(currentUser.role)&&<Approvals user={currentUser} {...db_props}/>}
            {tab==="reports"    &&currentUser.role==="partner"&&<Reports  user={currentUser} {...db_props} setLocked={setLocked}/>}
            {tab==="profitability"&&currentUser.role==="partner"&&<Profitability {...db_props}/>}
            {tab==="productivity" &&currentUser.role==="partner"&&<ProductivityDashboard user={currentUser} {...db_props}/>}
            {tab==="compliance" &&["partner","manager"].includes(currentUser.role)&&<Compliance user={currentUser} {...db_props}/>}
            {tab==="leave"       &&<Leave user={currentUser} {...db_props} tss={tss}/>}
            {tab==="audit"      &&currentUser.role==="partner"&&<AuditTrail audit={audit}/>}
            {tab==="users"      &&currentUser.role==="partner"&&<UserManagement user={currentUser} {...db_props}/>}
            {tab==="changepassword"&&<ChangePassword user={currentUser} setTab={setTab}/>}
          </div>
        </div>
      </div>
    </>
  );
}
